// Ultimate Card Game (UCG) - Stratos Dreadnought Battlefield Background
//
// Fifth battlefield backdrop (js/engine/maps.js selects between 'human',
// 'sul', 'swamp', 'voidglass', and 'stratos'). Ported from the standalone art
// prototype at sandbox/stratos-map/ into the real renderer's standard mixin
// contract: renderStratosStaticBackground/renderStratosStaticForeground are
// baked once into the offscreen world-space canvases by
// Renderer.ensureStaticBackground() (see renderer.js), while
// renderStratosDynamicBackground/renderStratosDynamicForeground/
// renderStratosForegroundCritters/renderStratosWeatherBackground/
// renderStratosWeather run every frame on top of that cached bake.
//
// The concept: a colossal heavy stratospheric dreadnought cruising at
// Mach 1.2 across colossal alpine mountain ranges at sunset, with a 3D
// perspective arena deck, massive barbette-mounted turrets and radar dish,
// trans-sonic hull slipstream, atmospheric cirrus/thunderstorm weather
// with sheet lightning ("Wetterleuchten") and air ionization crackles,
// and atmospheric pigeon encounters.
// Stratos Map Prototype — Heavy Stratospheric Dreadnought Battlefield (v4.0)
//
// Standalone Canvas 2D sandbox, not wired into the game engine.
// Coordinates strictly mirror js/engine/render/background.js:
//   WIDTH = 1600, HEIGHT = 540, LANE_Y = 176, LANE_H = 320, LANE_BOTTOM = 496.
//
// v4.0 Precision Polish:
// 1. Base-Pads Perspective: True perspective convergence towards SCREEN CENTER (x=800, y=336).
//    West pad and East pad slope inward with authentic trapezoidal convergence.
// 2. High-Detail Asymmetric Arena Floor: Deleted repetitive black vents; built rich irregular
//    composite armor patchwork, diamond-tread access hatches, recessed fiber-optic raceways,
//    scorch marks, welded seam beads, and military fleet decals.
// 3. Iconic Single Radar Dish: Exactly ONE heroic Star Wars dish on a heavy gantry turntable.
// 4. Dense Antenna Forest: Dedicated sensor cluster at x=1180..1320 (lattice masts, dipoles, whip needles, radomes).
// 5. Two Clean 2.5D Heavy Turbolaser Turrets: Dual-barrel artillery emplacements with sloping armored
//    mantlets, elevation sleeves, and muzzle brakes at x=640 and x=960.
// 6. Aerodynamic Hull-Edge Slipstream ("Die Luft spüren"): Transonic condensation vapor sheet,
//    compression shock ribbons, and turbulent vapor curls ripping over the upper hull crest.

const WIDTH = 1600;
const HEIGHT = 540;
const LANE_Y = 176;
const LANE_H = 320;
const LANE_BOTTOM = LANE_Y + LANE_H; // 496
const SHELF_H = HEIGHT - LANE_BOTTOM; // 44

const SUN_DIAMETER = 1200;
const SUN_RADIUS = SUN_DIAMETER / 2; // 600
const W_LOOP = 3200; // Seamless loop period (2x canvas width)


// Deterministic PRNG for stable, reproducible procedural generation
function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Global Sandbox State
const state = {
  seed: 904,
  quality: 'high', // 'low' | 'medium' | 'high'
  animate: true,
  showGuides: false,
  speed: 11,       // Forward flight speed (px/s)
  bobAmp: 5,       // Kinetic bobbing amplitude (px)
  bobFreq: 0.35,   // Kinetic bobbing frequency (rad/s)
  sunY: 246,       // Sun center Y position
  t: 0,
  visibleLeft: 0,
  visibleRight: WIDTH
};

// Atmospheric Weather, Clouds & Sheet Lightning State Machine
const weatherState = {
  mode: 'auto',              // 'auto' | 'clear' | 'cirrus' | 'storm'
  cloudCover: 0.22,          // 0.0 .. 1.0 (density and breadth of cloud layers)
  stormDarkness: 0.0,        // 0.0 .. 1.0 (dark blue nightlight & sun occlusion)
  lightningIntensity: 0.0,   // 0.0 .. 1.0 (active flash envelope)
  flashX: 800,               // Screen X of current/last flash (screen center = 800)
  flashY: 65,                // Screen Y of current/last flash
  flashStartTime: -999,      // Timestamp of current flash
  nextAutoLightningTime: 88, // Timestamp for next auto flash in storm
  nextAutoSparkTime: 5.0,    // Timestamp for next ambient electrostatic spark
  flashSpark1Triggered: false,
  flashSpark2Triggered: false,
  soundEnabled: true,        // Procedural Web Audio thunder enabled
  cycleDuration: 130.0       // Automatic cycle period in seconds
};

function isStormActiveOrApproaching(t) {
  if (weatherState.mode === 'storm') return true;
  if (weatherState.stormDarkness > 0.06) return true;
  if (weatherState.lightningIntensity > 0.01) return true;
  if (weatherState.mode === 'auto') {
    const cTime = ((t % weatherState.cycleDuration) + weatherState.cycleDuration) % weatherState.cycleDuration;
    // At cTime >= 65, storm clouds gather and thunderheads roll in
    if (cTime >= 65.0 && cTime < 118.0) return true;
  }
  return false;
}

function qualityScale() {
  if (state.quality === 'low') return 0;
  if (state.quality === 'medium') return 0.5;
  return 1;
}

// ---------------------------------------------------------------------------
// REAL ALPINE MASSIFS ENGINE (SEAMLESS INFINITE LOOP)
// True Alpine Topography: Jagged Arêtes, Couloirs, Snowfields, Strata & Scree
// ---------------------------------------------------------------------------

let farMassifs = [];
let midMassifs = [];
let nearMassifs = [];

function buildAlpineMassifData(rng, baseX, apexY, width, height, tier, archetype) {
  if (!archetype) archetype = 'pyramid';
  const halfW = width * 0.5;
  const isFar = (tier === 'far');
  const isNear = (tier === 'near');
  const numSteps = isFar ? 8 : (isNear ? 14 : 12);

  const leftRidge = [];
  const rightRidge = [];
  const spineRidge = [];
  let plateauTop = null;
  let colRidge = null;

  if (archetype === 'plateau') {
    // 1. Plateau Archetype (Tafelberg / Sella / Pelmo): Flat summit plateau, steep 80° stepped lateral cliffs
    const platW = width * (0.28 + rng() * 0.16);
    const platHalf = platW * 0.5;
    const cliffDepthFrac = 0.40 + rng() * 0.14;
    const cliffY = height * cliffDepthFrac;

    // Left Ridge: table top corner -> sheer vertical drop -> scree flare
    leftRidge.push({ x: -platHalf, y: 0 });
    const cliffSteps = Math.floor(numSteps * 0.5);
    for (let s = 1; s <= cliffSteps; s++) {
      const f = s / cliffSteps;
      const jx = (rng() - 0.5) * (width * 0.02);
      leftRidge.push({
        x: -platHalf - (width * 0.04 * f) + jx,
        y: cliffY * f + (rng() - 0.5) * 3
      });
    }
    const skirtSteps = numSteps - cliffSteps;
    const cliffEndL = leftRidge[leftRidge.length - 1];
    for (let s = 1; s <= skirtSteps; s++) {
      const f = s / skirtSteps;
      const jx = (rng() - 0.5) * (width * 0.05);
      leftRidge.push({
        x: cliffEndL.x + (-halfW - cliffEndL.x) * f + jx,
        y: cliffY + (height - cliffY) * f + (rng() - 0.5) * 4
      });
    }

    // Right Ridge: table top corner -> sheer vertical drop -> scree flare
    rightRidge.push({ x: platHalf, y: 0 });
    for (let s = 1; s <= cliffSteps; s++) {
      const f = s / cliffSteps;
      const jx = (rng() - 0.5) * (width * 0.02);
      rightRidge.push({
        x: platHalf + (width * 0.04 * f) + jx,
        y: cliffY * f + (rng() - 0.5) * 3
      });
    }
    const cliffEndR = rightRidge[rightRidge.length - 1];
    for (let s = 1; s <= skirtSteps; s++) {
      const f = s / skirtSteps;
      const jx = (rng() - 0.5) * (width * 0.05);
      rightRidge.push({
        x: cliffEndR.x + (halfW - cliffEndR.x) * f + jx,
        y: cliffY + (height - cliffY) * f + (rng() - 0.5) * 4
      });
    }

    // Spine Ridge: center front face
    spineRidge.push({ x: 0, y: 0 });
    const spineBias = (rng() - 0.5) * (width * 0.12);
    for (let s = 1; s <= numSteps; s++) {
      const f = s / numSteps;
      spineRidge.push({
        x: spineBias * f + (rng() - 0.5) * (width * 0.04),
        y: height * f + (rng() - 0.5) * 4
      });
    }

    // Plateau Top facet (perspective foreshortened roof)
    plateauTop = [
      { x: -platHalf, y: 0 },
      { x: platHalf, y: 0 },
      { x: platHalf * 0.82, y: Math.min(10, height * 0.07) },
      { x: -platHalf * 0.82, y: Math.min(10, height * 0.07) }
    ];
  } else if (archetype === 'twin_peak') {
    // 2. Twin Peak Archetype (Tre Cime / Grossglockner / Zwillingsgipfel): Dual horns with a deep central col/saddle
    const colDepthFrac = 0.22 + rng() * 0.10;
    const peakL = { x: -width * (0.13 + rng() * 0.07), y: 0 };
    const peakR = { x: width * (0.13 + rng() * 0.07), y: height * (0.05 + rng() * 0.08) };
    const col = { x: (peakL.x + peakR.x) * 0.45, y: height * colDepthFrac };
    colRidge = [peakL, col, peakR];

    // Left ridge descends from left peak
    leftRidge.push(peakL);
    for (let s = 1; s <= numSteps; s++) {
      const f = s / numSteps;
      leftRidge.push({
        x: peakL.x + (-halfW - peakL.x) * f + (rng() - 0.5) * (width * 0.05),
        y: height * f + (rng() - 0.5) * 4
      });
    }

    // Right ridge descends from right peak
    rightRidge.push(peakR);
    for (let s = 1; s <= numSteps; s++) {
      const f = s / numSteps;
      rightRidge.push({
        x: peakR.x + (halfW - peakR.x) * f + (rng() - 0.5) * (width * 0.05),
        y: peakR.y + (height - peakR.y) * f + (rng() - 0.5) * 4
      });
    }

    // Spine descends from col between the peaks
    spineRidge.push(col);
    for (let s = 1; s <= numSteps; s++) {
      const f = s / numSteps;
      spineRidge.push({
        x: col.x + (rng() - 0.5) * (width * 0.15) * f,
        y: col.y + (height - col.y) * f + (rng() - 0.5) * 4
      });
    }
  } else if (archetype === 'asymmetric_sheer') {
    // 3. Asymmetric Sheer Archetype (Eiger-Nordwand / Civetta): Sheer 85° vertical cliff drop on one side, long gentle arête on the other
    const sheerLeft = rng() < 0.5;
    const summitX = sheerLeft ? -width * (0.18 + rng() * 0.12) : width * (0.18 + rng() * 0.12);
    const summit = { x: summitX, y: 0 };

    if (sheerLeft) {
      leftRidge.push(summit);
      const cliffSteps = Math.floor(numSteps * 0.65);
      for (let s = 1; s <= cliffSteps; s++) {
        const f = s / cliffSteps;
        leftRidge.push({
          x: summit.x - width * 0.035 * f + (rng() - 0.5) * (width * 0.02),
          y: height * 0.70 * f + (rng() - 0.5) * 3
        });
      }
      const skirtSteps = numSteps - cliffSteps;
      const endCliff = leftRidge[leftRidge.length - 1];
      for (let s = 1; s <= skirtSteps; s++) {
        const f = s / skirtSteps;
        leftRidge.push({
          x: endCliff.x + (-halfW - endCliff.x) * f + (rng() - 0.5) * (width * 0.05),
          y: height * 0.70 + (height * 0.30) * f + (rng() - 0.5) * 4
        });
      }

      rightRidge.push(summit);
      for (let s = 1; s <= numSteps; s++) {
        const f = s / numSteps;
        rightRidge.push({
          x: summit.x + (halfW - summit.x) * f + (rng() - 0.5) * (width * 0.06),
          y: height * f + (rng() - 0.5) * 4
        });
      }
    } else {
      rightRidge.push(summit);
      const cliffSteps = Math.floor(numSteps * 0.65);
      for (let s = 1; s <= cliffSteps; s++) {
        const f = s / cliffSteps;
        rightRidge.push({
          x: summit.x + width * 0.035 * f + (rng() - 0.5) * (width * 0.02),
          y: height * 0.70 * f + (rng() - 0.5) * 3
        });
      }
      const skirtSteps = numSteps - cliffSteps;
      const endCliff = rightRidge[rightRidge.length - 1];
      for (let s = 1; s <= skirtSteps; s++) {
        const f = s / skirtSteps;
        rightRidge.push({
          x: endCliff.x + (halfW - endCliff.x) * f + (rng() - 0.5) * (width * 0.05),
          y: height * 0.70 + (height * 0.30) * f + (rng() - 0.5) * 4
        });
      }

      leftRidge.push(summit);
      for (let s = 1; s <= numSteps; s++) {
        const f = s / numSteps;
        leftRidge.push({
          x: summit.x + (-halfW - summit.x) * f + (rng() - 0.5) * (width * 0.06),
          y: height * f + (rng() - 0.5) * 4
        });
      }
    }

    spineRidge.push(summit);
    for (let s = 1; s <= numSteps; s++) {
      const f = s / numSteps;
      spineRidge.push({
        x: summit.x * (1 - f) + (rng() - 0.5) * (width * 0.1),
        y: height * f + (rng() - 0.5) * 4
      });
    }
  } else if (archetype === 'serrated_ridge') {
    // 4. Serrated Ridge Archetype (Sägezahn / Aiguilles): Multi-toothed jagged crest with distinct pinnacle teeth
    const crestW = width * 0.30;
    const summit = { x: 0, y: 0 };

    leftRidge.push(summit);
    const notchY1 = height * 0.08;
    const notchX1 = -crestW * 0.45;
    leftRidge.push({ x: notchX1 * 0.5, y: notchY1 * 0.5 });
    leftRidge.push({ x: notchX1, y: notchY1 });
    leftRidge.push({ x: notchX1 * 1.2, y: notchY1 * 0.7 });
    for (let s = 1; s <= numSteps; s++) {
      const f = s / numSteps;
      leftRidge.push({
        x: notchX1 * 1.2 + (-halfW - notchX1 * 1.2) * f + (rng() - 0.5) * (width * 0.05),
        y: notchY1 * 0.7 + (height - notchY1 * 0.7) * f + (rng() - 0.5) * 4
      });
    }

    rightRidge.push(summit);
    const notchYR = height * 0.09;
    const notchXR = crestW * 0.48;
    rightRidge.push({ x: notchXR * 0.5, y: notchYR * 0.6 });
    rightRidge.push({ x: notchXR, y: notchYR });
    rightRidge.push({ x: notchXR * 1.2, y: notchYR * 0.8 });
    for (let s = 1; s <= numSteps; s++) {
      const f = s / numSteps;
      rightRidge.push({
        x: notchXR * 1.2 + (halfW - notchXR * 1.2) * f + (rng() - 0.5) * (width * 0.05),
        y: notchYR * 0.8 + (height - notchYR * 0.8) * f + (rng() - 0.5) * 4
      });
    }

    spineRidge.push(summit);
    for (let s = 1; s <= numSteps; s++) {
      const f = s / numSteps;
      spineRidge.push({
        x: (rng() - 0.5) * (width * 0.15) * f,
        y: height * f + (rng() - 0.5) * 4
      });
    }
  } else if (archetype === 'dome') {
    // 5. Dome Archetype (Mont Blanc / Breithorn): Monumental rounded firn domes (convex arching crests)
    const domeW = width * 0.22;
    leftRidge.push({ x: -domeW * 0.5, y: height * 0.02 });
    for (let s = 1; s <= numSteps; s++) {
      const f = s / numSteps;
      const archF = Math.sin(f * Math.PI * 0.5);
      leftRidge.push({
        x: -domeW * 0.5 + (-halfW + domeW * 0.5) * archF + (rng() - 0.5) * (width * 0.04),
        y: height * (f * 0.95 + 0.05) + (rng() - 0.5) * 3
      });
    }

    rightRidge.push({ x: domeW * 0.5, y: height * 0.02 });
    for (let s = 1; s <= numSteps; s++) {
      const f = s / numSteps;
      const archF = Math.sin(f * Math.PI * 0.5);
      rightRidge.push({
        x: domeW * 0.5 + (halfW - domeW * 0.5) * archF + (rng() - 0.5) * (width * 0.04),
        y: height * (f * 0.95 + 0.05) + (rng() - 0.5) * 3
      });
    }

    spineRidge.push({ x: 0, y: 0 });
    for (let s = 1; s <= numSteps; s++) {
      const f = s / numSteps;
      spineRidge.push({
        x: (rng() - 0.5) * (width * 0.08) * f,
        y: height * f + (rng() - 0.5) * 3
      });
    }
  } else {
    // 6. Pyramid Archetype (Matterhorn / Finsteraarhorn): Classic sharp 3/4-sided horn
    const apexOffset = (rng() - 0.5) * (width * 0.14);
    leftRidge.push({ x: apexOffset, y: 0 });
    for (let s = 1; s <= numSteps; s++) {
      const f = s / numSteps;
      leftRidge.push({
        x: apexOffset + (-halfW - apexOffset) * f + (rng() - 0.5) * (width * 0.06),
        y: height * f + (rng() - 0.5) * 4
      });
    }

    rightRidge.push({ x: apexOffset, y: 0 });
    for (let s = 1; s <= numSteps; s++) {
      const f = s / numSteps;
      rightRidge.push({
        x: apexOffset + (halfW - apexOffset) * f + (rng() - 0.5) * (width * 0.06),
        y: height * f + (rng() - 0.5) * 4
      });
    }

    spineRidge.push({ x: apexOffset, y: 0 });
    const spineBias = (rng() - 0.5) * (width * 0.25);
    for (let s = 1; s <= numSteps; s++) {
      const f = s / numSteps;
      spineRidge.push({
        x: apexOffset + spineBias * f + (rng() - 0.5) * (width * 0.05),
        y: height * f + (rng() - 0.5) * 4
      });
    }
  }

  // Secondary structural ribs: Left Spur & Right Spur (branching from spine at shoulder)
  const shoulderLIdx = Math.floor(spineRidge.length * 0.38);
  const shoulderRIdx = Math.floor(spineRidge.length * 0.38);

  const leftSpur = [];
  const startSpurL = spineRidge[shoulderLIdx];
  leftSpur.push({ x: startSpurL.x, y: startSpurL.y });
  const spurLBaseX = -halfW * (0.35 + rng() * 0.25);
  const spurStepsL = spineRidge.length - shoulderLIdx;
  for (let s = 1; s <= spurStepsL; s++) {
    const frac = s / spurStepsL;
    const px = startSpurL.x + (spurLBaseX - startSpurL.x) * frac + (rng() - 0.5) * (width * 0.04);
    const py = startSpurL.y + (height - startSpurL.y) * frac + (rng() - 0.5) * (height * 0.04);
    leftSpur.push({ x: px, y: py });
  }

  const rightSpur = [];
  const startSpurR = spineRidge[shoulderRIdx];
  rightSpur.push({ x: startSpurR.x, y: startSpurR.y });
  const spurRBaseX = halfW * (0.35 + rng() * 0.25);
  const spurStepsR = spineRidge.length - shoulderRIdx;
  for (let s = 1; s <= spurStepsR; s++) {
    const frac = s / spurStepsR;
    const qx = startSpurR.x + (spurRBaseX - startSpurR.x) * frac + (rng() - 0.5) * (width * 0.04);
    const qy = startSpurR.y + (height - startSpurR.y) * frac + (rng() - 0.5) * (height * 0.04);
    rightSpur.push({ x: qx, y: qy });
  }

  // Interlocking 4 Planar Facets (Perspektivische 3D-Flächen & Täler):
  // Facet 1: Outer Left Cirque / Kar-Wand (Nx = -0.75, Valley Trough)
  const facetLeftKar = [];
  for (let i = 0; i < leftRidge.length; i++) facetLeftKar.push(leftRidge[i]);
  for (let i = leftSpur.length - 1; i >= 0; i--) facetLeftKar.push(leftSpur[i]);
  for (let i = shoulderLIdx; i >= 0; i--) facetLeftKar.push(spineRidge[i]);

  // Facet 2: Inner Left Peak Face (Nx = -0.30, Main Left Sloped Face)
  const facetLeftFace = [];
  for (let i = 0; i < leftSpur.length; i++) facetLeftFace.push(leftSpur[i]);
  for (let i = spineRidge.length - 1; i >= shoulderLIdx; i--) facetLeftFace.push(spineRidge[i]);

  // Facet 3: Inner Right Peak Face (Nx = +0.30, Main Right Sloped Face)
  const facetRightFace = [];
  for (let i = shoulderRIdx; i < spineRidge.length; i++) facetRightFace.push(spineRidge[i]);
  for (let i = rightSpur.length - 1; i >= 0; i--) facetRightFace.push(rightSpur[i]);

  // Facet 4: Outer Right Cirque / Kar-Wand (Nx = +0.75, Valley Trough)
  const facetRightKar = [];
  for (let i = 0; i <= shoulderRIdx; i++) facetRightKar.push(spineRidge[i]);
  for (let i = 0; i < rightSpur.length; i++) facetRightKar.push(rightSpur[i]);
  for (let i = rightRidge.length - 1; i >= 0; i--) facetRightKar.push(rightRidge[i]);

  // 6. Tier-Specific Geological Texturing & Details:
  // A. Tier 'far': Sweeping Glacial Firn Sheets & Glaciers in the Cirques
  const glaciers = [];
  if (isFar) {
    const numGlaciers = 2;
    for (let g = 0; g < numGlaciers; g++) {
      const isLeft = (g === 0);
      const gy = height * (0.32 + rng() * 0.28);
      const gx = (isLeft ? -1 : 1) * (halfW * (gy / height) * 0.48);
      const gw = 45 + rng() * 40;
      const gh = 18 + rng() * 20;
      glaciers.push({ x: gx, y: gy, w: gw, h: gh, nx: isLeft ? -0.65 : +0.65 });
    }
  }

  // B. Tier 'mid': Erosional Couloirs (Talschluchten) & Nestled Firn Ribbons
  const couloirs = [];
  if (tier === 'mid') {
    const numCouloirs = 3 + Math.floor(rng() * 3);
    for (let c = 0; c < numCouloirs; c++) {
      const startFrac = 0.20 + rng() * 0.30;
      const isLeft = (c % 2 === 0);
      const sideSign = isLeft ? -1 : 1;
      const topX = sideSign * (halfW * startFrac * 0.45) + (rng() - 0.5) * 12;
      const topY = height * startFrac;
      const botX = sideSign * (halfW * (startFrac + 0.38)) + (rng() - 0.5) * 18;
      const botY = Math.min(height, topY + height * 0.45);
      const hasSnow = rng() < 0.65;
      couloirs.push({
        topX, topY, botX, botY,
        width: 2.2 + rng() * 3.2,
        hasSnow,
        nx: isLeft ? -0.5 : +0.5
      });
    }
  }

  // C. Tier 'near': Tilted Geological Strata (Felsbänder) & Scree Slopes (Schutthalden), NO snow
  const strataLines = [];
  const screeFans = [];
  if (isNear) {
    const strataAngle = -0.25 + (rng() - 0.5) * 0.16;
    for (let st = 1; st <= 5; st++) {
      strataLines.push({
        fracY: 0.18 + st * 0.15,
        angle: strataAngle,
        opacity: 0.22 + rng() * 0.28
      });
    }

    // Basal Scree Cones (Schuttfächer along the canyon floor)
    const numFans = 3 + Math.floor(rng() * 2);
    for (let f = 0; f < numFans; f++) {
      const fx = (rng() - 0.5) * (halfW * 1.4);
      const fTopY = height * (0.65 + rng() * 0.15);
      const fSpread = 25 + rng() * 35;
      screeFans.push({ x: fx, topY: fTopY, spread: fSpread });
    }
  }

  return {
    baseX,
    apexY,
    width,
    height,
    baseY: apexY + height,
    tier,
    archetype,
    plateauTop,
    colRidge,
    leftRidge,
    rightRidge,
    spineRidge,
    leftSpur,
    rightSpur,
    facetLeftKar,
    facetLeftFace,
    facetRightFace,
    facetRightKar,
    glaciers,
    couloirs,
    strataLines,
    screeFans
  };
}

function generateAlpineMassifs(seed) {
  const rng = mulberry32(seed);

  // Diverse Geological Archetypes per Tier to completely eliminate "Zuckerhut" repetition:
  // Tier 1: Distant 4000m+ Titans (Rounded Firn Domes, Massive Glacial Plateaus, Dual Horns, Pyramids)
  const farArchetypes = [
    'dome', 'plateau', 'twin_peak', 'dome',
    'pyramid', 'plateau', 'twin_peak', 'dome',
    'plateau', 'pyramid', 'dome', 'twin_peak',
    'plateau', 'dome', 'twin_peak', 'pyramid'
  ];

  // Tier 2: Mid-Range Horns & Massifs (Twin Horns with deep cols, Sheer Asymmetric Faces, Sawtooth Ridges, Sharp Matterhorn Horns)
  const midArchetypes = [
    'twin_peak', 'asymmetric_sheer', 'pyramid', 'serrated_ridge',
    'twin_peak', 'asymmetric_sheer', 'serrated_ridge', 'pyramid',
    'twin_peak', 'asymmetric_sheer', 'serrated_ridge', 'pyramid',
    'twin_peak', 'asymmetric_sheer', 'pyramid', 'serrated_ridge',
    'twin_peak', 'asymmetric_sheer', 'serrated_ridge', 'pyramid',
    'twin_peak', 'asymmetric_sheer', 'serrated_ridge', 'pyramid'
  ];

  // Tier 3: Near Canyon Crags (Stepped Mesa Plateaus, Sheer Monolith Cliffs, Jagged Needle Teeth, Pyramids)
  const nearArchetypes = [
    'plateau', 'asymmetric_sheer', 'serrated_ridge', 'plateau',
    'asymmetric_sheer', 'pyramid', 'plateau', 'asymmetric_sheer',
    'serrated_ridge', 'plateau', 'asymmetric_sheer', 'pyramid',
    'plateau', 'asymmetric_sheer', 'serrated_ridge', 'plateau',
    'asymmetric_sheer', 'pyramid', 'plateau', 'asymmetric_sheer',
    'serrated_ridge', 'plateau', 'asymmetric_sheer', 'pyramid',
    'plateau', 'asymmetric_sheer', 'serrated_ridge', 'pyramid'
  ];

  // Layer 1: Distant 4000m+ Colossal Alpine Titans (Mont Blanc / Monte Rosa scale)
  farMassifs = [];
  const numFar = 16;
  const farSpacing = W_LOOP / numFar;
  for (let i = 0; i < numFar; i++) {
    const baseX = i * farSpacing + (rng() - 0.5) * (farSpacing * 0.45);
    const apexY = 18 + rng() * 52; // Immense summits (y = 18..70)
    const height = 275 - apexY;
    const width = 310 + rng() * 160;
    const arch = farArchetypes[i % farArchetypes.length];
    farMassifs.push(buildAlpineMassifData(rng, baseX, apexY, width, height, 'far', arch));
  }

  // Layer 2: Mid-Range Sharp Horns & Massifs (Matterhorn / Dolomite Spire Chain)
  midMassifs = [];
  const numMid = 24;
  const midSpacing = W_LOOP / numMid;
  for (let i = 0; i < numMid; i++) {
    const baseX = i * midSpacing + (rng() - 0.5) * (midSpacing * 0.4);
    const apexY = 38 + rng() * 65; // Razor sharp horns (y = 38..103)
    const height = 285 - apexY;
    const width = 185 + rng() * 115;
    const arch = midArchetypes[i % midArchetypes.length];
    midMassifs.push(buildAlpineMassifData(rng, baseX, apexY, width, height, 'mid', arch));
  }

  // Layer 3: Near Canyon Crags & Felsbastionen (Immediate Canyon Walls)
  nearMassifs = [];
  const numNear = 28;
  const nearSpacing = W_LOOP / numNear;
  for (let i = 0; i < numNear; i++) {
    const baseX = i * nearSpacing + (rng() - 0.5) * (nearSpacing * 0.35);
    const apexY = 65 + rng() * 75; // Close rugged rock needles (y = 65..140)
    const height = 295 - apexY;
    const width = 125 + rng() * 95;
    const arch = nearArchetypes[i % nearArchetypes.length];
    nearMassifs.push(buildAlpineMassifData(rng, baseX, apexY, width, height, 'near', arch));
  }
}

// ---------------------------------------------------------------------------
// FOREGROUND SLIPSTREAM PARTICLES
// ---------------------------------------------------------------------------

const MAX_PARTICLES = 36;
const particles = [];

// Dynamic Vorderkante Welding & Electrical Sparks System
const MAX_SPARKS = 36;
const sparks = [];

function initSparks() {
  sparks.length = 0;
  for (let i = 0; i < MAX_SPARKS; i++) {
    sparks.push({
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
      life: 0,
      maxLife: 1,
      size: 1.5,
      emitter: (i % 2 === 0 ? 'west' : 'east')
    });
  }
}

function updateSparks(dt, t) {
  // Intermittent bursts for realistic industrial welding / electrical discharge
  const isBurstWest = Math.sin(t * 3.2) > -0.15;
  const isBurstEast = Math.cos(t * 2.7) > -0.05;

  for (let i = 0; i < sparks.length; i++) {
    const s = sparks[i];
    if (s.life > 0) {
      s.life -= dt;
      s.x += s.vx * dt;
      s.y += s.vy * dt;
      s.vy += 260 * dt; // gravity pulling sparks downward over the hull
      s.vx -= 18 * dt;  // stratospheric headwind drag
    } else {
      const canSpawn = (s.emitter === 'west' && isBurstWest) || (s.emitter === 'east' && isBurstEast);
      if (canSpawn && Math.random() < 0.28) {
        s.life = 0.25 + Math.random() * 0.40;
        s.maxLife = s.life;
        if (s.emitter === 'west') {
          s.x = 138 + (Math.random() - 0.5) * 6;
          s.y = 480 + (Math.random() - 0.5) * 4;
          s.vx = (Math.random() - 0.35) * 70;
          s.vy = -Math.random() * 65 - 15;
        } else {
          s.x = 945 + (Math.random() - 0.5) * 6;
          s.y = 512 + (Math.random() - 0.5) * 4;
          s.vx = (Math.random() - 0.45) * 75;
          s.vy = -Math.random() * 70 - 20;
        }
        s.size = 1.0 + Math.random() * 1.5;
      }
    }
  }
}

function initParticles() {
  particles.length = 0;
  for (let i = 0; i < MAX_PARTICLES; i++) {
    particles.push({
      x: Math.random() * WIDTH,
      y: 180 + Math.random() * 320,
      len: 20 + Math.random() * 55,
      speed: 40 + Math.random() * 35,
      alpha: 0.12 + Math.random() * 0.28,
      width: 0.75 + Math.random() * 1.5,
      type: Math.random() < 0.2 ? 'amber_spark' : 'vapor'
    });
  }
  initSparks();
}

function updateParticles(dt) {
  const minX = (state.visibleLeft !== undefined ? state.visibleLeft : 0);
  const maxX = (state.visibleRight !== undefined ? state.visibleRight : WIDTH);
  for (let i = 0; i < particles.length; i++) {
    const p = particles[i];
    p.x -= p.speed * dt;
    if (p.x < minX - p.len) {
      p.x = maxX + Math.random() * 60;
      p.y = 180 + Math.random() * 320;
      p.alpha = 0.12 + Math.random() * 0.28;
    }
  }
  updateSparks(dt, state.t);
}

// ---------------------------------------------------------------------------
// RENDERING: SKY & GIANT SUNSET (Colossal Background Vista)
// ---------------------------------------------------------------------------

let _staticSkyCanvas = null;
function getStaticSkyCanvas() {
  if (!_staticSkyCanvas && typeof document !== 'undefined') {
    _staticSkyCanvas = document.createElement('canvas');
    _staticSkyCanvas.width = WIDTH;
    _staticSkyCanvas.height = 280;
    const sctx = _staticSkyCanvas.getContext('2d');

    const skyGrad = sctx.createLinearGradient(0, 0, 0, 260);
    skyGrad.addColorStop(0.00, '#040206');
    skyGrad.addColorStop(0.22, '#180620');
    skyGrad.addColorStop(0.48, '#460617');
    skyGrad.addColorStop(0.70, '#830e2c');
    skyGrad.addColorStop(0.88, '#be1818');
    skyGrad.addColorStop(1.00, '#ea1e4d');

    sctx.fillStyle = skyGrad;
    sctx.fillRect(0, 0, WIDTH, 280);

    const hazeGrad = sctx.createLinearGradient(0, 130, 0, 250);
    hazeGrad.addColorStop(0.0, 'rgba(244, 63, 94, 0)');
    hazeGrad.addColorStop(0.5, 'rgba(251, 146, 60, 0.22)');
    hazeGrad.addColorStop(1.0, 'rgba(254, 215, 170, 0.08)');
    sctx.fillStyle = hazeGrad;
    sctx.fillRect(0, 130, WIDTH, 120);
  }
  return _staticSkyCanvas;
}

function drawSky(ctx, visibleLeft = 0, visibleRight = WIDTH, visibleTop = 0) {
  const bgW = visibleRight - visibleLeft;
  // Deep space / stratospheric zenith fill (y = visibleTop .. 280)
  ctx.fillStyle = '#040206';
  ctx.fillRect(visibleLeft, visibleTop, bgW, 280 - visibleTop);

  const skyCanvas = getStaticSkyCanvas();
  if (skyCanvas) {
    const sx = Math.max(0, Math.min(WIDTH, visibleLeft));
    const sw = Math.min(bgW, WIDTH - sx);
    const sy = Math.max(0, Math.min(280, visibleTop));
    const sh = Math.min(280 - sy, 280);
    if (sw > 0 && sh > 0) {
      ctx.drawImage(skyCanvas, sx, sy, sw, sh, sx, sy, sw, sh);
    }
    return;
  }

  const skyGrad = ctx.createLinearGradient(0, Math.min(0, visibleTop), 0, 260);
  skyGrad.addColorStop(0.00, '#040206');
  skyGrad.addColorStop(0.22, '#180620');
  skyGrad.addColorStop(0.48, '#460617');
  skyGrad.addColorStop(0.70, '#830e2c');
  skyGrad.addColorStop(0.88, '#be1818');
  skyGrad.addColorStop(1.00, '#ea1e4d');
  ctx.fillStyle = skyGrad;
  ctx.fillRect(visibleLeft, Math.min(0, visibleTop), bgW, 260 - Math.min(0, visibleTop));

  const hazeGrad = ctx.createLinearGradient(0, 130, 0, 250);
  hazeGrad.addColorStop(0.0, 'rgba(244, 63, 94, 0)');
  hazeGrad.addColorStop(0.5, 'rgba(251, 146, 60, 0.22)');
  hazeGrad.addColorStop(1.0, 'rgba(254, 215, 170, 0.08)');
  ctx.fillStyle = hazeGrad;
  ctx.fillRect(visibleLeft, 130, bgW, 120);
}

// ---------------------------------------------------------------------------
// GIANT ASTRONOMICAL SUN (Layer 1)
// With Animated Long-Wave Atmospheric Thermal Refraction Undulations,
// Chromatic Limb Diffusion (Brechungssaum) & Stratified Inversion Mirage Ribbons
// ---------------------------------------------------------------------------

const _sunPtsX = new Float32Array(161);
const _sunPtsY = new Float32Array(161);
let _lastSunPtsT = -99999;

const SUN_MIRAGE_BANDS = Object.freeze([
  { y: -75, h: 4, alpha: 0.16 },
  { y: -50, h: 6, alpha: 0.18 },
  { y: -22, h: 8, alpha: 0.20 },
  { y: 8,   h: 11, alpha: 0.22 },
  { y: 32,  h: 14, alpha: 0.24 }
]);

let _sunCoronaGrad = null;
let _sunDiscGrad = null;
let _sunGlareGrad = null;

function ensureSunGradients(ctx) {
  if (!_sunCoronaGrad) {
    _sunCoronaGrad = ctx.createRadialGradient(0, 0, SUN_RADIUS * 0.15, 0, 0, SUN_RADIUS * 1.18);
    _sunCoronaGrad.addColorStop(0.00, 'rgba(254, 240, 138, 0.55)');
    _sunCoronaGrad.addColorStop(0.35, 'rgba(249, 115, 22, 0.35)');
    _sunCoronaGrad.addColorStop(0.65, 'rgba(225, 29, 72, 0.22)');
    _sunCoronaGrad.addColorStop(0.90, 'rgba(136, 19, 55, 0.08)');
    _sunCoronaGrad.addColorStop(1.00, 'rgba(0, 0, 0, 0)');

    _sunDiscGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, SUN_RADIUS);
    _sunDiscGrad.addColorStop(0.00, '#fffef2'); // White-hot incandescent solar center
    _sunDiscGrad.addColorStop(0.12, '#fef08a');
    _sunDiscGrad.addColorStop(0.32, '#fb923c');
    _sunDiscGrad.addColorStop(0.58, '#ea580c');
    _sunDiscGrad.addColorStop(0.80, '#dc2626');
    _sunDiscGrad.addColorStop(0.96, '#881337');
    _sunDiscGrad.addColorStop(1.00, '#4c0519'); // Deep crimson solar limb

    _sunGlareGrad = ctx.createLinearGradient(0, state.sunY - 35, 0, state.sunY + 35);
    _sunGlareGrad.addColorStop(0.0, 'rgba(255, 237, 213, 0)');
    _sunGlareGrad.addColorStop(0.5, 'rgba(255, 237, 213, 0.24)');
    _sunGlareGrad.addColorStop(1.0, 'rgba(255, 237, 213, 0)');
  }
}

let _lastSunPtsSegs = -1;

function updateSunPerimeter(t, segments = (state.quality === 'high' ? 64 : 48)) {
  if (Math.abs(t - _lastSunPtsT) < 0.001 && _lastSunPtsSegs === segments) return;
  _lastSunPtsT = t;
  _lastSunPtsSegs = segments;
  for (let i = 0; i <= segments; i++) {
    const theta = (i / segments) * Math.PI * 2;
    // Animated long-wave thermal undulations (harmonic atmospheric refraction):
    const w1 = Math.sin(theta * 5.0 + t * 1.5) * 3.6;
    const w2 = Math.cos(theta * 8.0 - t * 2.1) * 2.2;
    const w3 = Math.sin(theta * 13.0 + t * 3.4) * 1.1;

    const horizonFactor = 0.75 + 0.50 * Math.max(0, Math.sin(theta));
    const r = SUN_RADIUS + (w1 + w2 + w3) * horizonFactor;

    _sunPtsX[i] = Math.cos(theta) * r;
    _sunPtsY[i] = Math.sin(theta) * r;
  }
}

function traceSunPerimeter(ctx, radius, t, segments = (state.quality === 'high' ? 64 : 48)) {
  updateSunPerimeter(t, segments);
  const factor = radius / SUN_RADIUS;
  ctx.beginPath();
  if (Math.abs(factor - 1.0) < 0.001) {
    ctx.moveTo(_sunPtsX[0], _sunPtsY[0]);
    for (let i = 1; i <= segments; i++) {
      ctx.lineTo(_sunPtsX[i], _sunPtsY[i]);
    }
  } else {
    ctx.moveTo(_sunPtsX[0] * factor, _sunPtsY[0] * factor);
    for (let i = 1; i <= segments; i++) {
      ctx.lineTo(_sunPtsX[i] * factor, _sunPtsY[i] * factor);
    }
  }
  ctx.closePath();
}

function drawGiantSun(ctx, t = 0) {
  const sunX = WIDTH / 2;
  const sunY = state.sunY;

  ctx.save();
  ctx.translate(sunX, sunY);
  ctx.scale(1.0, 0.90);

  // Storm darkness attenuation (Option 3A: Sun blotted out by dark thunderheads into faint ember)
  if (weatherState.stormDarkness > 0.001) {
    ctx.globalAlpha = Math.max(0.08, 1.0 - weatherState.stormDarkness * 0.88);
  }

  ensureSunGradients(ctx);

  // 1. Volumetric Thermal Corona & Outer Atmospheric Glow
  ctx.fillStyle = _sunCoronaGrad;
  traceSunPerimeter(ctx, SUN_RADIUS * 1.18, t);
  ctx.fill();

  // 2. Atmospheric Limb Chromatic Diffusion (Brechungssaum)
  // Softens the edge by 2.5-3.5px into the surrounding crimson sky without losing the contour definition
  if (state.quality !== 'low') {
    // Outer thermal dissipation aura
    ctx.strokeStyle = 'rgba(234, 88, 12, 0.38)';
    ctx.lineWidth = 6.0;
    traceSunPerimeter(ctx, SUN_RADIUS, t);
    ctx.stroke();

    // Mid refraction chromatic band
    ctx.strokeStyle = 'rgba(251, 146, 60, 0.55)';
    ctx.lineWidth = 3.2;
    traceSunPerimeter(ctx, SUN_RADIUS, t);
    ctx.stroke();

    // Inner incandescent thermal lip
    ctx.strokeStyle = 'rgba(254, 240, 138, 0.75)';
    ctx.lineWidth = 1.4;
    traceSunPerimeter(ctx, SUN_RADIUS, t);
    ctx.stroke();
  }

  // 3. Core Thermonuclear Solar Disc with Radial Color Stratification
  ctx.fillStyle = _sunDiscGrad;
  traceSunPerimeter(ctx, SUN_RADIUS, t);
  ctx.fill();

  // 4. Subtle Stratified Thermal Inversion Mirage Ribbons
  if (state.quality !== 'low') {
    const mirageBands = SUN_MIRAGE_BANDS;

    for (let i = 0; i < mirageBands.length; i++) {
      const b = mirageBands[i];
      const bandWave = Math.sin(t * 2.2 + i * 1.5) * 1.8;
      const by = b.y + bandWave;
      const halfW = Math.sqrt(Math.max(0, SUN_RADIUS * SUN_RADIUS - by * by)) * 0.94;
      if (halfW <= 0) continue;

      ctx.fillStyle = `rgba(76, 5, 25, ${b.alpha})`;
      ctx.beginPath();
      // Curved ribbon with subtle long-wave thermal ripples
      ctx.moveTo(-halfW, by);
      for (let x = -halfW; x <= halfW; x += 40) {
        const ripple = Math.sin(x * 0.012 + t * 2.5) * 1.2;
        ctx.lineTo(x, by + ripple);
      }
      ctx.lineTo(halfW, by + b.h);
      for (let x = halfW; x >= -halfW; x -= 40) {
        const ripple = Math.sin(x * 0.012 + t * 2.5 + 1.0) * 1.2;
        ctx.lineTo(x, by + b.h + ripple);
      }
      ctx.closePath();
      ctx.fill();
    }
  }

  ctx.restore();

  // 5. Horizontal Solar Glare Flare across the Horizon
  ensureSunGradients(ctx);
  ctx.fillStyle = _sunGlareGrad;
  const minX = (state.visibleLeft !== undefined ? state.visibleLeft : 0);
  const maxX = (state.visibleRight !== undefined ? state.visibleRight : WIDTH);
  ctx.fillRect(minX, sunY - 35, maxX - minX, 70);
}

function drawHeatHaze(ctx, t) {
  if (state.quality === 'low') return;

  const minX = (state.visibleLeft !== undefined ? state.visibleLeft : 0);
  const maxX = (state.visibleRight !== undefined ? state.visibleRight : WIDTH);
  const shimmerY = state.sunY - 12;
  const numBands = state.quality === 'high' ? 16 : 8;
  const bandH = 3;

  ctx.save();
  for (let i = 0; i < numBands; i++) {
    const y = shimmerY + i * bandH;
    const wave = Math.sin(y * 0.28 + t * 4.5) * 0.5 + Math.cos(y * 0.42 - t * 3.2) * 0.5;
    const alpha = 0.04 + Math.abs(wave) * 0.08;

    ctx.fillStyle = `rgba(254, 215, 170, ${alpha * qualityScale()})`;
    ctx.fillRect(minX, y, maxX - minX, bandH - 0.5);
  }
  ctx.restore();
}

// ---------------------------------------------------------------------------
// RENDERING: AUTHENTIC 3D ALPENGLOW ALPINE MASSIFS
// Multi-Tier Panorama: 4-Facet Polyhedral Massifs (Flächen & Täler), Continuous Lighting
// Eliminates center jumps via continuous horizontal sun-vector interpolation
// ---------------------------------------------------------------------------

function fillPolygon(ctx, pts, scaleH) {
  if (pts.length === 0) return;
  ctx.beginPath();
  ctx.moveTo(pts[0].x, pts[0].y * scaleH);
  for (let i = 1; i < pts.length; i++) {
    ctx.lineTo(pts[i].x, pts[i].y * scaleH);
  }
  ctx.closePath();
  ctx.fill();
}

function strokePath(ctx, pts, scaleH) {
  if (!pts || pts.length === 0) return;
  ctx.beginPath();
  ctx.moveTo(pts[0].x, pts[0].y * scaleH);
  for (let i = 1; i < pts.length; i++) {
    ctx.lineTo(pts[i].x, pts[i].y * scaleH);
  }
  ctx.stroke();
}

function strokePaths(ctx, pathList, scaleH) {
  ctx.beginPath();
  let hasPts = false;
  for (let p = 0; p < pathList.length; p++) {
    const pts = pathList[p];
    if (!pts || pts.length === 0) continue;
    hasPts = true;
    ctx.moveTo(pts[0].x, pts[0].y * scaleH);
    for (let i = 1; i < pts.length; i++) {
      ctx.lineTo(pts[i].x, pts[i].y * scaleH);
    }
  }
  if (hasPts) ctx.stroke();
}

function getFacetGradientColors(tier, dot, isValley) {
  // Base Twilight Silhouette Color at dot = 0 (Continuous transition across screen center)
  let c0Top, c0Bot, cLitTop, cLitBot, cShdTop, cShdBot;

  if (tier === 'far') {
    c0Top = [85, 18, 42];      // Rich twilight crimson
    c0Bot = [28, 6, 16];
    cLitTop = [235, 75, 105];  // Glowing alpenglow rose
    cLitBot = [72, 18, 34];
    cShdTop = [32, 8, 20];     // Distant twilight indigo
    cShdBot = [12, 3, 8];
  } else if (tier === 'mid') {
    c0Top = [85, 20, 45];      // Deep twilight slate
    c0Bot = [22, 5, 16];
    cLitTop = [248, 115, 30];  // Fiery amber summit
    cLitBot = [68, 16, 26];
    cShdTop = [28, 8, 24];     // Cold basalt shadow
    cShdBot = [8, 2, 7];
  } else { // near
    c0Top = [45, 12, 28];      // Dark charcoal-umber
    c0Bot = [14, 4, 10];
    cLitTop = [165, 58, 26];   // Burnt sienna rock
    cLitBot = [38, 10, 18];
    cShdTop = [16, 4, 14];     // Pitch black volcanic rock
    cShdBot = [5, 1, 4];
  }

  let rT, gT, bT, rB, gB, bB;
  if (dot >= 0) {
    const t = Math.min(1.0, dot);
    rT = c0Top[0] + (cLitTop[0] - c0Top[0]) * t;
    gT = c0Top[1] + (cLitTop[1] - c0Top[1]) * t;
    bT = c0Top[2] + (cLitTop[2] - c0Top[2]) * t;
    rB = c0Bot[0] + (cLitBot[0] - c0Bot[0]) * t;
    gB = c0Bot[1] + (cLitBot[1] - c0Bot[1]) * t;
    bB = c0Bot[2] + (cLitBot[2] - c0Bot[2]) * t;
  } else {
    const t = Math.min(1.0, -dot);
    rT = c0Top[0] + (cShdTop[0] - c0Top[0]) * t;
    gT = c0Top[1] + (cShdTop[1] - c0Top[1]) * t;
    bT = c0Top[2] + (cShdTop[2] - c0Top[2]) * t;
    rB = c0Bot[0] + (cShdBot[0] - c0Bot[0]) * t;
    gB = c0Bot[1] + (cShdBot[1] - c0Bot[1]) * t;
    bB = c0Bot[2] + (cShdBot[2] - c0Bot[2]) * t;
  }

  if (isValley && dot < 0) {
    // Recessed cirque valley ambient occlusion (Talmulden-Verschattung)
    const ao = 0.78;
    rT *= ao; gT *= ao; bT *= ao;
    rB *= ao; gB *= ao; bB *= ao;
  }

  return {
    top: `rgb(${Math.round(rT)},${Math.round(gT)},${Math.round(bT)})`,
    bot: `rgb(${Math.round(rB)},${Math.round(gB)},${Math.round(bB)})`
  };
}

const _facetColorTable = {
  far: { valley: new Map(), face: new Map() },
  mid: { valley: new Map(), face: new Map() },
  near: { valley: new Map(), face: new Map() }
};

function getFacetGradientColorsCached(tier, dot, isValley) {
  const bucket = Math.max(-25, Math.min(25, Math.round(dot * 25)));
  const map = isValley ? _facetColorTable[tier].valley : _facetColorTable[tier].face;
  let entry = map.get(bucket);
  if (!entry) {
    entry = getFacetGradientColors(tier, bucket / 25, isValley);
    map.set(bucket, entry);
  }
  return entry;
}

function drawAlpineMassif(ctx, m, screenApexX, curApexY, curBaseY, tier, t) {
  const scaleH = (curBaseY - curApexY) / m.height;
  const dx = 800 - screenApexX;
  const u = Math.tanh(dx / 400); // Continuous horizontal light vector in [-1, 1]

  ctx.save();
  ctx.translate(screenApexX, curApexY);

  const facets = [
    { pts: m.facetLeftKar,   nx: -0.75, isValley: true },
    { pts: m.facetLeftFace,  nx: -0.30, isValley: false },
    { pts: m.facetRightFace, nx: +0.30, isValley: false },
    { pts: m.facetRightKar,  nx: +0.75, isValley: true }
  ];

  if (!m._facetGradients) m._facetGradients = [[], [], [], []];

  // 1. Draw 4 Planar Facets (Perspektivische Flächen & Täler)
  for (let f = 0; f < facets.length; f++) {
    const fc = facets[f];
    const dot = fc.nx * u;
    const bucket = Math.max(0, Math.min(50, Math.round(dot * 25) + 25));
    let grad = m._facetGradients[f][bucket];
    if (!grad) {
      const col = getFacetGradientColorsCached(tier, dot, fc.isValley);
      grad = ctx.createLinearGradient(0, 0, 0, m.height);
      grad.addColorStop(0.0, col.top);
      grad.addColorStop(1.0, col.bot);
      m._facetGradients[f][bucket] = grad;
    }
    ctx.fillStyle = grad;

    fillPolygon(ctx, fc.pts, scaleH);
  }

  // 1b. Plateau Summit Roof (if archetype === 'plateau')
  if (m.plateauTop) {
    const topCol = (tier === 'far')
      ? 'rgba(254, 215, 170, 0.75)'
      : (tier === 'mid' ? 'rgba(251, 191, 36, 0.70)' : 'rgba(249, 115, 22, 0.65)');
    ctx.fillStyle = topCol;
    fillPolygon(ctx, m.plateauTop, scaleH);
  }

  // 2. Tier-Specific Geological Textures & Depth Detail
  if (state.quality !== 'low') {
    if (tier === 'far') {
      // Tier 1: Sweeping Glacial Firn Sheets & Hanging Glaciers in Cirques (batched)
      if (m.glaciers.length > 0) {
        let hasSunny = false;
        let hasShaded = false;
        for (let g = 0; g < m.glaciers.length; g++) {
          if (m.glaciers[g].nx * u > 0) hasSunny = true;
          else hasShaded = true;
        }
        if (hasSunny) {
          ctx.fillStyle = 'rgba(254, 215, 170, 0.65)';
          ctx.beginPath();
          for (let g = 0; g < m.glaciers.length; g++) {
            const gl = m.glaciers[g];
            if (gl.nx * u > 0) {
              ctx.moveTo(gl.x - gl.w * 0.5, gl.y * scaleH);
              ctx.quadraticCurveTo(gl.x, (gl.y - gl.h * 0.4) * scaleH, gl.x + gl.w * 0.5, gl.y * scaleH);
              ctx.quadraticCurveTo(gl.x + gl.w * 0.3, (gl.y + gl.h) * scaleH, gl.x, (gl.y + gl.h * 0.8) * scaleH);
              ctx.quadraticCurveTo(gl.x - gl.w * 0.3, (gl.y + gl.h) * scaleH, gl.x - gl.w * 0.5, gl.y * scaleH);
              ctx.closePath();
            }
          }
          ctx.fill();
        }
        if (hasShaded) {
          ctx.fillStyle = 'rgba(129, 140, 248, 0.28)';
          ctx.beginPath();
          for (let g = 0; g < m.glaciers.length; g++) {
            const gl = m.glaciers[g];
            if (gl.nx * u <= 0) {
              ctx.moveTo(gl.x - gl.w * 0.5, gl.y * scaleH);
              ctx.quadraticCurveTo(gl.x, (gl.y - gl.h * 0.4) * scaleH, gl.x + gl.w * 0.5, gl.y * scaleH);
              ctx.quadraticCurveTo(gl.x + gl.w * 0.3, (gl.y + gl.h) * scaleH, gl.x, (gl.y + gl.h * 0.8) * scaleH);
              ctx.quadraticCurveTo(gl.x - gl.w * 0.3, (gl.y + gl.h) * scaleH, gl.x - gl.w * 0.5, gl.y * scaleH);
              ctx.closePath();
            }
          }
          ctx.fill();
        }
      }
    } else if (tier === 'mid') {
      // Tier 2: Deep Erosional Couloirs (Talschluchten between facets) - batched fill
      if (m.couloirs.length > 0) {
        ctx.fillStyle = 'rgba(6, 1, 8, 0.70)';
        ctx.beginPath();
        for (let c = 0; c < m.couloirs.length; c++) {
          const cl = m.couloirs[c];
          ctx.moveTo(cl.topX, cl.topY * scaleH);
          ctx.lineTo(cl.botX, cl.botY * scaleH);
          ctx.lineTo(cl.botX + cl.width, cl.botY * scaleH);
          ctx.lineTo(cl.topX + cl.width * 0.4, cl.topY * scaleH);
          ctx.closePath();
        }
        ctx.fill();

        // High Couloir Firn Ribbons nestled inside the couloir ravine (batched into sunny & shaded strokes)
        let hasSunnyRibbons = false;
        let hasShadedRibbons = false;
        for (let c = 0; c < m.couloirs.length; c++) {
          const cl = m.couloirs[c];
          if (cl.hasSnow) {
            if (cl.nx * u > 0) hasSunnyRibbons = true;
            else hasShadedRibbons = true;
          }
        }
        if (hasSunnyRibbons) {
          ctx.strokeStyle = 'rgba(255, 247, 237, 0.85)';
          ctx.lineWidth = 1.3;
          ctx.beginPath();
          for (let c = 0; c < m.couloirs.length; c++) {
            const cl = m.couloirs[c];
            if (cl.hasSnow && cl.nx * u > 0) {
              ctx.moveTo(cl.topX + cl.width * 0.3, (cl.topY + 4) * scaleH);
              ctx.lineTo(cl.botX + cl.width * 0.3, (cl.botY - 4) * scaleH);
            }
          }
          ctx.stroke();
        }
        if (hasShadedRibbons) {
          ctx.strokeStyle = 'rgba(67, 56, 202, 0.45)';
          ctx.lineWidth = 1.3;
          ctx.beginPath();
          for (let c = 0; c < m.couloirs.length; c++) {
            const cl = m.couloirs[c];
            if (cl.hasSnow && cl.nx * u <= 0) {
              ctx.moveTo(cl.topX + cl.width * 0.3, (cl.topY + 4) * scaleH);
              ctx.lineTo(cl.botX + cl.width * 0.3, (cl.botY - 4) * scaleH);
            }
          }
          ctx.stroke();
        }
      }
    } else {
      // Tier 3 (Near): Tilted Geological Strata (Felsbänder) & Scree Slopes (Schutthalden), NO snow
      // Tilted Bedding Planes (Strata) - batched into 2 strokes
      if (m.strataLines.length > 0) {
        ctx.strokeStyle = 'rgba(217, 119, 6, 0.26)';
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        for (let st = 0; st < m.strataLines.length; st++) {
          const s = m.strataLines[st];
          const sy = m.height * scaleH * s.fracY;
          const dy = m.width * 0.48 * Math.sin(s.angle);
          ctx.moveTo(-m.width * 0.48, sy - dy);
          ctx.lineTo(m.width * 0.48, sy + dy);
        }
        ctx.stroke();

        ctx.strokeStyle = 'rgba(0, 0, 0, 0.50)';
        ctx.lineWidth = 1.0;
        ctx.beginPath();
        for (let st = 0; st < m.strataLines.length; st++) {
          const s = m.strataLines[st];
          const sy = m.height * scaleH * s.fracY + 1.2;
          const dy = m.width * 0.48 * Math.sin(s.angle);
          ctx.moveTo(-m.width * 0.48, sy - dy);
          ctx.lineTo(m.width * 0.48, sy + dy);
        }
        ctx.stroke();
      }

      // Fan-shaped Scree Aprons at base of cliffs (Schuttfächer) - batched fill
      if (m.screeFans.length > 0) {
        ctx.fillStyle = 'rgba(15, 3, 12, 0.55)';
        ctx.beginPath();
        for (let f = 0; f < m.screeFans.length; f++) {
          const sf = m.screeFans[f];
          ctx.moveTo(sf.x, sf.topY * scaleH);
          ctx.lineTo(sf.x + sf.spread, m.height * scaleH);
          ctx.lineTo(sf.x - sf.spread, m.height * scaleH);
          ctx.closePath();
        }
        ctx.fill();
      }
    }
  }

  // 3. Structural Ridge Edges (Kanten & Grate with Smooth Rim Lighting)
  // Continuous rim exposure from giant sun sitting behind (centered at X=800)
  const rimExposure = 0.30 + 0.70 * (1.0 - Math.min(1.0, Math.abs(dx) / 750));

  if (tier === 'far') {
    ctx.strokeStyle = `rgba(254, 215, 170, ${rimExposure * 0.50})`;
    ctx.lineWidth = 1.0;
    strokePaths(ctx, [m.leftRidge, m.rightRidge, m.colRidge, m.plateauTop], scaleH);
    ctx.strokeStyle = `rgba(251, 146, 60, ${rimExposure * 0.35})`;
    ctx.lineWidth = 0.8;
    strokePath(ctx, m.spineRidge, scaleH);
  } else if (tier === 'mid') {
    // Sharp Matterhorn ridges & spires
    ctx.strokeStyle = `rgba(254, 240, 138, ${rimExposure * 0.85})`;
    ctx.lineWidth = 1.4;
    strokePaths(ctx, [m.leftRidge, m.rightRidge, m.colRidge, m.plateauTop], scaleH);

    ctx.strokeStyle = `rgba(251, 191, 36, ${rimExposure * 0.65})`;
    ctx.lineWidth = 1.2;
    strokePath(ctx, m.spineRidge, scaleH);

    ctx.strokeStyle = 'rgba(8, 1, 8, 0.50)';
    ctx.lineWidth = 1.0;
    strokePaths(ctx, [m.leftSpur, m.rightSpur], scaleH);
  } else {
    // Near Canyon Crags: Blazing fiery rim contours
    ctx.strokeStyle = `rgba(255, 237, 213, ${rimExposure * 0.95})`;
    ctx.lineWidth = 1.6;
    strokePaths(ctx, [m.leftRidge, m.rightRidge, m.colRidge, m.plateauTop], scaleH);

    ctx.strokeStyle = `rgba(249, 115, 22, ${rimExposure * 0.75})`;
    ctx.lineWidth = 1.4;
    strokePath(ctx, m.spineRidge, scaleH);

    ctx.strokeStyle = 'rgba(5, 0, 5, 0.75)';
    ctx.lineWidth = 1.2;
    strokePaths(ctx, [m.leftSpur, m.rightSpur], scaleH);
  }

  // Backlit Alpenglow Saum when mountain peak is directly in front of the giant sun
  const sunBacklight = Math.max(0, 1.0 - Math.abs(dx) / 480);
  if (sunBacklight > 0.08 && state.quality !== 'low') {
    ctx.strokeStyle = `rgba(255, 255, 255, ${sunBacklight * 0.45})`;
    ctx.lineWidth = tier === 'near' ? 2.4 : 1.8;
    strokePaths(ctx, [m.leftRidge, m.rightRidge, m.colRidge, m.plateauTop], scaleH);
    ctx.strokeStyle = `rgba(254, 240, 138, ${sunBacklight * 0.80})`;
    ctx.lineWidth = 1.0;
    strokePaths(ctx, [m.leftRidge, m.rightRidge, m.colRidge, m.plateauTop], scaleH);
  }

  ctx.restore();
}

function drawAlpineChain(ctx, massifs, scrollOffset, bobAmp, freq, tier, t) {
  const minX = (state.visibleLeft !== undefined ? state.visibleLeft : 0);
  const maxX = (state.visibleRight !== undefined ? state.visibleRight : WIDTH);

  function renderMassifAt(m, screenApexX) {
    const pitchFactor = (screenApexX - 800) / 800;
    const bobY = Math.sin(t * freq) * bobAmp + Math.cos(t * freq * 0.5 + pitchFactor * 0.6) * (bobAmp * 0.35);
    const curApexY = m.apexY + bobY;
    const curBaseY = m.baseY + bobY;
    drawAlpineMassif(ctx, m, screenApexX, curApexY, curBaseY, tier, t);
  }

  for (let i = 0; i < massifs.length; i++) {
    const m = massifs[i];
    const halfW = m.width * 0.6;
    const relX = ((m.baseX - scrollOffset) % W_LOOP + W_LOOP) % W_LOOP;

    if (relX >= minX - halfW && relX <= maxX + halfW) {
      renderMassifAt(m, relX);
    }
    if (relX - W_LOOP >= minX - halfW && relX - W_LOOP <= maxX + halfW) {
      renderMassifAt(m, relX - W_LOOP);
    } else if (relX + W_LOOP >= minX - halfW && relX + W_LOOP <= maxX + halfW) {
      renderMassifAt(m, relX + W_LOOP);
    }
  }
}

// ---------------------------------------------------------------------------
// CANYON DEPTH HAZE VEILS & SUBLIMINAL PRISMATIC DIAMOND-DUST SPARKLES
// Gossamer atmospheric haze veils in the deep alpine chasms and couloirs,
// with forward Mie scattering, light refraction fringes, and subliminal
// crystalline micro-glints blinking gently in the mountain sunbeams.
// ---------------------------------------------------------------------------

const CANYON_MICRO_GLINTS = (() => {
  const glints = [];
  const numGlints = 56;
  for (let i = 0; i < numGlints; i++) {
    const seed = i * 137.508;
    const u = ((seed * 19.37) % 1000) / 1000.0;
    const yFrac = ((seed * 7.91) % 1000) / 1000.0;
    // tier 1: deep valley between far & mid (y: 165..215)
    // tier 2: mid canyon ravine between mid & near (y: 182..228)
    const tier = (i % 2 === 0) ? 1 : 2;
    const y = tier === 1 ? (165 + yFrac * 50) : (182 + yFrac * 46);
    const speed = (tier === 1 ? 14 : 26) + (i % 5) * 2.8;
    const freq = 2.8 + (i % 7) * 0.55;
    const phase = (((seed * 3.14) % 1000) / 1000.0) * Math.PI * 2;
    const baseSize = 0.80 + (i % 4) * 0.22;
    const colorType = (i % 6 === 0) ? 'white' : ((i % 4 === 0) ? 'rose' : 'gold');
    glints.push({ u, y, tier, speed, freq, phase, baseSize, colorType });
  }
  return glints;
})();

function drawCanyonMicroSparkles(ctx, t, targetTier, bobY) {
  if (state.quality === 'low') return;

  const sunX = WIDTH / 2;
  const numGlints = CANYON_MICRO_GLINTS.length;

  for (let i = 0; i < numGlints; i++) {
    const g = CANYON_MICRO_GLINTS[i];
    if (g.tier !== targetTier) continue;

    // In medium quality, render half the sparkles
    if (state.quality === 'medium' && (i % 2 !== 0)) continue;

    const x = ((g.u * W_LOOP - t * g.speed) % W_LOOP + W_LOOP) % WIDTH;
    const y = g.y + bobY + Math.sin(t * 1.5 + g.phase) * 2.2;

    // Only sparkle where light floods the valley / gorge (within range of sun axis)
    const dx = Math.abs(x - sunX);
    if (dx > 650) continue;

    const sunFactor = 1.0 - dx / 650;
    const rawTwinkle = Math.sin(t * g.freq + g.phase);
    if (rawTwinkle <= 0.55) continue;

    const sparkFrac = (rawTwinkle - 0.55) / 0.45;
    // Subliminal non-linear flare
    const intensity = Math.pow(sparkFrac, 3.2) * sunFactor;
    if (intensity < 0.05) continue;

    // Prismatic color selection
    let coreCol, haloCol;
    if (g.colorType === 'white') {
      coreCol = `rgba(255, 255, 255, ${(intensity * 0.95).toFixed(3)})`;
      haloCol = `rgba(254, 240, 138, ${(intensity * 0.35).toFixed(3)})`;
    } else if (g.colorType === 'rose') {
      coreCol = `rgba(254, 215, 170, ${(intensity * 0.90).toFixed(3)})`;
      haloCol = `rgba(251, 146, 60,  ${(intensity * 0.32).toFixed(3)})`;
    } else {
      coreCol = `rgba(254, 240, 138, ${(intensity * 0.92).toFixed(3)})`;
      haloCol = `rgba(251, 191, 36,  ${(intensity * 0.34).toFixed(3)})`;
    }

    // 1. Soft atmospheric light halo (diffraction around aerosol crystal)
    ctx.fillStyle = haloCol;
    ctx.beginPath();
    ctx.arc(x, y, g.baseSize * 2.5, 0, Math.PI * 2);
    ctx.fill();

    // 2. Pinpoint crystalline core
    ctx.fillStyle = coreCol;
    ctx.beginPath();
    ctx.arc(x, y, g.baseSize * (0.8 + 0.35 * intensity), 0, Math.PI * 2);
    ctx.fill();

    // 3. Delicate micro 4-point diffraction cross for peak flares
    if (intensity > 0.65) {
      const spikeAlpha = ((intensity - 0.65) / 0.35 * 0.70).toFixed(3);
      ctx.strokeStyle = `rgba(255, 255, 255, ${spikeAlpha})`;
      ctx.lineWidth = 0.7;
      ctx.beginPath();
      ctx.moveTo(x - 2.5, y);
      ctx.lineTo(x + 2.5, y);
      ctx.moveTo(x, y - 2.5);
      ctx.lineTo(x, y + 2.5);
      ctx.stroke();
    }
  }
}

const _hazeGradCache = {};
const _scatterGradCache = {};
const _baseCloudGradCache = {};

function ensureHazeGradients(ctx) {
  if (!_hazeGradCache.far_mid) {
    const farGrad = ctx.createLinearGradient(0, 158, 0, 234);
    farGrad.addColorStop(0.00, 'rgba(254, 215, 170, 0)');
    farGrad.addColorStop(0.35, 'rgba(254, 215, 170, 0.12)');
    farGrad.addColorStop(0.65, 'rgba(251, 146, 60,  0.20)');
    farGrad.addColorStop(0.88, 'rgba(180, 24, 64,   0.28)');
    farGrad.addColorStop(1.00, 'rgba(76, 5, 25,    0.42)');
    _hazeGradCache.far_mid = farGrad;

    const floorGrad = ctx.createLinearGradient(0, 190, 0, 220);
    floorGrad.addColorStop(0.00, 'rgba(254, 215, 170, 0)');
    floorGrad.addColorStop(0.40, 'rgba(251, 146, 60,  0.10)');
    floorGrad.addColorStop(0.80, 'rgba(159, 18, 57,   0.18)');
    floorGrad.addColorStop(1.00, 'rgba(50, 4, 15,    0.28)');
    _hazeGradCache.canyon_floor = floorGrad;

    const midGrad = ctx.createLinearGradient(0, 177, 0, 252);
    midGrad.addColorStop(0.00, 'rgba(254, 215, 170, 0)');
    midGrad.addColorStop(0.30, 'rgba(251, 146, 60,  0.15)');
    midGrad.addColorStop(0.65, 'rgba(225, 29, 72,   0.24)');
    midGrad.addColorStop(0.90, 'rgba(136, 19, 55,   0.34)');
    midGrad.addColorStop(1.00, 'rgba(60, 4, 18,     0.48)');
    _hazeGradCache.mid_near = midGrad;

    const sunX = WIDTH / 2;
    const farScatter = ctx.createRadialGradient(sunX, (166 + 234) * 0.5, 20, sunX, (166 + 234) * 0.5, 460);
    farScatter.addColorStop(0.00, 'rgba(255, 247, 237, 0.16)');
    farScatter.addColorStop(0.40, 'rgba(254, 215, 170, 0.10)');
    farScatter.addColorStop(0.75, 'rgba(251, 146, 60,  0.04)');
    farScatter.addColorStop(1.00, 'rgba(251, 146, 60,  0)');
    _scatterGradCache.far_mid = farScatter;

    const floorScatter = ctx.createRadialGradient(sunX, (198 + 220) * 0.5, 20, sunX, (198 + 220) * 0.5, 380);
    floorScatter.addColorStop(0.00, 'rgba(255, 247, 237, 0.10)');
    floorScatter.addColorStop(0.40, 'rgba(254, 215, 170, 0.06)');
    floorScatter.addColorStop(0.75, 'rgba(251, 146, 60,  0.02)');
    floorScatter.addColorStop(1.00, 'rgba(251, 146, 60,  0)');
    _scatterGradCache.canyon_floor = floorScatter;

    const midScatter = ctx.createRadialGradient(sunX, (185 + 252) * 0.5, 20, sunX, (185 + 252) * 0.5, 380);
    midScatter.addColorStop(0.00, 'rgba(255, 247, 237, 0.16)');
    midScatter.addColorStop(0.40, 'rgba(254, 215, 170, 0.10)');
    midScatter.addColorStop(0.75, 'rgba(251, 146, 60,  0.04)');
    midScatter.addColorStop(1.00, 'rgba(251, 146, 60,  0)');
    _scatterGradCache.mid_near = midScatter;
  }
}

function drawDeepValleyHazeVeil(ctx, tier, t, bobAmp, freq, scrollOffset) {
  if (state.quality === 'low') return;

  const minX = (state.visibleLeft !== undefined ? state.visibleLeft : 0);
  const maxX = (state.visibleRight !== undefined ? state.visibleRight : WIDTH);
  const isFar = (tier === 'far_mid');
  const isCanyonFloor = (tier === 'canyon_floor');
  const bobY = Math.sin(t * freq) * (bobAmp * (isFar ? 0.32 : (isCanyonFloor ? 0.85 : 0.55)));
  const baseY = isFar ? 234 : (isCanyonFloor ? 220 : 252);
  const topNominal = isFar ? 166 : (isCanyonFloor ? 198 : 185);

  ensureHazeGradients(ctx);

  ctx.save();
  ctx.translate(0, bobY);

  // 1. Organic Undulating Gossamer Veil Ribbon (Hauchzarter Dunstschleier)
  ctx.beginPath();
  ctx.moveTo(minX, baseY);

  const waveFreq1 = isFar ? 0.0038 : (isCanyonFloor ? 0.0075 : 0.0052);
  const waveFreq2 = isFar ? 0.0082 : (isCanyonFloor ? 0.0140 : 0.0105);
  const driftRate = isFar ? 0.22 : (isCanyonFloor ? 0.55 : 0.38);

  for (let x = minX; x <= maxX; x += 32) {
    const w1 = Math.sin((x + scrollOffset * 0.4) * waveFreq1 + t * driftRate) * (isFar ? 7.5 : (isCanyonFloor ? 3.5 : 6.0));
    const w2 = Math.cos((x - scrollOffset * 0.2) * waveFreq2 - t * (driftRate * 1.4)) * (isFar ? 4.5 : (isCanyonFloor ? 2.0 : 3.5));
    const vy = topNominal + w1 + w2;
    ctx.lineTo(x, vy);
  }

  ctx.lineTo(maxX, baseY);
  ctx.closePath();

  ctx.fillStyle = _hazeGradCache[tier] || _hazeGradCache.far_mid;
  ctx.fill();

  // 2. Prismatic Light Refraction Edge along the top rim (Lichtkante der Dunstschicht)
  ctx.strokeStyle = isFar ? 'rgba(254, 240, 138, 0.20)' : (isCanyonFloor ? 'rgba(254, 215, 170, 0.14)' : 'rgba(254, 215, 170, 0.18)');
  ctx.lineWidth = 1.4;
  ctx.stroke();

  // 3. Forward Solar Mie Scattering Pocket (Lichtbrechung im Talzentrum)
  ctx.globalCompositeOperation = 'screen';
  ctx.fillStyle = _scatterGradCache[tier] || _scatterGradCache.far_mid;
  ctx.fillRect(minX, topNominal - 10, maxX - minX, (baseY - topNominal) + 20);

  ctx.restore();

  // 4. Subliminal Micro-Sparkles drifting through the illuminated haze
  if (!isCanyonFloor) {
    drawCanyonMicroSparkles(ctx, t, isFar ? 1 : 2, bobY);
  }
}

let _cloudDomeNormalCanvas = null;
function getCloudDomeNormalCanvas() {
  if (!_cloudDomeNormalCanvas && typeof document !== 'undefined') {
    _cloudDomeNormalCanvas = document.createElement('canvas');
    _cloudDomeNormalCanvas.width = 128;
    _cloudDomeNormalCanvas.height = 128;
    const cctx = _cloudDomeNormalCanvas.getContext('2d');
    const grad = cctx.createRadialGradient(64, 48, 3.2, 64, 64, 64);
    grad.addColorStop(0.00, 'rgba(68, 80, 105, 0.82)');
    grad.addColorStop(0.35, 'rgba(42, 44, 72, 0.72)');
    grad.addColorStop(0.70, 'rgba(22, 24, 45, 0.55)');
    grad.addColorStop(0.90, 'rgba(12, 14, 28, 0.25)');
    grad.addColorStop(1.00, 'rgba(8, 10, 20, 0.00)');
    cctx.fillStyle = grad;
    cctx.beginPath();
    cctx.arc(64, 64, 64, 0, Math.PI * 2);
    cctx.fill();
  }
  return _cloudDomeNormalCanvas;
}

let _cloudDomeFlashCanvas = null;
function getCloudDomeFlashCanvas() {
  if (!_cloudDomeFlashCanvas && typeof document !== 'undefined') {
    _cloudDomeFlashCanvas = document.createElement('canvas');
    _cloudDomeFlashCanvas.width = 128;
    _cloudDomeFlashCanvas.height = 128;
    const cctx = _cloudDomeFlashCanvas.getContext('2d');
    const grad = cctx.createRadialGradient(64, 40, 2.0, 64, 64, 64);
    grad.addColorStop(0.00, 'rgba(254, 240, 138, 0.95)');
    grad.addColorStop(0.28, 'rgba(251, 146, 60, 0.80)');
    grad.addColorStop(0.60, 'rgba(147, 51, 234, 0.45)');
    grad.addColorStop(0.85, 'rgba(30, 27, 75, 0.20)');
    grad.addColorStop(1.00, 'rgba(15, 23, 42, 0.00)');
    cctx.fillStyle = grad;
    cctx.beginPath();
    cctx.arc(64, 64, 64, 0, Math.PI * 2);
    cctx.fill();
  }
  return _cloudDomeFlashCanvas;
}

// ---------------------------------------------------------------------------
// INTER-MOUNTAIN STORM CLOUDS & RAGGED VALLEY SCUD (Pass- & Schluchtwolken)
// "beim gewitter müssen auch wolken zwischen den bergen sitzen"
// Billowing cumulonimbus cushions, low stratus blankets & fractostratus scud
// nestled in the alpine cols, ravines and passes between mountain tiers.
// ---------------------------------------------------------------------------

const INTER_MOUNTAIN_CLOUDS_FAR = (() => {
  const nodes = [];
  const rng = mulberry32(7719);
  const count = 28;
  const spacing = W_LOOP / count;
  for (let i = 0; i < count; i++) {
    const u = (i * spacing + (rng() - 0.5) * spacing * 0.7) / W_LOOP;
    const y = 96 + rng() * 44;   // 96..140 in far mountain saddles & passes
    const rx = 85 + rng() * 55;  // 85..140px wide
    const ry = 28 + rng() * 18;  // 28..46px high
    const tilt = (rng() - 0.5) * 0.16;
    const waveFreq = 2.0 + rng() * 1.5;
    const phase = rng() * Math.PI * 2;
    nodes.push({ u, y, rx, ry, tilt, waveFreq, phase });
  }
  return nodes;
})();

const INTER_MOUNTAIN_CLOUDS_MID = (() => {
  const nodes = [];
  const rng = mulberry32(8841);
  const count = 34;
  const spacing = W_LOOP / count;
  for (let i = 0; i < count; i++) {
    const u = (i * spacing + (rng() - 0.5) * spacing * 0.65) / W_LOOP;
    const y = 126 + rng() * 42;  // 126..168 in mid-range cols & ravines
    const rx = 75 + rng() * 45;  // 75..120px wide
    const ry = 22 + rng() * 16;  // 22..38px high
    const tilt = (rng() - 0.5) * 0.14;
    const waveFreq = 2.3 + rng() * 1.7;
    const phase = rng() * Math.PI * 2;
    nodes.push({ u, y, rx, ry, tilt, waveFreq, phase });
  }
  return nodes;
})();

const INTER_MOUNTAIN_CLOUDS_NEAR = (() => {
  const nodes = [];
  const rng = mulberry32(9923);
  const count = 38;
  const spacing = W_LOOP / count;
  for (let i = 0; i < count; i++) {
    const u = (i * spacing + (rng() - 0.5) * spacing * 0.6) / W_LOOP;
    const y = 168 + rng() * 34;  // 168..202 low canyon floor mist & cliff base scud
    const rx = 65 + rng() * 40;  // 65..105px wide
    const ry = 18 + rng() * 14;  // 18..32px high
    const tilt = (rng() - 0.5) * 0.12;
    const waveFreq = 2.6 + rng() * 1.9;
    const phase = rng() * Math.PI * 2;
    nodes.push({ u, y, rx, ry, tilt, waveFreq, phase });
  }
  return nodes;
})();

function drawInterMountainStormClouds(ctx, tier, t, bobAmp, freq, scrollOffset) {
  const darkness = weatherState.stormDarkness;
  const cover = weatherState.cloudCover;
  const lightning = weatherState.lightningIntensity;
  const flashX = weatherState.flashX;
  const flashY = weatherState.flashY;

  // Active during storm or cirrus overcast, scaling with storm intensity
  let stormFactor = Math.max(darkness, (cover - 0.18) * 1.25);
  if (weatherState.mode === 'storm') stormFactor = 1.0;
  else if (weatherState.mode === 'cirrus') stormFactor = Math.max(stormFactor, 0.40);

  if (stormFactor <= 0.01 && lightning <= 0.005) return;

  const isFar = (tier === 'far_mid');
  const isCanyonFloor = (tier === 'canyon_floor');
  const nodes = isFar ? INTER_MOUNTAIN_CLOUDS_FAR : (isCanyonFloor ? INTER_MOUNTAIN_CLOUDS_NEAR : INTER_MOUNTAIN_CLOUDS_MID);

  const bobY = Math.sin(t * freq) * (bobAmp * (isFar ? 0.35 : (isCanyonFloor ? 0.90 : 0.65)));
  const baseY = isFar ? 260 : (isCanyonFloor ? 232 : 270);
  const topNominal = isFar ? 108 : (isCanyonFloor ? 174 : 138);

  ctx.save();
  ctx.globalAlpha = Math.min(1.0, stormFactor * (isFar ? 0.95 : (isCanyonFloor ? 0.88 : 0.96)));

  const minX = (state.visibleLeft !== undefined ? state.visibleLeft : 0);
  const maxX = (state.visibleRight !== undefined ? state.visibleRight : WIDTH);

  // 1. Continuous Undulating Valley Base Mist Ribbon (Pass- und Schluchtdecken)
  // Continuous ribbon that blankets the ravines and fades smoothly at its top edge
  const driftRate = isFar ? 0.22 : (isCanyonFloor ? 0.55 : 0.35);
  const waveFreq1 = isFar ? 0.0035 : (isCanyonFloor ? 0.0070 : 0.0048);

  ctx.beginPath();
  ctx.moveTo(minX - 60, baseY + bobY);

  for (let x = minX - 60; x <= maxX + 60; x += 32) {
    const w1 = Math.sin((x + scrollOffset * 0.5) * waveFreq1 + t * driftRate) * (isFar ? 16.0 : 10.5);
    const w2 = Math.cos((x - scrollOffset * 0.3) * (waveFreq1 * 2.3) - t * (driftRate * 1.2)) * (isFar ? 8.0 : 5.5);
    const cy = topNominal + bobY + w1 + w2;
    ctx.lineTo(x, cy);
  }
  ctx.lineTo(maxX + 60, baseY + bobY);
  ctx.closePath();

  // Vertical atmospheric gradient fading gently into the mountain crevice:
  // Base dark ribbon is ALWAYS rendered (never pops on/off)
  ensureHazeGradients(ctx);
  if (!_baseCloudGradCache[tier]) {
    const g = ctx.createLinearGradient(0, topNominal - 25, 0, baseY);
    g.addColorStop(0.00, 'rgba(38, 42, 65, 0.00)'); // soft fade at top
    g.addColorStop(0.30, 'rgba(32, 34, 58, 0.65)');
    g.addColorStop(0.65, 'rgba(20, 22, 40, 0.85)');
    g.addColorStop(1.00, 'rgba(10, 12, 22, 0.95)');
    _baseCloudGradCache[tier] = g;
  }
  ctx.save();
  ctx.translate(0, bobY);
  ctx.fillStyle = _baseCloudGradCache[tier];
  ctx.fill();
  ctx.restore();

  // If illuminated by lightning, overlay the warm flash gradient smoothly proportional to lightning intensity
  if (lightning > 0.005) {
    ctx.save();
    ctx.globalAlpha = Math.min(1.0, lightning * 1.25);
    const flashRibbonGrad = ctx.createLinearGradient(0, topNominal + bobY - 25, 0, baseY + bobY);
    flashRibbonGrad.addColorStop(0.00, 'rgba(254, 215, 170, 0.75)');
    flashRibbonGrad.addColorStop(0.30, 'rgba(251, 146, 60,  0.60)');
    flashRibbonGrad.addColorStop(0.70, 'rgba(79, 70, 229,   0.35)');
    flashRibbonGrad.addColorStop(1.00, 'rgba(12, 14, 24, 0.00)');
    ctx.fillStyle = flashRibbonGrad;
    ctx.fill();
    ctx.restore();
  }

  // 2. Horizontally Stretched Elliptical Cloud Cushions (Wolkenkissen in den Bergen)
  const wrapOffsets = [-2 * W_LOOP, -W_LOOP, 0, W_LOOP, 2 * W_LOOP];
  const stepMod = (state.quality === 'medium') ? 2 : 1;
  const normalCanvas = getCloudDomeNormalCanvas();
  const flashCanvas = getCloudDomeFlashCanvas();

  for (let i = 0; i < nodes.length; i += stepMod) {
    const node = nodes[i];
    const baseScreenX = node.u * W_LOOP - scrollOffset;

    for (let w = 0; w < wrapOffsets.length; w++) {
      const nx = baseScreenX + wrapOffsets[w];
      if (nx + node.rx < minX - 120 || nx - node.rx > maxX + 120) continue;

      const ny = node.y + bobY + Math.sin(t * node.waveFreq + node.phase) * 3.5;

      // Distance to intra-cloud sheet lightning flash
      const distToFlash = Math.hypot(nx - flashX, ny - flashY);
      const flashGlow = (lightning > 0.005 && distToFlash < 850)
        ? Math.max(0, 1.0 - distToFlash / 850) * lightning
        : 0;

      ctx.save();
      ctx.translate(nx, ny);
      ctx.rotate(node.tilt);
      ctx.scale(1.0, node.ry / node.rx); // transforms circle into wide ellipse

      // 2A. Base dark cloud dome is ALWAYS rendered (no popping / no disappearances)
      if (normalCanvas) {
        ctx.drawImage(normalCanvas, -node.rx, -node.rx, node.rx * 2, node.rx * 2);
      } else {
        const domeGrad = ctx.createRadialGradient(0, -node.rx * 0.25, node.rx * 0.05, 0, 0, node.rx);
        domeGrad.addColorStop(0.00, 'rgba(68, 80, 105, 0.82)');
        domeGrad.addColorStop(0.35, 'rgba(42, 44, 72, 0.72)');
        domeGrad.addColorStop(0.70, 'rgba(22, 24, 45, 0.55)');
        domeGrad.addColorStop(0.90, 'rgba(12, 14, 28, 0.25)');
        domeGrad.addColorStop(1.00, 'rgba(8, 10, 20, 0.00)');
        ctx.fillStyle = domeGrad;
        ctx.beginPath();
        ctx.arc(0, 0, node.rx, 0, Math.PI * 2);
        ctx.fill();
      }

      // 2B. Smooth lightning illumination overlay: globalAlpha smoothly tracks flashGlow
      // Completely eliminates abrupt cascade pops and threshold flickering!
      if (flashGlow > 0.005) {
        ctx.save();
        ctx.globalAlpha = Math.min(1.0, flashGlow * 1.25);
        if (flashCanvas) {
          ctx.drawImage(flashCanvas, -node.rx, -node.rx, node.rx * 2, node.rx * 2);
        } else {
          const fgGrad = ctx.createRadialGradient(0, -node.rx * 0.25, node.rx * 0.05, 0, 0, node.rx);
          fgGrad.addColorStop(0.00, 'rgba(254, 240, 138, 0.95)');
          fgGrad.addColorStop(0.28, 'rgba(251, 146, 60, 0.80)');
          fgGrad.addColorStop(0.60, 'rgba(147, 51, 234, 0.45)');
          fgGrad.addColorStop(0.85, 'rgba(30, 27, 75, 0.20)');
          fgGrad.addColorStop(1.00, 'rgba(15, 23, 42, 0.00)');
          ctx.fillStyle = fgGrad;
          ctx.beginPath();
          ctx.arc(0, 0, node.rx, 0, Math.PI * 2);
          ctx.fill();
        }

        // Soft illuminated crest rim during flash - alpha scales continuously with flashGlow
        if (state.quality !== 'low') {
          ctx.strokeStyle = `rgba(254, 240, 138, ${(flashGlow * 0.70).toFixed(3)})`;
          ctx.lineWidth = 1.6 * (node.rx / node.ry);
          ctx.beginPath();
          ctx.arc(0, 0, node.rx * 0.95, Math.PI * 1.15, Math.PI * 1.85);
          ctx.stroke();
        }
        ctx.restore();
      }

      ctx.restore();
    }
  }

  // 3. Fast-Drifting Ragged Scud Wisps (Fractostratus) tearing across cols
  if (state.quality === 'high') {
    const scudSpeed = isFar ? 18.0 : (isCanyonFloor ? 42.0 : 28.0);
    const numScuds = isFar ? 5 : (isCanyonFloor ? 7 : 6);
    ctx.lineWidth = isFar ? 3.0 : 2.2;
    const spanW = Math.max(WIDTH, maxX - minX);

    for (let s = 0; s < numScuds; s++) {
      const sSeed = s * 73.19 + (isFar ? 11 : 47);
      const sU = ((sSeed * 13.7) % 1000) / 1000.0;
      const sY = topNominal + bobY + ((sSeed * 5.9) % 28) - 10;
      const sx = minX + (((sU * spanW - t * scudSpeed) % spanW + spanW) % spanW);
      const sLen = 50 + ((sSeed * 3.3) % 45);

      // Base scud stroke is always rendered in slate
      ctx.strokeStyle = 'rgba(71, 85, 105, 0.22)';
      ctx.beginPath();
      ctx.moveTo(sx - sLen * 0.5, sY);
      ctx.quadraticCurveTo(sx, sY - 5, sx + sLen * 0.5, sY);
      ctx.stroke();

      // Smooth lightning illumination overlay
      if (lightning > 0.005) {
        ctx.strokeStyle = `rgba(254, 215, 170, ${(lightning * 0.45).toFixed(3)})`;
        ctx.stroke();
      }
    }
  }

  ctx.restore();
}

function drawAllMountainMassifs(ctx, t) {
  const freq = state.bobFreq;
  const amp = state.bobAmp;

  // Multi-Tier Speeds for Deep Parallaxe:
  const farSpeed  = state.speed * 1.4;
  const midSpeed  = state.speed * 2.8;
  const nearSpeed = state.speed * 4.8;

  const farOffset  = (t * farSpeed)  % W_LOOP;
  const midOffset  = (t * midSpeed)  % W_LOOP;
  const nearOffset = (t * nearSpeed) % W_LOOP;

  // 1. Far 4000m+ Colossal Titans (Mont Blanc / Monte Rosa Scale)
  drawAlpineChain(ctx, farMassifs, farOffset, amp * 0.25, freq, 'far', t);

  // Deep Valley Haze Veil 1 (Inter-Tier Depth with Light Refraction & Micro-Sparkles)
  drawDeepValleyHazeVeil(ctx, 'far_mid', t, amp, freq, farOffset);

  // Inter-Mountain Storm Clouds 1 (Between Far Titans and Mid Spires)
  drawInterMountainStormClouds(ctx, 'far_mid', t, amp, freq, farOffset);

  // 2. Mid-Range Alpine Horns & Spires (Matterhorn / Dolomite Spire Chain)
  drawAlpineChain(ctx, midMassifs, midOffset, amp * 0.65, freq, 'mid', t);

  // Deep Valley Haze Veil 2 (Canyon Ravine Depth with Light Refraction & Micro-Sparkles)
  drawDeepValleyHazeVeil(ctx, 'mid_near', t, amp, freq, midOffset);

  // Inter-Mountain Storm Clouds 2 (Between Mid Spires and Near Crags)
  drawInterMountainStormClouds(ctx, 'mid_near', t, amp, freq, midOffset);

  // 3. Near Canyon Crags & Felsbastionen (Immediate Canyon Walls)
  drawAlpineChain(ctx, nearMassifs, nearOffset, amp * 1.0, freq, 'near', t);

  // Low Canyon Chasm Floor Haze (Delicate Ground Mist right at the foot of the cliffs)
  drawDeepValleyHazeVeil(ctx, 'canyon_floor', t, amp, freq, nearOffset);

  // Inter-Mountain Storm Clouds 3 (Low Chasm Scud along the foot of the cliffs)
  drawInterMountainStormClouds(ctx, 'canyon_floor', t, amp, freq, nearOffset);
}

// ---------------------------------------------------------------------------
// VOLUMETRIC SUNLIGHT SHAFTS STREAMING THROUGH MOUNTAIN PASSES & VALLEYS
// Radiant Golden Crepuscular Rays (God Rays), Stratospheric Haze Scattering
// & Drifting Golden Solar Dust Motes
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// VOLUMETRIC CREPUSCULAR GOD RAYS OCCLUDED BEHIND MOUNTAIN MASSIFS
// Physical Backlit Crepuscular Fan: Sunlight streams through natural mountain
// cols & canyon gorges, naturally occluded by the mountain topography.
// ---------------------------------------------------------------------------

const SUN_BEAM_PROFILES = [
  // relAngle (-1 to 1 across upper hemisphere), halfWidth (rad), baseAlpha, coreWarmth
  { relAngle: -0.94, halfW: 0.038, alpha: 0.65, warm: false },
  { relAngle: -0.87, halfW: 0.054, alpha: 0.50, warm: true  },
  { relAngle: -0.80, halfW: 0.024, alpha: 0.75, warm: false }, // sharp razor beam
  { relAngle: -0.73, halfW: 0.046, alpha: 0.58, warm: true  },
  { relAngle: -0.66, halfW: 0.032, alpha: 0.72, warm: false },
  { relAngle: -0.59, halfW: 0.062, alpha: 0.48, warm: true  },
  { relAngle: -0.52, halfW: 0.022, alpha: 0.85, warm: false }, // brilliant intense shaft
  { relAngle: -0.45, halfW: 0.048, alpha: 0.62, warm: true  },
  { relAngle: -0.38, halfW: 0.035, alpha: 0.76, warm: false },
  { relAngle: -0.31, halfW: 0.058, alpha: 0.52, warm: true  },
  { relAngle: -0.24, halfW: 0.026, alpha: 0.82, warm: false }, // sharp razor beam
  { relAngle: -0.17, halfW: 0.044, alpha: 0.68, warm: true  },
  { relAngle: -0.09, halfW: 0.030, alpha: 0.78, warm: false },
  { relAngle:  0.00, halfW: 0.065, alpha: 0.90, warm: false }, // zenith vertical flood beam!
  { relAngle:  0.09, halfW: 0.032, alpha: 0.78, warm: false },
  { relAngle:  0.17, halfW: 0.045, alpha: 0.66, warm: true  },
  { relAngle:  0.24, halfW: 0.026, alpha: 0.82, warm: false }, // sharp razor beam
  { relAngle:  0.31, halfW: 0.056, alpha: 0.52, warm: true  },
  { relAngle:  0.38, halfW: 0.035, alpha: 0.74, warm: false },
  { relAngle:  0.45, halfW: 0.050, alpha: 0.60, warm: true  },
  { relAngle:  0.52, halfW: 0.021, alpha: 0.86, warm: false }, // brilliant intense shaft
  { relAngle:  0.59, halfW: 0.064, alpha: 0.48, warm: true  },
  { relAngle:  0.66, halfW: 0.034, alpha: 0.72, warm: false },
  { relAngle:  0.73, halfW: 0.048, alpha: 0.58, warm: true  },
  { relAngle:  0.80, halfW: 0.025, alpha: 0.76, warm: false }, // sharp razor beam
  { relAngle:  0.87, halfW: 0.055, alpha: 0.50, warm: true  },
  { relAngle:  0.94, halfW: 0.036, alpha: 0.65, warm: false }
];

let _warmRayGrad = null;
let _coolRayGrad = null;
let _valleyGlowGrad = null;

function ensureSunbeamGradients(ctx, rayLength) {
  if (!_warmRayGrad) {
    _warmRayGrad = ctx.createLinearGradient(0, 0, rayLength, 0);
    _warmRayGrad.addColorStop(0.00, 'rgba(255, 250, 220, 0.60)');
    _warmRayGrad.addColorStop(0.20, 'rgba(254, 215, 170, 0.52)');
    _warmRayGrad.addColorStop(0.48, 'rgba(251, 146, 60,  0.36)');
    _warmRayGrad.addColorStop(0.75, 'rgba(234, 88, 12,   0.18)');
    _warmRayGrad.addColorStop(1.00, 'rgba(225, 29, 72, 0)');

    _coolRayGrad = ctx.createLinearGradient(0, 0, rayLength, 0);
    _coolRayGrad.addColorStop(0.00, 'rgba(255, 255, 245, 0.70)');
    _coolRayGrad.addColorStop(0.18, 'rgba(254, 240, 138, 0.58)');
    _coolRayGrad.addColorStop(0.50, 'rgba(251, 191, 36,  0.38)');
    _coolRayGrad.addColorStop(0.78, 'rgba(249, 115, 22,  0.16)');
    _coolRayGrad.addColorStop(1.00, 'rgba(225, 29, 72, 0)');

    _valleyGlowGrad = ctx.createLinearGradient(0, 110, 0, 230);
    _valleyGlowGrad.addColorStop(0.0, 'rgba(251, 146, 60, 0)');
    _valleyGlowGrad.addColorStop(0.4, 'rgba(254, 215, 170, 0.12)');
    _valleyGlowGrad.addColorStop(0.8, 'rgba(251, 146, 60, 0.20)');
    _valleyGlowGrad.addColorStop(1.0, 'rgba(234, 88, 12, 0.08)');
  }
}

function drawVolumetricSunshafts(ctx, t) {
  if (state.quality === 'low') return;

  // Storm darkness attenuation (Option 3A: Sunbeams smoothly fade to zero under storm clouds)
  const shaftVis = Math.max(0.0, 1.0 - weatherState.stormDarkness * 1.15);
  if (shaftVis <= 0.001) return;

  const minX = (state.visibleLeft !== undefined ? state.visibleLeft : 0);
  const maxX = (state.visibleRight !== undefined ? state.visibleRight : WIDTH);
  const sunX = WIDTH / 2;
  const sunY = state.sunY;

  ctx.save();
  ctx.globalAlpha = shaftVis;

  // Screen blending ensures sunlight gracefully floods mountain passes and gorges
  ctx.globalCompositeOperation = 'screen';

  const numBeams = SUN_BEAM_PROFILES.length;
  const rayLength = Math.max(1250, Math.hypot(Math.max(sunX - minX, maxX - sunX), 400));
  const qScale = state.quality === 'high' ? 1.0 : 0.70;

  ensureSunbeamGradients(ctx, rayLength);

  ctx.save();
  ctx.translate(sunX, sunY);

  for (let b = 0; b < numBeams; b++) {
    // In medium quality, skip every 2nd beam
    if (state.quality === 'medium' && (b % 2 !== 0)) continue;

    const prof = SUN_BEAM_PROFILES[b];

    // Angular placement across upper hemisphere (-0.96*PI to -0.04*PI)
    const baseAngle = -Math.PI * 0.5 + prof.relAngle * (Math.PI * 0.46);

    // Subtle atmospheric thermal shimmer
    const angleJitter = Math.sin(t * 0.35 + b * 1.7) * 0.008 + Math.cos(t * 0.18 + b * 2.5) * 0.004;
    const angle = baseAngle + angleJitter;

    // Atmospheric density scintillation
    const pulse = 0.82 + 0.18 * Math.sin(t * 1.4 + b * 2.1);
    const beamAlpha = prof.alpha * pulse * qScale;

    const beamHalfW = prof.halfW;
    const cosHalfW = Math.cos(beamHalfW) * rayLength;
    const sinHalfW = Math.sin(beamHalfW) * rayLength;

    ctx.rotate(angle);
    ctx.globalAlpha = Math.min(1.0, shaftVis * beamAlpha);
    ctx.fillStyle = prof.warm ? _warmRayGrad : _coolRayGrad;

    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(cosHalfW, -sinHalfW);
    ctx.lineTo(cosHalfW, sinHalfW);
    ctx.closePath();
    ctx.fill();
    ctx.rotate(-angle);
  }
  ctx.restore();

  // Atmospheric Valley Light Pools (Warmer glow pooling in mountain saddles)
  if (state.quality === 'high') {
    ctx.fillStyle = _valleyGlowGrad;
    ctx.fillRect(minX, 110, maxX - minX, 120);

    // Drifting Golden Solar Dust Motes in the Sunbeams
    const numMotes = 32;
    const motesSpanW = Math.max(WIDTH, maxX - minX);
    for (let m = 0; m < numMotes; m++) {
      const seedVal = m * 137.5;
      const speedX = 18 + (m % 7) * 4;
      const mx = minX + (((seedVal * 19.3 - t * speedX) % motesSpanW + motesSpanW) % motesSpanW);
      const my = 30 + ((seedVal * 7.7) % 170) + Math.sin(t * 1.5 + m) * 6;

      const dx = mx - sunX;
      const dy = my - sunY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 100 || dist > 700 || my > 220) continue;

      const moteAngle = Math.atan2(dy, dx);
      if (moteAngle > -0.05 || moteAngle < -Math.PI + 0.05) continue;

      const sparkle = 0.4 + 0.6 * Math.sin(t * 4.0 + m * 2.1);
      ctx.fillStyle = `rgba(254, 240, 138, ${sparkle * 0.55})`;
      ctx.beginPath();
      ctx.arc(mx, my, 1.3, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  ctx.restore();
}

// ---------------------------------------------------------------------------
// ATMOSPHERIC WEATHER, CLOUDS, WETTERLEUCHTEN & PROCEDURAL THUNDER ENGINE
// High-altitude drifting cirrus veils, colossal bruised cumulonimbus storm front,
// staccato intra-cloud sheet lightning with molten golden radiance, cool deep-indigo
// nocturnal battlefield ambient ("Nachtlicht"), warm deck bounce illumination,
// and procedural Web Audio API low-frequency thunder rumble with stereo panning.
// ---------------------------------------------------------------------------

let audioCtx = null;
let _cachedThunderBuffer = null;
let _cachedThunderSampleRate = 0;
let _cachedCrackleBuffer = null;
let _cachedCrackleSampleRate = 0;

function getAudioContext() {
  if (typeof window === 'undefined') return null;
  const shared = window.Howler?.ctx || window.game?.audio?.ctx || window.tcgGame?.audio?.ctx;
  if (shared) return shared;
  if (!audioCtx) {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (AudioCtx) {
      audioCtx = new AudioCtx();
    }
  }
  if (audioCtx && audioCtx.state === 'suspended') {
    audioCtx.resume().catch(() => {});
  }
  return audioCtx;
}

function getAudioDestination(actx) {
  if (typeof window !== 'undefined' && window.Howler?.masterGain) {
    return window.Howler.masterGain;
  }
  return actx.destination;
}

function isAudioMuted() {
  if (typeof window === 'undefined') return true;
  if (window.game?.audio?.isMuted || window.tcgGame?.audio?.isMuted) return true;
  if (window.Howler && window.Howler._muted) return true;
  return false;
}

function getThunderNoiseBuffer(actx) {
  if (_cachedThunderBuffer && _cachedThunderSampleRate === actx.sampleRate) {
    return _cachedThunderBuffer;
  }
  const duration = 3.8;
  const bufferSize = Math.floor(actx.sampleRate * duration);
  const buffer = actx.createBuffer(1, bufferSize, actx.sampleRate);
  const output = buffer.getChannelData(0);
  let lastOut = 0.0;
  for (let i = 0; i < bufferSize; i++) {
    const white = Math.random() * 2 - 1;
    lastOut = (lastOut + (0.022 * white)) / 1.022;
    output[i] = lastOut * 3.8;
  }
  _cachedThunderBuffer = buffer;
  _cachedThunderSampleRate = actx.sampleRate;
  return buffer;
}

function getCrackleNoiseBuffer(actx) {
  if (_cachedCrackleBuffer && _cachedCrackleSampleRate === actx.sampleRate) {
    return _cachedCrackleBuffer;
  }
  const duration = 0.18; // covers max crackle duration
  const bufferSize = Math.floor(actx.sampleRate * duration);
  const buffer = actx.createBuffer(1, bufferSize, actx.sampleRate);
  const output = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    const isPop = Math.random() < 0.07;
    output[i] = isPop ? (Math.random() * 2 - 1) * 0.88 : (Math.random() * 0.08 - 0.04);
  }
  _cachedCrackleBuffer = buffer;
  _cachedCrackleSampleRate = actx.sampleRate;
  return buffer;
}

function playThunderSound(flashX = 800) {
  if (!weatherState.soundEnabled || isAudioMuted()) return;
  const actx = getAudioContext();
  if (!actx || actx.state !== 'running') return;

  try {
    const now = actx.currentTime;
    // Acoustic atmospheric propagation delay: sound arrives 0.16s .. 0.28s after optical flash
    const startTime = now + 0.18;
    const duration = 3.8;

    // 1. Procedural Brown Noise Buffer (cached to prevent 182,400-loop frame drops)
    const noiseBuffer = getThunderNoiseBuffer(actx);
    const noiseSource = actx.createBufferSource();
    noiseSource.buffer = noiseBuffer;

    // Resonant Sweeping Lowpass Filter (simulates acoustic diffraction and mountain reverberation)
    const filter = actx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(145, startTime);
    filter.frequency.exponentialRampToValueAtTime(75, startTime + 0.5);
    filter.frequency.linearRampToValueAtTime(115, startTime + 1.3); // acoustic echo bounce
    filter.frequency.exponentialRampToValueAtTime(42, startTime + duration);
    filter.Q.setValueAtTime(3.8, startTime);

    // Gain Envelope: Sudden subterranean thud -> rolling rumble -> long echoing tail
    const noiseGain = actx.createGain();
    noiseGain.gain.setValueAtTime(0.001, startTime);
    noiseGain.gain.linearRampToValueAtTime(0.70, startTime + 0.06); // sharp thunder impact
    noiseGain.gain.exponentialRampToValueAtTime(0.36, startTime + 0.40);
    noiseGain.gain.linearRampToValueAtTime(0.52, startTime + 1.05); // secondary rolling wave
    noiseGain.gain.exponentialRampToValueAtTime(0.18, startTime + 2.2);
    noiseGain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);

    // 2. Sub-Bass Sine Wave Oscillator (subterranean physical shockwave pressure)
    const subOsc = actx.createOscillator();
    subOsc.type = 'sine';
    subOsc.frequency.setValueAtTime(40, startTime);
    subOsc.frequency.exponentialRampToValueAtTime(26, startTime + duration * 0.85);

    const subGain = actx.createGain();
    subGain.gain.setValueAtTime(0.001, startTime);
    subGain.gain.linearRampToValueAtTime(0.60, startTime + 0.08);
    subGain.gain.exponentialRampToValueAtTime(0.22, startTime + 0.9);
    subGain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration * 0.85);

    // 3. Stereo Panner (Sound origin matches lightning flash X relative to center 800)
    let panner = null;
    if (actx.createStereoPanner) {
      panner = actx.createStereoPanner();
      const panVal = Math.max(-0.85, Math.min(0.85, (flashX - 800) / 750));
      panner.pan.setValueAtTime(panVal, startTime);
    }

    // Master Output Gain
    const masterGain = actx.createGain();
    masterGain.gain.setValueAtTime(0.75, startTime);

    // Connect audio processing graph
    noiseSource.connect(filter);
    filter.connect(noiseGain);
    subOsc.connect(subGain);

    if (panner) {
      noiseGain.connect(panner);
      subGain.connect(panner);
      panner.connect(masterGain);
    } else {
      noiseGain.connect(masterGain);
      subGain.connect(masterGain);
    }
    masterGain.connect(getAudioDestination(actx));

    noiseSource.start(startTime);
    noiseSource.stop(startTime + duration);
    subOsc.start(startTime);
    subOsc.stop(startTime + duration);
  } catch (err) {
    // Non-blocking fallback
  }
}

// Procedural Atmospheric Electrostatic Knistern & Ionization Discharge Crackle
function playIonizationCrackle(panX = 800) {
  if (!weatherState.soundEnabled || isAudioMuted()) return;
  const actx = getAudioContext();
  if (!actx || actx.state !== 'running') return;

  try {
    const now = actx.currentTime;
    const duration = 0.09 + Math.random() * 0.08; // 0.09 .. 0.17s

    // Highpass filtered micro-spark noise buffer (cached buffer)
    const noiseBuffer = getCrackleNoiseBuffer(actx);
    const source = actx.createBufferSource();
    source.buffer = noiseBuffer;

    const filter = actx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.setValueAtTime(2400 + Math.random() * 800, now);
    filter.Q.setValueAtTime(3.4, now);

    const gainNode = actx.createGain();
    gainNode.gain.setValueAtTime(0.001, now);
    gainNode.gain.linearRampToValueAtTime(0.15, now + 0.015);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    let panner = null;
    if (actx.createStereoPanner) {
      panner = actx.createStereoPanner();
      const panVal = Math.max(-0.85, Math.min(0.85, (panX - 800) / 750));
      panner.pan.setValueAtTime(panVal, now);
    }

    const dest = getAudioDestination(actx);
    source.connect(filter);
    filter.connect(gainNode);
    if (panner) {
      gainNode.connect(panner);
      panner.connect(dest);
    } else {
      gainNode.connect(dest);
    }

    source.start(now);
    source.stop(now + duration);
  } catch (err) {
    // Non-blocking fallback
  }
}

// ---------------------------------------------------------------------------
// ATMOSPHERIC IONIZATION SPARKS (St. Elmo's Fire & Micro-Arc Discharges)
// "in der Luft knistern - wie ionisierte Luft, mit leichtem Leuchten
// und kleinen, punktuellen Entladungen"
// ---------------------------------------------------------------------------

const ionizationSparks = [];

function spawnIonizationSpark(t, forcedX = null, forcedY = null) {
  // Keep simultaneous active micro-sparks limited (at most 3) so it stays subtle and atmospheric
  if (ionizationSparks.length >= 3) return;

  let sx, sy;
  if (forcedX !== null && forcedY !== null) {
    sx = forcedX;
    sy = forcedY;
  } else {
    // 40% chance of tip discharge (antennas, gun barrels, radar rim), 60% free air
    if (Math.random() < 0.40) {
      const tipTargets = [
        { x: 1234 + (Math.random() - 0.5) * 35, y: 110 + (Math.random() - 0.5) * 30 }, // antenna mast spikes
        { x: 640 + (Math.random() - 0.5) * 20,  y: 145 + (Math.random() - 0.5) * 18 }, // turret 1 barrel tips
        { x: 960 + (Math.random() - 0.5) * 20,  y: 145 + (Math.random() - 0.5) * 18 }, // turret 2 barrel tips
        { x: 350 + (Math.random() - 0.5) * 26,  y: 170 + (Math.random() - 0.5) * 18 }  // radar dish rim
      ];
      const target = tipTargets[Math.floor(Math.random() * tipTargets.length)];
      sx = target.x;
      sy = target.y;
    } else {
      // Atmospheric mid-air discharge pocket across visible width
      const minX = (state.visibleLeft !== undefined ? state.visibleLeft : 0);
      const maxX = (state.visibleRight !== undefined ? state.visibleRight : WIDTH);
      sx = minX + 160 + Math.random() * Math.max(100, maxX - minX - 320);
      sy = 45 + Math.random() * 120;
    }
  }

  const types = ['violet', 'cyan', 'gold'];
  const typeWeights = [0.50, 0.30, 0.20];
  let r = Math.random(), colorType = 'violet';
  if (r < typeWeights[0]) colorType = 'violet';
  else if (r < typeWeights[0] + typeWeights[1]) colorType = 'cyan';
  else colorType = 'gold';

  // Procedural jagged electric arc segments (3-4 zigzag steps + 1 branch)
  const segments = [];
  const numSteps = 3 + Math.floor(Math.random() * 2);
  let curX = 0, curY = 0;
  const dirAngle = Math.random() * Math.PI * 2;
  const stepLen = 5.5 + Math.random() * 3.5;

  for (let i = 0; i < numSteps; i++) {
    const angleDev = (Math.random() - 0.5) * 1.3;
    const nextX = curX + Math.cos(dirAngle + angleDev) * stepLen;
    const nextY = curY + Math.sin(dirAngle + angleDev) * stepLen;
    segments.push({ x0: curX, y0: curY, x1: nextX, y1: nextY });

    // Branch off from intermediate node
    if (i === 1 && Math.random() < 0.75) {
      const branchAngle = dirAngle + (Math.random() > 0.5 ? 0.75 : -0.75);
      const bLen = stepLen * 0.7;
      segments.push({
        x0: nextX,
        y0: nextY,
        x1: nextX + Math.cos(branchAngle) * bLen,
        y1: nextY + Math.sin(branchAngle) * bLen
      });
    }

    curX = nextX;
    curY = nextY;
  }

  const duration = 0.16 + Math.random() * 0.12; // 0.16 .. 0.28s
  const radius = 16 + Math.random() * 10;

  ionizationSparks.push({
    x: sx,
    y: sy,
    startTime: t,
    duration,
    radius,
    colorType,
    segments
  });

  playIonizationCrackle(sx);
}

function triggerLightning(customX, customY) {
  const flashX = (customX !== undefined) ? customX : (380 + Math.random() * 840);
  const flashY = (customY !== undefined) ? customY : (35 + Math.random() * 60);

  weatherState.flashStartTime = state.t;
  weatherState.flashX = flashX;
  weatherState.flashY = flashY;
  weatherState.flashSpark1Triggered = false;
  weatherState.flashSpark2Triggered = false;

  playThunderSound(flashX);
}

function updateWeather(dt) {
  const t = state.t;
  let targetCover = 0.20;
  let targetDark = 0.0;

  if (weatherState.mode === 'auto') {
    const cTime = (t % weatherState.cycleDuration);
    if (cTime < 45.0) {
      // Phase 1: Clear sunset with gentle wispy cirrus veils
      targetCover = 0.18 + 0.08 * Math.sin(cTime * 0.15);
      targetDark = 0.0;
    } else if (cTime < 70.0) {
      // Phase 2: Cirrus gathering & stratocumulus thickening
      const p = (cTime - 45.0) / 25.0;
      targetCover = 0.26 + 0.34 * p;
      targetDark = 0.18 * p;
    } else if (cTime < 92.0) {
      // Phase 3: Storm front ingress (colossal thunderheads roll in, sun dims, nightlight sets in)
      const p = (cTime - 70.0) / 22.0;
      const smoothP = p * p * (3 - 2 * p);
      targetCover = 0.60 + 0.40 * smoothP;
      targetDark = 0.18 + 0.82 * smoothP;
    } else if (cTime < 116.0) {
      // Phase 4: Peak Thunderstorm with active Wetterleuchten
      targetCover = 1.0;
      targetDark = 1.0;

      // Auto-trigger periodic Wetterleuchten
      if (t >= weatherState.nextAutoLightningTime) {
        triggerLightning();
        weatherState.nextAutoLightningTime = t + 4.2 + Math.random() * 3.5;
      }
    } else {
      // Phase 5: Storm front clearing to the east, golden sunset returning
      const p = (cTime - 116.0) / 14.0;
      const smoothP = p * p * (3 - 2 * p);
      targetCover = 1.0 - 0.78 * smoothP;
      targetDark = 1.0 - 1.0 * smoothP;
    }
  } else if (weatherState.mode === 'storm') {
    targetCover = 1.0;
    targetDark = 1.0;
    if (t >= weatherState.nextAutoLightningTime) {
      triggerLightning();
      weatherState.nextAutoLightningTime = t + 4.2 + Math.random() * 3.5;
    }
  } else if (weatherState.mode === 'cirrus') {
    targetCover = 0.45;
    targetDark = 0.18;
  } else if (weatherState.mode === 'clear') {
    targetCover = 0.06;
    targetDark = 0.0;
  }

  // Smooth exponential approach for organic, cinematic light transitions ("weiche lichtwechsel")
  const easeRate = Math.min(1.0, dt * 1.8);
  weatherState.cloudCover += (targetCover - weatherState.cloudCover) * easeRate;
  weatherState.stormDarkness += (targetDark - weatherState.stormDarkness) * easeRate;

  // Compute active lightning flash envelope (Option 2A: Staccato micro-bursts + warm rolling afterglow)
  const tau = t - weatherState.flashStartTime;
  if (tau >= 0 && tau < 1.35) {
    let intensity = 0;
    if (tau < 0.07) {
      // Micro-burst 1: sudden sharp ignition (0.0 -> 0.88)
      intensity = Math.sin((tau / 0.07) * (Math.PI * 0.5)) * 0.88;
    } else if (tau < 0.12) {
      // Micro-dip 1: brief discharge interval
      intensity = 0.22 + 0.10 * Math.sin(((tau - 0.07) / 0.05) * Math.PI);
    } else if (tau < 0.22) {
      // Micro-burst 2: blinding secondary core spike to 1.0!
      const p = (tau - 0.12) / 0.10;
      intensity = 0.30 + 0.70 * Math.sin(p * Math.PI);
    } else {
      // Rolling warm afterglow: exponential decay with atmospheric acoustic flicker
      const elapsed = tau - 0.22;
      const decay = Math.exp(-3.4 * elapsed);
      const flicker = 1.0 + 0.12 * Math.sin(elapsed * 42.0);
      intensity = Math.max(0, 0.65 * decay * flicker);
    }
    weatherState.lightningIntensity = intensity;
  } else {
    weatherState.lightningIntensity = 0.0;
  }

  // Manage active atmospheric ionization sparks (clean up expired)
  for (let i = ionizationSparks.length - 1; i >= 0; i--) {
    if (t - ionizationSparks[i].startTime >= ionizationSparks[i].duration) {
      ionizationSparks.splice(i, 1);
    }
  }

  // Trigger ionization sparks during Wetterleuchten & storm electrostatic tension
  const isStorming = isStormActiveOrApproaching(t);
  const flashAge = t - weatherState.flashStartTime;

  // During active sheet lightning flash: burst 1 near ignition (0.06s), burst 2 during afterglow (0.24s)
  if (flashAge >= 0.05 && flashAge < 0.12 && !weatherState.flashSpark1Triggered) {
    weatherState.flashSpark1Triggered = true;
    spawnIonizationSpark(t, weatherState.flashX + (Math.random() - 0.5) * 160, weatherState.flashY + 30 + Math.random() * 50);
  }
  if (flashAge >= 0.22 && flashAge < 0.32 && !weatherState.flashSpark2Triggered) {
    weatherState.flashSpark2Triggered = true;
    spawnIonizationSpark(t);
  }

  // Between flashes: occasional ambient static crackles in the air ("mal hier mal da in der luft")
  if (isStorming && t >= weatherState.nextAutoSparkTime) {
    spawnIonizationSpark(t);
    weatherState.nextAutoSparkTime = t + 2.8 + Math.random() * 3.4;
  }
}

// ---------------------------------------------------------------------------
// CLOUD RENDERING PASSES
// ---------------------------------------------------------------------------

function drawCirrusVeils(ctx, t, alpha) {
  // Delicate wispy ice-crystal cloud ribbons drifting across the upper screen edge (Y: 10..105)
  const ribbons = [
    { baseY: 18, waveAmp: 8.0, speed: 5.5,  thickness: 24, c1: 'rgba(255, 250, 240,', c2: 'rgba(254, 215, 170,' },
    { baseY: 42, waveAmp: 9.5, speed: 7.8,  thickness: 28, c1: 'rgba(254, 215, 170,', c2: 'rgba(251, 146, 60,' },
    { baseY: 68, waveAmp: 8.5, speed: 10.2, thickness: 26, c1: 'rgba(251, 146, 60,',  c2: 'rgba(244, 114, 182,' },
    { baseY: 94, waveAmp: 7.0, speed: 12.5, thickness: 20, c1: 'rgba(254, 240, 138,', c2: 'rgba(249, 115, 22,' }
  ];

  ctx.save();
  const minX = (state.visibleLeft !== undefined ? state.visibleLeft : 0);
  const maxX = (state.visibleRight !== undefined ? state.visibleRight : WIDTH);

  for (let r = 0; r < ribbons.length; r++) {
    const rb = ribbons[r];
    const offset = (t * rb.speed) % 1200;

    const grad = ctx.createLinearGradient(0, rb.baseY - rb.waveAmp, 0, rb.baseY + rb.thickness + rb.waveAmp);
    grad.addColorStop(0.00, `${rb.c1} 0)`);
    grad.addColorStop(0.25, `${rb.c1} ${(alpha * 0.90).toFixed(3)})`);
    grad.addColorStop(0.65, `${rb.c2} ${(alpha * 0.70).toFixed(3)})`);
    grad.addColorStop(1.00, `${rb.c2} 0)`);

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(minX - 100, rb.baseY);

    // Top wavy edge with organic harmonics
    for (let x = minX - 100; x <= maxX + 100; x += 35) {
      const yTop = rb.baseY + Math.sin((x + offset) * 0.008) * rb.waveAmp + Math.cos((x - offset * 0.35) * 0.017) * (rb.waveAmp * 0.45);
      ctx.lineTo(x, yTop);
    }

    // Bottom wavy edge with varied wavelength for tapered filament thickness
    for (let x = maxX + 100; x >= minX - 100; x -= 35) {
      const yBot = rb.baseY + rb.thickness + Math.sin((x + offset + 90) * 0.007) * (rb.waveAmp * 1.3) + Math.cos(x * 0.015) * 5.0;
      ctx.lineTo(x, yBot);
    }

    ctx.closePath();
    ctx.fill();

    // Delicate brush wisps (feathery cirrus uncinus filaments)
    if (state.quality !== 'low' && r % 2 === 0) {
      ctx.strokeStyle = `${rb.c1} ${(alpha * 0.50).toFixed(3)})`;
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      for (let x = minX - 60; x <= maxX + 60; x += 90) {
        const wx = x + ((offset * 1.2) % 90);
        const wy = rb.baseY + Math.sin((wx + offset) * 0.008) * rb.waveAmp;
        ctx.moveTo(wx, wy);
        ctx.quadraticCurveTo(wx + 25, wy - 8, wx + 45, wy - 3);
      }
      ctx.stroke();
    }
  }
  ctx.restore();
}

const CLOUD_NODES = [
  { x: -80,  y: 65,  r: 95 },
  { x: 60,   y: 40,  r: 110 },
  { x: 190,  y: 75,  r: 85 },
  { x: 310,  y: 35,  r: 125 },
  { x: 450,  y: 60,  r: 100 },
  { x: 590,  y: 30,  r: 135 },
  { x: 740,  y: 55,  r: 105 },
  { x: 880,  y: 25,  r: 140 }, // Zenith massive thunderhead
  { x: 1030, y: 50,  r: 110 },
  { x: 1170, y: 35,  r: 130 },
  { x: 1310, y: 65,  r: 95 },
  { x: 1450, y: 40,  r: 120 },
  { x: 1590, y: 55,  r: 105 },
  { x: 1720, y: 35,  r: 115 }
];

function drawStormFrontMassifs(ctx, t, alpha, darkness, lightning, flashX, flashY) {
  ctx.save();
  ctx.globalAlpha = alpha;

  const minX = (state.visibleLeft !== undefined ? state.visibleLeft : 0);
  const maxX = (state.visibleRight !== undefined ? state.visibleRight : WIDTH);
  const bgW = maxX - minX;

  const drift = (t * 7.5) % 1840;

  // 1. Broad Anvil Atmosphere & Sky Dimming (Full upper sky Y: -60 .. 220)
  // Seamlessly dissolves into the mountain passes without any abrupt line
  const anvilGrad = ctx.createLinearGradient(0, -60, 0, 220);
  anvilGrad.addColorStop(0.00, '#070a12');
  anvilGrad.addColorStop(0.30, '#0f172a');
  anvilGrad.addColorStop(0.60, '#1e1b4b');
  anvilGrad.addColorStop(0.85, 'rgba(15, 23, 42, 0.65)');
  anvilGrad.addColorStop(1.00, 'rgba(15, 23, 42, 0.00)');

  ctx.fillStyle = anvilGrad;
  ctx.fillRect(minX, -60, bgW, 280);

  // 2. Continuous Volumetric Cumulonimbus Cloud Bank
  const cloudBodyGrad = ctx.createLinearGradient(0, -60, 0, 185);
  cloudBodyGrad.addColorStop(0.00, '#090d16');
  cloudBodyGrad.addColorStop(0.35, '#0f172a');
  cloudBodyGrad.addColorStop(0.70, '#1e1b4b');
  cloudBodyGrad.addColorStop(1.00, '#090d16');

  // Solid compound foundation silhouette
  ctx.beginPath();
  ctx.moveTo(minX - 140, -80);
  ctx.lineTo(maxX + 140, -80);
  ctx.lineTo(maxX + 140, 155);

  // Bottom edge: undulating mammatus pouches
  for (let x = maxX + 140; x >= minX - 140; x -= 35) {
    const mamY = 135 + Math.sin((x + drift * 0.7) * 0.012) * 22 + Math.cos((x - drift * 0.3) * 0.024) * 10;
    ctx.lineTo(x, mamY);
  }
  ctx.closePath();
  ctx.fillStyle = cloudBodyGrad;
  ctx.fill();

  // Billowing Cumulus Domes across the visible span
  const wrapOffsets = [-2 * 1840, -1840, 0, 1840, 2 * 1840];
  ctx.beginPath();
  for (let w = 0; w < wrapOffsets.length; w++) {
    for (let i = 0; i < CLOUD_NODES.length; i++) {
      const node = CLOUD_NODES[i];
      const nx = ((node.x - drift + 3680) % 1840) - 120 + wrapOffsets[w];
      if (nx + node.r < minX - 120 || nx - node.r > maxX + 120) continue;
      const ny = node.y;
      const nr = node.r;

      ctx.moveTo(nx + nr, ny);
      ctx.arc(nx, ny, nr, 0, Math.PI * 2);
    }
  }
  ctx.fillStyle = cloudBodyGrad;
  ctx.fill();

  // Internal Volumetric Depth Shading on Domes
  for (let w = 0; w < wrapOffsets.length; w++) {
    for (let i = 0; i < CLOUD_NODES.length; i++) {
      const node = CLOUD_NODES[i];
      const nx = ((node.x - drift + 3680) % 1840) - 120 + wrapOffsets[w];
      if (nx + node.r < minX - 120 || nx - node.r > maxX + 120) continue;
      const ny = node.y;
      const nr = node.r;

      // Check distance to lightning flash for intra-cloud illumination
      const distToFlash = Math.hypot(nx - flashX, ny - flashY);
      const flashGlow = (lightning > 0.005 && distToFlash < 650) ? Math.max(0, 1.0 - distToFlash / 650) * lightning : 0;

      // Base volumetric shading: Fades smoothly to ALPHA = 0 at perimeter (eliminates circular ring artifacts!)
      const domeGrad = ctx.createRadialGradient(nx - nr * 0.2, ny - nr * 0.4, nr * 0.1, nx, ny, nr);
      domeGrad.addColorStop(0.00, 'rgba(46, 16, 101, 0.45)');  // bruised plum highlight
      domeGrad.addColorStop(0.40, 'rgba(30, 27, 75, 0.28)');
      domeGrad.addColorStop(0.75, 'rgba(15, 23, 42, 0.18)');
      domeGrad.addColorStop(1.00, 'rgba(15, 23, 42, 0.00)');   // Completely transparent at rim!
      ctx.fillStyle = domeGrad;
      ctx.beginPath();
      ctx.arc(nx, ny, nr, 0, Math.PI * 2);
      ctx.fill();

      // Smooth flash illumination overlay without sudden threshold pops:
      if (flashGlow > 0.005) {
        const flashGrad = ctx.createRadialGradient(nx - nr * 0.2, ny - nr * 0.4, nr * 0.05, nx, ny, nr);
        flashGrad.addColorStop(0.00, `rgba(254, 240, 138, ${(0.85 * flashGlow).toFixed(3)})`);
        flashGrad.addColorStop(0.35, `rgba(249, 115, 22,  ${(0.65 * flashGlow).toFixed(3)})`);
        flashGrad.addColorStop(0.70, `rgba(129, 140, 248, ${(0.35 * flashGlow).toFixed(3)})`);
        flashGrad.addColorStop(1.00, 'rgba(15, 23, 42, 0.00)');
        ctx.fillStyle = flashGrad;
        ctx.beginPath();
        ctx.arc(nx, ny, nr, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  // Soft Mist Feathering at Bottom of Cloud Bank (Y: 115 .. 200)
  // Blends seamlessly into the mountain peaks and valley haze
  const mistFeather = ctx.createLinearGradient(0, 115, 0, 200);
  mistFeather.addColorStop(0.00, 'rgba(15, 23, 42, 0)');
  mistFeather.addColorStop(0.40, 'rgba(15, 23, 42, 0.45)');
  mistFeather.addColorStop(0.80, 'rgba(15, 23, 42, 0.20)');
  mistFeather.addColorStop(1.00, 'rgba(15, 23, 42, 0)');
  ctx.fillStyle = mistFeather;
  ctx.fillRect(minX, 115, bgW, 85);

  ctx.restore();
}

function drawIntraCloudWetterleuchten(ctx, lightning, flashX, flashY) {
  ctx.save();
  ctx.globalCompositeOperation = 'screen';

  // 1. Colossal Radial Flash Core (Sheet lightning illuminating cloud vapor from within)
  const coreGrad = ctx.createRadialGradient(flashX, flashY, 0, flashX, flashY, 480);
  coreGrad.addColorStop(0.00, `rgba(255, 255, 255, ${(lightning * 0.98).toFixed(3)})`);
  coreGrad.addColorStop(0.12, `rgba(254, 240, 138, ${(lightning * 0.90).toFixed(3)})`);
  coreGrad.addColorStop(0.35, `rgba(251, 191, 36,  ${(lightning * 0.72).toFixed(3)})`);
  coreGrad.addColorStop(0.65, `rgba(234, 88, 12,   ${(lightning * 0.35).toFixed(3)})`);
  coreGrad.addColorStop(0.90, `rgba(180, 83, 9,    ${(lightning * 0.10).toFixed(3)})`);
  coreGrad.addColorStop(1.00, 'rgba(0, 0, 0, 0)');

  ctx.fillStyle = coreGrad;
  ctx.beginPath();
  ctx.arc(flashX, flashY, 480, 0, Math.PI * 2);
  ctx.fill();

  // 2. Intra-Cloud Discharge Filaments (Veins of sheet lightning rippling through the vapor)
  if (state.quality !== 'low') {
    const seed = Math.floor(weatherState.flashStartTime * 50);
    const rng = mulberry32(seed);

    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Left discharge branch
    let vx = flashX;
    let vy = flashY;
    ctx.beginPath();
    ctx.moveTo(vx, vy);
    for (let step = 0; step < 7; step++) {
      vx -= 28 + rng() * 26;
      vy += (rng() - 0.48) * 24;
      ctx.lineTo(vx, vy);
    }
    ctx.strokeStyle = `rgba(251, 191, 36, ${(lightning * 0.65).toFixed(3)})`;
    ctx.lineWidth = 5.5;
    ctx.stroke();

    ctx.strokeStyle = `rgba(255, 255, 245, ${(lightning * 0.95).toFixed(3)})`;
    ctx.lineWidth = 2.4;
    ctx.stroke();

    // Right discharge branch
    let rx = flashX;
    let ry = flashY;
    ctx.beginPath();
    ctx.moveTo(rx, ry);
    for (let step = 0; step < 8; step++) {
      rx += 32 + rng() * 28;
      ry += (rng() - 0.48) * 22;
      ctx.lineTo(rx, ry);
    }
    ctx.strokeStyle = `rgba(251, 191, 36, ${(lightning * 0.65).toFixed(3)})`;
    ctx.lineWidth = 5.5;
    ctx.stroke();

    ctx.strokeStyle = `rgba(255, 255, 245, ${(lightning * 0.95).toFixed(3)})`;
    ctx.lineWidth = 2.4;
    ctx.stroke();

    ctx.restore();
  }

  // 3. Diffuse Atmospheric Sky Glow (covers upper canvas Y: 0 .. 250 with smooth fade)
  const minX = (state.visibleLeft !== undefined ? state.visibleLeft : 0);
  const maxX = (state.visibleRight !== undefined ? state.visibleRight : WIDTH);
  const skyWash = ctx.createLinearGradient(0, 0, 0, 250);
  skyWash.addColorStop(0.00, `rgba(254, 240, 138, ${(lightning * 0.32).toFixed(3)})`);
  skyWash.addColorStop(0.50, `rgba(251, 146, 60,  ${(lightning * 0.16).toFixed(3)})`);
  skyWash.addColorStop(1.00, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = skyWash;
  ctx.fillRect(minX, 0, maxX - minX, 250);

  ctx.restore();
}

function drawAtmosphericClouds(ctx, t) {
  const cover = weatherState.cloudCover;
  const darkness = weatherState.stormDarkness;
  const lightning = weatherState.lightningIntensity;
  const flashX = weatherState.flashX;
  const flashY = weatherState.flashY;

  if (cover <= 0.02 && darkness <= 0.02 && lightning <= 0.005) return;

  ctx.save();

  // 1. High-Altitude Cirrus Veils (Delicate wispy ice-crystal filaments, Y: 10..105)
  if (cover > 0.04) {
    const cirrusAlpha = Math.min(0.75, cover * 0.75) * (1.0 - darkness * 0.60);
    if (cirrusAlpha > 0.01) {
      drawCirrusVeils(ctx, t, cirrusAlpha);
    }
  }

  // 2. Colossal Storm Cumulonimbus & Mammatus Clouds (Layered billowing thunderheads)
  if (cover > 0.22 || darkness > 0.04) {
    const stormAlpha = Math.min(1.0, Math.max((cover - 0.18) * 1.25, darkness * 1.15));
    if (stormAlpha > 0.01) {
      drawStormFrontMassifs(ctx, t, stormAlpha, darkness, lightning, flashX, flashY);
    }
  }

  // 3. Intra-Cloud Wetterleuchten (Molten golden intra-cloud radiance & sky flash)
  if (lightning > 0.005) {
    drawIntraCloudWetterleuchten(ctx, lightning, flashX, flashY);
  }

  ctx.restore();
}

// ---------------------------------------------------------------------------
// ATMOSPHERIC NOCTURNAL NIGHTLIGHT & WETTERLEUCHTEN ARENA ILLUMINATION
// Layer 6.8: Deep blue/indigo twilight wash across mountains and dreadnought
// deck ("Nachtlicht"), with warm golden diffuse bounce illumination during
// Wetterleuchten strikes for spectacular high-contrast cinematography.
// ---------------------------------------------------------------------------

let _staticNightGrad = null;
let _staticNightGradTop = 0;
let _staticNightGradBottom = HEIGHT;
function getStaticNightGrad(ctx, vTop = 0, vBottom = HEIGHT) {
  if (!_staticNightGrad || _staticNightGradTop !== vTop || _staticNightGradBottom !== vBottom) {
    _staticNightGradTop = vTop;
    _staticNightGradBottom = vBottom;
    _staticNightGrad = ctx.createLinearGradient(0, vTop, 0, vBottom);
    _staticNightGrad.addColorStop(0.00, 'rgba(15, 23, 42, 0.20)');
    _staticNightGrad.addColorStop(0.35, 'rgba(15, 23, 42, 0.42)');
    _staticNightGrad.addColorStop(0.65, 'rgba(23, 37, 84, 0.52)');
    _staticNightGrad.addColorStop(1.00, 'rgba(8, 14, 28,  0.65)');
  }
  return _staticNightGrad;
}

function drawAtmosphericNightlightAmbient(ctx, t, vLeft = 0, vRight = WIDTH, vTop = 0, vBottom = HEIGHT) {
  const darkness = weatherState.stormDarkness;
  const lightning = weatherState.lightningIntensity;
  const flashX = weatherState.flashX;
  const flashY = weatherState.flashY;

  if (darkness <= 0.001 && lightning <= 0.001) return;

  const bgW = vRight - vLeft;
  const bgH = vBottom - vTop;

  ctx.save();

  // 1. Cool Indigo Nocturnal Wash ("Nachtlicht" effect)
  // Spans the ENTIRE canvas seamlessly from top to bottom
  // Deepens shadows across mountains, dreadnought deck plates, turrets, and railings,
  // shifting color temperature from warm sunset copper into stormy midnight slate/blue.
  if (darkness > 0.001) {
    ctx.globalAlpha = Math.min(1.0, darkness);
    ctx.fillStyle = getStaticNightGrad(ctx, vTop, vBottom);
    ctx.fillRect(vLeft, vTop, bgW, bgH);
    ctx.globalAlpha = 1.0;
  }

  // 2. Diffuse Warm Golden Wetterleuchten Bounce on Arena & Dreadnought Deck
  // Breathtaking contrast: the cool dark blue deck suddenly lights up with warm gold!
  // Uses full-canvas radial falloff to zero so there are NEVER any harsh bounding box edges.
  if (lightning > 0.005) {
    ctx.globalCompositeOperation = 'screen';

    // Deck flood bounce radiating downward from the lightning flash in the clouds
    const bounceGrad = ctx.createRadialGradient(flashX, flashY, 10, flashX, flashY, 1100);
    bounceGrad.addColorStop(0.00, `rgba(255, 248, 220, ${(lightning * 0.38).toFixed(3)})`);
    bounceGrad.addColorStop(0.25, `rgba(254, 215, 170, ${(lightning * 0.26).toFixed(3)})`);
    bounceGrad.addColorStop(0.55, `rgba(251, 146, 60,  ${(lightning * 0.14).toFixed(3)})`);
    bounceGrad.addColorStop(0.80, `rgba(234, 88, 12,   ${(lightning * 0.04).toFixed(3)})`);
    bounceGrad.addColorStop(1.00, 'rgba(0, 0, 0, 0)');

    ctx.fillStyle = bounceGrad;
    ctx.fillRect(vLeft, vTop, bgW, bgH);

    // Specular bounce along dreadnought architectural horizontal bevels
    if (state.quality !== 'low') {
      ctx.strokeStyle = `rgba(254, 240, 138, ${(lightning * 0.35).toFixed(3)})`;
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      // Upper hull rim bevel
      ctx.moveTo(vLeft, 230);
      ctx.lineTo(vRight, 230);
      // Catapult trench arrestor guide
      ctx.moveTo(vLeft, 382);
      ctx.lineTo(vRight, 382);
      // Forward perimeter safety railing
      ctx.moveTo(370, 472.5);
      ctx.lineTo(445, 472.5);
      // Lower winglet rim
      ctx.moveTo(vLeft, 496);
      ctx.lineTo(vRight, 496);
      ctx.stroke();

      // Specular glint on turret barrel caps facing lightning
      const flashInfluenceLeft = Math.max(0, 1.0 - Math.abs(flashX - 640) / 600);
      const flashInfluenceRight = Math.max(0, 1.0 - Math.abs(flashX - 960) / 600);

      if (flashInfluenceLeft > 0) {
        ctx.fillStyle = `rgba(255, 255, 240, ${(lightning * flashInfluenceLeft * 0.60).toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(636, 178, 6.0, 0, Math.PI * 2);
        ctx.arc(656, 180, 6.0, 0, Math.PI * 2);
        ctx.fill();
      }

      if (flashInfluenceRight > 0) {
        ctx.fillStyle = `rgba(255, 255, 240, ${(lightning * flashInfluenceRight * 0.60).toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(944, 180, 6.0, 0, Math.PI * 2);
        ctx.arc(964, 178, 6.0, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  ctx.restore();
}

// ---------------------------------------------------------------------------
// ATMOSPHERIC IONIZATION MICRO-DISCHARGES (St. Elmo's Sparks & Air Crackles)
// ---------------------------------------------------------------------------

function drawAtmosphericIonizationSparks(ctx, t) {
  if (ionizationSparks.length === 0) return;

  ctx.save();
  for (let i = 0; i < ionizationSparks.length; i++) {
    const s = ionizationSparks[i];
    const age = t - s.startTime;
    if (age < 0 || age >= s.duration) continue;

    const p = age / s.duration;
    // Fast attack (first 0.03s), high-frequency electrostatic flicker, exponential fade
    const attack = Math.min(1.0, age / 0.03);
    const flicker = 0.85 + 0.15 * Math.sin(age * 85.0);
    const alpha = attack * (1.0 - p) * flicker;
    if (alpha <= 0.01) continue;

    const isViolet = (s.colorType === 'violet');
    const isCyan = (s.colorType === 'cyan');

    // 1. Soft localized ionization aura (St. Elmo's atmospheric glow)
    const auraGrad = ctx.createRadialGradient(s.x, s.y, 1, s.x, s.y, s.radius);
    if (isViolet) {
      auraGrad.addColorStop(0.0, `rgba(233, 213, 255, ${(alpha * 0.65).toFixed(3)})`);
      auraGrad.addColorStop(0.4, `rgba(192, 132, 252, ${(alpha * 0.30).toFixed(3)})`);
      auraGrad.addColorStop(1.0, 'rgba(147, 51, 234, 0)');
    } else if (isCyan) {
      auraGrad.addColorStop(0.0, `rgba(224, 242, 254, ${(alpha * 0.65).toFixed(3)})`);
      auraGrad.addColorStop(0.4, `rgba(56, 189, 248, ${(alpha * 0.32).toFixed(3)})`);
      auraGrad.addColorStop(1.0, 'rgba(6, 182, 212, 0)');
    } else {
      auraGrad.addColorStop(0.0, `rgba(254, 243, 199, ${(alpha * 0.70).toFixed(3)})`);
      auraGrad.addColorStop(0.4, `rgba(245, 158, 11, ${(alpha * 0.35).toFixed(3)})`);
      auraGrad.addColorStop(1.0, 'rgba(217, 119, 6, 0)');
    }

    ctx.fillStyle = auraGrad;
    ctx.beginPath();
    ctx.arc(s.x, s.y, s.radius, 0, Math.PI * 2);
    ctx.fill();

    // 2. Branching micro-electric filaments (sharp jagged arcs)
    ctx.save();
    ctx.translate(s.x, s.y);

    // Glow envelope stroke
    ctx.strokeStyle = isViolet
      ? `rgba(192, 132, 252, ${(alpha * 0.85).toFixed(3)})`
      : (isCyan ? `rgba(56, 189, 248, ${(alpha * 0.85).toFixed(3)})` : `rgba(251, 191, 36, ${(alpha * 0.85).toFixed(3)})`);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    for (let j = 0; j < s.segments.length; j++) {
      const seg = s.segments[j];
      ctx.moveTo(seg.x0, seg.y0);
      ctx.lineTo(seg.x1, seg.y1);
    }
    ctx.stroke();

    // White-hot incandescent core
    ctx.strokeStyle = `rgba(255, 255, 255, ${(alpha * 0.95).toFixed(3)})`;
    ctx.lineWidth = 0.85;
    ctx.beginPath();
    for (let j = 0; j < s.segments.length; j++) {
      const seg = s.segments[j];
      ctx.moveTo(seg.x0, seg.y0);
      ctx.lineTo(seg.x1, seg.y1);
    }
    ctx.stroke();

    // Point discharge spark dots at arc tips
    ctx.fillStyle = '#ffffff';
    for (let j = 0; j < s.segments.length; j++) {
      const seg = s.segments[j];
      ctx.fillRect(seg.x1 - 0.75, seg.y1 - 0.75, 1.5, 1.5);
    }

    ctx.restore();
  }
  ctx.restore();
}

const SW = {
  hullWhite: '#e2e6ed',
  hullLight: '#c2c8d2',
  hullMid:   '#878e9c',
  hullDark:  '#515764',
  hullDeep:  '#282c35',
  cavity:    '#0c0e12',
  amberGlow: '#f59e0b',
  redBeacon: '#ef4444',
  cyanPower: '#38bdf8',
  pipeBronze:'#b45309',
  pipeSteel: '#64748b'
};

// ---------------------------------------------------------------------------
// AERODYNAMIC HULL-EDGE SLIPSTREAM ("DIE LUFT SPÜREN")
// ---------------------------------------------------------------------------

function drawAerodynamicSlipstream(ctx, t) {
  if (state.quality === 'low') return;

  const minX = (state.visibleLeft !== undefined ? state.visibleLeft : 0);
  const maxX = (state.visibleRight !== undefined ? state.visibleRight : WIDTH);

  ctx.save();
  // High-speed transonic airflow rushing over the leading knife-edge of the hull (y ≈ 198..212)
  const streamSpeed = 260; // Searing air speed
  const streamOffset = (t * streamSpeed) % 800;

  // 1. Aerodynamic Shockwave Compression Sheen along the metal crest
  const sheenGrad = ctx.createLinearGradient(0, 196, 0, 214);
  sheenGrad.addColorStop(0.0, 'rgba(254, 215, 170, 0)');
  sheenGrad.addColorStop(0.5, 'rgba(254, 215, 170, 0.28)');
  sheenGrad.addColorStop(1.0, 'rgba(244, 63, 94, 0)');
  ctx.fillStyle = sheenGrad;
  ctx.fillRect(minX, 196, maxX - minX, 18);

  // 2. High-Speed Transonic Condensation Vapor Ribbons
  ctx.lineWidth = 1.6;
  for (let pass = 0; pass < 3; pass++) {
    const pY = 200 + pass * 4;
    const waveFreq = 0.035 + pass * 0.015;
    const wavePhase = pass * 1.8;

    ctx.strokeStyle = `rgba(255, 255, 255, ${0.18 + pass * 0.06})`;
    ctx.beginPath();

    for (let x = minX; x <= maxX; x += 12) {
      const flow = Math.sin((x + streamOffset) * waveFreq + wavePhase) * 2.5 +
                   Math.cos((x - streamOffset * 0.5) * 0.02) * 1.5;
      const vy = pY + flow;
      if (x === minX) ctx.moveTo(x, vy);
      else ctx.lineTo(x, vy);
    }
    ctx.stroke();
  }

  // 3. Turbulent Vortex Wisps curling off cutout corners
  const vortexCorners = [230, 370, 630, 750, 850, 970, 1230, 1370];
  for (let i = 0; i < vortexCorners.length; i++) {
    const cx = vortexCorners[i];
    const curlPhase = (t * 6.0 + i) % (Math.PI * 2);
    const curlX = cx - ((t * streamSpeed * 0.6 + i * 40) % 70);
    const curlY = 202 - Math.sin(curlPhase) * 3.5;

    ctx.strokeStyle = 'rgba(255, 241, 242, 0.25)';
    ctx.lineWidth = 1.0;
    ctx.beginPath();
    ctx.arc(curlX, curlY, 3.5, 0, Math.PI * 1.6);
    ctx.stroke();
  }

  ctx.restore();
}

// ---------------------------------------------------------------------------
// HERO GADGET 1: EXACTLY ONE 3D PARABOLIC RADAR DISH (x = 350)
// Heavy Military Targeting & Telemetry Array with Space-Frame, Yoke & Actuators
// ---------------------------------------------------------------------------

function drawHeroRadarPedestal(ctx, x, y, radius = 34) {
  const r = Math.max(radius || 34, 32);
  // 1. Broad Directional Cast Shadow onto the Dreadnought Superstructure
  ctx.fillStyle = 'rgba(0, 0, 0, 0.62)';
  ctx.beginPath();
  ctx.ellipse(x + 18, y + 16, r * 1.15, r * 0.40, 0.22, 0, Math.PI * 2);
  ctx.fill();

  // Contact Shadow grounding the Sockel firmly onto the deck
  ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
  ctx.beginPath();
  ctx.ellipse(x, y + 33, 48, 14, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgba(0, 0, 0, 0.88)';
  ctx.beginPath();
  ctx.ellipse(x, y + 32, 42, 10, 0, 0, Math.PI * 2);
  ctx.fill();

  // -------------------------------------------------------------------------
  // MASSIVER 2.5D FUNDAMENT-SOCKEL FÜR DIE RADARSCHÜSSEL (y + 5 .. y + 33)
  // Wuchtiger, architektonischer Barbette-Pfeiler mit Kopfplatte,
  // Versteifungsrippen und Fundamentanker. Auf dieser massiven Plattform sitzt
  // die schon vorhandene verschraubte Verankerung 100% sichtbar und unverrückbar fest!
  // -------------------------------------------------------------------------

  // A. Unterer Fundament-Sockelfuß (Deck Footing Sponson Collar anchored into hull)
  // Stepped industrial footing plinth spanning y + 26 .. y + 33 (width 84px)
  const footingGrad = ctx.createLinearGradient(0, y + 26, 0, y + 33);
  footingGrad.addColorStop(0.0, '#334155');
  footingGrad.addColorStop(0.5, '#1e293b');
  footingGrad.addColorStop(1.0, '#0f172a');
  ctx.fillStyle = footingGrad;
  ctx.beginPath();
  ctx.ellipse(x, y + 31, 40, 10.5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#475569';
  ctx.lineWidth = 1.4;
  ctx.stroke();

  // Upper bevel ring on the footing plinth
  ctx.fillStyle = '#384456';
  ctx.beginPath();
  ctx.ellipse(x, y + 28, 38, 9.2, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#64748b';
  ctx.lineWidth = 1.0;
  ctx.stroke();

  // Heavy Foundation Anchor Studs securing the footing to Dreadnought Hull
  for (let a = -2.7; a <= 2.7; a += 0.60) {
    const fbx = x + Math.cos(a) * 35;
    const fby = y + 29.5 + Math.sin(a) * 8.2;
    // 3D Hex Bolt Head with shadow and metallic facet
    ctx.fillStyle = '#080c14';
    ctx.fillRect(fbx - 1.6, fby - 1.0, 3.4, 3.0);
    ctx.fillStyle = '#cbd5e1';
    ctx.fillRect(fbx - 1.4, fby - 1.2, 2.8, 2.2);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(fbx - 1.0, fby - 1.2, 1.2, 1.0);
  }

  // B. Structural Heavy Recoil / Wind Gusset Buttresses (Massive seitliche Versteifungsrippen)
  // Left Gusset (braced against supersonic bow slipstream airflow)
  const leftGussetGrad = ctx.createLinearGradient(x - 44, 0, x - 26, 0);
  leftGussetGrad.addColorStop(0.0, '#56657a');
  leftGussetGrad.addColorStop(0.6, '#3e4a5d');
  leftGussetGrad.addColorStop(1.0, '#222c3b');
  ctx.fillStyle = leftGussetGrad;
  ctx.beginPath();
  ctx.moveTo(x - 26, y + 7.5);
  ctx.lineTo(x - 44, y + 31);
  ctx.lineTo(x - 26, y + 31);
  ctx.closePath();
  ctx.fill();
  // Welded chamfer rim on left gusset
  ctx.strokeStyle = '#94a3b8';
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(x - 26, y + 7.5);
  ctx.lineTo(x - 44, y + 31);
  ctx.stroke();
  // Circular Weight-Reduction Lightening Port with depth shadow
  ctx.fillStyle = '#0a0e16';
  ctx.beginPath();
  ctx.arc(x - 33, y + 24, 4.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#64748b';
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.arc(x - 33, y + 24, 4.5, 0, Math.PI);
  ctx.stroke();

  // Right Gusset (in aerodynamic leeward shadow)
  const rightGussetGrad = ctx.createLinearGradient(x + 26, 0, x + 44, 0);
  rightGussetGrad.addColorStop(0.0, '#1c2432');
  rightGussetGrad.addColorStop(0.7, '#131924');
  rightGussetGrad.addColorStop(1.0, '#0a0e15');
  ctx.fillStyle = rightGussetGrad;
  ctx.beginPath();
  ctx.moveTo(x + 26, y + 7.5);
  ctx.lineTo(x + 44, y + 31);
  ctx.lineTo(x + 26, y + 31);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = '#334155';
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  ctx.moveTo(x + 26, y + 7.5);
  ctx.lineTo(x + 44, y + 31);
  ctx.stroke();
  // Circular Weight-Reduction Lightening Port
  ctx.fillStyle = '#060910';
  ctx.beginPath();
  ctx.arc(x + 33, y + 24, 4.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#273244';
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.arc(x + 33, y + 24, 4.5, 0, Math.PI);
  ctx.stroke();

  // C. Massiver Armierter Barbette-Pfeiler (Faceted Armored Pedestal Column y + 5 .. y + 28)
  // Left bevel facet (catching directional western sunset rim light)
  const leftPlinthGrad = ctx.createLinearGradient(x - 28, 0, x - 15, 0);
  leftPlinthGrad.addColorStop(0.0, '#536277');
  leftPlinthGrad.addColorStop(0.5, '#788aa6');
  leftPlinthGrad.addColorStop(1.0, '#3f4c61');
  ctx.fillStyle = leftPlinthGrad;
  ctx.beginPath();
  ctx.moveTo(x - 27, y + 6);
  ctx.lineTo(x - 15, y + 6);
  ctx.lineTo(x - 17, y + 28);
  ctx.lineTo(x - 30, y + 28);
  ctx.closePath();
  ctx.fill();
  // Vertical edge highlight pinstripe
  ctx.strokeStyle = '#cbd5e1';
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  ctx.moveTo(x - 15, y + 6);
  ctx.lineTo(x - 17, y + 28);
  ctx.stroke();

  // Front center armored facet (battleship plate with vertical strength)
  const centerPlinthGrad = ctx.createLinearGradient(0, y + 6, 0, y + 28);
  centerPlinthGrad.addColorStop(0.0, '#4a576d');
  centerPlinthGrad.addColorStop(0.4, '#343f51');
  centerPlinthGrad.addColorStop(1.0, '#1d2533');
  ctx.fillStyle = centerPlinthGrad;
  ctx.beginPath();
  ctx.moveTo(x - 15, y + 6);
  ctx.lineTo(x + 15, y + 6);
  ctx.lineTo(x + 17, y + 28);
  ctx.lineTo(x - 17, y + 28);
  ctx.closePath();
  ctx.fill();

  // Right bevel facet (shadow flank)
  const rightPlinthGrad = ctx.createLinearGradient(x + 15, 0, x + 28, 0);
  rightPlinthGrad.addColorStop(0.0, '#222b3a');
  rightPlinthGrad.addColorStop(0.6, '#161c27');
  rightPlinthGrad.addColorStop(1.0, '#0c1017');
  ctx.fillStyle = rightPlinthGrad;
  ctx.beginPath();
  ctx.moveTo(x + 15, y + 6);
  ctx.lineTo(x + 27, y + 6);
  ctx.lineTo(x + 30, y + 28);
  ctx.lineTo(x + 17, y + 28);
  ctx.closePath();
  ctx.fill();

  // Horizontal Reinforcing Armored Belt across Pedestal Center (y + 15 .. y + 18.5)
  ctx.fillStyle = '#101622';
  ctx.fillRect(x - 28, y + 15, 56, 3.5);
  ctx.fillStyle = '#64748b';
  ctx.fillRect(x - 27.5, y + 15, 55, 0.8);
  // High-tensile armor rivet studs on belt
  ctx.fillStyle = '#cbd5e1';
  ctx.fillRect(x - 22, y + 16, 2, 2);
  ctx.fillRect(x - 8, y + 16, 2, 2);
  ctx.fillRect(x + 6, y + 16, 2, 2);
  ctx.fillRect(x + 20, y + 16, 2, 2);

  // Recessed Sensor Electronics Maintenance Hatch on front facet
  ctx.fillStyle = '#090d15';
  ctx.fillRect(x - 9, y + 9, 18, 5.5);
  ctx.fillStyle = '#263140';
  ctx.fillRect(x - 8, y + 9.5, 16, 4.5);
  ctx.fillStyle = '#38bdf8';
  ctx.fillRect(x + 4, y + 10.5, 2.5, 2.5); // Cyan telemetry diagnostic LED
  // Telemetry LED soft glow
  ctx.fillStyle = 'rgba(56, 189, 248, 0.35)';
  ctx.beginPath();
  ctx.arc(x + 5.2, y + 11.7, 3.5, 0, Math.PI * 2);
  ctx.fill();

  // D. Sockel-Kopfplatte (Machined Steel Pedestal Cap Platform at y + 5)
  // Perfectly supports the existing bolted flange! Wider than the flange (rx=33 vs 25)
  // Rim side thickness
  ctx.fillStyle = '#1e2736';
  ctx.beginPath();
  ctx.ellipse(x, y + 6.2, 33, 12.8, 0, 0, Math.PI * 2);
  ctx.fill();
  // Machined top surface
  const kopfGrad = ctx.createLinearGradient(x - 30, 0, x + 30, 0);
  kopfGrad.addColorStop(0.0, '#384558');
  kopfGrad.addColorStop(0.3, '#4c5d75');
  kopfGrad.addColorStop(0.7, '#344052');
  kopfGrad.addColorStop(1.0, '#222b39');
  ctx.fillStyle = kopfGrad;
  ctx.beginPath();
  ctx.ellipse(x, y + 5, 32.5, 12.2, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#5b6b82';
  ctx.lineWidth = 1.2;
  ctx.stroke();

  // Brilliant specular bevel glint along top rim (catching overhead horizon light)
  ctx.strokeStyle = '#e2e8f0';
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  ctx.ellipse(x, y + 4.2, 31.5, 11.2, 0, Math.PI * 0.9, Math.PI * 2.1);
  ctx.stroke();

  // 2. Heavy Multi-Tiered Armored Barbette / Gantry Turntable Foundation
  // Tier A: Foundation Base Flange with Deck Anchor Bolts (SITZT SICHTBAR FEST AUF DEM SOCKEL)
  // Deep Contact Shadow grounding the bolted flange onto the machined Kopfplatte
  ctx.fillStyle = 'rgba(0, 0, 0, 0.82)';
  ctx.beginPath();
  ctx.ellipse(x, y + 5.8, 26, 10.2, 0, 0, Math.PI * 2);
  ctx.fill();

  // Flange Plate (Tier A)
  ctx.fillStyle = '#151c27';
  ctx.beginPath();
  ctx.ellipse(x, y + 5, 25, 9.5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#475569';
  ctx.lineWidth = 1.3;
  ctx.stroke();

  // Top lip specular glint on the bolted flange
  ctx.strokeStyle = '#94a3b8';
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.ellipse(x, y + 4.4, 24, 8.8, 0, Math.PI, 0);
  ctx.stroke();

  // Hexagonal Anchor Fasteners along flange perimeter
  const boltAngles = [-2.5, -1.8, -1.2, -0.6, 0, 0.6, 1.2, 1.8, 2.5];
  for (let i = 0; i < boltAngles.length; i++) {
    const ba = boltAngles[i];
    const bx = x + Math.cos(ba) * 22;
    const by = y + 5 + Math.sin(ba) * 7.5;
    // Bolt drop shadow onto flange plate
    ctx.fillStyle = '#080c14';
    ctx.fillRect(bx - 1.2, by - 0.6, 2.8, 2.6);
    // Steel Hex Bolt Head
    ctx.fillStyle = '#cbd5e1';
    ctx.fillRect(bx - 1.2, by - 1.2, 2.4, 2.2);
    // Specular top highlight
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(bx - 1.0, by - 1.2, 1.2, 1.0);
  }

  // Tier B: Recessed Azimuth Traverse Gear Ring with Machined Teeth
  ctx.fillStyle = '#060910';
  ctx.fillRect(x - 22, y - 1, 44, 4.5);
  ctx.fillStyle = '#475569';
  for (let gx = x - 20; gx <= x + 20; gx += 4) {
    ctx.fillRect(gx, y - 0.5, 2, 3.5);
  }

}

function drawHeroRadarDish(ctx, x, y, radius, t, drawBase = true) {
  ctx.save();
  const r = Math.max(radius || 34, 32);

  if (drawBase) {
    drawHeroRadarPedestal(ctx, x, y, r);
  }

  // Tier C: Cylindrical Rotating Turntable Drum with Sunset Cylindrical Shading
  const drumGrad = ctx.createLinearGradient(x - 21, 0, x + 21, 0);
  drumGrad.addColorStop(0.0, '#1c222e');
  drumGrad.addColorStop(0.25, '#738096');
  drumGrad.addColorStop(0.65, '#454e60');
  drumGrad.addColorStop(1.0, '#151922');
  ctx.fillStyle = drumGrad;
  ctx.fillRect(x - 21, y - 15, 42, 14);

  // Turntable Drum Top Rim with Specular Chamfer
  ctx.strokeStyle = SW.hullWhite;
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.ellipse(x, y - 15, 21, 7.5, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.fillStyle = '#2c3342';
  ctx.beginPath();
  ctx.ellipse(x, y - 15, 20, 7.0, 0, 0, Math.PI * 2);
  ctx.fill();

  // Starboard Azimuth Electric Drive Motor Pod & Heat-Sink Fins
  ctx.fillStyle = '#0b0f16';
  ctx.fillRect(x + 14, y - 13, 11, 10);
  ctx.fillStyle = '#334155';
  ctx.fillRect(x + 15, y - 12, 9, 8);
  ctx.fillStyle = '#64748b';
  ctx.fillRect(x + 16, y - 11, 1.2, 6);
  ctx.fillRect(x + 19, y - 11, 1.2, 6);
  ctx.fillRect(x + 22, y - 11, 1.2, 6);

  // Active Green/Amber Telemetry Status LED on Drive Pod
  const isOnline = Math.sin(t * 3.5) > -0.2;
  ctx.fillStyle = isOnline ? '#10b981' : '#f59e0b';
  ctx.fillRect(x + 18, y - 4, 3, 2);

  // 3. Heavy Box-Girder Elevation Yoke Uprights & Trunnion Hubs
  // Left Arm
  const leftArmGrad = ctx.createLinearGradient(x - 18, 0, x - 11, 0);
  leftArmGrad.addColorStop(0.0, '#1a1f2b');
  leftArmGrad.addColorStop(0.6, '#556075');
  leftArmGrad.addColorStop(1.0, '#2d3545');
  ctx.fillStyle = leftArmGrad;
  ctx.beginPath();
  ctx.moveTo(x - 19, y - 14);
  ctx.lineTo(x - 13, y - 28);
  ctx.lineTo(x - 7, y - 28);
  ctx.lineTo(x - 11, y - 14);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = '#94a3b8';
  ctx.lineWidth = 1.0;
  ctx.stroke();

  // Right Arm
  const rightArmGrad = ctx.createLinearGradient(x + 11, 0, x + 18, 0);
  rightArmGrad.addColorStop(0.0, '#333c4d');
  rightArmGrad.addColorStop(0.5, '#6b778e');
  rightArmGrad.addColorStop(1.0, '#1c222e');
  ctx.fillStyle = rightArmGrad;
  ctx.beginPath();
  ctx.moveTo(x + 11, y - 14);
  ctx.lineTo(x + 7, y - 28);
  ctx.lineTo(x + 13, y - 28);
  ctx.lineTo(x + 19, y - 14);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = '#94a3b8';
  ctx.lineWidth = 1.0;
  ctx.stroke();

  // Trunnion Bearing Hubs at Pivot Axis (y - 28)
  ctx.fillStyle = '#0f172a';
  ctx.beginPath();
  ctx.arc(x - 10, y - 28, 4.5, 0, Math.PI * 2);
  ctx.arc(x + 10, y - 28, 4.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#cbd5e1';
  ctx.beginPath();
  ctx.arc(x - 10, y - 28, 2.5, 0, Math.PI * 2);
  ctx.arc(x + 10, y - 28, 2.5, 0, Math.PI * 2);
  ctx.fill();

  // Telescopic Chrome Hydraulic Elevation Ram
  ctx.fillStyle = '#0f172a';
  ctx.fillRect(x - 3, y - 17, 6, 6);
  ctx.fillStyle = '#e2e8f0';
  ctx.fillRect(x - 1.5, y - 26, 3, 9); // Polished chrome rod
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(x - 0.5, y - 26, 1, 9); // Specular highlight

  // Heavy Armored Umbilical Conduit Loop
  ctx.strokeStyle = '#090d16';
  ctx.lineWidth = 2.2;
  ctx.beginPath();
  ctx.moveTo(x - 15, y - 12);
  ctx.bezierCurveTo(x - 22, y - 18, x - 20, y - 26, x - 12, y - 27);
  ctx.stroke();

  // 4. Dynamic Tracking Motion (Azimuth Scan & Elevation Breathing)
  const azimuth = Math.sin(t * 0.40) * 0.32;
  ctx.translate(x, y - 28);
  ctx.rotate(azimuth);

  // 5. Heavy 3D Parabolic Reflector Structure (Plastisch & Wuchtig)
  const ry = r * 0.48;

  // A. Rear Structural Space-Frame / Radial Truss Girders (Visible behind/under rim)
  ctx.strokeStyle = '#1e2634';
  ctx.lineWidth = 2.2;
  for (let a = 0; a < Math.PI * 2; a += Math.PI / 4) {
    const rx = Math.cos(a) * (r + 1.5);
    const ryP = Math.sin(a) * (ry + 1.0);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(rx, ryP);
    ctx.stroke();
  }
  // Cross-bracing ring behind dish
  ctx.strokeStyle = '#151c27';
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.ellipse(0, 0, r * 0.68, ry * 0.68, 0, 0, Math.PI * 2);
  ctx.stroke();

  // B. Volumetric Parabolic Dish Outer Armored Shell
  ctx.fillStyle = '#0e121a';
  ctx.beginPath();
  ctx.ellipse(0, 0, r + 2, ry + 2, 0, 0, Math.PI * 2);
  ctx.fill();

  // Outer Beveled Rim Face
  const rimGrad = ctx.createLinearGradient(-r, 0, r, 0);
  rimGrad.addColorStop(0.0, '#323a49');
  rimGrad.addColorStop(0.3, '#748197');
  rimGrad.addColorStop(0.7, '#4b5568');
  rimGrad.addColorStop(1.0, '#1c222e');
  ctx.fillStyle = rimGrad;
  ctx.beginPath();
  ctx.ellipse(0, 0, r, ry, 0, 0, Math.PI * 2);
  ctx.fill();

  // Crisp Lit Specular Outer Lip
  ctx.strokeStyle = SW.hullWhite;
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.ellipse(0, 0, r, ry, 0, 0, Math.PI * 2);
  ctx.stroke();

  // Secondary Warm Sunset Glint on Top Crest
  ctx.strokeStyle = 'rgba(254, 215, 170, 0.85)';
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  ctx.ellipse(0, -0.8, r - 0.8, ry - 0.8, 0, Math.PI, Math.PI * 2);
  ctx.stroke();

  // Calibration Tick Marks along the Rim
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)';
  ctx.lineWidth = 1.0;
  for (let a = -Math.PI; a <= Math.PI; a += Math.PI / 8) {
    const tx = Math.cos(a) * r;
    const ty = Math.sin(a) * ry;
    const inX = Math.cos(a) * (r - 2.5);
    const inY = Math.sin(a) * (ry - 2.0);
    ctx.beginPath();
    ctx.moveTo(tx, ty);
    ctx.lineTo(inX, inY);
    ctx.stroke();
  }

  // C. Deep Concave Parabolic Interior Gradient (True 3D Cavity Depth)
  const bowlGrad = ctx.createRadialGradient(0, -3, r * 0.08, 0, 0, r);
  bowlGrad.addColorStop(0.0, '#020306'); // Infinite receiver void
  bowlGrad.addColorStop(0.35, '#0b0f16');
  bowlGrad.addColorStop(0.70, '#1e2636');
  bowlGrad.addColorStop(0.92, '#4a5568');
  bowlGrad.addColorStop(1.0, '#94a3b8'); // Specular inner lip
  ctx.fillStyle = bowlGrad;
  ctx.beginPath();
  ctx.ellipse(0, 0, r - 2.5, ry - 2.0, 0, 0, Math.PI * 2);
  ctx.fill();

  // Curved Quadrant Panel Seam Lines with Micro-Rivets
  ctx.strokeStyle = 'rgba(148, 163, 184, 0.35)';
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  ctx.moveTo(0, -(ry - 2));
  ctx.lineTo(0, ry - 2);
  ctx.moveTo(-(r - 2.5), 0);
  ctx.lineTo(r - 2.5, 0);
  ctx.stroke();

  // Concentric Waveguide Tuning Grooves
  ctx.strokeStyle = 'rgba(203, 213, 225, 0.32)';
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  ctx.ellipse(0, 0, r * 0.42, ry * 0.42, 0, 0, Math.PI * 2);
  ctx.ellipse(0, 0, r * 0.72, ry * 0.72, 0, 0, Math.PI * 2);
  ctx.stroke();

  // 6. Rigid Quadpod Sub-Reflector & Active Transceiver Focal Core
  const focusY = -r * 0.65;

  // Quadpod 4-Strut Structural Assembly (Anchored at N, S, W, E rim points)
  ctx.strokeStyle = '#cbd5e1';
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  ctx.moveTo(-r * 0.75, 0);
  ctx.lineTo(0, focusY);
  ctx.moveTo(r * 0.75, 0);
  ctx.lineTo(0, focusY);
  ctx.stroke();

  ctx.strokeStyle = '#64748b';
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(0, ry * 0.65);
  ctx.lineTo(0, focusY);
  ctx.moveTo(0, -ry * 0.75);
  ctx.lineTo(0, focusY);
  ctx.stroke();

  // Cassegrain Convex Sub-Reflector Secondary Cone
  ctx.fillStyle = '#1e293b';
  ctx.beginPath();
  ctx.arc(0, focusY, 4.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#94a3b8';
  ctx.beginPath();
  ctx.arc(0, focusY, 3.2, 0, Math.PI * 2);
  ctx.fill();

  // Active Multi-Band Cyan Sensor Emitter Node
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(0, focusY, 1.8, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = SW.cyanPower;
  ctx.beginPath();
  ctx.arc(0, focusY, 3.2, 0, Math.PI * 2);
  ctx.fill();

  // Volumetric Pulsing Sensor Field Flare
  if (state.quality !== 'low') {
    const pulse = 0.5 + 0.5 * Math.sin(t * 5.0);
    ctx.fillStyle = `rgba(56, 189, 248, ${0.35 + pulse * 0.35})`;
    ctx.beginPath();
    ctx.arc(0, focusY, 8 + pulse * 3, 0, Math.PI * 2);
    ctx.fill();

    // Horizontal Sensor Flare Streak
    ctx.strokeStyle = `rgba(224, 242, 254, ${0.4 + pulse * 0.4})`;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(-16, focusY);
    ctx.lineTo(16, focusY);
    ctx.stroke();
  }

  ctx.restore();
}

// ---------------------------------------------------------------------------
// HERO GADGET 2: EXACTLY TWO 3D HEAVY TURBOLASER TURRETS (x = 640, x = 960)
// Heavy Dreadnought Artillery with Armored Citadel, Stepped Barbette,
// Massive Dual Caliber Barrels, Recoil Sleeves & Flared Muzzle Brakes
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// HERO GADGET 2: EXACTLY TWO 2.5D HEAVY TURBOLASER CITADELS (x = 640, x = 960)
// True 2.5D Turret Korpus with Faceted Glacis, Cheeks & Roof, Perspective
// Barrels Mounted on Armored Mantlet & Irregular Autonomous Sector Patrol
// ---------------------------------------------------------------------------

// Pre-defined irregular patrol keyframes (autonomous surveillance holding & traverse states)
// Time in seconds, azimuth angle in radians (relative to forward-facing 0.0)
const TURRET_PATROL_WEST = [
  { t: 0.0,  a: -0.22 }, // Hold at -12.6 deg (watching mountain flank)
  { t: 3.8,  a: -0.22 }, // Hold ends (3.8s observation)
  { t: 6.0,  a: -0.44 }, // Smooth heavy traverse left to -25.2 deg
  { t: 11.2, a: -0.44 }, // Long observation hold (5.2s stare)
  { t: 13.6, a:  0.06 }, // Fast sweep across towards forward-center (+3.4 deg)
  { t: 17.6, a:  0.06 }, // Hold at forward-center (4.0s hold)
  { t: 19.8, a: -0.16 }, // Settle back to mid-west (-9.2 deg)
  { t: 24.2, a: -0.16 }, // Hold (4.4s hold)
  { t: 26.0, a: -0.22 }  // Cycle loop (26.0s period)
];

const TURRET_PATROL_EAST = [
  { t: 0.0,  a:  0.20 }, // Hold at +11.5 deg
  { t: 4.6,  a:  0.20 }, // Hold ends (4.6s observation)
  { t: 7.2,  a:  0.46 }, // Smooth heavy traverse right to +26.4 deg
  { t: 13.0, a:  0.46 }, // Long surveillance hold (5.8s stare)
  { t: 15.4, a: -0.06 }, // Fast sweep across towards forward-center (-3.4 deg)
  { t: 19.8, a: -0.06 }, // Hold at forward-center (4.4s hold)
  { t: 22.0, a:  0.28 }, // Settle back to mid-east (+16.0 deg)
  { t: 27.5, a:  0.28 }, // Hold (5.5s hold)
  { t: 30.5, a:  0.20 }  // Cycle loop (30.5s period - prime with West)
];

function evalTurretPatrol(keyframes, t) {
  const period = keyframes[keyframes.length - 1].t;
  const cycleT = ((t % period) + period) % period;

  for (let i = 0; i < keyframes.length - 1; i++) {
    const k0 = keyframes[i];
    const k1 = keyframes[i + 1];
    if (cycleT >= k0.t && cycleT <= k1.t) {
      if (k0.a === k1.a) {
        // Holding state: subtle hydraulic servo-stabilization micro-tremor (+-0.003 rad)
        return k0.a + Math.sin(t * 1.8 + k0.t) * 0.0028;
      }
      // Traversing state: smooth heavy cubic Hermite smoothstep ease
      const u = (cycleT - k0.t) / (k1.t - k0.t);
      const ease = u * u * (3.0 - 2.0 * u);
      return k0.a + (k1.a - k0.a) * ease;
    }
  }
  return keyframes[0].a;
}

const _scrPtPool = [];
for (let i = 0; i < 64; i++) _scrPtPool.push([0, 0]);
let _scrPtIdx = 0;

function drawClean25DTurretPedestal(ctx, isWest) {
  // 3. Ground Cast Shadows thrown onto Deck (Stationary Base)
  ctx.fillStyle = 'rgba(0, 0, 0, 0.62)';
  ctx.beginPath();
  const shadowSlant = isWest ? 18 : -18;
  ctx.ellipse(shadowSlant, 15, 36, 13, isWest ? 0.22 : -0.22, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
  ctx.beginPath();
  ctx.ellipse(0, 6, 27, 12, 0, 0, Math.PI * 2);
  ctx.fill();

  // 4. Stepped 3D Armored Barbette Pedestal (Stationary on Deck)
  // -------------------------------------------------------------------------
  // MASSIVER 2.5D FUNDAMENT-SOCKEL FÜR DAS GESCHÜTZ (Local Y = 6 .. Y = 18)
  // Auf dieser massiven Plattform sitzt die verschraubte Verankerung fest verankert!
  // -------------------------------------------------------------------------

  // -------------------------------------------------------------------------
  // MASSIVER 2.5D FUNDAMENT-SOCKEL FÜR DAS GESCHÜTZ (Local Y = 6 .. Local Y = 33)
  // Wuchtiger, architektonischer Barbette-Pfeiler mit Kopfplatte,
  // Versteifungsrippen und Fundamentanker. Auf dieser massiven Plattform sitzt
  // die schon vorhandene verschraubte Verankerung 100% sichtbar und unverrückbar fest!
  // -------------------------------------------------------------------------

  // 1. Broad Grounding Contact Shadow cast onto the Dreadnought Arena Deck
  ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
  ctx.beginPath();
  ctx.ellipse(0, 33, 48, 14, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgba(0, 0, 0, 0.88)';
  ctx.beginPath();
  ctx.ellipse(0, 32, 42, 10.5, 0, 0, Math.PI * 2);
  ctx.fill();

  // 2. Unterer Fundament-Sockelfuß (Base Footing Sponson Collar anchored into hull)
  // Stepped industrial footing plinth spanning Local Y = 26 .. 33 (width 88px)
  const barbFootingGrad = ctx.createLinearGradient(0, 26, 0, 33);
  barbFootingGrad.addColorStop(0.0, '#334155');
  barbFootingGrad.addColorStop(0.5, '#1e293b');
  barbFootingGrad.addColorStop(1.0, '#0f172a');
  ctx.fillStyle = barbFootingGrad;
  ctx.beginPath();
  ctx.ellipse(0, 31, 41, 11, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#475569';
  ctx.lineWidth = 1.4;
  ctx.stroke();

  // Upper bevel ring on the footing plinth
  ctx.fillStyle = '#384456';
  ctx.beginPath();
  ctx.ellipse(0, 28, 39, 9.6, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#64748b';
  ctx.lineWidth = 1.0;
  ctx.stroke();

  // Heavy Foundation Anchor Studs securing the footing to Dreadnought Hull
  for (let a = -2.7; a <= 2.7; a += 0.60) {
    const fbx = Math.cos(a) * 36;
    const fby = 29.5 + Math.sin(a) * 8.6;
    // 3D Hex Bolt Head with shadow and metallic facet
    ctx.fillStyle = '#080c14';
    ctx.fillRect(fbx - 1.6, fby - 1.0, 3.4, 3.0);
    ctx.fillStyle = '#cbd5e1';
    ctx.fillRect(fbx - 1.4, fby - 1.2, 2.8, 2.2);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(fbx - 1.0, fby - 1.2, 1.2, 1.0);
  }

  // 3. Structural Heavy Recoil Gusset Buttresses (Massive seitliche Versteifungsrippen)
  // Left Gusset (bracing against heavy artillery recoil)
  const leftGussetGrad = ctx.createLinearGradient(-44, 0, -26, 0);
  if (isWest) {
    leftGussetGrad.addColorStop(0.0, '#56657a');
    leftGussetGrad.addColorStop(0.6, '#3e4a5d');
    leftGussetGrad.addColorStop(1.0, '#222c3b');
  } else {
    leftGussetGrad.addColorStop(0.0, '#1c2432');
    leftGussetGrad.addColorStop(0.7, '#131924');
    leftGussetGrad.addColorStop(1.0, '#0a0e15');
  }
  ctx.fillStyle = leftGussetGrad;
  ctx.beginPath();
  ctx.moveTo(-26, 8);
  ctx.lineTo(-44, 31);
  ctx.lineTo(-26, 31);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = isWest ? '#94a3b8' : '#334155';
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(-26, 8);
  ctx.lineTo(-44, 31);
  ctx.stroke();
  // Circular Weight-Reduction Lightening Port
  ctx.fillStyle = '#0a0e16';
  ctx.beginPath();
  ctx.arc(-33, 24, 4.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = isWest ? '#64748b' : '#273244';
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.arc(-33, 24, 4.5, 0, Math.PI);
  ctx.stroke();

  // Right Gusset (bracing against heavy artillery recoil)
  const rightGussetGrad = ctx.createLinearGradient(26, 0, 44, 0);
  if (isWest) {
    rightGussetGrad.addColorStop(0.0, '#1c2432');
    rightGussetGrad.addColorStop(0.7, '#131924');
    rightGussetGrad.addColorStop(1.0, '#0a0e15');
  } else {
    rightGussetGrad.addColorStop(0.0, '#222c3b');
    rightGussetGrad.addColorStop(0.4, '#3e4a5d');
    rightGussetGrad.addColorStop(1.0, '#56657a');
  }
  ctx.fillStyle = rightGussetGrad;
  ctx.beginPath();
  ctx.moveTo(26, 8);
  ctx.lineTo(44, 31);
  ctx.lineTo(26, 31);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = isWest ? '#334155' : '#94a3b8';
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(26, 8);
  ctx.lineTo(44, 31);
  ctx.stroke();
  // Circular Weight-Reduction Lightening Port
  ctx.fillStyle = '#060910';
  ctx.beginPath();
  ctx.arc(33, 24, 4.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = isWest ? '#273244' : '#64748b';
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.arc(33, 24, 4.5, 0, Math.PI);
  ctx.stroke();

  // 4. Massiver Armierter Barbette-Pfeiler (Faceted Armored Barbette Column Local Y = 6 .. 28)
  const leftBarbGrad = ctx.createLinearGradient(-28, 0, -15, 0);
  const centerBarbGrad = ctx.createLinearGradient(0, 6, 0, 28);
  const rightBarbGrad = ctx.createLinearGradient(15, 0, 28, 0);

  if (isWest) {
    leftBarbGrad.addColorStop(0.0, '#536277');
    leftBarbGrad.addColorStop(0.5, '#788aa6');
    leftBarbGrad.addColorStop(1.0, '#3f4c61');

    centerBarbGrad.addColorStop(0.0, '#4a576d');
    centerBarbGrad.addColorStop(0.4, '#343f51');
    centerBarbGrad.addColorStop(1.0, '#1d2533');

    rightBarbGrad.addColorStop(0.0, '#222b3a');
    rightBarbGrad.addColorStop(0.6, '#161c27');
    rightBarbGrad.addColorStop(1.0, '#0c1017');
  } else {
    leftBarbGrad.addColorStop(0.0, '#0c1017');
    leftBarbGrad.addColorStop(0.4, '#161c27');
    leftBarbGrad.addColorStop(1.0, '#222b3a');

    centerBarbGrad.addColorStop(0.0, '#4a576d');
    centerBarbGrad.addColorStop(0.4, '#343f51');
    centerBarbGrad.addColorStop(1.0, '#1d2533');

    rightBarbGrad.addColorStop(0.0, '#3f4c61');
    rightBarbGrad.addColorStop(0.5, '#788aa6');
    rightBarbGrad.addColorStop(1.0, '#536277');
  }

  // Left bevel facet
  ctx.fillStyle = leftBarbGrad;
  ctx.beginPath();
  ctx.moveTo(-27, 6.5);
  ctx.lineTo(-15, 6.5);
  ctx.lineTo(-17, 28);
  ctx.lineTo(-30, 28);
  ctx.closePath();
  ctx.fill();
  // Vertical edge highlight pinstripe
  ctx.strokeStyle = isWest ? '#cbd5e1' : '#334155';
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  ctx.moveTo(-15, 6.5);
  ctx.lineTo(-17, 28);
  ctx.stroke();

  // Front center facet
  ctx.fillStyle = centerBarbGrad;
  ctx.beginPath();
  ctx.moveTo(-15, 6.5);
  ctx.lineTo(15, 6.5);
  ctx.lineTo(17, 28);
  ctx.lineTo(-17, 28);
  ctx.closePath();
  ctx.fill();

  // Right bevel facet
  ctx.fillStyle = rightBarbGrad;
  ctx.beginPath();
  ctx.moveTo(15, 6.5);
  ctx.lineTo(27, 6.5);
  ctx.lineTo(30, 28);
  ctx.lineTo(17, 28);
  ctx.closePath();
  ctx.fill();
  // Vertical edge highlight pinstripe
  ctx.strokeStyle = isWest ? '#334155' : '#cbd5e1';
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  ctx.moveTo(15, 6.5);
  ctx.lineTo(17, 28);
  ctx.stroke();

  // Horizontal Reinforcing Armored Belt across Barbette (Local Y = 15 .. 18.5)
  ctx.fillStyle = '#101622';
  ctx.fillRect(-28, 15, 56, 3.5);
  ctx.fillStyle = '#64748b';
  ctx.fillRect(-27.5, 15, 55, 0.8);
  // High-tensile armor rivet studs on belt
  ctx.fillStyle = '#cbd5e1';
  ctx.fillRect(-22, 16, 2, 2);
  ctx.fillRect(-8, 16, 2, 2);
  ctx.fillRect(6, 16, 2, 2);
  ctx.fillRect(20, 16, 2, 2);

  // Recessed Heavy Hydraulic Recoil Dampening Port on Front Face
  ctx.fillStyle = '#080c14';
  ctx.fillRect(-8, 8.5, 16, 5.5);
  ctx.fillStyle = '#263140';
  ctx.fillRect(-7, 9, 14, 4.5);
  ctx.fillStyle = SW.amberGlow;
  ctx.fillRect(-5, 10.5, 10, 1.8); // Amber high-energy power coupling
  // Amber LED glow
  ctx.fillStyle = 'rgba(245, 158, 11, 0.40)';
  ctx.beginPath();
  ctx.arc(0, 11.4, 3.5, 0, Math.PI * 2);
  ctx.fill();

  // Recessed Lower Ventilation Louvers on Pedestal Front Plate
  ctx.fillStyle = '#0a0e16';
  ctx.fillRect(-10, 22.5, 20, 1.2);
  ctx.fillRect(-10, 24.5, 20, 1.2);
  ctx.fillStyle = '#475569';
  ctx.fillRect(-9.5, 23.5, 19, 0.5);
  ctx.fillRect(-9.5, 25.5, 19, 0.5);

  // 5. Sockel-Kopfplatte (Machined Steel Pedestal Cap Platform at Local Y = 6)
  // Perfectly supports the existing bolted flange! Wider than flange (rx=34 vs 24)
  // Rim side thickness
  ctx.fillStyle = '#1e2736';
  ctx.beginPath();
  ctx.ellipse(0, 7.2, 34, 14.2, 0, 0, Math.PI * 2);
  ctx.fill();
  // Machined top surface
  const kopfGradTurret = ctx.createLinearGradient(-30, 0, 30, 0);
  kopfGradTurret.addColorStop(0.0, isWest ? '#384558' : '#222b39');
  kopfGradTurret.addColorStop(0.3, isWest ? '#4c5d75' : '#344052');
  kopfGradTurret.addColorStop(0.7, isWest ? '#344052' : '#4c5d75');
  kopfGradTurret.addColorStop(1.0, isWest ? '#222b39' : '#384558');
  ctx.fillStyle = kopfGradTurret;
  ctx.beginPath();
  ctx.ellipse(0, 6, 33.5, 13.5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#5b6b82';
  ctx.lineWidth = 1.3;
  ctx.stroke();

  // Brilliant specular bevel glint along top rim
  ctx.strokeStyle = '#e2e8f0';
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  ctx.ellipse(0, 5.2, 32.5, 12.5, 0, Math.PI * 0.9, Math.PI * 2.1);
  ctx.stroke();

  // 6. Die schon vorhandene verschraubte Verankerung (Bolted Flange Plate)
  // SITZT SICHTBAR FEST AUF DER KOPFPLATTE DES SOCKELS!
  // Deep Contact Shadow grounding the bolted flange onto the machined Kopfplatte
  ctx.fillStyle = 'rgba(0, 0, 0, 0.82)';
  ctx.beginPath();
  ctx.ellipse(0, 6.8, 25, 11.8, 0, 0, Math.PI * 2);
  ctx.fill();

  // Flange Plate (Tier A)
  ctx.fillStyle = '#111722';
  ctx.beginPath();
  ctx.ellipse(0, 6, 24, 11, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#475569';
  ctx.lineWidth = 1.4;
  ctx.stroke();

  // Top lip specular glint on the bolted flange
  ctx.strokeStyle = '#94a3b8';
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.ellipse(0, 5.2, 23, 10.2, 0, Math.PI, 0);
  ctx.stroke();

  // 8 Heavy Armor Fastener Studs around Flange
  for (let a = -2.8; a <= 2.8; a += 0.7) {
    const fx = Math.cos(a) * 21.5;
    const fy = 6 + Math.sin(a) * 9.0;
    // Bolt drop shadow onto flange plate
    ctx.fillStyle = '#080c14';
    ctx.fillRect(fx - 1.2, fy - 0.6, 2.8, 2.6);
    // Steel Hex Bolt Head
    ctx.fillStyle = '#cbd5e1';
    ctx.fillRect(fx - 1.2, fy - 1.2, 2.4, 2.2);
    // Specular top highlight
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(fx - 1.0, fy - 1.2, 1.2, 1.0);
  }

  // Tier B: Azimuth Traverse Bearing Race & Gear Track
  ctx.fillStyle = '#090d14';
  ctx.fillRect(-22, 1.5, 44, 4.0);
  ctx.fillStyle = '#64748b';
  for (let gx = -20; gx <= 20; gx += 4) {
    ctx.fillRect(gx, 2, 2, 3.2);
  }

  // Tier C: Upper Cylindrical Armored Collar Wall with Directional Sunset Light
  const collarGrad = ctx.createLinearGradient(-23, 0, 23, 0);
  if (isWest) {
    collarGrad.addColorStop(0.0, '#1e2837');
    collarGrad.addColorStop(0.35, '#47566d');
    collarGrad.addColorStop(0.75, '#334155');
    collarGrad.addColorStop(1.0, '#18202d');
  } else {
    collarGrad.addColorStop(0.0, '#18202d');
    collarGrad.addColorStop(0.30, '#334155');
    collarGrad.addColorStop(0.65, '#47566d');
    collarGrad.addColorStop(1.0, '#1e2837');
  }
  ctx.fillStyle = collarGrad;
  ctx.fillRect(-23, -4.0, 46, 6.5);

  // Barbette Top Bearing Race Rim (subtle dark steel chamfer)
  ctx.fillStyle = '#1e2533';
  ctx.beginPath();
  ctx.ellipse(0, -4.0, 23, 10, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#475569';
  ctx.lineWidth = 1.1;
  ctx.stroke();

  // ---------------------------------------------------------------------------
}

function drawClean25DTurret(ctx, x, y, dirAngle, t = 0, label = 'T-01', drawBase = true) {
  ctx.save();

  const isWest = (x < 800);
  const patrolKeyframes = isWest ? TURRET_PATROL_WEST : TURRET_PATROL_EAST;

  // 1. Evaluate Autonomous Azimuth Angle from Irregular Patrol Schedule
  const phi = evalTurretPatrol(patrolKeyframes, t);

  // Subtle gun elevation breathing (aimed high across the horizon)
  const psi = 0.36 + 0.02 * Math.sin(t * 0.75 + (isWest ? 0 : 2.1));

  // 2. 2.5D Screen Projection Transform
  // Local turret coordinates:
  //   u: lateral right (+u) / left (-u)
  //   v: forward towards guns (-v) / rear bustle (+v)
  //   z: vertical height up from deck (+z)
  const S_TILT = 0.55; // Deck foreshortening compression
  const cosP = Math.cos(phi);
  const sinP = Math.sin(phi);

  _scrPtIdx = 0;
  const toScr = (u, v, z) => {
    const rx = u * cosP - v * sinP;
    const ry = u * sinP + v * cosP;
    const pt = _scrPtPool[_scrPtIdx++ & 63];
    pt[0] = rx;
    pt[1] = ry * S_TILT - z;
    return pt;
  };

  ctx.translate(x, y);

  if (drawBase) {
    drawClean25DTurretPedestal(ctx, isWest);
  }

  // Directional barrel shadow cast along deck in direction of aim
  const bTipShadow = toScr(0, -53, 0);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.40)';
  ctx.beginPath();
  ctx.ellipse(bTipShadow[0] * 0.5, bTipShadow[1] * 0.5 + 4, 8, Math.abs(bTipShadow[1]) * 0.52, -phi, 0, Math.PI * 2);
  ctx.fill();

  // 5. WUCHTIGER 2.5D TURRET BODY / CITADEL KORPUS (Rotating with Azimuth phi)
  // Heavy multi-faceted armored citadel: Bustle, Cheeks, Glacis, Roof & Mantlet
  // ---------------------------------------------------------------------------

  // Key 3D Vertices (Local Turret Space: -v is Front, +v is Rear, +z is Up):
  // Lower Rim (z = 6.0, resting firmly on the barbette top bearing):
  const b_FL = toScr(-15, -16, 6.0); // Front-Left nose
  const b_FR = toScr(15, -16, 6.0);  // Front-Right nose
  const b_ML = toScr(-21, 2, 6.0);   // Mid-Left shoulder
  const b_MR = toScr(21, 2, 6.0);    // Mid-Right shoulder
  const b_RL = toScr(-17, 18, 6.0);  // Rear-Left bustle
  const b_RR = toScr(17, 18, 6.0);   // Rear-Right bustle

  // Upper Roof Rim (z = 19.5 .. 21.0):
  const r_FL = toScr(-11, -10, 20.5); // Roof Front-Left
  const r_FR = toScr(11, -10, 20.5);  // Roof Front-Right
  const r_ML = toScr(-17, 2, 21.0);   // Roof Mid-Left
  const r_MR = toScr(17, 2, 21.0);    // Roof Mid-Right
  const r_RL = toScr(-14, 16, 19.5);  // Roof Rear-Left
  const r_RR = toScr(14, 16, 19.5);   // Roof Rear-Right

  // Under-turret mechanical contact shadow cast onto the barbette race
  ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
  ctx.beginPath();
  ctx.moveTo(b_FL[0], b_FL[1] + 1.5);
  ctx.lineTo(b_ML[0], b_ML[1] + 1.5);
  ctx.lineTo(b_RL[0], b_RL[1] + 1.5);
  ctx.lineTo(b_RR[0], b_RR[1] + 1.5);
  ctx.lineTo(b_MR[0], b_MR[1] + 1.5);
  ctx.lineTo(b_FR[0], b_FR[1] + 1.5);
  ctx.closePath();
  ctx.fill();

  // A. Rear Bustle Armor Plate (Turm-Heck - directly facing the viewer)
  const bustleGrad = ctx.createLinearGradient(b_RL[0], b_RL[1], b_RR[0], b_RR[1]);
  if (isWest) {
    bustleGrad.addColorStop(0.0, '#273242');
    bustleGrad.addColorStop(0.5, '#39465a');
    bustleGrad.addColorStop(1.0, '#4a5970');
  } else {
    bustleGrad.addColorStop(0.0, '#4a5970');
    bustleGrad.addColorStop(0.5, '#39465a');
    bustleGrad.addColorStop(1.0, '#273242');
  }
  ctx.fillStyle = bustleGrad;
  ctx.beginPath();
  ctx.moveTo(b_RL[0], b_RL[1]);
  ctx.lineTo(b_RR[0], b_RR[1]);
  ctx.lineTo(r_RR[0], r_RR[1]);
  ctx.lineTo(r_RL[0], r_RL[1]);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = '#506079';
  ctx.lineWidth = 1.1;
  ctx.stroke();

  // Horizontal Cooling Radiator Louvers on Bustle
  ctx.strokeStyle = '#0e141e';
  ctx.lineWidth = 1.6;
  const louver1_L = toScr(-12, 17, 9);
  const louver1_R = toScr(12, 17, 9);
  const louver2_L = toScr(-12, 17, 13);
  const louver2_R = toScr(12, 17, 13);
  const louver3_L = toScr(-12, 17, 16);
  const louver3_R = toScr(12, 17, 16);
  ctx.beginPath();
  ctx.moveTo(louver1_L[0], louver1_L[1]); ctx.lineTo(louver1_R[0], louver1_R[1]);
  ctx.moveTo(louver2_L[0], louver2_L[1]); ctx.lineTo(louver2_R[0], louver2_R[1]);
  ctx.moveTo(louver3_L[0], louver3_L[1]); ctx.lineTo(louver3_R[0], louver3_R[1]);
  ctx.stroke();

  // B. Left Cheek Armor Facet (Linke Turmflanke)
  const leftCheekSun = isWest ? (0.75 + 0.30 * Math.sin(phi)) : (0.45 - 0.25 * Math.sin(phi));
  const leftCheekGrad = ctx.createLinearGradient(b_ML[0], b_ML[1], r_ML[0], r_ML[1]);
  leftCheekGrad.addColorStop(0.0, `rgb(${Math.round(44 * leftCheekSun)},${Math.round(54 * leftCheekSun)},${Math.round(71 * leftCheekSun)})`);
  leftCheekGrad.addColorStop(0.5, `rgb(${Math.round(67 * leftCheekSun)},${Math.round(82 * leftCheekSun)},${Math.round(105 * leftCheekSun)})`);
  leftCheekGrad.addColorStop(1.0, `rgb(${Math.round(86 * leftCheekSun)},${Math.round(104 * leftCheekSun)},${Math.round(133 * leftCheekSun)})`);
  ctx.fillStyle = leftCheekGrad;
  ctx.beginPath();
  ctx.moveTo(b_FL[0], b_FL[1]);
  ctx.lineTo(b_ML[0], b_ML[1]);
  ctx.lineTo(b_RL[0], b_RL[1]);
  ctx.lineTo(r_RL[0], r_RL[1]);
  ctx.lineTo(r_ML[0], r_ML[1]);
  ctx.lineTo(r_FL[0], r_FL[1]);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = '#64748b';
  ctx.lineWidth = 1.1;
  ctx.stroke();

  // C. Right Cheek Armor Facet (Rechte Turmflanke)
  const rightCheekSun = isWest ? (0.45 - 0.25 * Math.sin(phi)) : (0.80 + 0.25 * Math.sin(phi));
  const rightCheekGrad = ctx.createLinearGradient(b_MR[0], b_MR[1], r_MR[0], r_MR[1]);
  rightCheekGrad.addColorStop(0.0, `rgb(${Math.round(44 * rightCheekSun)},${Math.round(54 * rightCheekSun)},${Math.round(71 * rightCheekSun)})`);
  rightCheekGrad.addColorStop(0.5, `rgb(${Math.round(67 * rightCheekSun)},${Math.round(82 * rightCheekSun)},${Math.round(105 * rightCheekSun)})`);
  rightCheekGrad.addColorStop(1.0, `rgb(${Math.round(86 * rightCheekSun)},${Math.round(104 * rightCheekSun)},${Math.round(133 * rightCheekSun)})`);
  ctx.fillStyle = rightCheekGrad;
  ctx.beginPath();
  ctx.moveTo(b_FR[0], b_FR[1]);
  ctx.lineTo(b_MR[0], b_MR[1]);
  ctx.lineTo(b_RR[0], b_RR[1]);
  ctx.lineTo(r_RR[0], r_RR[1]);
  ctx.lineTo(r_MR[0], r_MR[1]);
  ctx.lineTo(r_FR[0], r_FR[1]);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = '#64748b';
  ctx.lineWidth = 1.1;
  ctx.stroke();

  // D. Sloped Front Glacis Armor Plate (Bugpanzer / Frontschürze)
  const glacisSun = 0.60 + 0.40 * Math.cos(phi);
  const glacisGrad = ctx.createLinearGradient(b_FL[0], b_FL[1], r_FL[0], r_FL[1]);
  glacisGrad.addColorStop(0.0, `rgb(${Math.round(58 * glacisSun)},${Math.round(71 * glacisSun)},${Math.round(90 * glacisSun)})`);
  glacisGrad.addColorStop(1.0, `rgb(${Math.round(97 * glacisSun)},${Math.round(114 * glacisSun)},${Math.round(140 * glacisSun)})`);
  ctx.fillStyle = glacisGrad;
  ctx.beginPath();
  ctx.moveTo(b_FL[0], b_FL[1]);
  ctx.lineTo(b_FR[0], b_FR[1]);
  ctx.lineTo(r_FR[0], r_FR[1]);
  ctx.lineTo(r_FL[0], r_FL[1]);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = '#71839e';
  ctx.lineWidth = 1.1;
  ctx.stroke();

  // E. Armored Roof Plate (Dachpanzer Crown - Top Facet)
  const roofGrad = ctx.createLinearGradient(r_ML[0], r_ML[1], r_MR[0], r_MR[1]);
  if (isWest) {
    roofGrad.addColorStop(0.0, '#5a6a84');
    roofGrad.addColorStop(0.5, '#788aa6');
    roofGrad.addColorStop(1.0, '#9bb0ce'); // Sunlit east rim
  } else {
    roofGrad.addColorStop(0.0, '#9bb0ce'); // Sunlit west rim
    roofGrad.addColorStop(0.5, '#788aa6');
    roofGrad.addColorStop(1.0, '#5a6a84');
  }
  ctx.fillStyle = roofGrad;
  ctx.beginPath();
  ctx.moveTo(r_FL[0], r_FL[1]);
  ctx.lineTo(r_FR[0], r_FR[1]);
  ctx.lineTo(r_MR[0], r_MR[1]);
  ctx.lineTo(r_RR[0], r_RR[1]);
  ctx.lineTo(r_RL[0], r_RL[1]);
  ctx.lineTo(r_ML[0], r_ML[1]);
  ctx.closePath();
  ctx.fill();

  // Specular Ridge Lines across Roof Chamfers & Glacis
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1.3;
  ctx.beginPath();
  ctx.moveTo(r_FL[0], r_FL[1]); ctx.lineTo(r_FR[0], r_FR[1]);
  ctx.moveTo(r_FL[0], r_FL[1]); ctx.lineTo(r_ML[0], r_ML[1]);
  ctx.moveTo(r_FR[0], r_FR[1]); ctx.lineTo(r_MR[0], r_MR[1]);
  ctx.stroke();

  // Fleet Stenciled ID on the Visible Flank Plate of the Turret
  ctx.save();
  const stencilPos = toScr(isWest ? -14 : 14, 2, 12);
  ctx.font = 'bold 6.0px monospace';
  ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
  ctx.textAlign = 'center';
  ctx.fillText(label, stencilPos[0], stencilPos[1]);
  ctx.restore();

  // F. Commander / Targeting Optics Cupola Blister (Mounted on Roof)
  const cupola_Base = toScr(0, 3, 21.0);
  const cupola_Top  = toScr(0, 3, 24.5);
  ctx.fillStyle = '#1e293b';
  ctx.beginPath();
  ctx.ellipse(cupola_Base[0], cupola_Base[1], 6.5, 3.5, phi, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#475569';
  ctx.beginPath();
  ctx.ellipse(cupola_Top[0], cupola_Top[1], 5.5, 2.8, phi, 0, Math.PI * 2);
  ctx.fill();

  // Sapphire Rangefinder Lenses (Cyan Glow)
  const lensL = toScr(-3.0, 1.5, 23.0);
  const lensR = toScr(3.0, 1.5, 23.0);
  ctx.fillStyle = '#38bdf8';
  ctx.fillRect(lensL[0] - 1.2, lensL[1] - 1.2, 2.4, 2.4);
  ctx.fillRect(lensR[0] - 1.2, lensR[1] - 1.2, 2.4, 2.4);

  // Active Laser Targeting Sensor Slit (Red Glow)
  const laserPos = toScr(0, -0.5, 22.0);
  ctx.fillStyle = '#ef4444';
  ctx.fillRect(laserPos[0] - 1.5, laserPos[1] - 0.8, 3.0, 1.6);
  if (state.quality !== 'low') {
    ctx.fillStyle = 'rgba(239, 68, 68, 0.50)';
    ctx.beginPath();
    ctx.arc(laserPos[0], laserPos[1], 3.8, 0, Math.PI * 2);
    ctx.fill();
  }

  // ---------------------------------------------------------------------------
  // 6. HEAVY ARMORED GUN MANTLET & TRUNNION HOUSING (Walzenblende am Korpus)
  // Forward-projecting curved cradle from which the twin barrels emerge
  // ---------------------------------------------------------------------------
  const m_TL = toScr(-11, -15, 18.0);
  const m_TR = toScr(11, -15, 18.0);
  const m_FL_Top = toScr(-11, -23, 18.0);
  const m_FR_Top = toScr(11, -23, 18.0);
  const m_FL_Bot = toScr(-11, -23, 7.5);
  const m_FR_Bot = toScr(11, -23, 7.5);

  // Mantlet Top Slope
  ctx.fillStyle = '#55667e';
  ctx.beginPath();
  ctx.moveTo(m_TL[0], m_TL[1]);
  ctx.lineTo(m_TR[0], m_TR[1]);
  ctx.lineTo(m_FR_Top[0], m_FR_Top[1]);
  ctx.lineTo(m_FL_Top[0], m_FL_Top[1]);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = '#7b8ea8';
  ctx.lineWidth = 1.0;
  ctx.stroke();

  // Mantlet Front Vertical Trunnion Face
  const mantletGrad = ctx.createLinearGradient(m_FL_Top[0], m_FL_Top[1], m_FR_Top[0], m_FR_Top[1]);
  mantletGrad.addColorStop(0.0, isWest ? '#677894' : '#3a475a');
  mantletGrad.addColorStop(0.5, '#56667f');
  mantletGrad.addColorStop(1.0, isWest ? '#3a475a' : '#677894');
  ctx.fillStyle = mantletGrad;
  ctx.beginPath();
  ctx.moveTo(m_FL_Top[0], m_FL_Top[1]);
  ctx.lineTo(m_FR_Top[0], m_FR_Top[1]);
  ctx.lineTo(m_FR_Bot[0], m_FR_Bot[1]);
  ctx.lineTo(m_FL_Bot[0], m_FL_Bot[1]);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = '#8da0bc';
  ctx.lineWidth = 1.2;
  ctx.stroke();

  // Hydraulic Elevation Pushrod Rams flanking Mantlet
  const hydrL_Top = toScr(-12, -15, 17);
  const hydrL_Bot = toScr(-12, -15, 6);
  const hydrR_Top = toScr(12, -15, 17);
  const hydrR_Bot = toScr(12, -15, 6);
  ctx.strokeStyle = '#cbd5e1';
  ctx.lineWidth = 2.2;
  ctx.beginPath();
  ctx.moveTo(hydrL_Bot[0], hydrL_Bot[1]);
  ctx.lineTo(hydrL_Top[0], hydrL_Top[1]);
  ctx.moveTo(hydrR_Bot[0], hydrR_Bot[1]);
  ctx.lineTo(hydrR_Top[0], hydrR_Top[1]);
  ctx.stroke();

  // ---------------------------------------------------------------------------
  // 7. COLOSSAL DUAL GUN BARRELS (Perspektivisch exakt ausgerichtet & montiert)
  // Extruded directly from mantlet ports with 2.5D foreshortening & elevation
  // ---------------------------------------------------------------------------
  const bLen = 35.0;
  const halfCal = 3.2;

  // Direction Vector:
  // Forward length component along v: -bLen * Math.cos(psi)
  // Height component along z: bLen * Math.sin(psi)
  const dV = -bLen * Math.cos(psi);
  const dZ = bLen * Math.sin(psi);

  const drawProjectedBarrel = (gunPortU, gunPortV, gunPortZ, isLeft) => {
    // 3D Base endpoints on mantlet:
    const pBaseL = toScr(gunPortU - halfCal, gunPortV, gunPortZ);
    const pBaseR = toScr(gunPortU + halfCal, gunPortV, gunPortZ);

    // 3D Tip endpoints in sky:
    const pTipL  = toScr(gunPortU - halfCal, gunPortV + dV, gunPortZ + dZ);
    const pTipR  = toScr(gunPortU + halfCal, gunPortV + dV, gunPortZ + dZ);

    // Main Cylindrical Shading with Directional Sun Specular Ridge
    const bGrad = ctx.createLinearGradient(pBaseL[0], pBaseL[1], pBaseR[0], pBaseR[1]);
    if (isWest) {
      bGrad.addColorStop(0.0, '#1a1f2c');
      bGrad.addColorStop(0.35, '#cbd5e1'); // Sharp sunlit spine
      bGrad.addColorStop(0.70, '#515c6f');
      bGrad.addColorStop(1.0, '#141822');
    } else {
      bGrad.addColorStop(0.0, '#141822');
      bGrad.addColorStop(0.30, '#515c6f');
      bGrad.addColorStop(0.65, '#cbd5e1'); // Sharp sunlit spine
      bGrad.addColorStop(1.0, '#1a1f2c');
    }

    ctx.fillStyle = bGrad;
    ctx.beginPath();
    ctx.moveTo(pBaseL[0], pBaseL[1]);
    ctx.lineTo(pTipL[0], pTipL[1]);
    ctx.lineTo(pTipR[0], pTipR[1]);
    ctx.lineTo(pBaseR[0], pBaseR[1]);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 0.9;
    ctx.stroke();

    // A. Heavy Breech Recoil Sleeve (L = 10px along barrel)
    const sleeveFrac = 10.0 / bLen;
    const pSleeveL = toScr(gunPortU - halfCal * 1.3, gunPortV + dV * sleeveFrac, gunPortZ + dZ * sleeveFrac);
    const pSleeveR = toScr(gunPortU + halfCal * 1.3, gunPortV + dV * sleeveFrac, gunPortZ + dZ * sleeveFrac);
    const pSleeveBaseL = toScr(gunPortU - halfCal * 1.3, gunPortV, gunPortZ);
    const pSleeveBaseR = toScr(gunPortU + halfCal * 1.3, gunPortV, gunPortZ);

    ctx.fillStyle = '#1e2430';
    ctx.beginPath();
    ctx.moveTo(pSleeveBaseL[0], pSleeveBaseL[1]);
    ctx.lineTo(pSleeveL[0], pSleeveL[1]);
    ctx.lineTo(pSleeveR[0], pSleeveR[1]);
    ctx.lineTo(pSleeveBaseR[0], pSleeveBaseR[1]);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = '#64748b';
    ctx.lineWidth = 1.0;
    ctx.stroke();

    // Cooling Slits on Recoil Sleeve
    ctx.strokeStyle = '#06080d';
    ctx.lineWidth = 1.2;
    for (let sl = 0.25; sl <= 0.75; sl += 0.25) {
      const sL = toScr(gunPortU - halfCal * 1.2, gunPortV + dV * sleeveFrac * sl, gunPortZ + dZ * sleeveFrac * sl);
      const sR = toScr(gunPortU + halfCal * 1.2, gunPortV + dV * sleeveFrac * sl, gunPortZ + dZ * sleeveFrac * sl);
      ctx.beginPath();
      ctx.moveTo(sL[0], sL[1]);
      ctx.lineTo(sR[0], sR[1]);
      ctx.stroke();
    }

    // B. Mid-Barrel Electromagnetic Accelerator Rings (at 50% and 68% length)
    const drawAccelRing = (frac) => {
      const rL = toScr(gunPortU - halfCal * 1.35, gunPortV + dV * frac, gunPortZ + dZ * frac);
      const rR = toScr(gunPortU + halfCal * 1.35, gunPortV + dV * frac, gunPortZ + dZ * frac);
      ctx.strokeStyle = '#090d16';
      ctx.lineWidth = 3.2;
      ctx.beginPath();
      ctx.moveTo(rL[0], rL[1]);
      ctx.lineTo(rR[0], rR[1]);
      ctx.stroke();
      ctx.strokeStyle = '#e2e8f0';
      ctx.lineWidth = 1.0;
      ctx.stroke();
    };
    drawAccelRing(0.50);
    drawAccelRing(0.68);

    // C. Flared Multi-Chamber Muzzle Brake (Tip Block)
    const mbFracStart = 0.84;
    const pMbBaseL = toScr(gunPortU - halfCal * 1.45, gunPortV + dV * mbFracStart, gunPortZ + dZ * mbFracStart);
    const pMbBaseR = toScr(gunPortU + halfCal * 1.45, gunPortV + dV * mbFracStart, gunPortZ + dZ * mbFracStart);
    const pMbTipL  = toScr(gunPortU - halfCal * 1.45, gunPortV + dV, gunPortZ + dZ);
    const pMbTipR  = toScr(gunPortU + halfCal * 1.45, gunPortV + dV, gunPortZ + dZ);

    ctx.fillStyle = '#1c222e';
    ctx.beginPath();
    ctx.moveTo(pMbBaseL[0], pMbBaseL[1]);
    ctx.lineTo(pMbTipL[0], pMbTipL[1]);
    ctx.lineTo(pMbTipR[0], pMbTipR[1]);
    ctx.lineTo(pMbBaseR[0], pMbBaseR[1]);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = SW.hullWhite;
    ctx.lineWidth = 1.1;
    ctx.stroke();

    // Lateral Muzzle Gas Vent Ports
    ctx.fillStyle = '#04060a';
    const pMidL = [(pMbBaseL[0] + pMbTipL[0]) * 0.5, (pMbBaseL[1] + pMbTipL[1]) * 0.5];
    const pMidR = [(pMbBaseR[0] + pMbTipR[0]) * 0.5, (pMbBaseR[1] + pMbTipR[1]) * 0.5];
    ctx.fillRect(pMidL[0] - 1.2, pMidL[1] - 1.8, 2.4, 3.6);
    ctx.fillRect(pMidR[0] - 1.2, pMidR[1] - 1.8, 2.4, 3.6);

    // D. Elliptical Muzzle Bore Aperture
    const pTipCenter = [(pTipL[0] + pTipR[0]) * 0.5, (pTipL[1] + pTipR[1]) * 0.5];
    ctx.fillStyle = '#000000';
    ctx.beginPath();
    ctx.ellipse(pTipCenter[0], pTipCenter[1], halfCal * 1.2, halfCal * 0.85, phi, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#334155';
    ctx.lineWidth = 0.8;
    ctx.stroke();

    // Subtle Ready-to-Fire Bore Plasma Glow
    if (state.quality !== 'low') {
      const borePulse = 0.45 + 0.55 * Math.sin(t * 7.5 + (isLeft ? 0 : 1.5));
      ctx.fillStyle = `rgba(239, 68, 68, ${borePulse * 0.75})`;
      ctx.beginPath();
      ctx.ellipse(pTipCenter[0], pTipCenter[1], halfCal * 0.7, halfCal * 0.45, phi, 0, Math.PI * 2);
      ctx.fill();
    }

    // Accordion Blast-Bag Collar Wrapping the Barrel Base at Mantlet Port
    const pPortCenter = toScr(gunPortU, gunPortV, gunPortZ);
    ctx.fillStyle = '#06080d';
    ctx.beginPath();
    ctx.ellipse(pPortCenter[0], pPortCenter[1], halfCal * 1.5, halfCal * 1.1, phi, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 1.0;
    ctx.stroke();
  };

  // Render Left and Right Barrels firmly mounted into the Mantlet trunnions
  drawProjectedBarrel(-6.0, -24.0, 12.0, true);
  drawProjectedBarrel(6.0, -24.0, 12.0, false);

  ctx.restore();
}

// ---------------------------------------------------------------------------
// HERO GADGET 3: DENSE 3D ANTENNA FOREST & SENSOR CLUSTER (x = 1180 .. 1320)
// With Cast Shadows, 3D Lattice Towers & Spherical Radomes
// ---------------------------------------------------------------------------

function drawAntennaForest(ctx, t) {
  ctx.save();
  const baseX = 1180;
  const baseY = 204;

  // 1. Directional Cast Shadow onto the Deck
  ctx.fillStyle = 'rgba(0, 0, 0, 0.48)';
  ctx.beginPath();
  ctx.moveTo(baseX + 50, baseY + 6);
  ctx.lineTo(baseX + 115, baseY + 24);
  ctx.lineTo(baseX + 125, baseY + 20);
  ctx.lineTo(baseX + 70, baseY + 4);
  ctx.closePath();
  ctx.fill();

  // 2. Heavy Central 3D Lattice Communications Tower (x = 1240, h = 76)
  const mx = baseX + 60;

  // Rear leg (in deep shadow)
  ctx.strokeStyle = '#272c38';
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(mx, baseY);
  ctx.lineTo(mx, baseY - 76);
  ctx.stroke();

  // Crossbracing with depth
  ctx.strokeStyle = SW.hullDark;
  ctx.lineWidth = 1.0;
  for (let s = 1; s <= 4; s++) {
    const py = baseY - (76 / 5) * s;
    const w = 8 * (1 - s / 5);
    ctx.beginPath();
    ctx.moveTo(mx - w, py);
    ctx.lineTo(mx + w, py);
    ctx.stroke();
  }

  // Front legs (lit in crisp sunset white/gold)
  ctx.strokeStyle = SW.hullWhite;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(mx - 8, baseY);
  ctx.lineTo(mx, baseY - 76);
  ctx.lineTo(mx + 8, baseY);
  ctx.stroke();

  // Crossarm & Dipoles
  ctx.strokeStyle = SW.hullWhite;
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(mx - 14, baseY - 62);
  ctx.lineTo(mx + 14, baseY - 62);
  ctx.stroke();

  // Red Double-Strobe Aviation Beacon
  const strobe = Math.floor(t * 3.0) % 2 === 0 && ((t * 8.0) % 1.0 < 0.25);
  ctx.fillStyle = strobe ? '#ff2222' : '#770e0e';
  ctx.beginPath();
  ctx.arc(mx, baseY - 78, 3.2, 0, Math.PI * 2);
  ctx.fill();
  if (strobe && state.quality === 'high') {
    ctx.fillStyle = 'rgba(239, 68, 68, 0.45)';
    ctx.beginPath();
    ctx.arc(mx, baseY - 78, 9, 0, Math.PI * 2);
    ctx.fill();
  }

  // 3. High-Frequency Whip Needles with Base Springs
  const whips = [
    { x: baseX + 15, h: 48, sway: 0.08 },
    { x: baseX + 32, h: 56, sway: -0.1 },
    { x: baseX + 92, h: 52, sway: 0.09 },
    { x: baseX + 115, h: 42, sway: -0.07 }
  ];

  for (let w = 0; w < whips.length; w++) {
    const wh = whips[w];
    const tipSway = Math.sin(t * 3.5 + w) * 3.5;
    ctx.strokeStyle = SW.hullWhite;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(wh.x, baseY);
    ctx.lineTo(wh.x + tipSway, baseY - wh.h);
    ctx.stroke();

    // Antenna Base Spring Mount (3D collar)
    ctx.fillStyle = '#1c202a';
    ctx.fillRect(wh.x - 2.5, baseY - 4, 5, 4);
    ctx.fillStyle = '#94a3b8';
    ctx.fillRect(wh.x - 1.5, baseY - 3, 3, 2);

    // Tip Indicator Node
    ctx.fillStyle = SW.cyanPower;
    ctx.beginPath();
    ctx.arc(wh.x + tipSway, baseY - wh.h, 1.8, 0, Math.PI * 2);
    ctx.fill();
  }

  // 4. Directional Radome Sensor Globes with 3D Spherical Shading
  const domes = [baseX + 42, baseX + 80];
  for (let d = 0; d < domes.length; d++) {
    const dx = domes[d];
    // Spherical gradient
    const domeGrad = ctx.createRadialGradient(dx - 2, baseY - 13, 1, dx, baseY - 10, 7);
    domeGrad.addColorStop(0.0, '#ffffff'); // Specular sun highlight
    domeGrad.addColorStop(0.4, '#cbd5e1');
    domeGrad.addColorStop(0.8, '#515764');
    domeGrad.addColorStop(1.0, '#1c202a');
    ctx.fillStyle = domeGrad;
    ctx.beginPath();
    ctx.arc(dx, baseY - 10, 7, Math.PI, 0);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = SW.hullDeep;
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  // Connecting 3D Cable Trunk along base
  drawConduitRack(ctx, baseX, baseY, baseX + 130, baseY, SW.pipeBronze, 2.5);

  ctx.restore();
}

// ---------------------------------------------------------------------------
// STRATOS DREADNOUGHT PIGEON EASTER EGG ("DIE STRATOS-TAUBE")
// Arrives between 8..30s from the high sky, gets overtaken by the dreadnought's
// high-speed forward momentum, flaps vigorously to catch up, performs an
// aerodynamic landing flare on the antenna crossbeam, idles with characteristic
// head-bobbing, responds to clicks with a "Gurr! ❤️" speech bubble & heart emote,
// and eventually departs into the sunset.
// ---------------------------------------------------------------------------

const pigeonState = {
  spawnTime: 12.0,        // Arrives after 12s by default (or via button click)
  glideDuration: 4.5,     // 0.0 .. 4.5: High altitude glide entry from top-left (scale 0.32 -> 0.72)
  overtakeDuration: 2.8,  // 4.5 .. 7.3: Ship cruise speed pulls ahead, pigeon drops into slipstream downwash
  flapDuration: 2.8,      // 7.3 .. 10.1: Vigorous flapping catch-up sprint
  flareDuration: 1.5,     // 10.1 .. 11.6: Aerodynamic landing flare (braking pitch, feet extended)
  perchDuration: 45.0,    // 11.6 .. 56.6: Resting on antenna crossbeam (Option 2B)
  departDuration: 4.5,    // 56.6 .. 61.1: Fly-off forward to the right (+X) into the distance
  cycleCooldown: 25.0,    // Cooldown until next pigeon arrives
  lastCooTime: -999,
  targetX: 1234,          // Left crossarm strut of central lattice mast
  targetY: 141.5,
  fleeStartTime: -999,    // Storm evacuation timestamp
  fleeOriginX: 1234,
  fleeOriginY: 141.5,
  fleeOriginScale: 1.0,
  fleeDuration: 3.2
};

function getNormalPigeonFlight(dt) {
  const tGlide = pigeonState.glideDuration;
  const tOvertake = tGlide + pigeonState.overtakeDuration;
  const tFlap = tOvertake + pigeonState.flapDuration;
  const tFlare = tFlap + pigeonState.flareDuration;
  const tPerch = tFlare + pigeonState.perchDuration;
  const tDepart = tPerch + pigeonState.departDuration;

  if (dt < tGlide) {
    // Phase 1: High altitude glide entry from top-left sky, heading strictly to the right (+X)
    const p = dt / tGlide;
    const pEase = p * (1.5 - 0.5 * p);
    const x = 880 + (1165 - 880) * pEase;
    const y = 20 + (162 - 20) * (0.6 * p + 0.4 * p * p);
    const scale = 0.32 + (0.72 - 0.32) * p;
    // Gentle descending glide pitch (pointing forward & slightly down-right)
    const angle = 0.18 - 0.08 * Math.sin(p * Math.PI);
    return { phase: 'glide', x, y, scale, angle, p, dt };
  } else if (dt < tOvertake) {
    // Phase 2: Ship cruise velocity overtakes pigeon; bird drops lower into slipstream downwash
    // while continuously striving forward to the right (+X, strictly monotonic)
    const p = (dt - tGlide) / pigeonState.overtakeDuration;
    const x = 1165 + (1192 - 1165) * (p * (2 - p));
    const y = 162 + (192 - 162) * (p * p);
    const scale = 0.72 + (0.88 - 0.72) * p;
    // Shift from descending glide to fighting headwind level
    const angle = 0.12 * (1.0 - p) - 0.06 * p;
    return { phase: 'overtake', x, y, scale, angle, p, dt };
  } else if (dt < tFlap) {
    // Phase 3: Vigorous flapping catch-up sprint; climbs up out of slipstream towards mast
    const p = (dt - tOvertake) / pigeonState.flapDuration;
    const x = 1192 + (1230 - 1192) * (p * p * (3 - 2 * p));
    const y = 192 + (136 - 192) * Math.sin(p * Math.PI * 0.5);
    const scale = 0.88 + (1.0 - 0.88) * p;
    // Climbing pitch: nose pointed up-right (facing forward +X)
    const angle = -0.22 * (1.0 - 0.4 * p);
    return { phase: 'flap_sprint', x, y, scale, angle, p, dt };
  } else if (dt < tFlare) {
    // Phase 4: Aerodynamic landing flare onto crossbeam; settles smoothly at (1234, 141.5)
    // Strictly monotonic +X from 1230 to 1234 (never overshoots or reverses)
    const p = (dt - tFlap) / pigeonState.flareDuration;
    const x = 1230 + (pigeonState.targetX - 1230) * Math.sin(p * Math.PI * 0.5);
    const y = 136 + (pigeonState.targetY - 136) * (p * p);
    const scale = 1.0;
    // Flare: pitches chest up to brake (-0.28 rad nose up), leveling smoothly to 0 on touchdown
    const angle = -0.28 * (1.0 - p);
    return { phase: 'flare_landing', x, y, scale, angle, p, dt };
  } else if (dt < tPerch) {
    // Phase 5: Perched on antenna crossarm at (1234, 141.5), facing strictly right (+X)
    const p = (dt - tFlare) / pigeonState.perchDuration;
    return { phase: 'perched', x: pigeonState.targetX, y: pigeonState.targetY, scale: 1.0, angle: 0, p, dt };
  } else if (dt < tDepart) {
    // Phase 6: Departure forward into the distance to the right (+X), soaring into the sunrise
    const p = (dt - tPerch) / pigeonState.departDuration;
    const x = pigeonState.targetX + p * 320 + p * p * 180;
    const y = pigeonState.targetY - p * 75 - p * p * 40;
    const scale = Math.max(0.18, 1.0 - p * 0.82);
    // Climbing pitch forward to the right (+X)
    const angle = -0.24;
    return { phase: 'depart', x, y, scale, angle, p, dt };
  }
  return null;
}

function getPigeonFlight(t) {
  const tTotal = pigeonState.glideDuration + pigeonState.overtakeDuration + pigeonState.flapDuration +
                 pigeonState.flareDuration + pigeonState.perchDuration + pigeonState.departDuration;
  const cycleTotal = tTotal + pigeonState.cycleCooldown;

  const storm = isStormActiveOrApproaching(t);

  // Storm Reaction: "spätestens wenn gewitter aufkommt fliegen tauben weg"
  if (storm) {
    if (pigeonState.fleeStartTime > -900) {
      const fleeDt = t - pigeonState.fleeStartTime;
      if (fleeDt >= 0 && fleeDt < pigeonState.fleeDuration) {
        const p = fleeDt / pigeonState.fleeDuration;
        const x = pigeonState.fleeOriginX + p * 380 + p * p * 260;
        const y = pigeonState.fleeOriginY - p * 110 - p * p * 50;
        const scale = Math.max(0.12, pigeonState.fleeOriginScale * (1.0 - 0.75 * p));
        const angle = -0.32;
        return { phase: 'flee', x, y, scale, angle, p, dt: fleeDt };
      }
      // Flee completed: keep delaying respawn while storm persists
      pigeonState.spawnTime = Math.max(pigeonState.spawnTime, t + 25.0);
      return null;
    }

    // Was pigeon currently active on screen when storm broke out?
    if (t >= pigeonState.spawnTime) {
      const dt = (t - pigeonState.spawnTime) % cycleTotal;
      if (dt < tTotal) {
        const cur = getNormalPigeonFlight(dt);
        if (cur) {
          pigeonState.fleeStartTime = t;
          pigeonState.fleeOriginX = cur.x;
          pigeonState.fleeOriginY = cur.y;
          pigeonState.fleeOriginScale = cur.scale;
          return { phase: 'flee', x: cur.x, y: cur.y, scale: cur.scale, angle: -0.32, p: 0, dt: 0 };
        }
      }
    }

    // Not on screen: delay arrival until storm passes
    pigeonState.spawnTime = Math.max(pigeonState.spawnTime, t + 25.0);
    return null;
  }

  // Fair weather: reset flee state
  if (pigeonState.fleeStartTime > -900) {
    pigeonState.fleeStartTime = -999;
  }

  if (t < pigeonState.spawnTime) return null;
  const dt = (t - pigeonState.spawnTime) % cycleTotal;
  return getNormalPigeonFlight(dt);
}

function drawPigeonCooEmote(ctx, x, y, dtCoo) {
  if (dtCoo < 0 || dtCoo > 2.8) return;

  const p = dtCoo / 2.8;
  const floatY = y - 16 - p * 12;
  const alpha = p > 0.8 ? (1.0 - (p - 0.8) / 0.2) : Math.min(1.0, dtCoo * 6.0);

  ctx.save();
  ctx.globalAlpha = alpha;

  // 1. Comic Speech Bubble ("Gurr!") - placed near the head facing right
  const bw = 38;
  const bh = 17;
  const bx = x + 10;
  const by = floatY - bh;

  // Subtle drop shadow
  ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
  ctx.beginPath();
  if (ctx.roundRect) ctx.roundRect(bx - bw * 0.5 + 1.5, by + 1.5, bw, bh, 5);
  else ctx.rect(bx - bw * 0.5 + 1.5, by + 1.5, bw, bh);
  ctx.fill();

  // White bubble with crisp dark border
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = '#0f172a';
  ctx.lineWidth = 1.3;
  ctx.beginPath();
  if (ctx.roundRect) ctx.roundRect(bx - bw * 0.5, by, bw, bh, 5);
  else ctx.rect(bx - bw * 0.5, by, bw, bh);
  ctx.fill();
  ctx.stroke();

  // Pointer tail pointing to beak (beak is around x + 4, y - 8)
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.moveTo(bx - 10, by + bh - 0.5);
  ctx.lineTo(bx - 14, by + bh + 4.5);
  ctx.lineTo(bx - 4, by + bh - 0.5);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = '#0f172a';
  ctx.lineWidth = 1.3;
  ctx.beginPath();
  ctx.moveTo(bx - 10, by + bh - 0.5);
  ctx.lineTo(bx - 14, by + bh + 4.5);
  ctx.lineTo(bx - 4, by + bh - 0.5);
  ctx.stroke();

  // Speech Text (Clean comic lettering without emojis)
  ctx.font = 'bold 9px sans-serif';
  ctx.fillStyle = '#0f172a';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('Gurr!', bx, by + bh * 0.5 + 0.5);

  // Subtle acoustic sparkles
  const chirpFloat = by - 6 - p * 10;
  const sp1 = 0.5 + 0.5 * Math.sin(dtCoo * 10.0);
  ctx.fillStyle = `rgba(254, 240, 138, ${sp1 * 0.85})`;
  ctx.beginPath();
  ctx.arc(bx + 14, chirpFloat - 2, 1.2, 0, Math.PI * 2);
  ctx.arc(bx - 8, chirpFloat + 2, 1.0, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

function drawStratosPigeon(ctx, t) {
  const p = getPigeonFlight(t);
  if (!p) return;

  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.scale(p.scale, p.scale);

  // If clicked recently, pigeon does a joyful little flutter hop
  let hopY = 0;
  const cooDt = t - pigeonState.lastCooTime;
  if (p.phase === 'perched' && cooDt >= 0 && cooDt < 0.6) {
    hopY = -Math.sin((cooDt / 0.6) * Math.PI) * 2.8;
  }
  ctx.translate(0, hopY);

  if (p.phase === 'perched') {
    // -------------------------------------------------------------
    // PERCHED PIGEON (On the Antenna Crossarm at y = 141.5)
    // -------------------------------------------------------------
    // 1. Tiny coral/pink claws grasping the metal strut
    ctx.fillStyle = '#fb7185';
    ctx.fillRect(-2.5, -0.6, 2.0, 1.4);
    ctx.fillRect(0.6, -0.6, 2.0, 1.4);

    // 2. Plump pigeon body
    const bodyGrad = ctx.createLinearGradient(-3, -8, 2, 0);
    bodyGrad.addColorStop(0.0, '#94a3b8');
    bodyGrad.addColorStop(0.5, '#64748b');
    bodyGrad.addColorStop(1.0, '#475569');
    ctx.fillStyle = bodyGrad;
    ctx.beginPath();
    ctx.ellipse(-1.0, -4.6, 4.4, 3.4, -0.20, 0, Math.PI * 2);
    ctx.fill();

    // Soft iridescent chest sheen (rose-lavender)
    ctx.fillStyle = 'rgba(168, 85, 247, 0.35)';
    ctx.beginPath();
    ctx.ellipse(0.8, -4.2, 2.4, 2.2, -0.15, 0, Math.PI * 2);
    ctx.fill();

    // 3. Folded Wing with Dual Black Wingbars
    const wingGrad = ctx.createLinearGradient(-3, -7, 1, -2);
    wingGrad.addColorStop(0.0, '#475569');
    wingGrad.addColorStop(1.0, '#334155');
    ctx.fillStyle = wingGrad;
    ctx.beginPath();
    ctx.ellipse(-1.6, -4.8, 3.8, 2.3, -0.35, 0, Math.PI * 2);
    ctx.fill();

    // Dual black wingbars (charakteristische schwarze Flügelbinden)
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 0.9;
    ctx.beginPath();
    ctx.moveTo(-3.2, -4.0); ctx.lineTo(-1.2, -6.0);
    ctx.moveTo(-1.8, -3.4); ctx.lineTo(0.2, -5.2);
    ctx.stroke();

    // 4. Tail Feathers (with periodic subtle twitch)
    const tailTwitch = ((t % 4.2) < 0.28) ? Math.sin(t * 22) * 0.15 : 0;
    ctx.save();
    ctx.translate(-4.5, -4.0);
    ctx.rotate(tailTwitch);
    ctx.fillStyle = '#334155';
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(-4.2, -1.8);
    ctx.lineTo(-3.8, -3.2);
    ctx.closePath();
    ctx.fill();
    // Black tail tip
    ctx.fillStyle = '#0f172a';
    ctx.beginPath();
    ctx.moveTo(-3.0, -1.3);
    ctx.lineTo(-4.2, -1.8);
    ctx.lineTo(-3.8, -3.2);
    ctx.lineTo(-2.6, -2.2);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // 5. Neck & Head with typical Pigeon Head-Bobbing
    // Head bobs forward in a snappy motion every 2.2 seconds
    const bobCycle = (t * 1.8) % (Math.PI * 2);
    const headBob = Math.pow(Math.max(0, Math.sin(bobCycle)), 6.0) * 1.6;
    const hx = 2.4 + headBob;
    const hy = -7.6;

    // Iridescent emerald-green neck collar
    ctx.fillStyle = '#059669';
    ctx.beginPath();
    ctx.arc(hx - 1.2, hy + 1.4, 1.8, 0, Math.PI * 2);
    ctx.fill();

    // Rounded head
    ctx.fillStyle = '#94a3b8';
    ctx.beginPath();
    ctx.arc(hx, hy, 2.3, 0, Math.PI * 2);
    ctx.fill();

    // Peach/Orange Beak with dark nostril cere
    ctx.fillStyle = '#fb923c';
    ctx.beginPath();
    ctx.moveTo(hx + 1.8, hy - 0.5);
    ctx.lineTo(hx + 3.8, hy + 0.3);
    ctx.lineTo(hx + 1.6, hy + 1.1);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#f8fafc'; // White cere patch
    ctx.fillRect(hx + 1.2, hy - 0.7, 1.2, 0.8);

    // Amber Eye with Black Pupil & Periodic Blink
    const isBlinking = ((t % 3.6) < 0.14);
    if (!isBlinking) {
      // Golden-amber iris ring
      ctx.fillStyle = '#f59e0b';
      ctx.beginPath();
      ctx.arc(hx + 0.6, hy - 0.6, 0.95, 0, Math.PI * 2);
      ctx.fill();
      // Black pinpoint pupil
      ctx.fillStyle = '#020617';
      ctx.beginPath();
      ctx.arc(hx + 0.7, hy - 0.6, 0.50, 0, Math.PI * 2);
      ctx.fill();
    } else {
      // Closed eye slit
      ctx.strokeStyle = '#475569';
      ctx.lineWidth = 0.7;
      ctx.beginPath();
      ctx.moveTo(hx + 0.2, hy - 0.6);
      ctx.lineTo(hx + 1.1, hy - 0.6);
      ctx.stroke();
    }

  } else {
    // -------------------------------------------------------------
    // FLYING PIGEON (Glide / Overtake / Flapping / Flare / Depart)
    // -------------------------------------------------------------
    ctx.rotate(p.angle);

    const isFlapping = (p.phase === 'flap_sprint' || p.phase === 'depart' || p.phase === 'flee');
    const isFlare = (p.phase === 'flare_landing');
    const isGlide = (p.phase === 'glide');

    // Wing stroke angle
    let wingAngle = 0.12;
    if (p.phase === 'flee') {
      wingAngle = Math.sin(t * 54.0) * 0.95; // Frantic emergency flapping to escape the storm
    } else if (isFlapping) {
      wingAngle = Math.sin(t * 44.0) * 0.85;
    } else if (isFlare) {
      wingAngle = 0.78 + Math.sin(t * 18.0) * 0.20; // High cupped braking wings
    } else if (p.phase === 'overtake') {
      wingAngle = 0.20 + Math.sin(t * 30.0) * (0.35 + 0.45 * p.p);
    } else if (isGlide) {
      wingAngle = 0.10 + Math.sin(t * 4.0) * 0.04;
    }

    // A. Far Wing (Left wing behind body)
    ctx.save();
    ctx.translate(-0.5, -2.5);
    ctx.rotate(-wingAngle);
    ctx.fillStyle = '#334155';
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(2.0, -7.0);
    ctx.lineTo(-4.0, -6.0);
    ctx.lineTo(-2.5, 0);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // B. Streamlined Torso
    ctx.fillStyle = '#64748b';
    ctx.beginPath();
    ctx.ellipse(0, 0, 4.5, 2.6, 0, 0, Math.PI * 2);
    ctx.fill();

    // White Rump Patch (Iconic Columba livia Bürzel)
    ctx.fillStyle = '#f1f5f9';
    ctx.beginPath();
    ctx.ellipse(-2.8, -0.6, 1.4, 1.2, 0, 0, Math.PI * 2);
    ctx.fill();

    // Wedge Tail
    ctx.fillStyle = '#1e293b';
    ctx.beginPath();
    ctx.moveTo(-3.5, -0.8);
    ctx.lineTo(-7.5, isFlare ? -2.8 : -1.8);
    ctx.lineTo(-7.5, isFlare ? 2.8 : 1.8);
    ctx.lineTo(-3.5, 0.8);
    ctx.closePath();
    ctx.fill();

    // C. Near Wing (Right wing in front)
    ctx.save();
    ctx.translate(-0.5, 0.5);
    ctx.rotate(wingAngle);
    ctx.fillStyle = '#475569';
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(2.5, 7.5);
    ctx.lineTo(-4.5, 6.5);
    ctx.lineTo(-2.5, 0);
    ctx.closePath();
    ctx.fill();

    // Black wingbar stripes on near wing
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 0.9;
    ctx.beginPath();
    ctx.moveTo(-1.5, 2.0); ctx.lineTo(-0.5, 6.0);
    ctx.moveTo(-2.8, 1.5); ctx.lineTo(-1.8, 5.0);
    ctx.stroke();
    ctx.restore();

    // D. Extended landing legs during flare
    if (isFlare) {
      ctx.fillStyle = '#fb7185';
      ctx.fillRect(0.5, 2.0, 1.4, 3.2);
      ctx.fillRect(2.2, 2.0, 1.4, 3.2);
    }

    // E. Head & Beak in flight (strictly facing right +X)
    const headX = 3.6;
    const headY = -0.8;
    ctx.fillStyle = '#94a3b8';
    ctx.beginPath();
    ctx.arc(headX, headY, 1.9, 0, Math.PI * 2);
    ctx.fill();

    // Emerald iridescent sheen on neck
    ctx.fillStyle = '#059669';
    ctx.beginPath();
    ctx.arc(headX - 1.2, headY + 0.6, 1.3, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#fb923c';
    ctx.beginPath();
    ctx.moveTo(headX + 1.4, headY - 0.4);
    ctx.lineTo(headX + 3.2, headY);
    ctx.lineTo(headX + 1.4, headY + 0.6);
    ctx.closePath();
    ctx.fill();

    // Eye looking right (+X)
    ctx.fillStyle = '#0f172a';
    ctx.beginPath();
    ctx.arc(headX + 0.4, headY - 0.4, 0.5, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();

  // Draw Interactive Easter Egg Emote (Speech Bubble & Heart)
  if (p.phase === 'perched' && cooDt >= 0 && cooDt < 2.8) {
    drawPigeonCooEmote(ctx, p.x, p.y + hopY, cooDt);
  }
}

// ---------------------------------------------------------------------------
// FOREGROUND RAILING PIGEON EASTER EGG ("DIE GELÄNDER-TAUBE")
// Independent foreground pigeon that swoops in, lands on the forward perimeter
// safety railing (x = 370..445, yRail = 472.5), idles on the right, waddles to
// the left with authentic pigeon head-bobbing and alternating stepping feet,
// idles on the left, waddles back to the right, and eventually flies away.
// Responds to clicks with an affectionate "Rook-too! 💕" speech bubble & heart.
// ---------------------------------------------------------------------------

const pigeon2State = {
  spawnTime: 18.0,          // First arrival after 18s (or button click / query param)
  flyInDuration: 4.2,       // 0.0 .. 4.2: Swoop in from OUTSIDE left edge (x: -60) onto right railing
  perch1Duration: 4.0,      // 4.2 .. 8.2: Perched on right railing, looks around, pecks
  walkLeftDuration: 4.8,    // 8.2 .. 13.0: Waddle walk to the left (x: 432 -> 388)
  perch2Duration: 4.2,      // 13.0 .. 17.2: Perched on left railing, pecks, looks around
  walkRightDuration: 4.8,   // 17.2 .. 22.0: Waddle walk back to the right (x: 388 -> 432)
  perch3Duration: 3.2,      // 22.0 .. 25.2: Perched on right railing, crouch for takeoff
  flyOutDuration: 3.6,      // 25.2 .. 28.8: Leaps off rail, flaps wings, flies away into distance
  cycleCooldown: 32.0,      // Cooldown until next visit
  lastCooTime: -999,
  railY: 472.5,
  railRightX: 432,
  railLeftX: 388,
  fleeStartTime: -999,      // Storm evacuation timestamp
  fleeOriginX: 432,
  fleeOriginY: 472.5,
  fleeOriginScale: 1.55,
  fleeDuration: 3.4
};

function getNormalPigeon2Flight(dt) {
  const tFlyIn     = pigeon2State.flyInDuration;
  const tPerch1    = tFlyIn + pigeon2State.perch1Duration;
  const tWalkLeft  = tPerch1 + pigeon2State.walkLeftDuration;
  const tPerch2    = tWalkLeft + pigeon2State.perch2Duration;
  const tWalkRight = tPerch2 + pigeon2State.walkRightDuration;
  const tPerch3    = tWalkRight + pigeon2State.perch3Duration;
  const tFlyOut    = tPerch3 + pigeon2State.flyOutDuration;

  if (dt < tFlyIn) {
    // Phase 1: Swoop in from OUTSIDE THE LEFT SCREEN EDGE (x: -60 -> 432), descending arc onto right railing
    const p = dt / tFlyIn;
    const pEase = 1 - Math.pow(1 - p, 1.6);
    const startX = -60;
    const startY = 340;
    const x = startX + (pigeon2State.railRightX - startX) * pEase;
    const y = startY + (pigeon2State.railY - startY) * p - 26 * Math.sin(p * Math.PI);
    const scale = 1.10 + (1.55 - 1.10) * p;
    const isLandingFlare = (p > 0.72);
    const angle = isLandingFlare
      ? (-0.26 * (1.0 - (p - 0.72) / 0.28))
      : (0.16 - 0.10 * p);
    return { phase: 'fly_in', x, y, scale, angle, facing: 1, isLandingFlare, p, dt };
  } else if (dt < tPerch1) {
    // Phase 2: Perched on right end of railing at (432, 472.5), facing right
    const p = (dt - tFlyIn) / pigeon2State.perch1Duration;
    const peckY = (p > 0.45 && p < 0.65) ? Math.sin((p - 0.45) / 0.20 * Math.PI) * 2.8 : 0;
    return { phase: 'perch_right_1', x: pigeon2State.railRightX, y: pigeon2State.railY, scale: 1.55, angle: 0, facing: 1, peckY, p, dt };
  } else if (dt < tWalkLeft) {
    // Phase 3: Waddle walk along railing to the left (432 -> 388), facing left
    const p = (dt - tPerch1) / pigeon2State.walkLeftDuration;
    const x = pigeon2State.railRightX + (pigeon2State.railLeftX - pigeon2State.railRightX) * p;
    const stepPhase = p * pigeon2State.walkLeftDuration * 8.5;
    const waddleY = -Math.abs(Math.sin(stepPhase)) * 1.0;
    const headThrust = Math.pow(Math.max(0, Math.sin(stepPhase)), 2.0) * 2.5;
    const foot1Y = (Math.sin(stepPhase) > 0) ? -Math.sin(stepPhase) * 2.8 : 0;
    const foot2Y = (Math.sin(stepPhase + Math.PI) > 0) ? -Math.sin(stepPhase + Math.PI) * 2.8 : 0;
    return { phase: 'walk_left', x, y: pigeon2State.railY, scale: 1.55, angle: 0, facing: -1, waddleY, headThrust, foot1Y, foot2Y, p, dt };
  } else if (dt < tPerch2) {
    // Phase 4: Perched on left end of railing at (388, 472.5), facing left
    const p = (dt - tWalkLeft) / pigeon2State.perch2Duration;
    const peckY = (p > 0.40 && p < 0.60) ? Math.sin((p - 0.40) / 0.20 * Math.PI) * 2.8 : 0;
    return { phase: 'perch_left', x: pigeon2State.railLeftX, y: pigeon2State.railY, scale: 1.55, angle: 0, facing: -1, peckY, p, dt };
  } else if (dt < tWalkRight) {
    // Phase 5: Waddle walk along railing back to the right (388 -> 432), facing right
    const p = (dt - tPerch2) / pigeon2State.walkRightDuration;
    const x = pigeon2State.railLeftX + (pigeon2State.railRightX - pigeon2State.railLeftX) * p;
    const stepPhase = p * pigeon2State.walkRightDuration * 8.5;
    const waddleY = -Math.abs(Math.sin(stepPhase)) * 1.0;
    const headThrust = Math.pow(Math.max(0, Math.sin(stepPhase)), 2.0) * 2.5;
    const foot1Y = (Math.sin(stepPhase) > 0) ? -Math.sin(stepPhase) * 2.8 : 0;
    const foot2Y = (Math.sin(stepPhase + Math.PI) > 0) ? -Math.sin(stepPhase + Math.PI) * 2.8 : 0;
    return { phase: 'walk_right', x, y: pigeon2State.railY, scale: 1.55, angle: 0, facing: 1, waddleY, headThrust, foot1Y, foot2Y, p, dt };
  } else if (dt < tPerch3) {
    // Phase 6: Perched on right end of railing, crouches & springs ready for takeoff
    const p = (dt - tWalkRight) / pigeon2State.perch3Duration;
    const crouchY = (p > 0.65) ? Math.sin((p - 0.65) / 0.35 * Math.PI * 0.5) * 2.4 : 0;
    return { phase: 'perch_right_2', x: pigeon2State.railRightX, y: pigeon2State.railY, scale: 1.55, angle: 0, facing: 1, crouchY, p, dt };
  } else if (dt < tFlyOut) {
    // Phase 7: Fly away into the distance to the right
    const p = (dt - tPerch3) / pigeon2State.flyOutDuration;
    const x = pigeon2State.railRightX + p * 360 + p * p * 240;
    const y = pigeon2State.railY - p * 170 - p * p * 110;
    const scale = Math.max(0.35, 1.55 - p * 1.15);
    const angle = -0.30;
    return { phase: 'fly_out', x, y, scale, angle, facing: 1, p, dt };
  }

  return null;
}

function getPigeon2Flight(t) {
  const tTotal = pigeon2State.flyInDuration + pigeon2State.perch1Duration + pigeon2State.walkLeftDuration +
                 pigeon2State.perch2Duration + pigeon2State.walkRightDuration + pigeon2State.perch3Duration +
                 pigeon2State.flyOutDuration;
  const cycleTotal = tTotal + pigeon2State.cycleCooldown;

  const storm = isStormActiveOrApproaching(t);

  // Storm Reaction: "spätestens wenn gewitter aufkommt fliegen tauben weg"
  if (storm) {
    if (pigeon2State.fleeStartTime > -900) {
      const fleeDt = t - pigeon2State.fleeStartTime;
      if (fleeDt >= 0 && fleeDt < pigeon2State.fleeDuration) {
        const p = fleeDt / pigeon2State.fleeDuration;
        const x = pigeon2State.fleeOriginX + p * 420 + p * p * 280;
        const y = pigeon2State.fleeOriginY - p * 160 - p * p * 90;
        const scale = Math.max(0.35, pigeon2State.fleeOriginScale * (1.0 - 0.70 * p));
        const angle = -0.34;
        return { phase: 'flee', x, y, scale, angle, facing: 1, p, dt: fleeDt };
      }
      pigeon2State.spawnTime = Math.max(pigeon2State.spawnTime, t + 30.0);
      return null;
    }

    if (t >= pigeon2State.spawnTime) {
      const dt = (t - pigeon2State.spawnTime) % cycleTotal;
      if (dt < tTotal) {
        const cur = getNormalPigeon2Flight(dt);
        if (cur) {
          pigeon2State.fleeStartTime = t;
          pigeon2State.fleeOriginX = cur.x;
          pigeon2State.fleeOriginY = cur.y;
          pigeon2State.fleeOriginScale = cur.scale;
          return { phase: 'flee', x: cur.x, y: cur.y, scale: cur.scale, angle: -0.34, facing: 1, p: 0, dt: 0 };
        }
      }
    }

    pigeon2State.spawnTime = Math.max(pigeon2State.spawnTime, t + 30.0);
    return null;
  }

  if (pigeon2State.fleeStartTime > -900) {
    pigeon2State.fleeStartTime = -999;
  }

  if (t < pigeon2State.spawnTime) return null;
  const dt = (t - pigeon2State.spawnTime) % cycleTotal;
  return getNormalPigeon2Flight(dt);
}

function drawPigeon2CooEmote(ctx, x, y, dtCoo, facing) {
  if (dtCoo < 0 || dtCoo > 2.8) return;

  const p = dtCoo / 2.8;
  const floatY = y - 20 - p * 14;
  const alpha = p > 0.8 ? (1.0 - (p - 0.8) / 0.2) : Math.min(1.0, dtCoo * 6.0);

  ctx.save();
  ctx.globalAlpha = alpha;

  const bw = 48;
  const bh = 18;
  const bx = x + facing * 12;
  const by = floatY - bh;

  // Drop shadow
  ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
  ctx.beginPath();
  if (ctx.roundRect) ctx.roundRect(bx - bw * 0.5 + 1.5, by + 1.5, bw, bh, 5);
  else ctx.rect(bx - bw * 0.5 + 1.5, by + 1.5, bw, bh);
  ctx.fill();

  // White bubble with crisp border
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = '#0f172a';
  ctx.lineWidth = 1.3;
  ctx.beginPath();
  if (ctx.roundRect) ctx.roundRect(bx - bw * 0.5, by, bw, bh, 5);
  else ctx.rect(bx - bw * 0.5, by, bw, bh);
  ctx.fill();
  ctx.stroke();

  // Pointer tail pointing to pigeon beak
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = '#0f172a';
  ctx.lineWidth = 1.3;
  ctx.beginPath();
  ctx.moveTo(bx - facing * 6, by + bh - 0.5);
  ctx.lineTo(bx - facing * 12, by + bh + 5.0);
  ctx.lineTo(bx - facing * 16, by + bh - 0.5);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Speech Text (Clean comic lettering without emojis)
  ctx.font = 'bold 9px sans-serif';
  ctx.fillStyle = '#0f172a';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('Rook-too!', bx, by + bh * 0.5 + 0.5);

  // Subtle acoustic sparkles
  const chirpFloat = by - 7 - p * 12;
  const sp1 = 0.5 + 0.5 * Math.sin(dtCoo * 10.0);
  ctx.fillStyle = `rgba(254, 240, 138, ${sp1 * 0.85})`;
  ctx.beginPath();
  ctx.arc(bx + facing * 18, chirpFloat - 3, 1.3, 0, Math.PI * 2);
  ctx.arc(bx - facing * 8, chirpFloat + 2, 1.1, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

function drawForegroundPigeon(ctx, t) {
  const p = getPigeon2Flight(t);
  if (!p) return;

  ctx.save();
  ctx.translate(p.x, p.y + (p.waddleY || 0) + (p.crouchY || 0));
  ctx.scale(p.facing * p.scale, p.scale);

  // If clicked recently, pigeon does a joyful little flutter hop
  let hopY = 0;
  const cooDt = t - pigeon2State.lastCooTime;
  if ((p.phase.startsWith('perch') || p.phase.startsWith('walk')) && cooDt >= 0 && cooDt < 0.6) {
    hopY = -Math.sin((cooDt / 0.6) * Math.PI) * 3.5;
  }
  ctx.translate(0, hopY);

  if (p.phase.startsWith('perch') || p.phase.startsWith('walk')) {
    // -------------------------------------------------------------
    // PERCHED & WALKING PIGEON ON THE HANDRAIL (yRail = 472.5)
    // -------------------------------------------------------------
    // 1. Dual pink feet clasping / stepping on the tubular handrail
    const foot1Y = p.foot1Y || 0;
    const foot2Y = p.foot2Y || 0;

    // Rear / Far foot
    ctx.fillStyle = '#fb7185';
    ctx.fillRect(-2.8, -1.0 + foot1Y, 2.4, 2.0);
    ctx.beginPath();
    ctx.arc(-1.6, 1.0 + foot1Y, 1.2, 0, Math.PI);
    ctx.fill();

    // Front / Near foot
    ctx.fillRect(1.0, -1.0 + foot2Y, 2.4, 2.0);
    ctx.beginPath();
    ctx.arc(2.2, 1.0 + foot2Y, 1.2, 0, Math.PI);
    ctx.fill();

    // 2. Plump pigeon body
    const bodyGrad = ctx.createLinearGradient(-4, -10, 3, 0);
    bodyGrad.addColorStop(0.0, '#94a3b8');
    bodyGrad.addColorStop(0.5, '#64748b');
    bodyGrad.addColorStop(1.0, '#334155');
    ctx.fillStyle = bodyGrad;
    ctx.beginPath();
    ctx.ellipse(-1.2, -5.6, 5.2, 4.0, -0.22, 0, Math.PI * 2);
    ctx.fill();

    // Soft iridescent chest sheen (magenta-lavender)
    ctx.fillStyle = 'rgba(192, 132, 252, 0.45)';
    ctx.beginPath();
    ctx.ellipse(1.0, -5.2, 2.8, 2.6, -0.15, 0, Math.PI * 2);
    ctx.fill();

    // 3. Folded Wing with Dual Black Wingbars
    const wingGrad = ctx.createLinearGradient(-4, -8, 2, -2);
    wingGrad.addColorStop(0.0, '#475569');
    wingGrad.addColorStop(1.0, '#1e293b');
    ctx.fillStyle = wingGrad;
    ctx.beginPath();
    ctx.ellipse(-1.8, -5.8, 4.6, 2.8, -0.32, 0, Math.PI * 2);
    ctx.fill();

    // Dual black wingbars
    ctx.strokeStyle = '#020617';
    ctx.lineWidth = 1.1;
    ctx.beginPath();
    ctx.moveTo(-3.8, -4.8); ctx.lineTo(-1.2, -7.2);
    ctx.moveTo(-2.2, -4.0); ctx.lineTo(0.4, -6.4);
    ctx.stroke();

    // 4. Tail Feathers
    const tailTwitch = ((t % 3.8) < 0.25) ? Math.sin(t * 24) * 0.18 : 0;
    ctx.save();
    ctx.translate(-5.5, -4.8);
    ctx.rotate(tailTwitch);
    ctx.fillStyle = '#334155';
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(-5.2, -2.2);
    ctx.lineTo(-4.6, -3.8);
    ctx.closePath();
    ctx.fill();
    // Black tail tip
    ctx.fillStyle = '#0f172a';
    ctx.beginPath();
    ctx.moveTo(-3.6, -1.6);
    ctx.lineTo(-5.2, -2.2);
    ctx.lineTo(-4.6, -3.8);
    ctx.lineTo(-3.0, -2.6);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // 5. Neck & Head with Head-Bobbing & Pecking
    let headBob = p.headThrust || 0;
    if (p.phase.startsWith('perch')) {
      const bobCycle = (t * 2.0) % (Math.PI * 2);
      headBob = Math.pow(Math.max(0, Math.sin(bobCycle)), 6.0) * 1.8;
    }
    const peckY = p.peckY || 0;
    const hx = 3.0 + headBob;
    const hy = -9.2 + peckY;

    // Iridescent emerald-green neck collar
    ctx.fillStyle = '#059669';
    ctx.beginPath();
    ctx.arc(hx - 1.4, hy + 1.8, 2.2, 0, Math.PI * 2);
    ctx.fill();

    // Rounded head
    ctx.fillStyle = '#94a3b8';
    ctx.beginPath();
    ctx.arc(hx, hy, 2.7, 0, Math.PI * 2);
    ctx.fill();

    // Peach/Orange Beak with ivory cere patch
    ctx.fillStyle = '#fb923c';
    ctx.beginPath();
    ctx.moveTo(hx + 2.0, hy - 0.6);
    ctx.lineTo(hx + 4.6, hy + 0.3);
    ctx.lineTo(hx + 1.8, hy + 1.2);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#f8fafc'; // Ivory cere
    ctx.fillRect(hx + 1.4, hy - 0.8, 1.4, 0.9);

    // Amber Eye with Black Pupil & Blink
    const isBlinking = ((t % 3.4) < 0.13);
    if (!isBlinking) {
      ctx.fillStyle = '#f59e0b';
      ctx.beginPath();
      ctx.arc(hx + 0.7, hy - 0.7, 1.1, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#020617';
      ctx.beginPath();
      ctx.arc(hx + 0.8, hy - 0.7, 0.55, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.strokeStyle = '#475569';
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(hx + 0.2, hy - 0.7);
      ctx.lineTo(hx + 1.3, hy - 0.7);
      ctx.stroke();
    }

  } else {
    // -------------------------------------------------------------
    // FLYING PIGEON (Fly In, Fly Out & Storm Flee)
    // -------------------------------------------------------------
    ctx.rotate(p.angle || 0);

    const isFlare = (p.isLandingFlare === true);
    const isFlee = (p.phase === 'flee');
    const wingAngle = isFlare
      ? (0.80 + Math.sin(t * 20.0) * 0.18)
      : (isFlee ? (Math.sin(t * 54.0) * 0.95) : (Math.sin(t * 46.0) * 0.85));

    // Far Wing
    ctx.save();
    ctx.translate(-0.5, -3.0);
    ctx.rotate(-wingAngle);
    ctx.fillStyle = '#334155';
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(2.4, -8.2);
    ctx.lineTo(-4.8, -7.0);
    ctx.lineTo(-3.0, 0);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // Streamlined Torso
    ctx.fillStyle = '#64748b';
    ctx.beginPath();
    ctx.ellipse(0, 0, 5.4, 3.0, 0, 0, Math.PI * 2);
    ctx.fill();

    // White Rump Patch
    ctx.fillStyle = '#f1f5f9';
    ctx.beginPath();
    ctx.ellipse(-3.2, -0.7, 1.6, 1.4, 0, 0, Math.PI * 2);
    ctx.fill();

    // Wedge Tail
    ctx.fillStyle = '#1e293b';
    ctx.beginPath();
    ctx.moveTo(-4.0, -1.0);
    ctx.lineTo(-8.8, isFlare ? -3.4 : -2.0);
    ctx.lineTo(-8.8, isFlare ? 3.4 : 2.0);
    ctx.lineTo(-4.0, 1.0);
    ctx.closePath();
    ctx.fill();

    // Near Wing
    ctx.save();
    ctx.translate(-0.5, 0.6);
    ctx.rotate(wingAngle);
    ctx.fillStyle = '#475569';
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(3.0, 8.8);
    ctx.lineTo(-5.2, 7.6);
    ctx.lineTo(-3.0, 0);
    ctx.closePath();
    ctx.fill();

    // Black wingbar stripes on near wing
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 1.0;
    ctx.beginPath();
    ctx.moveTo(-1.8, 2.4); ctx.lineTo(-0.6, 7.0);
    ctx.moveTo(-3.2, 1.8); ctx.lineTo(-2.0, 6.0);
    ctx.stroke();
    ctx.restore();

    // Extended landing legs during flare
    if (isFlare) {
      ctx.fillStyle = '#fb7185';
      ctx.fillRect(0.6, 2.4, 1.8, 4.0);
      ctx.fillRect(2.6, 2.4, 1.8, 4.0);
    }

    // Head & Beak in flight
    const headX = 4.2;
    const headY = -1.0;
    ctx.fillStyle = '#94a3b8';
    ctx.beginPath();
    ctx.arc(headX, headY, 2.2, 0, Math.PI * 2);
    ctx.fill();

    // Emerald iridescent sheen on neck
    ctx.fillStyle = '#059669';
    ctx.beginPath();
    ctx.arc(headX - 1.4, headY + 0.8, 1.5, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#fb923c';
    ctx.beginPath();
    ctx.moveTo(headX + 1.6, headY - 0.5);
    ctx.lineTo(headX + 3.8, headY);
    ctx.lineTo(headX + 1.6, headY + 0.7);
    ctx.closePath();
    ctx.fill();

    // Eye
    ctx.fillStyle = '#0f172a';
    ctx.beginPath();
    ctx.arc(headX + 0.5, headY - 0.5, 0.6, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();

  // Emote for Pigeon 2
  if ((p.phase.startsWith('perch') || p.phase.startsWith('walk')) && cooDt >= 0 && cooDt < 2.8) {
    drawPigeon2CooEmote(ctx, p.x, p.y + hopY, cooDt, p.facing);
  }
}

// ---------------------------------------------------------------------------
// 3D RAISED UPPER HULL SUPERSTRUCTURE WITH SUNKEN CUTOUT WELLS
// With Lit Top Ledges, Perspective Inner Walls, Downward Drop Shadows
// ---------------------------------------------------------------------------

const UPPER_HULL_CUTOUTS = Object.freeze([
  { startX: 230,  endX: 370 },
  { startX: 630,  endX: 750 },
  { startX: 850,  endX: 970 },
  { startX: 1230, endX: 1370 }
]);

const UPPER_RAILING_SEGMENTS = Object.freeze([
  { x1: 230,  x2: 306, termEnd: true },
  { x1: 684,  x2: 750, termStart: true },
  { x1: 850,  x2: 916, termEnd: true },
  { x1: 1230, x2: 1370 }
]);

let _rimDropShadow = null;
let _shelfGrad = null;
let _bulkheadGrad = null;

function ensureUpperHullGradients(ctx) {
  if (!_rimDropShadow) {
    _rimDropShadow = ctx.createLinearGradient(0, 222, 0, 242);
    _rimDropShadow.addColorStop(0.0, 'rgba(0, 0, 0, 0.75)');
    _rimDropShadow.addColorStop(0.6, 'rgba(0, 0, 0, 0.35)');
    _rimDropShadow.addColorStop(1.0, 'rgba(0, 0, 0, 0)');

    _shelfGrad = ctx.createLinearGradient(0, 222, 0, 226);
    _shelfGrad.addColorStop(0.0, '#1a1f2b');
    _shelfGrad.addColorStop(1.0, '#2d3340');

    _bulkheadGrad = ctx.createLinearGradient(0, 204, 0, 226);
    _bulkheadGrad.addColorStop(0.0, '#515867'); // Mid-grey at top
    _bulkheadGrad.addColorStop(0.5, '#3b4250');
    _bulkheadGrad.addColorStop(1.0, '#242934'); // Dark shadow foundation
  }
}

let _staticUpperHullCanvas = null;
let _staticUpperHullKey = null;

function invalidateStaticUpperHull() {
  _staticUpperHullCanvas = null;
  _staticUpperHullKey = null;
}

function getStaticUpperHullCanvas(visibleLeft = 0, visibleRight = WIDTH) {
  const currentKey = `${state.quality}_${state.seed}_${visibleLeft}_${visibleRight}`;
  if (_staticUpperHullCanvas && _staticUpperHullKey === currentKey) {
    return _staticUpperHullCanvas;
  }
  if (typeof document === 'undefined') return null;

  const bgW = Math.max(WIDTH, visibleRight - visibleLeft);
  _staticUpperHullCanvas = document.createElement('canvas');
  _staticUpperHullCanvas.width = bgW;
  _staticUpperHullCanvas.height = 300;
  const bctx = _staticUpperHullCanvas.getContext('2d');
  _staticUpperHullKey = currentKey;

  bctx.save();
  bctx.translate(-visibleLeft, 0);
  bakeStaticUpperHull(bctx, visibleLeft, visibleRight);
  bctx.restore();

  return _staticUpperHullCanvas;
}

function bakeStaticUpperHull(ctx, visibleLeft = 0, visibleRight = WIDTH) {
  ctx.save();
  ensureUpperHullGradients(ctx);

  const cutouts = UPPER_HULL_CUTOUTS;

  // 1. Soft Ambient Occlusion Downward Drop Shadow onto the Arena Deck
  ctx.fillStyle = _rimDropShadow;
  ctx.fillRect(visibleLeft, 222, visibleRight - visibleLeft, 20);

  // 2. 3D Inner Perspective Sidewalls in the Sunken Cutout Wells
  for (let c = 0; c < cutouts.length; c++) {
    const cut = cutouts[c];
    const isLeft = cut.startX < 800;

    // Inner Cutout Well Floor (Recessed Walkway/Shelf at y=222..226)
    ctx.fillStyle = _shelfGrad;
    ctx.fillRect(cut.startX, 222, cut.endX - cut.startX, 4);

    if (isLeft) {
      ctx.fillStyle = '#10141d';
      ctx.beginPath();
      ctx.moveTo(cut.startX - 15, 204);
      ctx.lineTo(cut.startX, 222);
      ctx.lineTo(cut.startX + 6, 222);
      ctx.lineTo(cut.startX - 9, 204);
      ctx.closePath();
      ctx.fill();
    } else {
      ctx.fillStyle = '#10141d';
      ctx.beginPath();
      ctx.moveTo(cut.endX + 15, 204);
      ctx.lineTo(cut.endX, 222);
      ctx.lineTo(cut.endX - 6, 222);
      ctx.lineTo(cut.endX + 9, 204);
      ctx.closePath();
      ctx.fill();
    }
  }

  // 3. Main 3D Superstructure Bulkhead Face (Vertical Front Face y = 204..226)
  ctx.fillStyle = _bulkheadGrad;
  ctx.beginPath();
  ctx.moveTo(visibleLeft, 204);
  ctx.lineTo(0, 204);
  for (let c = 0; c < cutouts.length; c++) {
    const cut = cutouts[c];
    ctx.lineTo(cut.startX - 15, 204);
    ctx.lineTo(cut.startX, 222);
    ctx.lineTo(cut.endX, 222);
    ctx.lineTo(cut.endX + 15, 204);
  }
  ctx.lineTo(WIDTH, 204);
  ctx.lineTo(visibleRight, 204);
  ctx.lineTo(visibleRight, 226);
  ctx.lineTo(visibleLeft, 226);
  ctx.closePath();
  ctx.fill();

  // 4. Vertical Modular Armor Expansion Seams on the bulkhead
  ctx.strokeStyle = 'rgba(2, 6, 23, 0.75)';
  ctx.lineWidth = 1.4;
  const startSx = Math.floor(visibleLeft / 80) * 80 + 60;
  for (let sx = startSx; sx < visibleRight; sx += 80) {
    let inCut = false;
    for (let c = 0; c < cutouts.length; c++) {
      if (sx >= cutouts[c].startX - 10 && sx <= cutouts[c].endX + 10) {
        inCut = true;
        break;
      }
    }
    if (inCut) continue;
    ctx.beginPath();
    ctx.moveTo(sx, 204);
    ctx.lineTo(sx, 226);
    ctx.stroke();
  }

  // 5. Brilliant 3D Top Rim Glint (Catching the Sunset Fire)
  ctx.strokeStyle = SW.hullWhite;
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  if (visibleLeft < 0) {
    ctx.moveTo(visibleLeft, 204);
    ctx.lineTo(0, 204);
  }
  for (let c = 0; c < cutouts.length; c++) {
    const cut = cutouts[c];
    const prevX = c === 0 ? 0 : cutouts[c - 1].endX + 15;
    ctx.moveTo(prevX, 204);
    ctx.lineTo(cut.startX - 15, 204);
    ctx.moveTo(cut.startX, 222);
    ctx.lineTo(cut.endX, 222);
  }
  ctx.moveTo(cutouts[cutouts.length - 1].endX + 15, 204);
  ctx.lineTo(WIDTH, 204);
  if (visibleRight > WIDTH) {
    ctx.lineTo(visibleRight, 204);
  }
  ctx.stroke();

  // Secondary Warm Alpenglow Glint on Top Crest
  ctx.strokeStyle = 'rgba(254, 215, 170, 0.75)';
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  if (visibleLeft < 0) {
    ctx.moveTo(visibleLeft, 203);
    ctx.lineTo(0, 203);
  }
  for (let c = 0; c < cutouts.length; c++) {
    const cut = cutouts[c];
    const prevX = c === 0 ? 0 : cutouts[c - 1].endX + 15;
    ctx.moveTo(prevX, 203);
    ctx.lineTo(cut.startX - 15, 203);
  }
  ctx.moveTo(cutouts[cutouts.length - 1].endX + 15, 203);
  ctx.lineTo(WIDTH, 203);
  if (visibleRight > WIDTH) {
    ctx.lineTo(visibleRight, 203);
  }
  ctx.stroke();

  // 6. Amber Safety Railings Spanning the Cutouts
  const railingSegments = UPPER_RAILING_SEGMENTS;
  for (let s = 0; s < railingSegments.length; s++) {
    const seg = railingSegments[s];
    ctx.strokeStyle = 'rgba(245, 158, 11, 0.75)';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(seg.x1, 218);
    ctx.lineTo(seg.x2, 218);
    ctx.stroke();

    ctx.beginPath();
    for (let rx = seg.x1; rx <= seg.x2; rx += 16) {
      ctx.moveTo(rx, 218);
      ctx.lineTo(rx, 222);
    }
    ctx.moveTo(seg.x2, 218);
    ctx.lineTo(seg.x2, 222);
    ctx.stroke();

    if (seg.termEnd) {
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(seg.x2 - 1.5, 216, 4, 7);
      ctx.fillStyle = '#f59e0b';
      ctx.fillRect(seg.x2 - 0.5, 217, 2, 4);
    }
    if (seg.termStart) {
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(seg.x1 - 2.5, 216, 4, 7);
      ctx.fillStyle = '#f59e0b';
      ctx.fillRect(seg.x1 - 1.5, 217, 2, 4);
    }
  }

  // Heavy 3D Conduit Pipe Racks traversing the bulkhead
  if (visibleLeft < 0) {
    drawConduitRack(ctx, visibleLeft, 212, 0, 212, SW.pipeBronze, 3);
  }
  drawConduitRack(ctx, 0, 212, 215, 212, SW.pipeBronze, 3);
  drawConduitRack(ctx, 385, 212, 615, 212, SW.pipeSteel, 3);
  drawConduitRack(ctx, 985, 212, 1170, 212, SW.pipeBronze, 3);
  drawConduitRack(ctx, 1385, 212, Math.max(WIDTH, visibleRight), 212, SW.pipeSteel, 3);

  // Pre-baked stationary 2.5D Barbette Pedestals for Hero Radar Dish and Turrets
  drawHeroRadarPedestal(ctx, 350, 204, 34);

  ctx.save();
  ctx.translate(640, 208);
  drawClean25DTurretPedestal(ctx, true);
  ctx.restore();

  ctx.save();
  ctx.translate(960, 208);
  drawClean25DTurretPedestal(ctx, false);
  ctx.restore();

  ctx.restore();
}

function drawUpperHullRim(ctx, t, visibleLeft = 0, visibleRight = WIDTH) {
  ctx.save();

  // Fast GPU Blit of pre-baked static upper hull rim
  const staticUpper = getStaticUpperHullCanvas(visibleLeft, visibleRight);
  if (staticUpper) {
    ctx.drawImage(staticUpper, visibleLeft, 0);
  } else {
    bakeStaticUpperHull(ctx, visibleLeft, visibleRight);
  }

  // Aerodynamic Hull Edge Flow & Shockwave Vapor ("Die Luft spüren" rushing over bulkhead)
  drawAerodynamicSlipstream(ctx, t);

  // Hero Gadgets mounted on the 3D Superstructure
  // If staticUpper exists, the pedestals were already rendered into the static pass!
  const needPedestal = !staticUpper;
  drawHeroRadarDish(ctx, 350, 204, 34, t, needPedestal);
  drawClean25DTurret(ctx, 640, 208, 0.12, t, 'T-01', needPedestal);
  drawClean25DTurret(ctx, 960, 208, -0.12, t, 'T-02', needPedestal);
  drawAntennaForest(ctx, t);

  ctx.restore();
}

function drawConduitRack(ctx, x1, y1, x2, y2, color, r) {
  ctx.save();
  // Deep drop shadow onto the hull
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.70)';
  ctx.lineWidth = r * 2 + 2;
  ctx.beginPath();
  ctx.moveTo(x1, y1 + 3);
  ctx.lineTo(x2, y2 + 3);
  ctx.stroke();

  // Cylindrical pipe body
  ctx.strokeStyle = color;
  ctx.lineWidth = r * 2;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();

  // Specular top highlight
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)';
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  ctx.moveTo(x1, y1 - r * 0.45);
  ctx.lineTo(x2, y2 - r * 0.45);
  ctx.stroke();

  // Dark shadow underside
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.6)';
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  ctx.moveTo(x1, y1 + r * 0.5);
  ctx.lineTo(x2, y2 + r * 0.5);
  ctx.stroke();

  // Heavy Mounting Clamp Brackets every 45px
  ctx.fillStyle = '#222733';
  ctx.strokeStyle = '#94a3b8';
  ctx.lineWidth = 1.0;
  for (let bx = x1 + 20; bx < x2 - 10; bx += 45) {
    ctx.fillRect(bx - 2, y1 - r - 2, 4, r * 2 + 4);
    ctx.strokeRect(bx - 2, y1 - r - 2, 4, r * 2 + 4);
  }

  ctx.restore();
}

// ---------------------------------------------------------------------------
// 3D FANNED PERSPECTIVE ARENA DECK (STANDARDIZED WITH ENGINE BACKGROUND.JS)
// Zero Base Pads: Battlefield Deck flows continuously across entire width
// Articulated Asymmetric 3D Perimeter: Completely destroys the flat horizontal edge
// ---------------------------------------------------------------------------

const PERIMETER_SEGMENTS = [
  // Port outer wing walkway
  { x0: 0,    y0: 488, x1: 70,   y1: 488, hasRailing: true },
  // West Service Berth - chamfered inflow ramp
  { x0: 70,   y0: 488, x1: 85,   y1: 480, isChamfer: true },
  // West Service Berth edge (normal deck floor behind it)
  { x0: 85,   y0: 480, x1: 195,  y1: 480, isBay: true, hasRailing: false, hasCables: true, hasSparks: true, bayId: 'west' },
  // West Service Berth - chamfered outflow ramp
  { x0: 195,  y0: 480, x1: 210,  y1: 490, isChamfer: true },
  // Port forward catwalk
  { x0: 210,  y0: 490, x1: 255,  y1: 490, hasRailing: true, hasRaceway: true },
  // West Heavy Armor Sponson - 45° forward flare
  { x0: 255,  y0: 490, x1: 280,  y1: 524, isSponsonFlare: true, sponsonSide: 'west' },
  // West Heavy Armor Sponson - armored front face (protrudes 34px forward!)
  { x0: 280,  y0: 524, x1: 345,  y1: 524, isSponson: true, hasRailing: false, sponsonSide: 'west' },
  // West Heavy Armor Sponson - 45° return angle
  { x0: 345,  y0: 524, x1: 370,  y1: 488, isSponsonReturn: true, sponsonSide: 'west' },
  // Port mid walkway
  { x0: 370,  y0: 488, x1: 445,  y1: 488, hasRailing: true, hasRaceway: true },
  // West open drop-off - chamfer inset
  { x0: 445,  y0: 488, x1: 465,  y1: 478, isChamfer: true },
  // West open drop-off platform ("gar kein Geländer")
  { x0: 465,  y0: 478, x1: 535,  y1: 478, isOpenDrop: true, hasRailing: false, hasCables: true },
  // Balcony approach ramp
  { x0: 535,  y0: 478, x1: 560,  y1: 496, isChamfer: true },
  // Balcony approach walkway
  { x0: 560,  y0: 496, x1: 585,  y1: 496, hasRailing: true },
  // Main Observation Balcony - cantilever flare
  { x0: 585,  y0: 496, x1: 610,  y1: 530, isBalconyFlare: true, balconyId: 'main' },
  // Main Observation Balcony - protruding front walkway (grating with abyss underglow!)
  { x0: 610,  y0: 530, x1: 690,  y1: 530, isBalcony: true, hasRailing: true, balconyId: 'main' },
  // Main Observation Balcony - cantilever return
  { x0: 690,  y0: 530, x1: 715,  y1: 496, isBalconyReturn: true, balconyId: 'main' },
  // Keel approach catwalk
  { x0: 715,  y0: 496, x1: 745,  y1: 496, hasRailing: true, hasRaceway: true },
  // Center Dreadnought Prow Keel - forward ram wedge (West facet lit by alpenglow!)
  { x0: 745,  y0: 496, x1: 800,  y1: 544, isKeel: true, hasRailing: false, keelFace: 'west' },
  // Center Dreadnought Prow Keel - forward ram wedge (East facet in deep shadow!)
  { x0: 800,  y0: 544, x1: 855,  y1: 496, isKeel: true, hasRailing: false, keelFace: 'east' },
  // Keel exit catwalk
  { x0: 855,  y0: 496, x1: 885,  y1: 496, hasRailing: true, hasRaceway: true },
  // Maintenance Platform - cantilever flare
  { x0: 885,  y0: 496, x1: 910,  y1: 512, isMaintFlare: true },
  // Maintenance Platform - working front face (open edge with sparks!)
  { x0: 910,  y0: 512, x1: 980,  y1: 512, isMaintPlatform: true, hasRailing: false, hasSparks: true },
  // Maintenance Platform - return ramp
  { x0: 980,  y0: 512, x1: 1000, y1: 484, isMaintReturn: true },
  // East open drop-off platform ("gar kein Geländer")
  { x0: 1000, y0: 484, x1: 1065, y1: 484, isOpenDrop: true, hasRailing: false, hasCables: true },
  // East Fueling Berth - chamfered inflow ramp
  { x0: 1065, y0: 484, x1: 1085, y1: 480, isChamfer: true },
  // East Reactor Fueling Berth edge (normal deck floor behind it)
  { x0: 1085, y0: 480, x1: 1195, y1: 480, isBay: true, hasRailing: false, bayId: 'east' },
  // East Fueling Berth - chamfered outflow ramp
  { x0: 1195, y0: 480, x1: 1220, y1: 492, isChamfer: true },
  // Approach to East Sponson
  { x0: 1220, y0: 492, x1: 1245, y1: 492, hasRailing: true },
  // East Sensor Sponson - forward flare
  { x0: 1245, y0: 492, x1: 1270, y1: 522, isSponsonFlare: true, sponsonSide: 'east' },
  // East Sensor Sponson - armored front face (protrudes 30px forward!)
  { x0: 1270, y0: 522, x1: 1335, y1: 522, isSponson: true, hasRailing: false, sponsonSide: 'east' },
  // East Sensor Sponson - return angle
  { x0: 1335, y0: 522, x1: 1360, y1: 488, isSponsonReturn: true, sponsonSide: 'east' },
  // Starboard catwalk corridor
  { x0: 1360, y0: 488, x1: 1420, y1: 488, hasRailing: true, hasRaceway: true },
  // Starboard Lookout Balcony - cantilever flare
  { x0: 1420, y0: 488, x1: 1445, y1: 518, isBalconyFlare: true, balconyId: 'starboard' },
  // Starboard Lookout Balcony - front rim (grating with abyss underglow!)
  { x0: 1445, y0: 518, x1: 1530, y1: 518, isBalcony: true, hasRailing: true, hasCables: true, balconyId: 'starboard' },
  // Starboard Lookout Balcony - return angle
  { x0: 1530, y0: 518, x1: 1555, y1: 490, isBalconyReturn: true, balconyId: 'starboard' },
  // Starboard outer wing walkway
  { x0: 1555, y0: 490, x1: 1600, y1: 490, hasRailing: true }
];

const VENTRAL_BOTTOM = [
  { x0: 0,    y0: 536, x1: 220,  y1: 536 },
  { x0: 220,  y0: 536, x1: 230,  y1: 528 }, // West thruster recess notch
  { x0: 230,  y0: 528, x1: 250,  y1: 528 }, // recess roof
  { x0: 250,  y0: 528, x1: 260,  y1: 542 }, // drop to West Sponson keel
  { x0: 260,  y0: 542, x1: 365,  y1: 542 }, // West Sponson keel face
  { x0: 365,  y0: 542, x1: 380,  y1: 536 }, // rise to mid keel
  { x0: 380,  y0: 536, x1: 580,  y1: 536 },
  { x0: 580,  y0: 536, x1: 595,  y1: 532 }, // Balcony truss clearance
  { x0: 595,  y0: 532, x1: 705,  y1: 532 },
  { x0: 705,  y0: 532, x1: 725,  y1: 536 },
  { x0: 725,  y0: 536, x1: 770,  y1: 548 }, // Center Prow Keel dip (deep dreadnought spine)
  { x0: 770,  y0: 548, x1: 830,  y1: 548 }, // Center Prow Keel lowest point
  { x0: 830,  y0: 548, x1: 875,  y1: 536 }, // Center Prow Keel rise
  { x0: 875,  y0: 536, x1: 1075, y1: 536 },
  { x0: 1075, y0: 536, x1: 1085, y1: 528 }, // East thruster recess notch
  { x0: 1085, y0: 528, x1: 1105, y1: 528 }, // recess roof
  { x0: 1105, y0: 528, x1: 1120, y1: 542 }, // drop to East Sponson keel
  { x0: 1120, y0: 542, x1: 1225, y1: 542 }, // East Sponson keel face
  { x0: 1225, y0: 542, x1: 1240, y1: 536 }, // rise to starboard keel
  { x0: 1240, y0: 536, x1: 1420, y1: 536 },
  { x0: 1420, y0: 536, x1: 1435, y1: 532 }, // Starboard balcony truss clearance
  { x0: 1435, y0: 532, x1: 1540, y1: 532 },
  { x0: 1540, y0: 532, x1: 1555, y1: 536 },
  { x0: 1555, y0: 536, x1: 1600, y1: 536 }
];

// ---------------------------------------------------------------------------
// DECK ARMOR CREVICE GLOW & CONDUIT NETWORK (Sci-Fi Expansion Joints)
// Matching SW.cyanPower (#38bdf8) and SW.amberGlow (#f59e0b)
// ---------------------------------------------------------------------------

// Perspective projection formula matching js/engine/render/background.js lines 1101 & 1135:
// Vanishing horizon at y = 208, perspective factor = 0.34, center = 800
function getDeckFloorX(col, py) {
  const depthT = (py - 208) / 288;
  return col + (col - 800) * 0.34 * depthT;
}

// Compute ambient darkness & halation intensity:
// "je dunkler die umgebung (cirrus gewitter), desto mehr müssen die streifen
// auf dem boden ein leichtes gausschess leuchten (wie halation) erhalten"
function getDeckHalationFactor(t) {
  let dark = weatherState.stormDarkness;

  if (weatherState.mode === 'cirrus') {
    dark = Math.max(dark, 0.35);
  } else if (weatherState.mode === 'auto') {
    const cTime = ((t % weatherState.cycleDuration) + weatherState.cycleDuration) % weatherState.cycleDuration;
    if (cTime >= 45.0 && cTime < 70.0) {
      // Phase 2: Cirrus gathering & stratocumulus thickening
      const p = (cTime - 45.0) / 25.0;
      dark = Math.max(dark, 0.10 + 0.25 * p);
    }
  }

  // Cloud cover contribution (dense cirrus or storm clouds occluding sunlight)
  if (weatherState.cloudCover > 0.20) {
    const cloudDark = Math.min(0.40, ((weatherState.cloudCover - 0.20) / 0.80) * 0.45);
    dark = Math.max(dark, cloudDark);
  }

  // Intra-cloud sheet lightning flash excitation (Wetterleuchten surge)
  if (weatherState.lightningIntensity > 0.01) {
    dark = Math.min(1.0, dark + weatherState.lightningIntensity * 0.25);
  }

  return Math.max(0.0, Math.min(1.0, dark));
}

const DECK_CREVICE_H_SEAMS = [
  // Row 7 (y = 432) — Major power bus trunks (includes user-highlighted lower deck plate)
  { r: 7, x0: 698.8, x1: 926.4, type: 'amber', speed: 2.8, phase: 0.6 }, // Traces top seam of user plate and continues east
  { r: 7, x0: 180,   x1: 450,   type: 'amber', speed: 2.7, phase: 0.5 }, // West power bus loop
  { r: 7, x0: 1150,  x1: 1420,  type: 'amber', speed: 2.7, phase: 2.2 }, // East power bus loop

  // Row 8 (y = 464) — Lower perimeter armor seams & telemetry shielding
  { r: 8, x0: 695.8, x1: 813.0, type: 'amber', speed: 2.8, phase: 1.2 }, // Traces bottom seam of user plate
  { r: 8, x0: 240,   x1: 450,   type: 'cyan',  speed: 3.0, phase: 2.8 }, // West perimeter data line
  { r: 8, x0: 1150,  x1: 1380,  type: 'cyan',  speed: 3.0, phase: 1.5 }, // East perimeter data line

  // Row 5 (y = 368) — Ventral mid-deck conduits below catapult trench
  { r: 5, x0: 180,   x1: 450,   type: 'amber', speed: 2.7, phase: 0.3 }, // West power bus feeder
  { r: 5, x0: 640,   x1: 960,   type: 'cyan',  speed: 3.5, phase: 1.7 }, // Center optical data line
  { r: 5, x0: 1150,  x1: 1420,  type: 'amber', speed: 2.7, phase: 2.4 }, // East power bus feeder

  // Row 3 (y = 304) — Dorsal mid-deck conduits above catapult trench
  { r: 3, x0: 340,   x1: 580,    type: 'amber', speed: 3.1, phase: 1.1 },
  { r: 3, x0: 1020,  x1: 1260,   type: 'amber', speed: 3.1, phase: 2.9 },

  // Row 1 (y = 240) — Upper dorsal armor seams
  { r: 1, x0: 140,   x1: 290,    type: 'cyan',  speed: 2.5, phase: 0.8 },
  { r: 1, x0: 1310,  x1: 1470,   type: 'cyan',  speed: 2.5, phase: 1.9 }
];

const DECK_CREVICE_V_SEAMS = [
  // Perspective seams precisely framing the user-highlighted plate
  { col: 720,  r0: 6, r1: 8, type: 'amber', speed: 2.8, phase: 0.6 }, // Left edge of user plate, feeds row 6 raceway to row 7 & 8
  { col: 810,  r0: 7, r1: 8, type: 'amber', speed: 2.8, phase: 1.2 }, // Right edge of user plate, ties row 7 to row 8

  // West & East sector bus ties
  { col: 450,  r0: 5, r1: 7, type: 'amber', speed: 2.7, phase: 0.5 }, // West amber power feeder
  { col: 1170, r0: 5, r1: 7, type: 'amber', speed: 2.7, phase: 2.2 }, // East amber power feeder
  { col: 360,  r0: 2, r1: 4, type: 'cyan',  speed: 3.0, phase: 0.2 }, // West cyan raceway feeder to catapult rim
  { col: 1260, r0: 2, r1: 4, type: 'cyan',  speed: 3.0, phase: 1.9 }  // East cyan raceway feeder to catapult rim
];

const DECK_CREVICE_JUNCTIONS = [
  // Precise junction hubs around user-highlighted plate
  { col: 720,  r: 6, type: 'amber' }, // Tap from row 6 raceway
  { col: 720,  r: 7, type: 'amber' }, // Top-left of user plate
  { col: 810,  r: 7, type: 'amber' }, // Top-right of user plate
  { col: 720,  r: 8, type: 'amber' }, // Bottom-left of user plate
  { col: 810,  r: 8, type: 'amber' }, // Bottom-right of user plate

  // West & East sector junctions
  { col: 450,  r: 5, type: 'amber' },
  { col: 450,  r: 7, type: 'amber' },
  { col: 1170, r: 5, type: 'amber' },
  { col: 1170, r: 7, type: 'amber' },
  { col: 360,  r: 2, type: 'cyan' },
  { col: 1260, r: 2, type: 'cyan' }
];

function drawGlowingSeamSegment(ctx, x0, y0, x1, y1, type, speed, phase, t, quality) {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const len = Math.hypot(dx, dy);
  if (len < 1) return;

  const nx = -dy / len;
  const ny = dx / len;

  ctx.save();
  ctx.lineCap = 'round';

  // 1. Recessed dark seam cavity
  ctx.strokeStyle = '#04070d';
  ctx.lineWidth = 2.4;
  ctx.beginPath();
  ctx.moveTo(x0, y0);
  ctx.lineTo(x1, y1);
  ctx.stroke();

  // Organic breath pulse
  const pulse = 0.70 + 0.30 * Math.sin(t * speed + phase);
  const isCyan = (type === 'cyan');

  // 2. Diffuse outer bloom (high/medium quality)
  if (quality !== 'low') {
    const halation = getDeckHalationFactor(t);
    const halationScale = 1.0 + halation * 0.55;
    ctx.strokeStyle = isCyan
      ? `rgba(56, 189, 248, ${(pulse * 0.20 * halationScale).toFixed(3)})`
      : `rgba(245, 158, 11, ${(pulse * 0.22 * halationScale).toFixed(3)})`;
    ctx.lineWidth = (quality === 'high' ? 3.6 : 2.8) * halationScale;
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.stroke();
  }

  // 3. High-intensity neon core
  ctx.strokeStyle = isCyan
    ? `rgba(56, 189, 248, ${0.72 + pulse * 0.28})`
    : `rgba(245, 158, 11, ${0.75 + pulse * 0.25})`;
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(x0, y0);
  ctx.lineTo(x1, y1);
  ctx.stroke();

  // 4. White-hot center filament (high/medium quality)
  if (quality !== 'low') {
    ctx.strokeStyle = isCyan
      ? `rgba(224, 242, 254, ${0.75 + pulse * 0.25})`
      : `rgba(254, 243, 199, ${0.75 + pulse * 0.25})`;
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.stroke();
  }

  // 5. Traveling energy packet (sweeping light pulse along conduit)
  if (quality !== 'low' && len > 40) {
    const travelSpeed = 0.45;
    const travelT = ((t * travelSpeed + phase * 0.2) % 1.0 + 1.0) % 1.0;
    const packetX = x0 + dx * travelT;
    const packetY = y0 + dy * travelT;
    const pLen = Math.min(14, len * 0.22);
    const pX0 = packetX - (dx / len) * (pLen * 0.5);
    const pY0 = packetY - (dy / len) * (pLen * 0.5);
    const pX1 = packetX + (dx / len) * (pLen * 0.5);
    const pY1 = packetY + (dy / len) * (pLen * 0.5);

    ctx.strokeStyle = isCyan ? 'rgba(186, 230, 253, 0.95)' : 'rgba(254, 240, 138, 0.95)';
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    ctx.moveTo(pX0, pY0);
    ctx.lineTo(pX1, pY1);
    ctx.stroke();

    if (quality === 'high') {
      ctx.strokeStyle = isCyan ? 'rgba(56, 189, 248, 0.45)' : 'rgba(245, 158, 11, 0.45)';
      ctx.lineWidth = 3.6;
      ctx.beginPath();
      ctx.moveTo(pX0, pY0);
      ctx.lineTo(pX1, pY1);
      ctx.stroke();
    }
  }

  // 6. Protective machined steel cross-clamps / retaining brackets
  ctx.lineCap = 'butt';
  ctx.strokeStyle = 'rgba(51, 65, 85, 0.75)';
  ctx.lineWidth = 0.8;
  const clampStep = 18;
  const clampArm = 1.6;
  for (let d = 8; d < len - 6; d += clampStep) {
    const cx = x0 + (dx / len) * d;
    const cy = y0 + (dy / len) * d;
    ctx.beginPath();
    ctx.moveTo(cx - nx * clampArm, cy - ny * clampArm);
    ctx.lineTo(cx + nx * clampArm, cy + ny * clampArm);
    ctx.stroke();
  }

  ctx.restore();
}

function drawCreviceJunctionNode(ctx, x, y, type, t, quality) {
  const isCyan = (type === 'cyan');
  const pulse = 0.70 + 0.30 * Math.sin(t * 3.6 + (x * 0.05 + y * 0.08));

  ctx.save();
  // 1. Machined steel square junction mounting collar (matches deck hex fasteners)
  ctx.fillStyle = '#0a0f18';
  ctx.fillRect(x - 2.5, y - 2.5, 5, 5);
  ctx.fillStyle = '#334155';
  ctx.fillRect(x - 2, y - 2, 4, 4);

  // 2. Soft emission halo (subtle, doesn't overwhelm)
  if (quality !== 'low') {
    ctx.fillStyle = isCyan
      ? `rgba(56, 189, 248, ${pulse * 0.28})`
      : `rgba(245, 158, 11, ${pulse * 0.28})`;
    ctx.beginPath();
    ctx.arc(x, y, quality === 'high' ? 4.2 : 3.2, 0, Math.PI * 2);
    ctx.fill();
  }

  // 3. Glowing micro-diode core
  ctx.fillStyle = isCyan
    ? `rgba(56, 189, 248, ${0.80 + pulse * 0.20})`
    : `rgba(245, 158, 11, ${0.80 + pulse * 0.20})`;
  ctx.beginPath();
  ctx.arc(x, y, 1.4, 0, Math.PI * 2);
  ctx.fill();

  // 4. White-hot center glint
  ctx.fillStyle = isCyan ? 'rgba(224, 242, 254, 0.95)' : 'rgba(254, 243, 199, 0.95)';
  ctx.fillRect(x - 0.5, y - 0.5, 1, 1);
  ctx.restore();
}

// ---------------------------------------------------------------------------
// DECK STRIPES GAUSSIAN HALATION PASS (SCI-FI OPTICAL HALATION IN DARKNESS)
// "je dunkler die umgebung (cirrus gewitter), desto mehr müssen die streifen
// auf dem boden ein leichtes gausschess leuchten (wie halation) erhalten"
// ---------------------------------------------------------------------------

function drawDeckStripesGaussianHalation(ctx, t, visibleLeft = 0, visibleRight = WIDTH) {
  const halation = getDeckHalationFactor(t);
  if (halation <= 0.01) return;

  ctx.save();

  // 1. Clip Halation strictly to the Deck Polygon (so it never bleeds into sky or abyss)
  ctx.beginPath();
  ctx.moveTo(visibleLeft, 204);
  ctx.lineTo(0, 204);
  const cutouts = [
    { startX: 230,  endX: 370 },
    { startX: 630,  endX: 750 },
    { startX: 850,  endX: 970 },
    { startX: 1230, endX: 1370 }
  ];
  for (let c = 0; c < cutouts.length; c++) {
    const cut = cutouts[c];
    ctx.lineTo(cut.startX - 15, 204);
    ctx.lineTo(cut.startX, 222);
    ctx.lineTo(cut.endX, 222);
    ctx.lineTo(cut.endX + 15, 204);
  }
  ctx.lineTo(WIDTH, 204);
  ctx.lineTo(visibleRight, 204);
  ctx.lineTo(visibleRight, 490);
  ctx.lineTo(WIDTH, 490);
  for (let s = PERIMETER_SEGMENTS.length - 1; s >= 0; s--) {
    const seg = PERIMETER_SEGMENTS[s];
    ctx.lineTo(seg.x1, seg.y1);
    ctx.lineTo(seg.x0, seg.y0);
  }
  ctx.lineTo(visibleLeft, 488);
  ctx.closePath();
  ctx.clip();

  // 2. Additive Screen Blending for authentic optical halation across dark metal
  ctx.globalCompositeOperation = 'screen';

  const floorTopY = 208;
  const rowH = 32;

  // Pulse & subtle energetic breathing
  const pulse = 0.88 + 0.12 * Math.sin(t * 2.8);
  const intensity = halation * 0.58 * pulse;

  // Multi-tier concentric Gaussian distribution (refined softer halation):
  // Tier 1: Wide gossamer halo (3 sigma, r ≈ 14px)
  // Tier 2: Mid halation body   (2 sigma, r ≈ 7px)
  // Tier 3: Inner bloom core    (1 sigma, r ≈ 3px)
  const isHigh = (state.quality === 'high');
  const isLow = (state.quality === 'low');

  const tiers = isLow
    ? [{ blur: 7 * halation, width: 5.5, alphaScale: 0.30 }]
    : (isHigh
      ? [
          { blur: 14 * halation, width: 9.0 + 4.5 * halation, alphaScale: 0.16 },
          { blur: 7 * halation,  width: 5.0 + 2.5 * halation, alphaScale: 0.28 },
          { blur: 3 * halation,  width: 2.6 + 1.2 * halation, alphaScale: 0.45 }
        ]
      : [
          { blur: 9 * halation,   width: 6.5 + 3.0 * halation, alphaScale: 0.22 },
          { blur: 3.5 * halation, width: 3.0 + 1.5 * halation, alphaScale: 0.40 }
        ]);

  for (let k = 0; k < tiers.length; k++) {
    const tier = tiers[k];
    const alpha = (intensity * tier.alphaScale).toFixed(3);
    if (alpha <= 0.003) continue;

    // --- CYAN CONDUITS & STRIPES ---
    ctx.strokeStyle = `rgba(56, 189, 248, ${alpha})`;
    ctx.lineWidth = tier.width;
    ctx.lineCap = 'round';
    ctx.shadowColor = `rgba(56, 189, 248, ${(intensity * 0.55).toFixed(3)})`;
    ctx.shadowBlur = tier.blur;

    ctx.beginPath();
    // A. Main Cyan Raceways (Row 2, y = 272)
    ctx.moveTo(220, floorTopY + 2 * rowH);  ctx.lineTo(580, floorTopY + 2 * rowH);
    ctx.moveTo(1080, floorTopY + 2 * rowH); ctx.lineTo(1380, floorTopY + 2 * rowH);

    // B. Cyan Horizontal Crevices
    for (let i = 0; i < DECK_CREVICE_H_SEAMS.length; i++) {
      const s = DECK_CREVICE_H_SEAMS[i];
      if (s.type !== 'cyan') continue;
      const sy = floorTopY + s.r * rowH;
      ctx.moveTo(s.x0, sy);
      ctx.lineTo(s.x1, sy);
    }

    // C. Cyan Vertical Crevices (Perspective mapped)
    for (let i = 0; i < DECK_CREVICE_V_SEAMS.length; i++) {
      const s = DECK_CREVICE_V_SEAMS[i];
      if (s.type !== 'cyan') continue;
      const sy0 = floorTopY + s.r0 * rowH;
      const sy1 = floorTopY + s.r1 * rowH;
      ctx.moveTo(getDeckFloorX(s.col, sy0), sy0);
      ctx.lineTo(getDeckFloorX(s.col, sy1), sy1);
    }
    ctx.stroke();

    // --- AMBER CONDUITS & CREVICES ---
    ctx.strokeStyle = `rgba(245, 158, 11, ${alpha})`;
    ctx.shadowColor = `rgba(245, 158, 11, ${(intensity * 0.60).toFixed(3)})`;

    ctx.beginPath();
    // A. Main Amber Center Raceway (Row 6, y = 400)
    ctx.moveTo(650, floorTopY + 6 * rowH); ctx.lineTo(1040, floorTopY + 6 * rowH);

    // B. Amber Horizontal Crevices (Framing lower user plate at Row 7 & 8, and bus lines)
    for (let i = 0; i < DECK_CREVICE_H_SEAMS.length; i++) {
      const s = DECK_CREVICE_H_SEAMS[i];
      if (s.type !== 'amber') continue;
      const sy = floorTopY + s.r * rowH;
      ctx.moveTo(s.x0, sy);
      ctx.lineTo(s.x1, sy);
    }

    // C. Amber Vertical Crevices (Columns 720, 810, 450, 1170)
    for (let i = 0; i < DECK_CREVICE_V_SEAMS.length; i++) {
      const s = DECK_CREVICE_V_SEAMS[i];
      if (s.type !== 'amber') continue;
      const sy0 = floorTopY + s.r0 * rowH;
      const sy1 = floorTopY + s.r1 * rowH;
      ctx.moveTo(getDeckFloorX(s.col, sy0), sy0);
      ctx.lineTo(getDeckFloorX(s.col, sy1), sy1);
    }

    // D. Central Catapult Runway Guide Rails (subtle golden runway halation)
    ctx.moveTo(180, floorTopY + 4 * rowH - 3.5); ctx.lineTo(1420, floorTopY + 4 * rowH - 3.5);
    ctx.moveTo(180, floorTopY + 4 * rowH + 3.5); ctx.lineTo(1420, floorTopY + 4 * rowH + 3.5);
    ctx.stroke();

    // E. Junction Hubs (Radial halos at junctions)
    ctx.fillStyle = `rgba(245, 158, 11, ${alpha})`;
    for (let i = 0; i < DECK_CREVICE_JUNCTIONS.length; i++) {
      const j = DECK_CREVICE_JUNCTIONS[i];
      const jy = floorTopY + j.r * rowH;
      const jx = getDeckFloorX(j.col, jy);
      ctx.beginPath();
      ctx.arc(jx, jy, tier.width * 0.50, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  ctx.restore();
}

let _staticDeckBaseCanvas = null;
let _staticDeckBaseKey = null;

function invalidateStaticDeckBase() {
  _staticDeckBaseCanvas = null;
  _staticDeckBaseKey = null;
  invalidateStaticUpperHull();
}

function getStaticDeckBaseCanvas(visibleLeft = 0, visibleRight = WIDTH) {
  const currentKey = `${state.quality}_${state.seed}_${visibleLeft}_${visibleRight}`;
  if (_staticDeckBaseCanvas && _staticDeckBaseKey === currentKey) {
    return _staticDeckBaseCanvas;
  }

  if (typeof document === 'undefined') return null;

  const bgW = Math.max(WIDTH, visibleRight - visibleLeft);
  _staticDeckBaseCanvas = document.createElement('canvas');
  _staticDeckBaseCanvas.width = bgW;
  _staticDeckBaseCanvas.height = HEIGHT;
  const bctx = _staticDeckBaseCanvas.getContext('2d');
  _staticDeckBaseKey = currentKey;

  bctx.save();
  bctx.translate(-visibleLeft, 0);
  bakeStaticDeckBase(bctx, visibleLeft, visibleRight);
  bctx.restore();

  return _staticDeckBaseCanvas;
}

function bakeStaticDeckBase(ctx, visibleLeft = 0, visibleRight = WIDTH) {
  ctx.save();

  const floorTopY = 208;
  const floorBottomY = LANE_BOTTOM; // 496
  const floorH = floorBottomY - floorTopY; // 288
  const plateRows = 9;
  const rowH = floorH / plateRows; // 32
  const colW = 90;
  const startCol = Math.floor((visibleLeft - 180) / colW) * colW;
  const endCol = Math.ceil((visibleRight + 180) / colW) * colW;

  // Perspective projection formula matching js/engine/render/background.js lines 1101 & 1135:
  // Vanishing horizon at y = 208, perspective factor = 0.34, center = 800
  function getFloorX(col, py) {
    const depthT = (py - floorTopY) / floorH;
    return col + (col - 800) * 0.34 * depthT;
  }

  // 1. Clip Deck to Hull Contour (Upper cutouts + Articulated stepped front edge)
  ctx.beginPath();
  ctx.moveTo(visibleLeft, 204);
  ctx.lineTo(0, 204);
  const cutouts = [
    { startX: 230,  endX: 370 },
    { startX: 630,  endX: 750 },
    { startX: 850,  endX: 970 },
    { startX: 1230, endX: 1370 }
  ];
  for (let c = 0; c < cutouts.length; c++) {
    const cut = cutouts[c];
    ctx.lineTo(cut.startX - 15, 204);
    ctx.lineTo(cut.startX, 222);
    ctx.lineTo(cut.endX, 222);
    ctx.lineTo(cut.endX + 15, 204);
  }
  ctx.lineTo(WIDTH, 204);
  ctx.lineTo(visibleRight, 204);
  ctx.lineTo(visibleRight, 490);
  ctx.lineTo(WIDTH, 490);

  // Trace articulated front deck edge backwards from east (WIDTH) to west (0):
  for (let s = PERIMETER_SEGMENTS.length - 1; s >= 0; s--) {
    const seg = PERIMETER_SEGMENTS[s];
    ctx.lineTo(seg.x1, seg.y1);
    ctx.lineTo(seg.x0, seg.y0);
  }
  ctx.lineTo(visibleLeft, 488);
  ctx.closePath();
  ctx.clip();

  // 2. Base Deck Alloy Substrate Gradient (fills completely down to lowest perimeter depth)
  const deckGrad = ctx.createLinearGradient(0, floorTopY, 0, 540);
  deckGrad.addColorStop(0.0, '#272d37'); // Imperial Battleship Slate at horizon
  deckGrad.addColorStop(0.35, '#353c48');
  deckGrad.addColorStop(0.7, '#434b5a'); // Specular titanium-steel lane center
  deckGrad.addColorStop(1.0, '#262d3a'); // Front deck plate
  ctx.fillStyle = deckGrad;
  ctx.fillRect(visibleLeft, 204, visibleRight - visibleLeft, 340);

  // 3. Modular 3D Perspective Armor Plates (Trapezoids)
  const plateTones = [
    '#434a58', '#3b414f', '#495262', '#363c49', '#4f5768',
    '#3e4554', '#474f5e', '#383e4c', '#4b5465', '#3f4656'
  ];

  for (let r = 0; r < plateRows; r++) {
    const y0 = floorTopY + r * rowH;
    const y1 = (r === plateRows - 1) ? 540 : y0 + rowH;

    for (let col = startCol; col < endCol; col += colW) {
      const xTL = getFloorX(col, y0);
      const xTR = getFloorX(col + colW, y0);
      const xBR = getFloorX(col + colW, y1);
      const xBL = getFloorX(col, y1);

      if (xTR < visibleLeft - 10 || xBL > visibleRight + 10) continue;

      const midX = (xTL + xTR + xBR + xBL) * 0.25;

      // Deterministic plate tone variation
      const toneIdx = Math.abs((Math.floor(col / colW) * 7 + r * 13) % plateTones.length);
      ctx.fillStyle = plateTones[toneIdx];
      ctx.beginPath();
      ctx.moveTo(xTL, y0);
      ctx.lineTo(xTR, y0);
      ctx.lineTo(xBR, y1);
      ctx.lineTo(xBL, y1);
      ctx.closePath();
      ctx.fill();

      // Physical Stepped Shingle Thickness Edge (2.2px vertical drop lip)
      ctx.fillStyle = '#141822';
      ctx.beginPath();
      ctx.moveTo(xBL, y1 - 0.5);
      ctx.lineTo(xBR, y1 - 0.5);
      ctx.lineTo(xBR, y1 + 1.8);
      ctx.lineTo(xBL, y1 + 1.8);
      ctx.closePath();
      ctx.fill();

      // Micro Drop-Shadow thrown downward onto the adjacent plate row
      ctx.fillStyle = 'rgba(2, 6, 23, 0.48)';
      ctx.beginPath();
      ctx.moveTo(xBL, y1 + 1.8);
      ctx.lineTo(xBR, y1 + 1.8);
      ctx.lineTo(xBR, y1 + 3.2);
      ctx.lineTo(xBL, y1 + 3.2);
      ctx.closePath();
      ctx.fill();

      // Top edge glint (lit by warm sunset alpenglow)
      ctx.strokeStyle = 'rgba(255, 238, 221, 0.26)';
      ctx.lineWidth = 1.0;
      ctx.beginPath();
      ctx.moveTo(xTL, y0 + 0.5);
      ctx.lineTo(xTR, y0 + 0.5);
      ctx.stroke();

      // Lateral perspective bevels (directional light vs shadow)
      if (midX < 800) {
        // Looking from the right: right seam in deep shadow, left seam catches light
        ctx.strokeStyle = 'rgba(2, 6, 23, 0.65)';
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.moveTo(xTR, y0);
        ctx.lineTo(xBR, y1);
        ctx.stroke();

        ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(xTL, y0);
        ctx.lineTo(xBL, y1);
        ctx.stroke();
      } else {
        // Looking from the left: left seam in deep shadow, right seam catches light
        ctx.strokeStyle = 'rgba(2, 6, 23, 0.65)';
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.moveTo(xTL, y0);
        ctx.lineTo(xBL, y1);
        ctx.stroke();

        ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(xTR, y0);
        ctx.lineTo(xBR, y1);
        ctx.stroke();
      }

      // 3D Recessed Cylindrical Tie-Down Sockets on select plates
      if ((Math.floor(col / colW) + r) % 3 === 0 && r !== 4) {
        const sockX = getFloorX(col + colW * 0.5, (y0 + y1) * 0.5);
        const sockY = (y0 + y1) * 0.5;
        if (sockX > visibleLeft + 20 && sockX < visibleRight - 20) {
          // Outer recessed shadow bevel
          ctx.fillStyle = '#090d14';
          ctx.beginPath();
          ctx.arc(sockX, sockY, 4.0, 0, Math.PI * 2);
          ctx.fill();

          // Cylindrical borehole wall
          ctx.fillStyle = '#1c222d';
          ctx.beginPath();
          ctx.arc(sockX, sockY + 0.5, 3.0, 0, Math.PI * 2);
          ctx.fill();

          // Borehole floor shadow
          ctx.fillStyle = '#05070a';
          ctx.beginPath();
          ctx.arc(sockX, sockY + 1.0, 2.0, 0, Math.PI * 2);
          ctx.fill();

          // Machined Steel Cross-Pin with 3D highlight & shadow
          ctx.fillStyle = '#475569';
          ctx.fillRect(sockX - 2.5, sockY - 0.5, 5.0, 1.0);
          ctx.fillRect(sockX - 0.5, sockY - 2.5, 1.0, 5.0);
          ctx.fillStyle = '#cbd5e1';
          ctx.fillRect(sockX - 2.0, sockY - 0.5, 4.0, 0.5);
          ctx.fillRect(sockX - 0.5, sockY - 2.0, 0.5, 4.0);
        }
      }
    }
  }

  // 4. Continuous 3D Fanned Perspective Grid Seams (Dual-Tone Metallic)
  for (let col = startCol; col <= endCol; col += colW) {
    const topX = col;
    const bottomX = getFloorX(col, floorBottomY);

    // Deep recessed shadow seam
    ctx.lineWidth = 1.8;
    ctx.strokeStyle = 'rgba(2, 6, 23, 0.65)';
    ctx.beginPath();
    ctx.moveTo(topX - 0.6, floorTopY);
    ctx.lineTo(bottomX - 0.6, floorBottomY);
    ctx.stroke();

    // Specular metallic highlight line
    ctx.lineWidth = 0.9;
    ctx.strokeStyle = 'rgba(203, 213, 225, 0.18)';
    ctx.beginPath();
    ctx.moveTo(topX + 0.6, floorTopY);
    ctx.lineTo(bottomX + 0.6, floorBottomY);
    ctx.stroke();
  }

  // 5. Hex-Bolts & Polished Chrome Fasteners at Grid Intersections
  for (let r = 0; r <= plateRows; r++) {
    const py = floorTopY + r * rowH;
    for (let col = startCol; col <= endCol; col += colW) {
      const rx = getFloorX(col, py);
      if (rx < visibleLeft - 5 || rx > visibleRight + 5) continue;

      // Heavy contact socket
      ctx.fillStyle = '#0a0f18';
      ctx.fillRect(rx - 2.5, py - 2.5, 5, 5);
      // Machined steel washer
      ctx.fillStyle = '#475569';
      ctx.fillRect(rx - 2, py - 2, 4, 4);
      // Polished chrome rivet head
      ctx.fillStyle = '#cbd5e1';
      ctx.fillRect(rx - 1, py - 1, 2, 2);
      // Specular glint
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(rx - 0.5, py - 1, 1, 1);
    }
  }

  // 6. Irregular Structures 1: 3D Diamond-Tread Access Inspection Hatches
  const inspectionHatches = [
    { col: 270, r: 2 }, // West sector
    { col: 630, r: 6 }, // Mid-West sector
    { col: 990, r: 1 }, // Mid-East sector
    { col: 1260, r: 5 } // East sector
  ];

  for (let h = 0; h < inspectionHatches.length; h++) {
    const ha = inspectionHatches[h];
    const y0 = floorTopY + ha.r * rowH + 2;
    const y1 = floorTopY + (ha.r + 1) * rowH - 2;
    const xTL = getFloorX(ha.col + 3, y0);
    const xTR = getFloorX(ha.col + colW - 3, y0);
    const xBR = getFloorX(ha.col + colW - 3, y1);
    const xBL = getFloorX(ha.col + 3, y1);

    // Directional Drop Shadow cast onto the deck plate
    const shadowDx = ha.col < 800 ? 5 : -5;
    ctx.fillStyle = 'rgba(2, 6, 23, 0.60)';
    ctx.beginPath();
    ctx.moveTo(xTL + shadowDx, y0 + 3);
    ctx.lineTo(xTR + shadowDx, y0 + 3);
    ctx.lineTo(xBR + shadowDx, y1 + 5);
    ctx.lineTo(xBL + shadowDx, y1 + 5);
    ctx.closePath();
    ctx.fill();

    // Recessed Mounting Socket Frame
    ctx.fillStyle = '#10151f';
    ctx.beginPath();
    ctx.moveTo(xTL - 1.5, y0 - 1.5);
    ctx.lineTo(xTR + 1.5, y0 - 1.5);
    ctx.lineTo(xBR + 1.5, y1 + 1.5);
    ctx.lineTo(xBL - 1.5, y1 + 1.5);
    ctx.closePath();
    ctx.fill();

    // Raised 3D Armor Hatch Body
    const hatchGrad = ctx.createLinearGradient(0, y0, 0, y1);
    hatchGrad.addColorStop(0.0, '#535c6e');
    hatchGrad.addColorStop(1.0, '#384050');
    ctx.fillStyle = hatchGrad;
    ctx.beginPath();
    ctx.moveTo(xTL, y0);
    ctx.lineTo(xTR, y0);
    ctx.lineTo(xBR, y1);
    ctx.lineTo(xBL, y1);
    ctx.closePath();
    ctx.fill();

    // Vertical Thickness Edge Lip
    ctx.fillStyle = '#1c2330';
    ctx.beginPath();
    ctx.moveTo(xBL, y1);
    ctx.lineTo(xBR, y1);
    ctx.lineTo(xBR, y1 + 2.5);
    ctx.lineTo(xBL, y1 + 2.5);
    ctx.closePath();
    ctx.fill();

    // Chamfered Metallic Frame Glint
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.32)';
    ctx.lineWidth = 1.0;
    ctx.beginPath();
    ctx.moveTo(xTL, y0 + 0.5);
    ctx.lineTo(xTR, y0 + 0.5);
    ctx.stroke();

    ctx.strokeStyle = 'rgba(2, 6, 23, 0.70)';
    ctx.beginPath();
    ctx.moveTo(xBL, y1 + 2.5);
    ctx.lineTo(xBR, y1 + 2.5);
    ctx.stroke();

    // Perspective Crosshatch Diamond Tread Lines
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
    ctx.lineWidth = 1.0;
    for (let f = 0.2; f <= 0.8; f += 0.2) {
      const d1X = xTL + (xTR - xTL) * f;
      const d2X = xBL + (xBR - xBL) * f;
      ctx.beginPath();
      ctx.moveTo(d1X, y0);
      ctx.lineTo(d2X, y1);
      ctx.stroke();

      const d3Y = y0 + (y1 - y0) * f;
      const d3Left = xTL + (xBL - xTL) * f;
      const d3Right = xTR + (xBR - xTR) * f;
      ctx.beginPath();
      ctx.moveTo(d3Left, d3Y);
      ctx.lineTo(d3Right, d3Y);
      ctx.stroke();
    }

    // 4 Heavy Corner Dog-Latches (3D lugs with highlights and drop shadows)
    const corners = [
      { x: xTL + 3, y: y0 + 3 },
      { x: xTR - 6, y: y0 + 3 },
      { x: xBR - 6, y: y1 - 6 },
      { x: xBL + 3, y: y1 - 6 }
    ];
    for (let c = 0; c < corners.length; c++) {
      const cn = corners[c];
      ctx.fillStyle = '#090d14';
      ctx.fillRect(cn.x, cn.y, 4, 4);
      ctx.fillStyle = '#64748b';
      ctx.fillRect(cn.x + 0.5, cn.y + 0.5, 3, 3);
      ctx.fillStyle = SW.amberGlow;
      ctx.fillRect(cn.x + 1, cn.y + 1, 1.5, 1.5);
    }
  }

  // 8. Central Catapult Runway Trench (Along Row 4 seam at y = 336, exact lane center)
  const catapultY = floorTopY + 4 * rowH; // 336
  const catapultStartX = 50; // Extended under base by 90px (one deck tile width)
  const catapultEndX = WIDTH - catapultStartX; // 1550
  const catapultWidth = catapultEndX - catapultStartX; // 1500

  // Deep Sunken Trench Cavity with Upper Ambient Shadow Wall
  ctx.fillStyle = '#060910';
  ctx.fillRect(catapultStartX, catapultY - 7, catapultWidth, 14);

  // Upper Trench Wall Drop Shadow (creates deep vertical depression)
  const trenchShadow = ctx.createLinearGradient(0, catapultY - 7, 0, catapultY - 2);
  trenchShadow.addColorStop(0.0, '#010204');
  trenchShadow.addColorStop(1.0, 'rgba(6, 9, 16, 0)');
  ctx.fillStyle = trenchShadow;
  ctx.fillRect(catapultStartX, catapultY - 7, catapultWidth, 5);

  // Trench Lower Rim Chamfer Glint
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.20)';
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  ctx.moveTo(catapultStartX, catapultY + 7);
  ctx.lineTo(catapultEndX, catapultY + 7);
  ctx.stroke();

  // Dual Machined Steel 3D Guide Rails
  // Upper Rail (y = catapultY - 3.5)
  ctx.fillStyle = '#05070c';
  ctx.fillRect(catapultStartX, catapultY - 2.5, catapultWidth, 1.0); // Shadow
  ctx.fillStyle = '#475569';
  ctx.fillRect(catapultStartX, catapultY - 4.5, catapultWidth, 2.0); // Rail Body
  ctx.fillStyle = '#cbd5e1';
  ctx.fillRect(catapultStartX, catapultY - 4.5, catapultWidth, 0.7); // Railhead specular

  // Lower Rail (y = catapultY + 3.5)
  ctx.fillStyle = '#05070c';
  ctx.fillRect(catapultStartX, catapultY + 4.5, catapultWidth, 1.0); // Shadow
  ctx.fillStyle = '#475569';
  ctx.fillRect(catapultStartX, catapultY + 2.5, catapultWidth, 2.0); // Rail Body
  ctx.fillStyle = '#cbd5e1';
  ctx.fillRect(catapultStartX, catapultY + 2.5, catapultWidth, 0.7); // Railhead specular

  // Guide Rail Cross-Ties (every 24px)
  ctx.fillStyle = '#1e2430';
  for (let rx = catapultStartX + 10; rx <= catapultEndX - 10; rx += 24) {
    ctx.fillRect(rx - 1.5, catapultY - 5, 3, 10);
  }

  // 9. Battlefield Wear & Deck Imperfections (Organic Scorch, Welds, Heat Bloom, Cracks)
  if (state.quality !== 'low') {
    // Welded Replacement Patch Plate (Asymmetric, Mid-West)
    const patchX = 510, patchY = floorTopY + 3 * rowH + 12, patchW = 42, patchH = 18;
    ctx.fillStyle = '#2d333e';
    ctx.fillRect(patchX - patchW / 2, patchY - patchH / 2, patchW, patchH);
    ctx.strokeStyle = '#64748b';
    ctx.lineWidth = 1.2;
    ctx.strokeRect(patchX - patchW / 2, patchY - patchH / 2, patchW, patchH);
    ctx.fillStyle = '#94a3b8';
    ctx.fillRect(patchX - patchW / 2 + 2, patchY - patchH / 2 + 2, 2, 2);
    ctx.fillRect(patchX + patchW / 2 - 4, patchY - patchH / 2 + 2, 2, 2);
    ctx.fillRect(patchX - patchW / 2 + 2, patchY + patchH / 2 - 4, 2, 2);
    ctx.fillRect(patchX + patchW / 2 - 4, patchY + patchH / 2 - 4, 2, 2);

    // Atmospheric Thruster Burn Scorch Halos
    const scorchMarks = [
      { x: 530, y: 300, rx: 28, ry: 12 },
      { x: 940, y: 380, rx: 34, ry: 14 }
    ];
    for (let sm = 0; sm < scorchMarks.length; sm++) {
      const sc = scorchMarks[sm];
      const scorchGrad = ctx.createRadialGradient(sc.x, sc.y, 2, sc.x, sc.y, sc.rx);
      scorchGrad.addColorStop(0.0, 'rgba(10, 10, 15, 0.45)');
      scorchGrad.addColorStop(0.6, 'rgba(15, 15, 20, 0.20)');
      scorchGrad.addColorStop(1.0, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = scorchGrad;
      ctx.beginPath();
      ctx.ellipse(sc.x, sc.y, sc.rx, sc.ry, 0.1, 0, Math.PI * 2);
      ctx.fill();
    }

    // Plasma Heat-Tempering Bloom (Iridescent violet/cyan/gold)
    const bloomX = 860, bloomY = 260;
    const bloomGrad = ctx.createRadialGradient(bloomX, bloomY, 3, bloomX, bloomY, 30);
    bloomGrad.addColorStop(0.0, 'rgba(147, 51, 234, 0.14)');
    bloomGrad.addColorStop(0.45, 'rgba(6, 182, 212, 0.10)');
    bloomGrad.addColorStop(0.8, 'rgba(245, 158, 11, 0.06)');
    bloomGrad.addColorStop(1.0, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = bloomGrad;
    ctx.beginPath();
    ctx.ellipse(bloomX, bloomY, 32, 12, 0.15, 0, Math.PI * 2);
    ctx.fill();

    // Hairline Micro-Cracks on Plate Lip
    const crackX = 1160, crackY = 420;
    ctx.strokeStyle = 'rgba(2, 6, 23, 0.88)';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(crackX - 14, crackY + 2);
    ctx.lineTo(crackX - 6, crackY - 1);
    ctx.lineTo(crackX, crackY + 1);
    ctx.lineTo(crackX + 10, crackY - 3);
    ctx.lineTo(crackX + 16, crackY + 2);
    ctx.moveTo(crackX, crackY + 1);
    ctx.lineTo(crackX + 4, crackY + 5);
    ctx.stroke();
    ctx.strokeStyle = 'rgba(226, 232, 240, 0.18)';
    ctx.lineWidth = 0.7;
    ctx.beginPath();
    ctx.moveTo(crackX - 13, crackY + 3);
    ctx.lineTo(crackX - 5, crackY);
    ctx.lineTo(crackX + 1, crackY + 2);
    ctx.stroke();

  }

  // 10. Fleet Military Stencils & Hazard Directional Chevrons
  ctx.save();
  ctx.font = '8.5px ui-monospace, SFMono-Regular, monospace';
  ctx.letterSpacing = '0.08em';
  ctx.fillStyle = 'rgba(255, 255, 255, 0.22)';

  // Upper rim markings rotated 180 degrees (authentically "von außen lesbar" from top deck edge)
  const drawFlippedUpperStencil = (text, startX, y) => {
    const textWidth = ctx.measureText ? (ctx.measureText(text)?.width || (text.length * 7)) : (text.length * 7);
    const centerX = startX + textWidth / 2;
    ctx.save();
    ctx.translate(centerX, y - 7);
    ctx.rotate(Math.PI);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(text, 0, 0);
    ctx.restore();
  };

  drawFlippedUpperStencil('STRATOS DREADNOUGHT // DORSAL DECK 04', 240, 234);
  drawFlippedUpperStencil('CATAPULT TRENCH ACTIVE // 10,000 FT AGL', 960, 234);

  // Lower rim markings standard upright ("von außen lesbar" from bottom deck edge)
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText('HYDRAULIC ARRESTOR PRESSURE: 4500 PSI', 240, 484);
  ctx.fillText('RESTRICTED DECK // KEEP CLEAR AT CRUISE', 980, 484);

  // Bidirectional Catapult Runway Chevrons
  ctx.strokeStyle = 'rgba(245, 158, 11, 0.45)';
  ctx.lineWidth = 1.8;
  for (let chX = 360; chX <= 1240; chX += 160) {
    ctx.beginPath();
    // Westbound chevron <
    ctx.moveTo(chX - 2, catapultY - 5);
    ctx.lineTo(chX - 7, catapultY);
    ctx.lineTo(chX - 2, catapultY + 5);
    // Eastbound chevron >
    ctx.moveTo(chX + 2, catapultY - 5);
    ctx.lineTo(chX + 7, catapultY);
    ctx.lineTo(chX + 2, catapultY + 5);
    ctx.stroke();
  }
  ctx.restore();

  // 11. Static Pre-Baked Conduits, Raceways & Runway Light Sockets
  const staticRaceways = [
    { y: floorTopY + 2 * rowH, x1: 220, x2: 580 },
    { y: floorTopY + 6 * rowH, x1: 650, x2: 1040 },
    { y: floorTopY + 2 * rowH, x1: 1080, x2: 1380 }
  ];
  for (let rw = 0; rw < staticRaceways.length; rw++) {
    const r = staticRaceways[rw];
    ctx.fillStyle = '#0c0f16';
    ctx.fillRect(r.x1, r.y - 2.5, r.x2 - r.x1, 5);

    ctx.strokeStyle = 'rgba(71, 85, 105, 0.7)';
    ctx.lineWidth = 1.0;
    ctx.beginPath();
    for (let gx = r.x1 + 6; gx < r.x2; gx += 10) {
      ctx.moveTo(gx, r.y - 2.5);
      ctx.lineTo(gx, r.y + 2.5);
    }
    ctx.stroke();
  }

  // Pre-baked dark crevice channels & retaining brackets
  ctx.strokeStyle = '#04070d';
  ctx.lineWidth = 2.4;
  ctx.lineCap = 'round';
  ctx.beginPath();
  for (let i = 0; i < DECK_CREVICE_H_SEAMS.length; i++) {
    const s = DECK_CREVICE_H_SEAMS[i];
    const sy = floorTopY + s.r * rowH;
    ctx.moveTo(s.x0, sy);
    ctx.lineTo(s.x1, sy);
  }
  for (let i = 0; i < DECK_CREVICE_V_SEAMS.length; i++) {
    const s = DECK_CREVICE_V_SEAMS[i];
    const sy0 = floorTopY + s.r0 * rowH;
    const sy1 = floorTopY + s.r1 * rowH;
    ctx.moveTo(getFloorX(s.col, sy0), sy0);
    ctx.lineTo(getFloorX(s.col, sy1), sy1);
  }
  ctx.stroke();

  ctx.lineCap = 'butt';
  ctx.strokeStyle = 'rgba(51, 65, 85, 0.75)';
  ctx.lineWidth = 0.8;
  const clampStep = 18;
  const clampArm = 1.6;
  ctx.beginPath();
  const bakeCreviceClamp = (x0, y0, x1, y1) => {
    const dx = x1 - x0;
    const dy = y1 - y0;
    const len = Math.hypot(dx, dy);
    if (len < 1) return;
    const nx = -dy / len;
    const ny = dx / len;
    for (let d = 8; d < len - 6; d += clampStep) {
      const cx = x0 + (dx / len) * d;
      const cy = y0 + (dy / len) * d;
      ctx.moveTo(cx - nx * clampArm, cy - ny * clampArm);
      ctx.lineTo(cx + nx * clampArm, cy + ny * clampArm);
    }
  };
  for (let i = 0; i < DECK_CREVICE_H_SEAMS.length; i++) {
    const s = DECK_CREVICE_H_SEAMS[i];
    const sy = floorTopY + s.r * rowH;
    bakeCreviceClamp(s.x0, sy, s.x1, sy);
  }
  for (let i = 0; i < DECK_CREVICE_V_SEAMS.length; i++) {
    const s = DECK_CREVICE_V_SEAMS[i];
    const sy0 = floorTopY + s.r0 * rowH;
    const sy1 = floorTopY + s.r1 * rowH;
    bakeCreviceClamp(getFloorX(s.col, sy0), sy0, getFloorX(s.col, sy1), sy1);
  }
  ctx.stroke();

  // Catapult runway guidance light sockets
  ctx.fillStyle = '#05070c';
  for (let x = 100; x <= 1500; x += 40) {
    ctx.fillRect(x - 5, catapultY - 2.5, 10, 5);
  }

  ctx.restore();
}

function drawHighDetailArenaDeck(ctx, t, visibleLeft = 0, visibleRight = WIDTH) {
  ctx.save();

  const floorTopY = 208;
  const floorBottomY = LANE_BOTTOM; // 496
  const floorH = floorBottomY - floorTopY; // 288
  const plateRows = 9;
  const rowH = floorH / plateRows; // 32
  const colW = 90;
  const catapultY = floorTopY + 4 * rowH; // 336 (exact lane center)

  function getFloorX(col, py) {
    const depthT = (py - floorTopY) / floorH;
    return col + (col - 800) * 0.34 * depthT;
  }

  // 1. Fast GPU Blit of pre-baked static deck base
  const staticDeck = getStaticDeckBaseCanvas(visibleLeft, visibleRight);
  if (staticDeck) {
    ctx.drawImage(staticDeck, visibleLeft, 0);
  } else {
    bakeStaticDeckBase(ctx, visibleLeft, visibleRight);
  }

  // 7. Illuminated Deck Plate Crevices & Fiber-Optic Energy Channels (Sci-Fi Conduit Network)
  const raceways = [
    { y: floorTopY + 2 * rowH, x1: 220, x2: 580, col: SW.cyanPower,  type: 'cyan' },
    { y: floorTopY + 6 * rowH, x1: 650, x2: 1040, col: SW.amberGlow, type: 'amber' },
    { y: floorTopY + 2 * rowH, x1: 1080, x2: 1380, col: SW.cyanPower, type: 'cyan' }
  ];

  for (let rw = 0; rw < raceways.length; rw++) {
    const r = raceways[rw];
    // Dynamic data flow core
    const laserPulse = 0.5 + 0.5 * Math.sin(t * 3.0 + rw * 1.5);
    const isCyan = (r.type === 'cyan');

    // Diffuse outer bloom halo
    if (state.quality !== 'low') {
      const halation = getDeckHalationFactor(t);
      const halationScale = 1.0 + halation * 0.60;
      ctx.strokeStyle = isCyan
        ? `rgba(56, 189, 248, ${(laserPulse * 0.22 * halationScale).toFixed(3)})`
        : `rgba(245, 158, 11, ${(laserPulse * 0.25 * halationScale).toFixed(3)})`;
      ctx.lineWidth = (state.quality === 'high' ? 4.0 : 3.2) * halationScale;
      ctx.beginPath();
      ctx.moveTo(r.x1, r.y);
      ctx.lineTo(r.x2, r.y);
      ctx.stroke();
    }

    ctx.strokeStyle = r.col;
    ctx.globalAlpha = 0.65 + laserPulse * 0.35;
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.moveTo(r.x1, r.y);
    ctx.lineTo(r.x2, r.y);
    ctx.stroke();
    ctx.globalAlpha = 1.0;

    // Incandescent white-hot center filament
    if (state.quality !== 'low') {
      ctx.strokeStyle = isCyan ? 'rgba(224, 242, 254, 0.85)' : 'rgba(254, 243, 199, 0.85)';
      ctx.lineWidth = 0.65;
      ctx.beginPath();
      ctx.moveTo(r.x1, r.y);
      ctx.lineTo(r.x2, r.y);
      ctx.stroke();

      // Traveling data packet sweep along raceway
      const rwLen = r.x2 - r.x1;
      const travelT = ((t * 0.5 + rw * 0.33) % 1.0 + 1.0) % 1.0;
      const pX = r.x1 + rwLen * travelT;
      const pLen = 22;
      ctx.strokeStyle = isCyan ? 'rgba(186, 230, 253, 0.95)' : 'rgba(254, 240, 138, 0.95)';
      ctx.lineWidth = 2.2;
      ctx.beginPath();
      ctx.moveTo(Math.max(r.x1, pX - pLen * 0.5), r.y);
      ctx.lineTo(Math.min(r.x2, pX + pLen * 0.5), r.y);
      ctx.stroke();
    }
  }

  // Draw the full network of glowing plate crevices (horizontal, vertical, junctions)
  ctx.lineCap = 'round';
  for (let i = 0; i < DECK_CREVICE_H_SEAMS.length; i++) {
    const s = DECK_CREVICE_H_SEAMS[i];
    const sy = floorTopY + s.r * rowH;
    drawGlowingSeamSegment(ctx, s.x0, sy, s.x1, sy, s.type, s.speed, s.phase, t, state.quality);
  }

  for (let i = 0; i < DECK_CREVICE_V_SEAMS.length; i++) {
    const s = DECK_CREVICE_V_SEAMS[i];
    const sy0 = floorTopY + s.r0 * rowH;
    const sy1 = floorTopY + s.r1 * rowH;
    const sx0 = getFloorX(s.col, sy0);
    const sx1 = getFloorX(s.col, sy1);
    drawGlowingSeamSegment(ctx, sx0, sy0, sx1, sy1, s.type, s.speed, s.phase, t, state.quality);
  }

  for (let i = 0; i < DECK_CREVICE_JUNCTIONS.length; i++) {
    const j = DECK_CREVICE_JUNCTIONS[i];
    const jy = floorTopY + j.r * rowH;
    const jx = getFloorX(j.col, jy);
    drawCreviceJunctionNode(ctx, jx, jy, j.type, t, state.quality);
  }

  // 3D Volumetric Kinetic Catapult Launch Shuttle (Bidirectional Ping-Pong Rebound)
  const catapultStartX = 50;
  const catapultEndX = WIDTH - catapultStartX; // 1550
  const trackMinX = catapultStartX + 43; // 93
  const trackMaxX = catapultEndX - 43;   // 1507
  const trackDist = trackMaxX - trackMinX; // 1414 px
  const shuttleSpeed = 280; // px/sec
  const tOneWay = trackDist / shuttleSpeed; // ~5.05 sec
  const tCycle = tOneWay * 2.0; // ~10.10 sec

  // Phase in [0, tCycle)
  const cycleTime = ((t % tCycle) + tCycle) % tCycle;
  const isForward = cycleTime < tOneWay;
  const legTime = isForward ? cycleTime : (cycleTime - tOneWay);
  const s = legTime / tOneWay; // [0, 1] for current leg

  // Elastic bumper turnaround profile (k = 0.04 -> ~0.18s spring deceleration & rebound)
  const k = 0.04;
  let legProgress;
  if (s < k) {
    legProgress = (s * s) / (2 * k);
  } else if (s <= 1.0 - k) {
    legProgress = s - k / 2;
  } else {
    const delta = 1.0 - s;
    legProgress = (1.0 - k) - (delta * delta) / (2 * k);
  }
  legProgress = legProgress / (1.0 - k);

  const shuttleDir = isForward ? 1 : -1;
  const shuttleX = isForward
    ? (trackMinX + legProgress * trackDist)
    : (trackMaxX - legProgress * trackDist);

  // Impact compression calculation (smooth 0..1..0 curve during bumper contact)
  const impactWindow = 0.07;
  let westBounce = 0;
  let eastBounce = 0;

  if (isForward) {
    if (s < impactWindow) {
      westBounce = 1.0 - s / impactWindow; // rebounding away from west bumper
    } else if (s > 1.0 - impactWindow) {
      eastBounce = (s - (1.0 - impactWindow)) / impactWindow; // compressing into east bumper
    }
  } else {
    if (s < impactWindow) {
      eastBounce = 1.0 - s / impactWindow; // rebounding away from east bumper
    } else if (s > 1.0 - impactWindow) {
      westBounce = (s - (1.0 - impactWindow)) / impactWindow; // compressing into west bumper
    }
  }

  // A. West Hydraulic Arrestor Bumper Assembly (anchored under base at x = 50..75)
  const westPistonX = (catapultStartX + 20) - westBounce * 4.5;
  // Steel Mount Housing anchored to bulkhead
  ctx.fillStyle = '#0f172a';
  ctx.fillRect(catapultStartX, catapultY - 6, 8, 12);
  ctx.fillStyle = '#334155';
  ctx.fillRect(catapultStartX + 2, catapultY - 5, 5, 10);
  // Chrome Telescopic Hydraulic Piston Shaft
  ctx.fillStyle = '#94a3b8';
  ctx.fillRect(catapultStartX + 8, catapultY - 2, Math.max(0, westPistonX - (catapultStartX + 8)), 4);
  ctx.fillStyle = '#e2e8f0';
  ctx.fillRect(catapultStartX + 8, catapultY - 2, Math.max(0, westPistonX - (catapultStartX + 8)), 1);
  // Rubberized Magnetic Impact Stop Head
  ctx.fillStyle = '#090d16';
  ctx.fillRect(westPistonX, catapultY - 5, 5, 10);
  ctx.fillStyle = '#f59e0b';
  ctx.fillRect(westPistonX + 3.5, catapultY - 4.5, 1.5, 9); // Amber hazard pad

  // B. East Hydraulic Arrestor Bumper Assembly (anchored under base at x = 1525..1550)
  const eastPistonX = (catapultEndX - 20) + eastBounce * 4.5;
  // Steel Mount Housing anchored to bulkhead
  ctx.fillStyle = '#0f172a';
  ctx.fillRect(catapultEndX - 8, catapultY - 6, 8, 12);
  ctx.fillStyle = '#334155';
  ctx.fillRect(catapultEndX - 7, catapultY - 5, 5, 10);
  // Chrome Telescopic Hydraulic Piston Shaft
  ctx.fillStyle = '#94a3b8';
  ctx.fillRect(eastPistonX, catapultY - 2, Math.max(0, (catapultEndX - 8) - eastPistonX), 4);
  ctx.fillStyle = '#e2e8f0';
  ctx.fillRect(eastPistonX, catapultY - 2, Math.max(0, (catapultEndX - 8) - eastPistonX), 1);
  // Rubberized Magnetic Impact Stop Head
  ctx.fillStyle = '#090d16';
  ctx.fillRect(eastPistonX - 5, catapultY - 5, 5, 10);
  ctx.fillStyle = '#f59e0b';
  ctx.fillRect(eastPistonX - 5, catapultY - 4.5, 1.5, 9); // Amber hazard pad

  // C. Kinetic Rebound Arc Flash & Impact Sparks
  if (state.quality !== 'low') {
    if (westBounce > 0.12) {
      const alpha = Math.sin(westBounce * Math.PI * 0.5);
      ctx.fillStyle = `rgba(251, 191, 36, ${alpha * 0.8})`;
      ctx.beginPath();
      ctx.arc(westPistonX + 5, catapultY, 10 * alpha, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = `rgba(255, 255, 255, ${alpha * 0.95})`;
      ctx.beginPath();
      ctx.arc(westPistonX + 5, catapultY, 4.5 * alpha, 0, Math.PI * 2);
      ctx.fill();
      // Kinetic spark streaks shooting Eastward
      ctx.strokeStyle = '#fef08a';
      ctx.lineWidth = 1.0;
      ctx.beginPath();
      ctx.moveTo(westPistonX + 5, catapultY - 2);
      ctx.lineTo(westPistonX + 17, catapultY - 5);
      ctx.moveTo(westPistonX + 5, catapultY + 2);
      ctx.lineTo(westPistonX + 16, catapultY + 6);
      ctx.moveTo(westPistonX + 5, catapultY);
      ctx.lineTo(westPistonX + 19, catapultY + 1);
      ctx.stroke();
    }

    if (eastBounce > 0.12) {
      const alpha = Math.sin(eastBounce * Math.PI * 0.5);
      ctx.fillStyle = `rgba(251, 191, 36, ${alpha * 0.8})`;
      ctx.beginPath();
      ctx.arc(eastPistonX - 5, catapultY, 10 * alpha, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = `rgba(255, 255, 255, ${alpha * 0.95})`;
      ctx.beginPath();
      ctx.arc(eastPistonX - 5, catapultY, 4.5 * alpha, 0, Math.PI * 2);
      ctx.fill();
      // Kinetic spark streaks shooting Westward
      ctx.strokeStyle = '#fef08a';
      ctx.lineWidth = 1.0;
      ctx.beginPath();
      ctx.moveTo(eastPistonX - 5, catapultY - 2);
      ctx.lineTo(eastPistonX - 17, catapultY - 5);
      ctx.moveTo(eastPistonX - 5, catapultY + 2);
      ctx.lineTo(eastPistonX - 16, catapultY + 6);
      ctx.moveTo(eastPistonX - 5, catapultY);
      ctx.lineTo(eastPistonX - 19, catapultY + 1);
      ctx.stroke();
    }
  }

  // D. 3D Faceted Kinetic Shuttle (Oriented along shuttleDir)
  ctx.save();
  ctx.translate(shuttleX, catapultY);
  if (shuttleDir < 0) {
    ctx.scale(-1, 1);
  }

  // 1. Shuttle Drop Shadow cast onto trench bed
  ctx.fillStyle = 'rgba(0, 0, 0, 0.82)';
  ctx.fillRect(-20, -5, 42, 10);

  // 2. Dynamic High-Speed Thruster Plasma Wake Trail (behind rear at x < -18)
  if (state.quality !== 'low') {
    const thrustLen = 15 + 7 * Math.sin(t * 26.0);
    const thrustGrad = ctx.createLinearGradient(0, 0, -thrustLen, 0);
    thrustGrad.addColorStop(0.0, 'rgba(251, 191, 36, 0.95)');
    thrustGrad.addColorStop(0.35, 'rgba(245, 158, 11, 0.65)');
    thrustGrad.addColorStop(0.7, 'rgba(56, 189, 248, 0.40)');
    thrustGrad.addColorStop(1.0, 'rgba(56, 189, 248, 0.0)');

    ctx.fillStyle = thrustGrad;
    ctx.beginPath();
    ctx.moveTo(-18, -2.5);
    ctx.lineTo(-18 - thrustLen, 0);
    ctx.lineTo(-18, 2.5);
    ctx.closePath();
    ctx.fill();
  }

  // 3. Shuttle 3D Faceted Body (Aerodynamic wedge nose facing forward +x)
  // Lit Upper Chamfer Facet
  ctx.fillStyle = '#5a657a';
  ctx.beginPath();
  ctx.moveTo(-18, -4);
  ctx.lineTo(14, -4);
  ctx.lineTo(22, 0);
  ctx.lineTo(-18, 0);
  ctx.closePath();
  ctx.fill();

  // Shaded Lower Hull Flank
  ctx.fillStyle = '#262d3a';
  ctx.beginPath();
  ctx.moveTo(-18, 0);
  ctx.lineTo(22, 0);
  ctx.lineTo(14, 4);
  ctx.lineTo(-18, 4);
  ctx.closePath();
  ctx.fill();

  // Specular Ridge Line
  ctx.strokeStyle = '#e2e8f0';
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  ctx.moveTo(-18, 0);
  ctx.lineTo(22, 0);
  ctx.stroke();

  // Forward Grapple Coupling & Cyan Optical Tracking Sensor
  ctx.fillStyle = '#94a3b8';
  ctx.fillRect(16, -2, 5, 4);
  ctx.fillStyle = '#38bdf8';
  ctx.fillRect(20, -1, 2, 2);

  // Twin Glowing Linear Induction Drive Cores (Pulsing Amber)
  const corePulse = 0.7 + 0.3 * Math.sin(t * 12.0);
  ctx.fillStyle = `rgba(245, 158, 11, ${corePulse})`;
  ctx.fillRect(-14, -3, 8, 2);
  ctx.fillRect(-14, 1, 8, 2);
  ctx.fillStyle = '#fbbf24';
  ctx.fillRect(-12, -2.5, 4, 1);
  ctx.fillRect(-12, 1.5, 4, 1);

  ctx.restore();

  // Running Amber Guidance Strobe Bars with traveling wave pulse aligned to motion
  const lightSpacing = 40;
  const pulseSpeed = 16.0;
  const runOffset = ((t * pulseSpeed * shuttleDir) % lightSpacing + lightSpacing) % lightSpacing;

  for (let x = 100; x <= 1500; x += lightSpacing) {
    const lx = x + runOffset;
    if (lx > 1500) continue;

    const wave = (Math.sin(t * 3.5 - shuttleDir * (lx / WIDTH) * 6.0) + 1.0) * 0.5;
    const alpha = 0.4 + wave * 0.6;

    // Glowing emitter lens
    ctx.fillStyle = `rgba(245, 158, 11, ${alpha})`;
    ctx.fillRect(lx - 4, catapultY - 2, 8, 4);

    if (state.quality === 'high') {
      ctx.fillStyle = `rgba(251, 191, 36, ${alpha * 0.3})`;
      ctx.fillRect(lx - 10, catapultY - 4, 20, 8);
    }
  }

  // 5. Dynamic Specular Glints catching Sunset
  if (state.quality === 'high') {
    // Occasional Specular Glints catching the Sunset
    if (state.quality === 'high') {
      const glintCount = 16;
      for (let g = 0; g < glintCount; g++) {
        const gxNorm = ((g * 137 + 43) % 1000) / 1000;
        const gyNorm = ((g * 229 + 17) % 1000) / 1000;
        const gx = visibleLeft + 60 + gxNorm * Math.max(100, (visibleRight - visibleLeft) - 120);
        const gy = floorTopY + 10 + gyNorm * (floorH - 20);
        const period = 7.0 + (g % 5) * 0.8;
        const phase = (t + g * 0.5) % period;
        if (phase < 0.4) {
          const prog = phase / 0.4;
          const glintAlpha = Math.sin(prog * Math.PI) * 0.65;
          ctx.fillStyle = `rgba(255, 255, 255, ${glintAlpha})`;
          ctx.fillRect(gx - 0.5, gy - 0.5, 1.5, 1.5);
          ctx.fillStyle = `rgba(254, 215, 170, ${glintAlpha * 0.5})`;
          ctx.fillRect(gx - 2, gy, 4, 0.8);
          ctx.fillRect(gx, gy - 2, 0.8, 4);
        }
      }
    }
  }

  ctx.restore();
}




// ---------------------------------------------------------------------------
// VORDERKANTE & VENTRAL PROW: SCULPTED 3D ASYMMETRIC PERIMETER (y: 484 .. 540)
// Features: Recessed Bays, Protruding Balconies, Outrigger Sponsons,
// Railings vs. Open Drop-Offs ("gar kein Geländer"), Draped Cables & Sparks
// ---------------------------------------------------------------------------

function drawHazardStripes(ctx, x, y, w, h, angleDir) {
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();

  ctx.fillStyle = '#141a24';
  ctx.fillRect(x, y, w, h);

  ctx.strokeStyle = '#f59e0b';
  ctx.lineWidth = 4.0;
  const dir = (angleDir === 'left') ? -1 : 1;
  const step = 8;
  for (let sx = x - h * 1.5; sx <= x + w + h * 1.5; sx += step) {
    ctx.beginPath();
    ctx.moveTo(sx, y);
    ctx.lineTo(sx + dir * h * 1.2, y + h);
    ctx.stroke();
  }
  ctx.restore();
}

function drawHazardStripesAlongSegment(ctx, x0, y0, x1, y1, h, angleDir) {
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(x0, y0);
  ctx.lineTo(x1, y1);
  ctx.lineTo(x1, y1 + h);
  ctx.lineTo(x0, y0 + h);
  ctx.closePath();
  ctx.clip();

  ctx.fillStyle = '#141a24';
  ctx.fill();

  ctx.strokeStyle = '#f59e0b';
  ctx.lineWidth = 4.0;
  const dir = (angleDir === 'left') ? -1 : 1;
  const step = 8;
  const minX = Math.min(x0, x1);
  const maxX = Math.max(x0, x1);
  const minY = Math.min(y0, y1);
  const maxY = Math.max(y0, y1);
  for (let sx = minX - h * 2.5; sx <= maxX + h * 2.5; sx += step) {
    ctx.beginPath();
    ctx.moveTo(sx, minY - 2);
    ctx.lineTo(sx + dir * (maxY - minY + h) * 1.2, maxY + h + 2);
    ctx.stroke();
  }
  ctx.restore();
}

function drawIndustrialRailing(ctx, x0, y0, x1, y1) {
  // Support both (ctx, x0, y0, x1, y1) and legacy (ctx, x0, x1, yFloor)
  if (arguments.length === 4 || typeof y1 === 'undefined') {
    const yFloor = y0;
    const xEnd = x1;
    y0 = yFloor;
    x1 = xEnd;
    y1 = yFloor;
  }
  const dx = x1 - x0;
  const dy = y1 - y0;
  const len = Math.hypot(dx, dy);
  if (len <= 8) return;

  const yHandrailOffset = -15.5;
  const yMidrailOffset = -7.5;

  // 1. Solid Kickplate / Toe Board along deck edge
  ctx.strokeStyle = '#101520';
  ctx.lineWidth = 3.0;
  ctx.beginPath();
  ctx.moveTo(x0, y0 - 1.5);
  ctx.lineTo(x1, y1 - 1.5);
  ctx.stroke();

  ctx.strokeStyle = '#475569';
  ctx.lineWidth = 0.9;
  ctx.beginPath();
  ctx.moveTo(x0, y0 - 3.0);
  ctx.lineTo(x1, y1 - 3.0);
  ctx.stroke();

  // 2. Intermediate Knee-Rail Pipe
  ctx.strokeStyle = '#334155';
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(x0, y0 + yMidrailOffset);
  ctx.lineTo(x1, y1 + yMidrailOffset);
  ctx.stroke();

  // 3. Top Tubular Handrail Pipe with Specular Highlight
  ctx.strokeStyle = '#1e293b';
  ctx.lineWidth = 2.4;
  ctx.beginPath();
  ctx.moveTo(x0, y0 + yHandrailOffset);
  ctx.lineTo(x1, y1 + yHandrailOffset);
  ctx.stroke();

  ctx.strokeStyle = '#cbd5e1';
  ctx.lineWidth = 1.3;
  ctx.beginPath();
  ctx.moveTo(x0, y0 + yHandrailOffset - 0.4);
  ctx.lineTo(x1, y1 + yHandrailOffset - 0.4);
  ctx.stroke();

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
  ctx.lineWidth = 0.6;
  ctx.beginPath();
  ctx.moveTo(x0, y0 + yHandrailOffset - 0.8);
  ctx.lineTo(x1, y1 + yHandrailOffset - 0.8);
  ctx.stroke();

  // 4. Vertical Stanchions (Posts)
  const numPosts = Math.max(2, Math.round(len / 22));
  for (let p = 0; p < numPosts; p++) {
    const frac = p / (numPosts - 1);
    const px = x0 + frac * dx;
    const py = y0 + frac * dy;
    const pTopY = py + yHandrailOffset;

    // Stanchion Base Mounting Flange
    ctx.fillStyle = '#0a0e17';
    ctx.fillRect(px - 3.5, py - 1.5, 7, 3.0);
    ctx.fillStyle = '#94a3b8';
    ctx.fillRect(px - 2.8, py - 1.2, 1.4, 1.4);
    ctx.fillRect(px + 1.4, py - 1.2, 1.4, 1.4);

    // Stanchion Post
    ctx.fillStyle = '#0a0e17';
    ctx.fillRect(px - 1.4, pTopY, 2.8, 15.5);
    ctx.fillStyle = '#334155';
    ctx.fillRect(px - 0.8, pTopY, 1.6, 15.5);
    ctx.fillStyle = '#cbd5e1';
    ctx.fillRect(px - 0.8, pTopY, 0.7, 15.5);

    // End-Stanchion Diagonal Braces & Safety Plates at railing terminations
    if (p === 0 || p === numPosts - 1) {
      const braceDir = (p === 0) ? 1 : -1;
      ctx.strokeStyle = '#64748b';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(px, pTopY + 2.5);
      ctx.lineTo(px + braceDir * 7, py);
      ctx.stroke();

      // Bright yellow safety tag on end post
      ctx.fillStyle = '#f59e0b';
      ctx.fillRect(px - 2.5, pTopY + 3.5, 5, 3.5);
    }
  }
}

function drawDrapedCable(ctx, x0, y0, xRim, yRim, xDrop, yDrop, thickness, color, stripeColor, sway) {
  const ctrlX = xRim + (xDrop - xRim) * 0.35 + sway;
  const ctrlY = yDrop + 2;

  // 1. Dark Cast Drop Shadow
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.75)';
  ctx.lineWidth = thickness + 2.0;
  ctx.beginPath();
  ctx.moveTo(x0, y0 + 1.5);
  ctx.lineTo(xRim, yRim + 1.5);
  ctx.quadraticCurveTo(ctrlX, ctrlY + 2.0, xDrop, yDrop + 2.0);
  ctx.stroke();

  // 2. Solid Cable Jacket Core
  ctx.strokeStyle = color;
  ctx.lineWidth = thickness;
  ctx.beginPath();
  ctx.moveTo(x0, y0);
  ctx.lineTo(xRim, yRim);
  ctx.quadraticCurveTo(ctrlX, ctrlY, xDrop, yDrop);
  ctx.stroke();

  // 3. Specular Ridge Highlight
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.38)';
  ctx.lineWidth = Math.max(0.6, thickness * 0.25);
  ctx.beginPath();
  ctx.moveTo(x0, y0 - 0.4);
  ctx.lineTo(xRim, yRim - 0.4);
  ctx.quadraticCurveTo(ctrlX - 0.4, ctrlY - 0.6, xDrop - 0.4, yDrop - 0.4);
  ctx.stroke();

  // 4. Terminal Metal Weight / Connector Sleeve at cable end
  ctx.fillStyle = '#475569';
  ctx.fillRect(xDrop - thickness * 0.7, yDrop - 2, thickness * 1.4, 5);
  ctx.fillStyle = '#e2e8f0';
  ctx.fillRect(xDrop - thickness * 0.7, yDrop - 1, 0.8, 3);

  // 5. Optional colored identification zip-ties
  if (stripeColor) {
    ctx.strokeStyle = stripeColor;
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.moveTo(xRim - 1, yRim + 3);
    ctx.lineTo(xRim + 1, yRim + 3);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(ctrlX - 1, (yRim + yDrop) * 0.5);
    ctx.lineTo(ctrlX + 1, (yRim + yDrop) * 0.5);
    ctx.stroke();
  }
}

function drawOutriggerSponson(ctx, rx, topW, botW, topY, botY, side, t) {
  const isWest = (side === 'west');
  const xTL = rx - topW * 0.5;
  const xTR = rx + topW * 0.5;
  const xBL = rx - botW * 0.5;
  const xBR = rx + botW * 0.5;

  const sideDepthTop = 16;
  const sideDepthBot = 22;

  if (isWest) {
    // Right 3D Perspective Side Wall & Heavy Shadow to the right
    ctx.fillStyle = 'rgba(2, 5, 10, 0.85)';
    ctx.beginPath();
    ctx.moveTo(xTR, topY + 2);
    ctx.lineTo(xTR + sideDepthTop + 16, topY + 6);
    ctx.lineTo(xBR + sideDepthBot + 20, botY);
    ctx.lineTo(xBR, botY);
    ctx.closePath();
    ctx.fill();

    const sideGrad = ctx.createLinearGradient(xTR, 0, xTR + sideDepthBot, 0);
    sideGrad.addColorStop(0.0, '#1c2432');
    sideGrad.addColorStop(0.5, '#121822');
    sideGrad.addColorStop(1.0, '#070a0f');
    ctx.fillStyle = sideGrad;
    ctx.beginPath();
    ctx.moveTo(xTR, topY);
    ctx.lineTo(xTR + sideDepthTop, topY + 4);
    ctx.lineTo(xBR + sideDepthBot, botY);
    ctx.lineTo(xBR, botY);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#2d3748';
    ctx.lineWidth = 1.0;
    ctx.beginPath();
    ctx.moveTo(xTR + 2, topY + 5);
    ctx.lineTo(xBR + sideDepthBot - 3, botY - 3);
    ctx.stroke();
  } else {
    // Left 3D Perspective Side Wall & Heavy Shadow to the left
    ctx.fillStyle = 'rgba(2, 5, 10, 0.85)';
    ctx.beginPath();
    ctx.moveTo(xTL, topY + 2);
    ctx.lineTo(xTL - sideDepthTop - 16, topY + 6);
    ctx.lineTo(xBL - sideDepthBot - 20, botY);
    ctx.lineTo(xBL, botY);
    ctx.closePath();
    ctx.fill();

    const sideGrad = ctx.createLinearGradient(xTL, 0, xTL - sideDepthBot, 0);
    sideGrad.addColorStop(0.0, '#1c2432');
    sideGrad.addColorStop(0.5, '#121822');
    sideGrad.addColorStop(1.0, '#070a0f');
    ctx.fillStyle = sideGrad;
    ctx.beginPath();
    ctx.moveTo(xTL, topY);
    ctx.lineTo(xTL - sideDepthTop, topY + 4);
    ctx.lineTo(xBL - sideDepthBot, botY);
    ctx.lineTo(xBL, botY);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#2d3748';
    ctx.lineWidth = 1.0;
    ctx.beginPath();
    ctx.moveTo(xTL - 2, topY + 5);
    ctx.lineTo(xBL - sideDepthBot + 3, botY - 3);
    ctx.stroke();
  }

  // Front Armor Face
  const frontGrad = ctx.createLinearGradient(0, topY, 0, botY);
  frontGrad.addColorStop(0.0, '#616c80');
  frontGrad.addColorStop(0.4, '#485262');
  frontGrad.addColorStop(1.0, '#272f3d');
  ctx.fillStyle = frontGrad;

  ctx.beginPath();
  ctx.moveTo(xTL, topY);
  ctx.lineTo(xTR, topY);
  ctx.lineTo(xBR, botY);
  ctx.lineTo(xBL, botY);
  ctx.closePath();
  ctx.fill();

  // Top specular chamfer
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)';
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(xTL, topY + 0.5);
  ctx.lineTo(xTR, topY + 0.5);
  ctx.stroke();

  // Hazard Chevrons on lower nose
  drawHazardStripes(ctx, xBL + 2, botY - 14, botW - 4, 10, isWest ? 'right' : 'left');

  // Hex corner bolts
  const bolts = [
    { x: xTL + 5, y: topY + 5 },
    { x: xTR - 10, y: topY + 5 },
    { x: xBL + 5, y: botY - 18 },
    { x: xBR - 10, y: botY - 18 }
  ];
  for (let b = 0; b < bolts.length; b++) {
    const bc = bolts[b];
    ctx.fillStyle = '#05070c';
    ctx.fillRect(bc.x, bc.y, 5, 5);
    ctx.fillStyle = '#94a3b8';
    ctx.fillRect(bc.x + 0.8, bc.y + 0.8, 3.4, 3.4);
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(bc.x + 1.2, bc.y + 1.2, 1.4, 1.4);
  }

  // Forward Navigation Strobe
  const isStrobeActive = Math.floor((t * 2.0) % 2) === 0 && ((t * 8.0) % 1.0 < 0.22);
  const strobeY = botY - 2;
  const strobeColor = isWest ? '#ef4444' : '#22c55e';
  ctx.fillStyle = '#070b12';
  ctx.fillRect(rx - 3, strobeY - 3, 6, 6);
  ctx.fillStyle = strobeColor;
  ctx.fillRect(rx - 2, strobeY - 2, 4, 4);
  if (isStrobeActive && state.quality !== 'low') {
    ctx.fillStyle = isWest ? 'rgba(239, 68, 68, 0.55)' : 'rgba(34, 197, 94, 0.55)';
    ctx.beginPath();
    ctx.arc(rx, strobeY, 11, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawCenterKeelWedge(ctx, rx, topW, botW, topY, botY, t) {
  const xTL = rx - topW * 0.5;
  const xTR = rx + topW * 0.5;
  const xBL = rx - botW * 0.5;
  const xBR = rx + botW * 0.5;

  // Left Facet (Direct Sunset Alpenglow reflection)
  const leftGrad = ctx.createLinearGradient(xTL, 0, rx, 0);
  leftGrad.addColorStop(0.0, '#4b5569');
  leftGrad.addColorStop(0.5, '#64748b');
  leftGrad.addColorStop(1.0, '#94a3b8');
  ctx.fillStyle = leftGrad;
  ctx.beginPath();
  ctx.moveTo(xTL, topY);
  ctx.lineTo(rx, topY - 3);
  ctx.lineTo(rx, botY + 3);
  ctx.lineTo(xBL, botY);
  ctx.closePath();
  ctx.fill();

  // Right Facet (Deep Navy Shadow)
  const rightGrad = ctx.createLinearGradient(rx, 0, xTR, 0);
  rightGrad.addColorStop(0.0, '#3e4758');
  rightGrad.addColorStop(0.5, '#293241');
  rightGrad.addColorStop(1.0, '#1a2230');
  ctx.fillStyle = rightGrad;
  ctx.beginPath();
  ctx.moveTo(rx, topY - 3);
  ctx.lineTo(xTR, topY);
  ctx.lineTo(xBR, botY);
  ctx.lineTo(rx, botY + 3);
  ctx.closePath();
  ctx.fill();

  // Knife-Edge Specular Ridge
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.moveTo(rx, topY - 3);
  ctx.lineTo(rx, botY + 3);
  ctx.stroke();

  // Center Hazard Chevrons
  drawHazardStripes(ctx, rx - 20, botY - 16, 40, 12, 'right');

  // Center Xenon White Flasher
  const isStrobeActive = Math.floor((t * 2.0) % 2) === 0 && ((t * 8.0) % 1.0 < 0.22);
  const strobeY = botY + 1;
  ctx.fillStyle = '#0f172a';
  ctx.fillRect(rx - 3, strobeY - 3, 6, 6);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(rx - 2, strobeY - 2, 4, 4);
  if (isStrobeActive && state.quality !== 'low') {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.65)';
    ctx.beginPath();
    ctx.arc(rx, strobeY, 14, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawSingleArticulatedEdgePlate(ctx, x0, y0, x1, y1) {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const len = Math.hypot(dx, dy);
  const plateThickness = 5.0;

  // Front/beveled thickness face of the plate
  ctx.beginPath();
  ctx.moveTo(x0, y0);
  ctx.lineTo(x1, y1);
  ctx.lineTo(x1, y1 + plateThickness);
  ctx.lineTo(x0, y0 + plateThickness);
  ctx.closePath();

  const isWest = (x0 < 800);
  const plateGrad = ctx.createLinearGradient(0, Math.min(y0, y1), 0, Math.max(y0, y1) + plateThickness);
  if (isWest) {
    plateGrad.addColorStop(0.0, '#5a687d');
    plateGrad.addColorStop(0.4, '#3e4959');
    plateGrad.addColorStop(1.0, '#1c2430');
  } else {
    plateGrad.addColorStop(0.0, '#2d3746');
    plateGrad.addColorStop(0.4, '#202834');
    plateGrad.addColorStop(1.0, '#0d131c');
  }
  ctx.fillStyle = plateGrad;
  ctx.fill();

  // Top metallic specular chamfer line
  ctx.strokeStyle = isWest ? 'rgba(255, 245, 235, 0.82)' : 'rgba(255, 255, 255, 0.55)';
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  ctx.moveTo(x0, y0 + 0.5);
  ctx.lineTo(x1, y1 + 0.5);
  ctx.stroke();

  // Bottom drip-edge shadow
  ctx.strokeStyle = '#04060a';
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(x0, y0 + plateThickness);
  ctx.lineTo(x1, y1 + plateThickness);
  ctx.stroke();

  // Deep volumetric cast shadow thrown downward onto lower hull / trench
  const minY = Math.min(y0, y1);
  const maxY = Math.max(y0, y1);
  const castShadow = ctx.createLinearGradient(0, minY + plateThickness, 0, maxY + plateThickness + 12);
  castShadow.addColorStop(0.0, 'rgba(2, 4, 8, 0.88)');
  castShadow.addColorStop(1.0, 'rgba(2, 4, 8, 0.0)');
  ctx.fillStyle = castShadow;
  ctx.beginPath();
  ctx.moveTo(x0, y0 + plateThickness);
  ctx.lineTo(x1, y1 + plateThickness);
  ctx.lineTo(x1, y1 + plateThickness + 10);
  ctx.lineTo(x0, y0 + plateThickness + 10);
  ctx.closePath();
  ctx.fill();

  // Heavy Hex Bolt Fasteners along plate face
  const boltSpacing = 20;
  const numBolts = Math.max(1, Math.floor(len / boltSpacing));
  for (let b = 1; b <= numBolts; b++) {
    const frac = b / (numBolts + 1);
    const bx = x0 + frac * dx;
    const by = y0 + frac * dy + 1.2;

    ctx.fillStyle = '#060a12';
    ctx.fillRect(bx - 1.5, by, 3, 2.8);
    ctx.fillStyle = isWest ? '#cbd5e1' : '#64748b';
    ctx.fillRect(bx - 1.0, by + 0.3, 2, 2);
    if (isWest) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(bx - 0.7, by + 0.3, 0.8, 0.8);
    }
  }
}

function drawArticulatedEdgePlates(ctx, t, visibleLeft = 0, visibleRight = WIDTH, isStaticBake = false) {
  // Left wing extension plate
  if (visibleLeft < 0) {
    drawSingleArticulatedEdgePlate(ctx, visibleLeft, 488, 0, 488);
  }
  // 1. 3D Plate Thickness Riser Facets along each articulated perimeter segment
  for (let s = 0; s < PERIMETER_SEGMENTS.length; s++) {
    const seg = PERIMETER_SEGMENTS[s];
    drawSingleArticulatedEdgePlate(ctx, seg.x0, seg.y0, seg.x1, seg.y1);
  }
  // Right wing extension plate
  if (visibleRight > WIDTH) {
    drawSingleArticulatedEdgePlate(ctx, WIDTH, 490, visibleRight, 490);
  }

  // 2. Heavy Corner Reinforcement Gussets (Knotenbleche) at segment junctions
  for (let s = 0; s < PERIMETER_SEGMENTS.length - 1; s++) {
    const s0 = PERIMETER_SEGMENTS[s];
    const vx = s0.x1;
    const vy = s0.y1;

    ctx.fillStyle = '#111722';
    ctx.beginPath();
    ctx.moveTo(vx - 3.5, vy);
    ctx.lineTo(vx + 3.5, vy);
    ctx.lineTo(vx + 2.5, vy + 5.5);
    ctx.lineTo(vx - 2.5, vy + 5.5);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#475569';
    ctx.lineWidth = 0.8;
    ctx.stroke();

    // Central corner rivet
    ctx.fillStyle = '#cbd5e1';
    ctx.fillRect(vx - 0.8, vy + 2.0, 1.6, 1.6);
  }

  // 3. West Service Berth Equipment & Hazard Lip (x = 85 .. 195, y = 480)
  // Normal deck floor is fully visible behind this edge (no dark backing box!)
  
  // A. Hydraulic Manifold Equipment Box at x = 98, y = 475
  ctx.fillStyle = '#080c14';
  ctx.fillRect(96, 475, 24, 7);
  ctx.fillStyle = '#334155';
  ctx.fillRect(97, 476, 22, 5.5);
  ctx.fillStyle = '#10b981';
  ctx.fillRect(99, 478, 3.5, 2.5); // Green pressure status LED
  ctx.fillStyle = '#f59e0b';
  ctx.fillRect(105, 478, 3.5, 2.5); // Amber hydraulic flow LED

  // B. Automated Robotic Welding Arm Fixture at x = 138, y = 478
  ctx.fillStyle = '#090d16';
  ctx.fillRect(134, 476, 9, 6.5);
  ctx.fillStyle = '#475569';
  ctx.fillRect(135.5, 476.5, 6, 5);
  // Articulated robotic forearm
  ctx.strokeStyle = '#94a3b8';
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  ctx.moveTo(138, 478);
  ctx.lineTo(136, 475);
  ctx.lineTo(138, 473);
  ctx.stroke();
  // Torch nozzle & incandescent arc tip
  ctx.fillStyle = '#fbbf24';
  ctx.fillRect(137, 472, 2.5, 2.5);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(137.5, 472.5, 1.5, 1.5);
  // Pulsating welding arc bloom
  const arcPulse = 0.5 + 0.5 * Math.sin(t * 18.0);
  if (state.quality !== 'low' && arcPulse > 0.4) {
    ctx.fillStyle = `rgba(251, 191, 36, ${arcPulse * 0.45})`;
    ctx.beginPath();
    ctx.arc(138, 473, 7, 0, Math.PI * 2);
    ctx.fill();
  }

  // C. Front Lip with Bold Hazard Striping (GAR KEIN GELÄNDER!)
  drawHazardStripesAlongSegment(ctx, 85, 480, 195, 480, 5.0, 'right');
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.65)';
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  ctx.moveTo(85, 480.5);
  ctx.lineTo(195, 480.5);
  ctx.stroke();

  // 4. East Reactor Fueling Berth Equipment & Hazard Lip (x = 1085 .. 1195, y = 480)
  // Normal deck floor is fully visible behind this edge (no dark backing box!)
  
  // A. High-Pressure Dual Fuel Pipe Manifolds & Gauges at x = 1130, y = 475
  ctx.fillStyle = '#080c14';
  ctx.fillRect(1130, 475, 30, 7);
  ctx.fillStyle = '#1e293b';
  ctx.fillRect(1131, 476, 28, 5.5);
  ctx.fillStyle = '#065f46';
  ctx.fillRect(1134, 477.5, 6, 3); // Digital green fuel flow indicator
  ctx.fillStyle = '#34d399';
  ctx.fillRect(1135, 478, 4, 1.8);
  ctx.fillStyle = '#0284c7';
  ctx.fillRect(1144, 477.5, 6, 3); // Coolant pressure telemetry
  ctx.fillStyle = '#38bdf8';
  ctx.fillRect(1145, 478, 4, 1.8);

  // B. Front Lip with Bold Hazard Striping (GAR KEIN GELÄNDER!)
  drawHazardStripesAlongSegment(ctx, 1085, 480, 1195, 480, 5.0, 'left');
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.65)';
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  ctx.moveTo(1085, 480.5);
  ctx.lineTo(1195, 480.5);
  ctx.stroke();

  // 5. Protruding Main Observation Balcony Platform (x = 585 .. 715, y = 496 .. 530)
  // Cantilever platform floor polygon
  ctx.beginPath();
  ctx.moveTo(585, 496);
  ctx.lineTo(715, 496);
  ctx.lineTo(690, 530);
  ctx.lineTo(610, 530);
  ctx.closePath();

  ctx.fillStyle = '#0e141d';
  ctx.fill();

  // Sunset Abyss Underglow filtering through grating
  const balconyGlow = ctx.createLinearGradient(0, 496, 0, 530);
  balconyGlow.addColorStop(0.0, 'rgba(249, 115, 22, 0.05)');
  balconyGlow.addColorStop(0.7, 'rgba(249, 115, 22, 0.24)');
  balconyGlow.addColorStop(1.0, 'rgba(251, 146, 60, 0.38)');
  ctx.fillStyle = balconyGlow;
  ctx.fill();

  // Grating mesh lines
  ctx.save();
  ctx.clip();
  ctx.strokeStyle = '#273447';
  ctx.lineWidth = 0.8;
  for (let gx = 585; gx <= 715; gx += 6) {
    ctx.beginPath();
    ctx.moveTo(gx, 496);
    ctx.lineTo(gx, 530);
    ctx.stroke();
  }
  for (let gy = 498; gy <= 528; gy += 4) {
    ctx.beginPath();
    ctx.moveTo(585, gy);
    ctx.lineTo(715, gy);
    ctx.stroke();
  }
  ctx.restore();

  // Heavy Outer Channel-Beam Rim Plate
  ctx.strokeStyle = '#475569';
  ctx.lineWidth = 2.4;
  ctx.beginPath();
  ctx.moveTo(585, 496);
  ctx.lineTo(610, 530);
  ctx.lineTo(690, 530);
  ctx.lineTo(715, 496);
  ctx.stroke();

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.80)';
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  ctx.moveTo(610, 530);
  ctx.lineTo(690, 530);
  ctx.stroke();

  // Center Spotlight Housing at x = 650
  ctx.fillStyle = '#0f172a';
  ctx.fillRect(646, 528, 8, 5);
  ctx.fillStyle = '#e2e8f0';
  ctx.fillRect(647.5, 529.5, 5, 2.5);

  // 6. Protruding Starboard Lookout Balcony Platform (x = 1420 .. 1555, y = 488 .. 518)
  ctx.beginPath();
  ctx.moveTo(1420, 488);
  ctx.lineTo(1555, 490);
  ctx.lineTo(1530, 518);
  ctx.lineTo(1445, 518);
  ctx.closePath();

  ctx.fillStyle = '#0e141d';
  ctx.fill();

  const stbGlow = ctx.createLinearGradient(0, 488, 0, 518);
  stbGlow.addColorStop(0.0, 'rgba(249, 115, 22, 0.04)');
  stbGlow.addColorStop(1.0, 'rgba(251, 146, 60, 0.28)');
  ctx.fillStyle = stbGlow;
  ctx.fill();

  ctx.save();
  ctx.clip();
  ctx.strokeStyle = '#273447';
  ctx.lineWidth = 0.8;
  for (let gx = 1420; gx <= 1555; gx += 6) {
    ctx.beginPath();
    ctx.moveTo(gx, 488);
    ctx.lineTo(gx, 518);
    ctx.stroke();
  }
  for (let gy = 490; gy <= 516; gy += 4) {
    ctx.beginPath();
    ctx.moveTo(1420, gy);
    ctx.lineTo(1555, gy);
    ctx.stroke();
  }
  ctx.restore();

  // Rim plate
  ctx.strokeStyle = '#384558';
  ctx.lineWidth = 2.0;
  ctx.beginPath();
  ctx.moveTo(1420, 488);
  ctx.lineTo(1445, 518);
  ctx.lineTo(1530, 518);
  ctx.lineTo(1555, 490);
  ctx.stroke();

  // Starboard green navigation strobe at x = 1528, y = 517
  const isStbStrobe = Math.floor((t * 2.0) % 2) === 0 && ((t * 8.0) % 1.0 < 0.22);
  ctx.fillStyle = '#070b12';
  ctx.fillRect(1526, 515, 6, 6);
  ctx.fillStyle = '#22c55e';
  ctx.fillRect(1527, 516, 4, 4);
  if (!isStaticBake && isStbStrobe && state.quality !== 'low') {
    ctx.fillStyle = 'rgba(34, 197, 94, 0.65)';
    ctx.beginPath();
    ctx.arc(1529, 518, 10, 0, Math.PI * 2);
    ctx.fill();
  }

  // 7. West Heavy Armor Sponson (x = 255 .. 370, y = 490 .. 524)
  // Armored nose plate jutting 34px forward
  const sponsonGrad = ctx.createLinearGradient(255, 0, 370, 0);
  sponsonGrad.addColorStop(0.0, '#5a667a');
  sponsonGrad.addColorStop(0.5, '#424d5e');
  sponsonGrad.addColorStop(1.0, '#1c2432');
  ctx.fillStyle = sponsonGrad;

  ctx.beginPath();
  ctx.moveTo(255, 490);
  ctx.lineTo(370, 488);
  ctx.lineTo(345, 524);
  ctx.lineTo(280, 524);
  ctx.closePath();
  ctx.fill();

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)';
  ctx.lineWidth = 1.0;
  ctx.stroke();

  // Hazard Chevrons on lower nose
  drawHazardStripes(ctx, 285, 516, 55, 8, 'right');

  // Hex corner bolts
  const westSponsonBolts = [
    { x: 265, y: 494 },
    { x: 360, y: 492 },
    { x: 288, y: 520 },
    { x: 337, y: 520 }
  ];
  for (let b = 0; b < westSponsonBolts.length; b++) {
    const bc = westSponsonBolts[b];
    ctx.fillStyle = '#060a12';
    ctx.fillRect(bc.x - 2, bc.y - 2, 4, 4);
    ctx.fillStyle = '#cbd5e1';
    ctx.fillRect(bc.x - 1.2, bc.y - 1.2, 2.4, 2.4);
  }

  // Port red navigation strobe at x = 312, y = 523
  const isPortStrobe = Math.floor((t * 2.0) % 2) === 0 && ((t * 8.0) % 1.0 < 0.22);
  ctx.fillStyle = '#070b12';
  ctx.fillRect(309, 520, 6, 6);
  ctx.fillStyle = '#ef4444';
  ctx.fillRect(310, 521, 4, 4);
  if (!isStaticBake && isPortStrobe && state.quality !== 'low') {
    ctx.fillStyle = 'rgba(239, 68, 68, 0.65)';
    ctx.beginPath();
    ctx.arc(312, 523, 10, 0, Math.PI * 2);
    ctx.fill();
  }

  // 8. East Sensor Sponson (x = 1245 .. 1360, y = 492 .. 522)
  const eastSponsonGrad = ctx.createLinearGradient(1245, 0, 1360, 0);
  eastSponsonGrad.addColorStop(0.0, '#3e495a');
  eastSponsonGrad.addColorStop(0.5, '#293342');
  eastSponsonGrad.addColorStop(1.0, '#131b26');
  ctx.fillStyle = eastSponsonGrad;

  ctx.beginPath();
  ctx.moveTo(1245, 492);
  ctx.lineTo(1360, 488);
  ctx.lineTo(1335, 522);
  ctx.lineTo(1270, 522);
  ctx.closePath();
  ctx.fill();

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
  ctx.lineWidth = 1.0;
  ctx.stroke();

  // Hazard Chevrons
  drawHazardStripes(ctx, 1275, 514, 55, 8, 'left');

  // Hex corner bolts
  const eastSponsonBolts = [
    { x: 1255, y: 495 },
    { x: 1350, y: 492 },
    { x: 1278, y: 518 },
    { x: 1327, y: 518 }
  ];
  for (let b = 0; b < eastSponsonBolts.length; b++) {
    const bc = eastSponsonBolts[b];
    ctx.fillStyle = '#060a12';
    ctx.fillRect(bc.x - 2, bc.y - 2, 4, 4);
    ctx.fillStyle = '#94a3b8';
    ctx.fillRect(bc.x - 1.2, bc.y - 1.2, 2.4, 2.4);
  }

  // Starboard green navigation strobe at x = 1302, y = 521
  ctx.fillStyle = '#070b12';
  ctx.fillRect(1299, 518, 6, 6);
  ctx.fillStyle = '#22c55e';
  ctx.fillRect(1300, 519, 4, 4);
  if (!isStaticBake && isStbStrobe && state.quality !== 'low') {
    ctx.fillStyle = 'rgba(34, 197, 94, 0.65)';
    ctx.beginPath();
    ctx.arc(1302, 521, 10, 0, Math.PI * 2);
    ctx.fill();
  }

  // 9. Center Dreadnought Prow Keel Ram Wedge (x = 745 .. 855, y = 496 .. 544)
  // Left Facet (Direct Sunset Alpenglow reflection)
  const keelLeftGrad = ctx.createLinearGradient(745, 0, 800, 0);
  keelLeftGrad.addColorStop(0.0, '#4b5768');
  keelLeftGrad.addColorStop(0.5, '#68788f');
  keelLeftGrad.addColorStop(1.0, '#9bb0cb');
  ctx.fillStyle = keelLeftGrad;
  ctx.beginPath();
  ctx.moveTo(745, 496);
  ctx.lineTo(800, 496);
  ctx.lineTo(800, 544);
  ctx.closePath();
  ctx.fill();

  // Right Facet (Deep Navy Shadow)
  const keelRightGrad = ctx.createLinearGradient(800, 0, 855, 0);
  keelRightGrad.addColorStop(0.0, '#384353');
  keelRightGrad.addColorStop(0.5, '#222b38');
  keelRightGrad.addColorStop(1.0, '#101620');
  ctx.fillStyle = keelRightGrad;
  ctx.beginPath();
  ctx.moveTo(800, 496);
  ctx.lineTo(855, 496);
  ctx.lineTo(800, 544);
  ctx.closePath();
  ctx.fill();

  // Knife-Edge Specular Ridge along centerline
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  ctx.moveTo(800, 496);
  ctx.lineTo(800, 544);
  ctx.stroke();

  // Center Hazard Chevrons
  drawHazardStripes(ctx, 775, 532, 50, 10, 'right');

  // Center Xenon White Flasher
  const isKeelStrobe = Math.floor((t * 2.0) % 2) === 0 && ((t * 8.0) % 1.0 < 0.22);
  ctx.fillStyle = '#0f172a';
  ctx.fillRect(797, 541, 6, 6);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(798, 542, 4, 4);
  if (!isStaticBake && isKeelStrobe && state.quality !== 'low') {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.75)';
    ctx.beginPath();
    ctx.arc(800, 544, 14, 0, Math.PI * 2);
    ctx.fill();
  }

  // 10. Protruding Maintenance Platform (x = 885 .. 1000, y = 496 .. 512)
  ctx.fillStyle = '#1c2432';
  ctx.fillRect(910, 496, 70, 16);
  drawHazardStripesAlongSegment(ctx, 910, 512, 980, 512, 4.0, 'left');

  // Electrical junction box at x = 945
  ctx.fillStyle = '#0f172a';
  ctx.fillRect(941, 506, 8, 5);
  ctx.fillStyle = '#64748b';
  ctx.fillRect(942.5, 507, 5, 3.5);
  ctx.fillStyle = '#f97316';
  ctx.fillRect(944, 509, 2, 2);

  // 11. Longitudinal Illuminated Cable Raceways (Kabelkanäle along catwalks)
  const racewaySegments = [
    { x0: 210, x1: 255, y: 489 },
    { x0: 370, x1: 445, y: 487 },
    { x0: 715, x1: 745, y: 495 },
    { x0: 855, x1: 885, y: 495 },
    { x0: 1360, x1: 1420, y: 487 }
  ];
  for (let r = 0; r < racewaySegments.length; r++) {
    const rw = racewaySegments[r];
    ctx.fillStyle = '#070a10';
    ctx.fillRect(rw.x0, rw.y, rw.x1 - rw.x0, 3.5);

    // Glowing Fiber-Optic Conduits inside raceway
    const pulseCyan = 0.5 + 0.5 * Math.sin(t * 3.5 + r);
    ctx.strokeStyle = `rgba(56, 189, 248, ${0.4 + pulseCyan * 0.5})`;
    ctx.lineWidth = 1.0;
    ctx.beginPath();
    ctx.moveTo(rw.x0, rw.y + 1.2);
    ctx.lineTo(rw.x1, rw.y + 1.2);
    ctx.stroke();

    const pulseAmber = 0.5 + 0.5 * Math.cos(t * 2.8 + r);
    ctx.strokeStyle = `rgba(245, 158, 11, ${0.35 + pulseAmber * 0.45})`;
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(rw.x0, rw.y + 2.5);
    ctx.lineTo(rw.x1, rw.y + 2.5);
    ctx.stroke();
  }
}

function drawSafetyRailingsAndOpenDrops(ctx, t, visibleLeft = 0, visibleRight = WIDTH) {
  if (visibleLeft < 0) {
    drawIndustrialRailing(ctx, visibleLeft, 488, 0, 488);
  }
  for (let s = 0; s < PERIMETER_SEGMENTS.length; s++) {
    const seg = PERIMETER_SEGMENTS[s];
    if (seg.hasRailing) {
      drawIndustrialRailing(ctx, seg.x0, seg.y0, seg.x1, seg.y1);
    } else if (seg.isOpenDrop) {
      // Bold diagonal hazard chevrons directly on unrailed open drops ("gar kein Geländer"):
      drawHazardStripesAlongSegment(ctx, seg.x0, seg.y0, seg.x1, seg.y1, 5.0, (seg.x0 < 800 ? 'right' : 'left'));
    }
  }
  if (visibleRight > WIDTH) {
    drawIndustrialRailing(ctx, WIDTH, 490, visibleRight, 490);
  }
}

function drawDrapedCables(ctx, t) {
  // Cluster 1: Spilling out of West Service Berth (x = 144 .. 166, y = 480 down to 526)
  const sway1 = Math.sin(t * 2.4) * 1.8;
  drawDrapedCable(ctx, 144, 478, 152, 480, 156, 526, 4.2, '#1e293b', '#f59e0b', sway1);
  drawDrapedCable(ctx, 152, 478, 160, 480, 166, 520, 3.0, '#334155', null, sway1 * 0.8);
  drawDrapedCable(ctx, 158, 478, 165, 480, 163, 514, 2.0, '#475569', null, sway1 * 1.2);

  // Cluster 2: Spilling over West Open Drop (x = 480 .. 515, y = 478 down to 532)
  const sway2 = Math.sin(t * 2.1 + 1.2) * 2.2;
  drawDrapedCable(ctx, 478, 475, 486, 478, 492, 532, 4.8, '#18202c', '#f59e0b', sway2);
  drawDrapedCable(ctx, 484, 475, 494, 478, 504, 526, 3.2, '#38bdf8', null, sway2 * 0.9);
  drawDrapedCable(ctx, 490, 476, 502, 478, 512, 520, 2.6, '#334155', null, sway2 * 1.1);

  // Cluster 3: Spilling over East Open Drop (x = 1018 .. 1045, y = 484 down to 528)
  const sway3 = Math.sin(t * 2.6 + 2.5) * 1.6;
  drawDrapedCable(ctx, 1018, 481, 1027, 484, 1032, 528, 3.6, '#475569', '#38bdf8', sway3);
  drawDrapedCable(ctx, 1026, 482, 1036, 484, 1044, 522, 2.8, '#1e293b', null, sway3 * 1.2);

  // Cluster 4: Spilling over Starboard Balcony Corner (x = 1475 .. 1500, y = 518 down to 534)
  const sway4 = Math.sin(t * 2.0 + 3.8) * 1.5;
  drawDrapedCable(ctx, 1472, 514, 1482, 518, 1488, 534, 2.4, '#ea580c', null, sway4);
  drawDrapedCable(ctx, 1478, 514, 1489, 518, 1496, 529, 2.2, '#0284c7', null, sway4 * 1.1);
}

function drawSparks(ctx) {
  if (state.quality === 'low') return;

  ctx.save();
  for (let i = 0; i < sparks.length; i++) {
    const s = sparks[i];
    if (s.life <= 0) continue;

    const lifeFrac = s.life / s.maxLife; // 1.0 (fresh) down to 0.0 (dead)
    const tailX = s.x - s.vx * 0.035;
    const tailY = s.y - s.vy * 0.035;

    // Glowing motion streak
    ctx.strokeStyle = `rgba(249, 115, 22, ${lifeFrac * 0.90})`;
    ctx.lineWidth = s.size * 1.3;
    ctx.beginPath();
    ctx.moveTo(tailX, tailY);
    ctx.lineTo(s.x, s.y);
    ctx.stroke();

    // Incandescent bloom halo on fresh hot sparks
    if (lifeFrac > 0.55 && state.quality !== 'low') {
      ctx.fillStyle = `rgba(251, 146, 60, ${(lifeFrac - 0.55) * 0.50})`;
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.size * 2.4, 0, Math.PI * 2);
      ctx.fill();
    }

    // Incandescent hot spark core
    ctx.fillStyle = `rgba(255, 255, 255, ${lifeFrac})`;
    ctx.fillRect(s.x - s.size * 0.5, s.y - s.size * 0.5, s.size, s.size);
  }
  ctx.restore();
}

const LOWER_TRUSS_SPANS = Object.freeze([
  { x0: 10, x1: 70 },
  { x0: 450, x1: 535 },
  { x0: 985, x1: 1065 },
  { x0: 1365, x1: 1420 },
  { x0: 1550, x1: 1595 }
]);

const LOWER_HATCH_XS = Object.freeze([40, 415, 500, 730, 870, 1035, 1390, 1575]);

const LOWER_THRUSTER_RECESSES = Object.freeze([
  { x0: 220, x1: 250, y: 528 },
  { x0: 1075, x1: 1105, y: 528 }
]);

let _trenchBgGrad = null;
let _underglowGrad = null;

function ensureLowerHullGradients(ctx) {
  if (!_trenchBgGrad) {
    _trenchBgGrad = ctx.createLinearGradient(0, 470, 0, 550);
    _trenchBgGrad.addColorStop(0.0, '#06090f');
    _trenchBgGrad.addColorStop(0.3, '#101622');
    _trenchBgGrad.addColorStop(1.0, '#04060a');

    _underglowGrad = ctx.createLinearGradient(0, 550, 0, 520);
    _underglowGrad.addColorStop(0.0, 'rgba(249, 115, 22, 0.38)');
    _underglowGrad.addColorStop(0.5, 'rgba(194, 65, 12, 0.16)');
    _underglowGrad.addColorStop(1.0, 'rgba(0, 0, 0, 0)');
  }
}

const STRATOS_BAY_SECTIONS = Object.freeze([
  { startX: 370,  endX: 445,  coreCol: SW.cyanPower },
  { startX: 465,  endX: 535,  coreCol: SW.amberGlow },
  { startX: 715,  endX: 745,  coreCol: SW.cyanPower },
  { startX: 855,  endX: 885,  coreCol: SW.amberGlow },
  { startX: 1000, endX: 1065, coreCol: SW.cyanPower },
  { startX: 1360, endX: 1420, coreCol: SW.amberGlow }
]);

function drawLowerHullDynamicOverlays(ctx, t, visibleLeft = 0, visibleRight = WIDTH) {
  ctx.save();

  // 1. Dynamic sub-surface repulsor glow pulse in bay chambers
  for (let b = 0; b < STRATOS_BAY_SECTIONS.length; b++) {
    const bay = STRATOS_BAY_SECTIONS[b];
    const bayW = bay.endX - bay.startX;
    if (bayW <= 20) continue;
    const bayMidX = (bay.startX + bay.endX) * 0.5;
    const chamberW = Math.min(80, bayW - 14);
    const chamberX = bayMidX - chamberW * 0.5;
    const pulse = 0.65 + 0.35 * Math.sin(t * 3.0 + b * 1.3);

    const coreGrad = ctx.createLinearGradient(0, 506, 0, 524);
    if (bay.coreCol === SW.cyanPower) {
      coreGrad.addColorStop(0.0, `rgba(6, 182, 212, ${pulse * 0.15})`);
      coreGrad.addColorStop(0.5, `rgba(56, 189, 248, ${pulse * 0.65})`);
      coreGrad.addColorStop(1.0, `rgba(6, 182, 212, ${pulse * 0.15})`);
    } else {
      coreGrad.addColorStop(0.0, `rgba(217, 119, 6, ${pulse * 0.15})`);
      coreGrad.addColorStop(0.5, `rgba(245, 158, 11, ${pulse * 0.75})`);
      coreGrad.addColorStop(1.0, `rgba(217, 119, 6, ${pulse * 0.15})`);
    }
    ctx.fillStyle = coreGrad;
    ctx.fillRect(chamberX, 506, chamberW, 18);
  }

  // 2. Dynamic navigation strobe flares on sponsons
  if (state.quality !== 'low') {
    const isStbStrobe = Math.floor((t * 2.0) % 2) === 0 && ((t * 8.0) % 1.0 < 0.22);
    if (isStbStrobe) {
      ctx.fillStyle = 'rgba(34, 197, 94, 0.65)';
      ctx.beginPath();
      ctx.arc(1529, 518, 10, 0, Math.PI * 2);
      ctx.fill();
    }

    const isPortStrobe = Math.floor((t * 2.0) % 2) === 0 && ((t * 8.0) % 1.0 < 0.22);
    if (isPortStrobe) {
      ctx.fillStyle = 'rgba(239, 68, 68, 0.65)';
      ctx.beginPath();
      ctx.arc(312, 523, 10, 0, Math.PI * 2);
      ctx.fill();
    }

    const isKeelStrobe = Math.floor((t * 2.0) % 2) === 0 && ((t * 8.0) % 1.0 < 0.22);
    if (isKeelStrobe) {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.75)';
      ctx.beginPath();
      ctx.arc(800, 544, 14, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // 3. Dynamic Draped Cable Bundles hanging over the edge
  drawDrapedCables(ctx, t);

  // 4. Active Welding & Electrical Sparks Shower
  drawSparks(ctx);

  ctx.restore();
}

function drawLowerHullWinglet(ctx, t, visibleLeft = 0, visibleRight = WIDTH, isStaticBake = false) {
  ctx.save();
  ensureLowerHullGradients(ctx);

  // 1. Recessed Engineering Trench Wall (Dark alloy substrate in deep shadow)
  // Perfectly traces between PERIMETER_SEGMENTS (top) and VENTRAL_BOTTOM (bottom)
  ctx.beginPath();
  ctx.moveTo(visibleLeft, 488);
  ctx.lineTo(0, 488);
  for (let s = 0; s < PERIMETER_SEGMENTS.length; s++) {
    const seg = PERIMETER_SEGMENTS[s];
    ctx.lineTo(seg.x1, seg.y1);
  }
  ctx.lineTo(visibleRight, 490);
  ctx.lineTo(visibleRight, 536);
  ctx.lineTo(WIDTH, 536);
  for (let b = VENTRAL_BOTTOM.length - 1; b >= 0; b--) {
    const vb = VENTRAL_BOTTOM[b];
    ctx.lineTo(vb.x1, vb.y1);
    ctx.lineTo(vb.x0, vb.y0);
  }
  ctx.lineTo(visibleLeft, 536);
  ctx.closePath();

  ctx.fillStyle = _trenchBgGrad;
  ctx.fill();

  // 2. Stirnseite Structural Paneling & Detail Lines (Breaks up the dark bulkhead monotone!)
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(visibleLeft, 488);
  ctx.lineTo(0, 488);
  for (let s = 0; s < PERIMETER_SEGMENTS.length; s++) {
    const seg = PERIMETER_SEGMENTS[s];
    ctx.lineTo(seg.x1, seg.y1);
  }
  ctx.lineTo(visibleRight, 490);
  ctx.lineTo(visibleRight, 536);
  ctx.lineTo(WIDTH, 536);
  for (let b = VENTRAL_BOTTOM.length - 1; b >= 0; b--) {
    const vb = VENTRAL_BOTTOM[b];
    ctx.lineTo(vb.x1, vb.y1);
    ctx.lineTo(vb.x0, vb.y0);
  }
  ctx.lineTo(visibleLeft, 536);
  ctx.closePath();
  ctx.clip();

  // A. Horizontal Structural Stringers / Plating Seams (3 crisp horizontal lines)
  const stringerYs = [498, 512, 526];
  for (let i = 0; i < stringerYs.length; i++) {
    const sy = stringerYs[i];
    // Dark groove line
    ctx.strokeStyle = '#04070d';
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.moveTo(visibleLeft, sy);
    ctx.lineTo(visibleRight, sy);
    ctx.stroke();

    // Subtle metallic specular glint line beneath the groove
    ctx.strokeStyle = 'rgba(148, 163, 184, 0.28)';
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(visibleLeft, sy + 0.8);
    ctx.lineTo(visibleRight, sy + 0.8);
    ctx.stroke();
  }

  // B. Vertical Bulkhead Armor Ribs & Expansion Seams (Every 36px)
  const startVx = Math.floor(visibleLeft / 36) * 36 + 18;
  for (let vx = startVx; vx < visibleRight; vx += 36) {
    const isWest = (vx < 800);
    // Dark vertical seam groove
    ctx.strokeStyle = '#050910';
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(vx, 470);
    ctx.lineTo(vx, 555);
    ctx.stroke();

    // Specular lit edge
    ctx.strokeStyle = isWest ? 'rgba(255, 245, 235, 0.18)' : 'rgba(148, 163, 184, 0.12)';
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(vx + 0.7, 470);
    ctx.lineTo(vx + 0.7, 555);
    ctx.stroke();

    // Fastener rivets at stringer intersections
    for (let i = 0; i < stringerYs.length; i++) {
      const sy = stringerYs[i];
      ctx.fillStyle = '#04060a';
      ctx.fillRect(vx - 1.2, sy - 1.2, 2.4, 2.4);
      ctx.fillStyle = isWest ? '#cbd5e1' : '#64748b';
      ctx.fillRect(vx - 0.7, sy - 0.7, 1.4, 1.4);
    }
  }

  // C. Diagonal Structural Bracing Lines (X-Trusses across open bulkhead spans)
  const trussSpans = LOWER_TRUSS_SPANS;
  ctx.strokeStyle = 'rgba(71, 85, 105, 0.30)';
  ctx.lineWidth = 1.0;
  for (let t = 0; t < trussSpans.length; t++) {
    const span = trussSpans[t];
    for (let bx = span.x0; bx < span.x1; bx += 24) {
      ctx.beginPath();
      ctx.moveTo(bx, 480);
      ctx.lineTo(bx + 20, 536);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(bx + 20, 480);
      ctx.lineTo(bx, 536);
      ctx.stroke();
    }
  }

  // D. Bulkhead Hydraulic & Conduit Hardlines (Continuous traversing lines)
  ctx.strokeStyle = '#05080e';
  ctx.lineWidth = 2.4;
  ctx.beginPath();
  ctx.moveTo(visibleLeft, 502);
  ctx.lineTo(visibleRight, 502);
  ctx.stroke();

  ctx.strokeStyle = '#475569';
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(visibleLeft, 502);
  ctx.lineTo(visibleRight, 502);
  ctx.stroke();

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.38)';
  ctx.lineWidth = 0.6;
  ctx.beginPath();
  ctx.moveTo(visibleLeft, 501.5);
  ctx.lineTo(visibleRight, 501.5);
  ctx.stroke();

  // Mounting bracket clamps every 44px
  const startPx = Math.floor(visibleLeft / 44) * 44 + 22;
  for (let px = startPx; px < visibleRight; px += 44) {
    ctx.fillStyle = '#0a0f18';
    ctx.fillRect(px - 1.5, 499.5, 3, 5);
    ctx.fillStyle = '#94a3b8';
    ctx.fillRect(px - 0.8, 500.5, 1.6, 3);
  }

  // E. Inspection Hatches & Service Louver Outlines
  const hatchXs = LOWER_HATCH_XS;
  for (let h = 0; h < hatchXs.length; h++) {
    const hx = hatchXs[h];
    const hy = 506;
    ctx.fillStyle = '#080d15';
    ctx.fillRect(hx - 10, hy - 6, 20, 12);
    ctx.strokeStyle = '#273447';
    ctx.lineWidth = 0.9;
    ctx.strokeRect(hx - 10, hy - 6, 20, 12);

    ctx.fillStyle = '#64748b';
    ctx.fillRect(hx - 8.5, hy - 4.5, 1.4, 1.4);
    ctx.fillRect(hx + 7.1, hy - 4.5, 1.4, 1.4);
    ctx.fillRect(hx - 8.5, hy + 3.1, 1.4, 1.4);
    ctx.fillRect(hx + 7.1, hy + 3.1, 1.4, 1.4);

    ctx.fillStyle = (h % 2 === 0) ? 'rgba(56, 189, 248, 0.65)' : 'rgba(245, 158, 11, 0.65)';
    ctx.fillRect(hx - 1, hy - 1, 2, 2);
  }

  ctx.restore();

  // 3. Atmospheric Ventral Abyss Underglow (Warm sunset reflection from clouds 10,000 ft below)
  // Fills upward from the sculpted ventral keel
  ctx.beginPath();
  if (visibleLeft < 0) {
    ctx.moveTo(visibleLeft, 536);
    ctx.lineTo(0, 536);
  }
  for (let b = 0; b < VENTRAL_BOTTOM.length; b++) {
    const vb = VENTRAL_BOTTOM[b];
    if (b === 0 && visibleLeft >= 0) ctx.moveTo(vb.x0, vb.y0);
    ctx.lineTo(vb.x1, vb.y1);
  }
  if (visibleRight > WIDTH) {
    ctx.lineTo(visibleRight, 536);
    ctx.lineTo(visibleRight, 518);
    ctx.lineTo(WIDTH, 518);
  }
  for (let b = VENTRAL_BOTTOM.length - 1; b >= 0; b--) {
    const vb = VENTRAL_BOTTOM[b];
    ctx.lineTo(vb.x1, vb.y1 - 18);
    ctx.lineTo(vb.x0, vb.y0 - 18);
  }
  if (visibleLeft < 0) {
    ctx.lineTo(visibleLeft, 518);
  }
  ctx.closePath();

  ctx.fillStyle = _underglowGrad;
  ctx.fill();

  // 3. Ventral Thruster Recesses (Warm glowing sky visible under the hull!)
  const thrusterRecesses = LOWER_THRUSTER_RECESSES;
  for (let tr = 0; tr < thrusterRecesses.length; tr++) {
    const thr = thrusterRecesses[tr];
    const thrGlow = ctx.createLinearGradient(0, thr.y, 0, thr.y + 12);
    thrGlow.addColorStop(0.0, 'rgba(251, 146, 60, 0.45)');
    thrGlow.addColorStop(1.0, 'rgba(249, 115, 22, 0.10)');
    ctx.fillStyle = thrGlow;
    ctx.fillRect(thr.x0, thr.y, thr.x1 - thr.x0, 10);

    // Heat nozzle heat sinks
    ctx.fillStyle = '#0a0f18';
    ctx.fillRect(thr.x0 + 4, thr.y, 4, 6);
    ctx.fillRect(thr.x0 + 12, thr.y, 4, 6);
    ctx.fillRect(thr.x0 + 20, thr.y, 4, 6);
  }

  // 4. Underlying Cantilever Trusses beneath Balconies & Sponsons
  // A. Trusses beneath Main Observation Balcony (x = 585 .. 715)
  const mainBalconyTrussXs = [610, 640, 670, 695];
  for (let b = 0; b < mainBalconyTrussXs.length; b++) {
    const tx = mainBalconyTrussXs[b];
    ctx.fillStyle = '#0f141d';
    ctx.beginPath();
    ctx.moveTo(tx - 4, 528);
    ctx.lineTo(tx + 4, 528);
    ctx.lineTo(tx + 1, 544);
    ctx.lineTo(tx - 1, 544);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#273244';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(tx - 3, 528);
    ctx.lineTo(tx - 0.5, 544);
    ctx.stroke();

    ctx.strokeStyle = '#475569';
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(tx - 3, 528);
    ctx.lineTo(tx + 3, 528);
    ctx.stroke();
  }

  // B. Trusses beneath Starboard Balcony (x = 1420 .. 1555)
  const stbBalconyTrussXs = [1450, 1485, 1520];
  for (let b = 0; b < stbBalconyTrussXs.length; b++) {
    const tx = stbBalconyTrussXs[b];
    ctx.fillStyle = '#0f141d';
    ctx.beginPath();
    ctx.moveTo(tx - 3.5, 516);
    ctx.lineTo(tx + 3.5, 516);
    ctx.lineTo(tx + 1, 534);
    ctx.lineTo(tx - 1, 534);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#273244';
    ctx.lineWidth = 1.0;
    ctx.beginPath();
    ctx.moveTo(tx - 2.5, 516);
    ctx.lineTo(tx - 0.5, 534);
    ctx.stroke();
  }

  // C. Hydraulic Ram Struts beneath West Sponson (x = 255 .. 370)
  const westSponsonStruts = [285, 315, 345];
  for (let s = 0; s < westSponsonStruts.length; s++) {
    const sx = westSponsonStruts[s];
    ctx.fillStyle = '#0d131c';
    ctx.fillRect(sx - 3, 524, 6, 18);
    ctx.fillStyle = '#334155';
    ctx.fillRect(sx - 1.5, 524, 3, 18);
    ctx.fillStyle = '#cbd5e1';
    ctx.fillRect(sx - 0.8, 524, 1.2, 18);
  }

  // D. Hydraulic Ram Struts beneath East Sponson (x = 1245 .. 1360)
  const eastSponsonStruts = [1275, 1305, 1335];
  for (let s = 0; s < eastSponsonStruts.length; s++) {
    const sx = eastSponsonStruts[s];
    ctx.fillStyle = '#0d131c';
    ctx.fillRect(sx - 3, 522, 6, 20);
    ctx.fillStyle = '#334155';
    ctx.fillRect(sx - 1.5, 522, 3, 20);
    ctx.fillStyle = '#94a3b8';
    ctx.fillRect(sx - 0.8, 522, 1.2, 20);
  }

  // 5. Recessed Engineering Machinery & Repulsor Chambers in Trench Bays
  const baySections = STRATOS_BAY_SECTIONS;

  for (let b = 0; b < baySections.length; b++) {
    const bay = baySections[b];
    const bayW = bay.endX - bay.startX;
    if (bayW <= 20) continue;
    const bayMidX = (bay.startX + bay.endX) * 0.5;

    // Sub-surface Repulsor Glow
    const chamberW = Math.min(80, bayW - 14);
    const chamberX = bayMidX - chamberW * 0.5;
    const pulse = 0.65 + 0.35 * Math.sin(t * 3.0 + b * 1.3);

    const coreGrad = ctx.createLinearGradient(0, 506, 0, 524);
    if (bay.coreCol === SW.cyanPower) {
      coreGrad.addColorStop(0.0, `rgba(6, 182, 212, ${pulse * 0.15})`);
      coreGrad.addColorStop(0.5, `rgba(56, 189, 248, ${pulse * 0.65})`);
      coreGrad.addColorStop(1.0, `rgba(6, 182, 212, ${pulse * 0.15})`);
    } else {
      coreGrad.addColorStop(0.0, `rgba(217, 119, 6, ${pulse * 0.15})`);
      coreGrad.addColorStop(0.5, `rgba(245, 158, 11, ${pulse * 0.75})`);
      coreGrad.addColorStop(1.0, `rgba(217, 119, 6, ${pulse * 0.15})`);
    }
    ctx.fillStyle = coreGrad;
    ctx.fillRect(chamberX, 506, chamberW, 18);

    // Radiator Cooling Vanes
    const vaneCount = Math.floor(chamberW / 14);
    for (let v = 1; v <= vaneCount; v++) {
      const vx = chamberX + (v / (vaneCount + 1)) * chamberW;
      ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
      ctx.fillRect(vx - 2, 506, 4.5, 18);
      ctx.fillStyle = '#222936';
      ctx.fillRect(vx - 1, 505, 2, 20);
      ctx.fillStyle = '#64748b';
      ctx.fillRect(vx - 1, 505, 0.7, 20);
    }

    // Heavy High-Pressure Hydraulic Pipe Line (y = 527)
    const pipeY = 527;
    ctx.fillStyle = '#020306';
    ctx.fillRect(bay.startX, pipeY + 3.0, bayW, 2.0);
    const pipeGrad = ctx.createLinearGradient(0, pipeY - 3, 0, pipeY + 3);
    pipeGrad.addColorStop(0.0, '#333e50');
    pipeGrad.addColorStop(0.3, '#718096');
    pipeGrad.addColorStop(0.7, '#cbd5e1');
    pipeGrad.addColorStop(1.0, '#131924');
    ctx.fillStyle = pipeGrad;
    ctx.fillRect(bay.startX, pipeY - 3, bayW, 6);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(bay.startX, pipeY - 2.5, bayW, 1.0);
  }

  // 6. The Articulated Front Deck & Walkway Platforms (Non-straight, dynamic 3D sculpted profile!)
  drawArticulatedEdgePlates(ctx, t, visibleLeft, visibleRight, isStaticBake);

  // 7. Industrial Safety Railings vs. Open Drop-Offs ("da gar kein geländer")
  drawSafetyRailingsAndOpenDrops(ctx, t, visibleLeft, visibleRight);

  if (!isStaticBake) {
    // 8. Heavy Draped Cable Bundles hanging over the edge
    drawDrapedCables(ctx, t);

    // 9. Active Welding & Electrical Sparks Shower
    drawSparks(ctx);
  }

  ctx.restore();
}

// ---------------------------------------------------------------------------
// ATMOSPHERIC SLIPSTREAM PARTICLES
// ---------------------------------------------------------------------------

function drawSlipstreamParticles(ctx) {
  if (state.quality === 'low') return;

  ctx.save();
  const qScale = qualityScale();
  for (let i = 0; i < particles.length; i++) {
    const p = particles[i];
    if (p.type === 'vapor') {
      ctx.strokeStyle = '#fff1f2';
      ctx.globalAlpha = Math.min(1.0, p.alpha * qScale);
      ctx.lineWidth = p.width;
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
      ctx.lineTo(p.x + p.len, p.y);
      ctx.stroke();
    } else {
      ctx.fillStyle = '#fb923c';
      ctx.globalAlpha = Math.min(1.0, p.alpha * qScale * 1.5);
      ctx.fillRect(p.x, p.y, p.len * 0.25, 1.5);
    }
  }
  ctx.restore();
}

// ---------------------------------------------------------------------------
// LANE GUIDES & HITBOX OVERLAYS
// ---------------------------------------------------------------------------

function drawGuides(ctx) {
  if (!state.showGuides) return;

  ctx.save();
  ctx.setLineDash([8, 6]);
  ctx.lineWidth = 1.5;

  ctx.strokeStyle = '#38bdf8';
  ctx.beginPath();
  ctx.moveTo(0, LANE_Y);
  ctx.lineTo(WIDTH, LANE_Y);
  ctx.stroke();

  ctx.strokeStyle = '#38bdf8';
  ctx.beginPath();
  ctx.moveTo(0, LANE_BOTTOM);
  ctx.lineTo(WIDTH, LANE_BOTTOM);
  ctx.stroke();

  ctx.strokeStyle = '#f43f5e';
  ctx.beginPath();
  ctx.moveTo(0, LANE_Y + LANE_H / 2);
  ctx.lineTo(WIDTH, LANE_Y + LANE_H / 2);
  ctx.stroke();

  // Base Bounding Guides (Standard Engine Hitbox Boxes)
  ctx.strokeStyle = 'rgba(34, 197, 94, 0.45)';
  ctx.strokeRect(60, 220, 160, 252);
  ctx.strokeRect(1380, 220, 160, 252);

  ctx.setLineDash([]);
  ctx.font = '11px ui-monospace, SFMono-Regular, monospace';
  ctx.fillStyle = '#38bdf8';
  ctx.fillText('LANE_Y: 176px (Battlefield Top)', 18, LANE_Y - 6);
  ctx.fillText('LANE_BOTTOM: 496px (Deck Shelf)', 18, LANE_BOTTOM + 15);
  ctx.fillStyle = '#f43f5e';
  ctx.fillText('LANE_CENTER: 336px', WIDTH / 2 - 60, LANE_Y + LANE_H / 2 - 6);
  ctx.fillStyle = '#22c55e';
  ctx.fillText('WEST BASE HITBOX [60..220]', 60, 214);
  ctx.fillText('EAST BASE HITBOX [1380..1540]', 1380, 214);

  ctx.restore();
}

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// STRATOS ENGINE INTEGRATION & MIXIN CONTRACT
// ---------------------------------------------------------------------------

function resolveBounds(renderer, customBounds) {
  const bounds = customBounds || (renderer && renderer.viewport ? renderer.viewport.getVisibleBounds() : null);
  const rHeight = (renderer && typeof renderer.height === 'number') ? renderer.height : HEIGHT;
  const rWidth = (renderer && typeof renderer.width === 'number') ? renderer.width : WIDTH;
  const padL = (renderer && typeof renderer._staticBgPadLeft === 'number') ? renderer._staticBgPadLeft : 40;
  const padR = (renderer && typeof renderer._staticBgPadRight === 'number') ? renderer._staticBgPadRight : 40;
  const rawTop = bounds ? (typeof bounds.top === 'number' ? bounds.top : (typeof bounds.visibleTop === 'number' ? bounds.visibleTop : -40)) : -40;
  const rawBottom = bounds ? (typeof bounds.bottom === 'number' ? bounds.bottom : (typeof bounds.visibleBottom === 'number' ? bounds.visibleBottom : (rHeight + 40))) : (rHeight + 40);
  const rawLeft = bounds ? (typeof bounds.left === 'number' ? bounds.left : (typeof bounds.visibleLeft === 'number' ? bounds.visibleLeft : -padL)) : -padL;
  const rawRight = bounds ? (typeof bounds.right === 'number' ? bounds.right : (typeof bounds.visibleRight === 'number' ? bounds.visibleRight : (rWidth + padR))) : (rWidth + padR);
  return {
    visibleTop: customBounds ? rawTop : Math.min(0, rawTop - 20),
    visibleBottom: customBounds ? rawBottom : Math.max(rHeight, rawBottom + 20),
    visibleLeft: customBounds ? rawLeft : Math.min(-padL, rawLeft - 20),
    visibleRight: customBounds ? rawRight : Math.max(rWidth + padR, rawRight + 20)
  };
}

let currentMassifSeed = null;
function ensureMassifs(seed) {
  if (currentMassifSeed !== seed || farMassifs.length === 0) {
    currentMassifSeed = seed;
    generateAlpineMassifs(seed);
  }
}

let lastWeatherT = -1;

function evaluateWeather(t) {
  state.t = t;
  const cTime = ((t % weatherState.cycleDuration) + weatherState.cycleDuration) % weatherState.cycleDuration;
  let targetCover = 0.20;
  let targetDark = 0.0;

  if (weatherState.mode === 'auto') {
    if (cTime < 45.0) {
      targetCover = 0.18 + 0.08 * Math.sin(cTime * 0.15);
      targetDark = 0.0;
    } else if (cTime < 70.0) {
      const p = (cTime - 45.0) / 25.0;
      targetCover = 0.26 + 0.34 * p;
      targetDark = 0.18 * p;
    } else if (cTime < 92.0) {
      const p = (cTime - 70.0) / 22.0;
      const smoothP = p * p * (3 - 2 * p);
      targetCover = 0.60 + 0.40 * smoothP;
      targetDark = 0.18 + 0.82 * smoothP;
    } else if (cTime < 116.0) {
      targetCover = 1.0;
      targetDark = 1.0;
    } else {
      const p = (cTime - 116.0) / 14.0;
      const smoothP = p * p * (3 - 2 * p);
      targetCover = 1.0 - 0.78 * smoothP;
      targetDark = 1.0 - 1.0 * smoothP;
    }
  } else if (weatherState.mode === 'storm') {
    targetCover = 1.0;
    targetDark = 1.0;
  } else if (weatherState.mode === 'cirrus') {
    targetCover = 0.45;
    targetDark = 0.18;
  } else if (weatherState.mode === 'clear') {
    targetCover = 0.06;
    targetDark = 0.0;
  }

  const dt = lastWeatherT < 0 ? 0 : (t - lastWeatherT);
  lastWeatherT = t;
  if (dt <= 0 || dt > 0.5) {
    weatherState.cloudCover = targetCover;
    weatherState.stormDarkness = targetDark;
  } else {
    const easeRate = Math.min(1.0, dt * 1.8);
    weatherState.cloudCover += (targetCover - weatherState.cloudCover) * easeRate;
    weatherState.stormDarkness += (targetDark - weatherState.stormDarkness) * easeRate;
  }

  // Periodic lightning flashes during storm (cTime in 92..116)
  if (cTime >= 92.0 && cTime < 116.0) {
    const flashTimes = [96.0, 103.0, 110.0];
    const flashXs = [720, 890, 610];
    let activeFlash = null;
    for (let f = 0; f < flashTimes.length; f++) {
      const fTime = flashTimes[f];
      const tau = cTime - fTime;
      if (tau >= 0 && tau < 1.35) {
        activeFlash = { tau, x: flashXs[f], y: 65 };
        break;
      }
    }
    if (activeFlash) {
      const tau = activeFlash.tau;
      weatherState.flashX = activeFlash.x;
      weatherState.flashY = activeFlash.y;
      weatherState.flashStartTime = t - tau;
      if (tau < 0.07) {
        weatherState.lightningIntensity = Math.sin((tau / 0.07) * (Math.PI * 0.5)) * 0.88;
      } else if (tau < 0.12) {
        weatherState.lightningIntensity = 0.22 + 0.10 * Math.sin(((tau - 0.07) / 0.05) * Math.PI);
      } else if (tau < 0.22) {
        const p = (tau - 0.12) / 0.10;
        weatherState.lightningIntensity = 0.30 + 0.70 * Math.sin(p * Math.PI);
      } else {
        const elapsed = tau - 0.22;
        const decay = Math.exp(-3.4 * elapsed);
        const flicker = 1.0 + 0.12 * Math.sin(elapsed * 42.0);
        weatherState.lightningIntensity = Math.max(0, 0.65 * decay * flicker);
      }
    } else {
      weatherState.lightningIntensity = 0.0;
    }
  } else {
    weatherState.lightningIntensity = 0.0;
  }
}

export function setStratosWeather(mode) {
  if (mode === 'storm' || mode === 'thunderstorm') weatherState.mode = 'storm';
  else if (mode === 'cirrus') weatherState.mode = 'cirrus';
  else if (mode === 'clear') weatherState.mode = 'clear';
  else weatherState.mode = 'auto';
}

export function releaseStratosCaches() {
  if (_staticDeckBaseCanvas) {
    _staticDeckBaseCanvas.width = 0;
    _staticDeckBaseCanvas.height = 0;
    _staticDeckBaseCanvas = null;
  }
  if (_staticUpperHullCanvas) {
    _staticUpperHullCanvas.width = 0;
    _staticUpperHullCanvas.height = 0;
    _staticUpperHullCanvas = null;
  }
  if (_staticSkyCanvas) {
    _staticSkyCanvas.width = 0;
    _staticSkyCanvas.height = 0;
    _staticSkyCanvas = null;
  }
  if (_cloudDomeNormalCanvas) {
    _cloudDomeNormalCanvas.width = 0;
    _cloudDomeNormalCanvas.height = 0;
    _cloudDomeNormalCanvas = null;
  }
  if (_cloudDomeFlashCanvas) {
    _cloudDomeFlashCanvas.width = 0;
    _cloudDomeFlashCanvas.height = 0;
    _cloudDomeFlashCanvas = null;
  }
  _staticDeckBaseKey = null;
  _staticUpperHullKey = null;
}

export const stratosBackgroundMixin = {
  setStratosWeather(mode) {
    setStratosWeather(mode);
  },

  renderStratosStaticBackground(ctx, battlefield, customBounds = null) {
    const { visibleTop, visibleBottom, visibleLeft, visibleRight } = resolveBounds(this, customBounds);
    const bgWidth = visibleRight - visibleLeft;
    const bgHeight = visibleBottom - visibleTop;

    state.visibleLeft = visibleLeft;
    state.visibleRight = visibleRight;
    state.quality = this.quality || 'high';
    const seed = (battlefield && typeof battlefield.mapSeed === 'number') ? battlefield.mapSeed : 904;
    state.seed = seed;
    ensureMassifs(seed);

    if (!this._stratosLayout || this._stratosLayout.seed !== seed) {
      this._stratosLayout = {
        seed,
        farMassifs,
        midMassifs,
        nearMassifs
      };
    }

    ctx.save();

    // 0. Solid Base Fill: Deep dark space base across the entire world-space bounding box
    // Guarantees NO transparent pixels exist behind the arena or below the hull edge
    // (prevents multiply color grading from turning transparent areas white/gray)
    ctx.fillStyle = '#070b12';
    ctx.fillRect(visibleLeft, visibleTop, bgWidth, bgHeight);

    // Horizon-Clip (y <= 226): sky, sun, and background mountains are clipped behind the deck
    // so the colossal sun (radius 600 at y=246) can never peek out underneath the hull/deck edge!
    ctx.save();
    ctx.beginPath();
    ctx.rect(visibleLeft, visibleTop, bgWidth, Math.max(0, 226 - visibleTop));
    ctx.clip();

    drawSky(ctx, visibleLeft, visibleRight, visibleTop);

    // Static sun baseline
    drawGiantSun(ctx, 0);

    // Static mountains baseline
    drawAllMountainMassifs(ctx, 0);

    ctx.restore(); // end horizon-clip

    // Static arena deck
    drawHighDetailArenaDeck(ctx, 0, visibleLeft, visibleRight);

    // Static upper hull rim
    drawUpperHullRim(ctx, 0, visibleLeft, visibleRight);

    ctx.restore();
  },

  renderStratosDynamicBackground(ctx, battlefield, nowSec = null, customBounds = null) {
    const { visibleTop, visibleBottom, visibleLeft, visibleRight } = resolveBounds(this, customBounds);
    const bgWidth = visibleRight - visibleLeft;
    const bgHeight = visibleBottom - visibleTop;

    state.visibleLeft = visibleLeft;
    state.visibleRight = visibleRight;
    const t = nowSec !== null ? nowSec : (battlefield?.time || (typeof performance !== 'undefined' ? performance.now() * 0.001 : 0));
    state.t = t;
    state.quality = this.quality || 'high';
    const seed = (battlefield && typeof battlefield.mapSeed === 'number') ? battlefield.mapSeed : 904;
    state.seed = seed;
    ensureMassifs(seed);

    if (!this._stratosLayout || this._stratosLayout.seed !== seed) {
      this._stratosLayout = {
        seed,
        farMassifs,
        midMassifs,
        nearMassifs
      };
    }

    evaluateWeather(t);

    ctx.save();

    // 0. Solid Base Fill: Deep dark space base across the entire world-space bounding box
    // Guarantees NO transparent pixels exist behind the arena or below the hull edge
    // (prevents multiply color grading from turning transparent areas white/gray)
    ctx.fillStyle = '#070b12';
    ctx.fillRect(visibleLeft, visibleTop, bgWidth, bgHeight);

    // Horizon-Clip (y <= 226): sky, sun, clouds, haze, sunshafts and mountains are clipped behind the deck
    // so the colossal sun (radius 600 at y=246) can never peek out underneath the hull/deck edge!
    ctx.save();
    ctx.beginPath();
    ctx.rect(visibleLeft, visibleTop, bgWidth, Math.max(0, 226 - visibleTop));
    ctx.clip();

    // Layer 0: Sky
    drawSky(ctx, visibleLeft, visibleRight, visibleTop);

    // Layer 1: Giant Sun
    drawGiantSun(ctx, t);

    // Layer 1.5: Atmospheric Clouds (Cirrus veils & Storm front massifs)
    drawAtmosphericClouds(ctx, t);

    // Layer 2: Thermal Heat Shimmer at horizon
    drawHeatHaze(ctx, t);

    // Layer 2.5: Volumetric Sunlight Shafts Behind Mountains
    drawVolumetricSunshafts(ctx, t);

    // Layer 3: Colossal Towering Mountains (Slicing across sun with 3-tier parallax & inter-mountain clouds)
    drawAllMountainMassifs(ctx, t);

    ctx.restore(); // end horizon-clip

    // Layer 4: High-Detail Arena Deck (Pre-baked static deck base + dynamic raceways & catapult shuttle)
    drawHighDetailArenaDeck(ctx, t, visibleLeft, visibleRight);

    // Layer 5: Upper Hull Perimeter & Skeletal Cutouts + Radar & Turrets with 2.5D Barbette Sockeln
    drawUpperHullRim(ctx, t, visibleLeft, visibleRight);

    // Layer 5.5: Stratos Pigeon 1 on upper antenna egg
    drawStratosPigeon(ctx, t);

    // Layer 6.8: Atmospheric Nocturnal Nightlight & Wetterleuchten Arena Illumination
    drawAtmosphericNightlightAmbient(ctx, t, visibleLeft, visibleRight, visibleTop, visibleBottom);

    // Layer 6.85: Deck Stripes Gaussian Halation Pass in dark/storm conditions
    drawDeckStripesGaussianHalation(ctx, t, visibleLeft, visibleRight);

    ctx.restore();
  },

  renderStratosWeatherBackground(ctx, battlefield) {
    // Inter-mountain clouds and sunshaft depth layers are drawn in dynamic background
  },

  renderStratosStaticForeground(ctx, _battlefield, _customBounds = null) {
    const { visibleBottom, visibleLeft, visibleRight } = resolveBounds(this, _customBounds);
    const bgWidth = visibleRight - visibleLeft;

    state.visibleLeft = visibleLeft;
    state.visibleRight = visibleRight;

    ctx.save();
    drawLowerHullWinglet(ctx, 0, visibleLeft, visibleRight, true);

    // Option A: Smooth letterbox fade below keel into game background #070b12
    if (visibleBottom > 530) {
      const vTop = 530;
      const vBottom = Math.max(vTop + 1, Math.min(visibleBottom, 555));
      const veil = ctx.createLinearGradient(0, vTop, 0, vBottom);
      veil.addColorStop(0, 'rgba(7, 11, 18, 0)');
      veil.addColorStop(1, 'rgba(7, 11, 18, 1)');
      ctx.fillStyle = veil;
      ctx.fillRect(visibleLeft, vTop, bgWidth, vBottom - vTop);
      if (visibleBottom > vBottom) {
        ctx.fillStyle = '#070b12';
        ctx.fillRect(visibleLeft, vBottom, bgWidth, visibleBottom - vBottom);
      }
    }

    ctx.restore();
  },

  renderStratosDynamicForeground(ctx, battlefield, nowSec = null) {
    const { visibleBottom, visibleLeft, visibleRight } = resolveBounds(this);
    const bgWidth = visibleRight - visibleLeft;

    state.visibleLeft = visibleLeft;
    state.visibleRight = visibleRight;

    const t = nowSec !== null ? nowSec : (battlefield?.time || (typeof performance !== 'undefined' ? performance.now() * 0.001 : 0));
    state.t = t;
    state.quality = this.quality || 'high';

    ctx.save();
    // Layer 6: Lower Hull Winglet & Lower Cutouts (y: 496 .. 540)
    // Sponson lights, articulated edge plates, safety railings, draped cables, welding sparks
    drawLowerHullDynamicOverlays(ctx, t, visibleLeft, visibleRight);

    // Layer 7: Atmospheric Slipstream Particles
    drawSlipstreamParticles(ctx);

    // Option A: Smooth letterbox fade below keel into game background #070b12
    if (visibleBottom > 530) {
      const vTop = 530;
      const vBottom = Math.max(vTop + 1, Math.min(visibleBottom, 555));
      const veil = ctx.createLinearGradient(0, vTop, 0, vBottom);
      veil.addColorStop(0, 'rgba(7, 11, 18, 0)');
      veil.addColorStop(1, 'rgba(7, 11, 18, 1)');
      ctx.fillStyle = veil;
      ctx.fillRect(visibleLeft, vTop, bgWidth, vBottom - vTop);
      if (visibleBottom > vBottom) {
        ctx.fillStyle = '#070b12';
        ctx.fillRect(visibleLeft, vBottom, bgWidth, visibleBottom - vBottom);
      }
    }

    ctx.restore();
  },

  renderStratosForegroundCritters(ctx, battlefield, nowSec = null) {
    const t = nowSec !== null ? nowSec : (battlefield?.time || (typeof performance !== 'undefined' ? performance.now() * 0.001 : 0));
    state.t = t;
    state.quality = this.quality || 'high';

    ctx.save();
    // Layer 6.5: Foreground Railing Pigeon Easter Egg ("Die Geländer-Taube" on lower railing)
    drawForegroundPigeon(ctx, t);
    ctx.restore();
  },

  renderStratosWeather(ctx, battlefield) {
    if (this.quality === 'low') return;
    const t = state.t;
    const lightning = weatherState.lightningIntensity;

    ctx.save();
    // Layer 6.9: Atmospheric Ionization Micro-Discharges (St. Elmo's Sparks & Air Crackles in the air)
    drawAtmosphericIonizationSparks(ctx, t);

    // Active sheet lightning foreground flash across entire viewport if lightning active
    if (lightning > 0.01) {
      const { visibleTop, visibleBottom, visibleLeft, visibleRight } = resolveBounds(this);
      ctx.fillStyle = `rgba(254, 243, 199, ${0.12 * lightning})`;
      ctx.fillRect(visibleLeft, visibleTop, visibleRight - visibleLeft, visibleBottom - visibleTop);
    }

    ctx.restore();
  }
};

// Initialize procedural particle pools and default massifs
initParticles();
ensureMassifs(904);
